<template>
    <div>
        <!-- Full-screen loading overlay -->
        <div v-if="busy" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 100000;">
            <div style="background-color: white; border-radius: 8px; padding: 30px; text-align: center; max-width: 90%; width: 400px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);">
                <div style="margin-bottom: 20px;">
                    <div style="display: inline-block; width: 60px; height: 60px; border: 5px solid rgba(0, 123, 255, 0.2); border-radius: 50%; border-top-color: #007bff; animation: spin 1s ease-in-out infinite;"></div>
                </div>
                <h3 style="font-size: 24px; margin-bottom: 15px; color: #333;" v-if="busyAction === 'clearing'">Clearing Knowledge Base</h3>
                <h3 style="font-size: 24px; margin-bottom: 15px; color: #333;" v-else>Updating Knowledge Base</h3>
                <p style="font-size: 16px; color: #666; margin-bottom: 0;" v-if="busyAction === 'clearing'">Please wait while we clear your TeamIO knowledge base...</p>
                <p style="font-size: 16px; color: #666; margin-bottom: 0;" v-else>Please wait while we index your TeamIO documentation...</p>
            </div>
        </div>

        <div v-if="!allowedToSee">You do not have proper authorization to view this page</div>
        <div v-else>
                <h2 class="page-title">
                    Super Admin
                </h2>
                <ErrorDisplay/>

            <!-- Page Loading Spinner -->
            <div v-if="pageLoading" class="text-center py-5">
                <div class="d-inline-block" style="width: 50px; height: 50px; border: 4px solid rgba(0, 123, 255, 0.2); border-radius: 50%; border-top-color: #007bff; animation: spin 1s ease-in-out infinite;"></div>
                <p class="mt-3 text-muted">Loading Super Admin data...</p>
            </div>

            <div v-else class="mb-5">
                <b-button variant="dark" class="btn" @click="startCustomerAdd">
                    <i class="fa fa-plus text-success mr-xs mb-xs" /> Add a new Customer
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showSQSRedrive=true">
                    <i class="fa fa-plus text-success mr-xs mb-xs" /> SQS Redrive
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showBulkFormCopy=true">
                    <i class="fa fa-copy text-info mr-xs mb-xs" /> Bulk Copy Forms
                </b-button>

                <b-button variant="dark" class="btn ml-5" data-test="open-section-copy" @click="showSectionCopy=true">
                    <i class="fa fa-clone text-info mr-xs mb-xs" /> Copy Form Section
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showBulkWikiCopy=true">
                    <i class="fa fa-book text-primary mr-xs mb-xs" /> Bulk Copy Wiki Pages
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showPdfImport=true">
                    <i class="fa fa-file-pdf text-danger mr-xs mb-xs" /> Import PDF to Forms
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="openCacheManagement">
                    <i class="fa fa-database text-warning mr-xs mb-xs" /> Cache Management
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="openTeamioKnowledge">
                    <i class="fa fa-brain text-purple mr-xs mb-xs" /> TeamIO Knowledge Base
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="openTwilioDashboard">
                    <i class="fa fa-phone-alt text-info mr-xs mb-xs" /> Twilio Dashboard
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showInsuranceChecks = !showInsuranceChecks">
                    <i class="fa fa-check-circle text-info mr-xs mb-xs" /> Insurance Checks
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showNudgeManager = !showNudgeManager">
                    <i class="fa fa-lightbulb-o text-success mr-xs mb-xs" /> Feature Nudges
                </b-button>

                <b-button v-if="whatsAppTechProviderEnabled" variant="dark" class="btn ml-5" @click="showWhatsAppOnboarding = !showWhatsAppOnboarding">
                    <i class="fa fa-whatsapp text-success mr-xs mb-xs" /> WhatsApp Onboarding
                </b-button>

                <b-button variant="dark" class="btn ml-5" @click="showMessagingSafety = !showMessagingSafety">
                    <i class="fa fa-shield text-danger mr-xs mb-xs" /> Messaging Safety
                </b-button>

                <b-button variant="dark" class="btn ml-5" data-test="open-customer-health" @click="showCustomerHealth = !showCustomerHealth">
                    <i class="fa fa-heartbeat text-danger mr-xs mb-xs" /> Customer Health
                </b-button>

                <!-- Nudge Manager -->
                <div class="mt-5" v-if="showNudgeManager">
                    <NudgeManager />
                </div>

                <!-- WhatsApp Tech Provider Onboarding -->
                <div class="mt-5" v-if="whatsAppTechProviderEnabled && showWhatsAppOnboarding">
                    <WhatsAppOnboarding />
                </div>

                <!-- Messaging Safety (kill switches) -->
                <div class="mt-5" v-if="showMessagingSafety">
                    <MessagingSafety />
                </div>

                <!-- Customer Health (the report is read-only; the server checks the Super Admin allowlist).
                     Each row's "Email" button opens the wording editor: it can only SAVE words, nothing sends. -->
                <div class="mt-5" v-if="showCustomerHealth">
                    <CustomerHealth>
                        <template v-slot:row-actions="{ site }">
                            <OutreachEmailEditor :site="site" />
                        </template>
                    </CustomerHealth>
                </div>

                <!-- NEW MULTI-OFFICE ADD CUSTOMER FORM -->
                <div class="mt-5" v-if="showCustomerAdd">
                    <h3 class="mb-4">Add New Customer</h3>
                    
                    <!-- SECTION 1: Customer Information (Global) -->
                    <b-card header="Customer Information" class="mb-4">
                        <b-input-group prepend="Customer Name" class="mb-3"> 
                            <b-form-input 
                                type="text" 
                                v-model="customerName" 
                                placeholder="e.g., Mint Dental"
                                trim
                            />           
                        </b-input-group>
                        
                        <b-input-group prepend="Logo URL" class="mb-3"> 
                            <b-form-input 
                                type="text" 
                                v-model="logoUrl" 
                                placeholder="https://..."
                                trim
                            />           
                        </b-input-group>

                        <!-- Live preview + sanity check of the Logo URL. A practice's LogoUrl once pointed at the
                             doctor's 800x800 headshot for three weeks, and two others at their HOMEPAGE (an HTML page),
                             because nobody saw the image at entry time (2026-08-27). Definite failures (not an image /
                             a web page) block Create; square / huge / photo-like images only warn. Logic lives in
                             src/utils/logoUrlCheck.js; the preview <img> is capped like every other logo surface. -->
                        <div v-if="logoUrl" class="logo-check mb-3" :class="'logo-check--' + createLogoCheck.level">
                            <img class="logo-check-img" :src="logoUrl" alt="Logo preview" v-show="createLogoCheck.level !== 'error'"
                                 @load="onLogoPreview('create', $event)" @error="onLogoPreview('create', null)">
                            <div class="logo-check-msgs">
                                <div v-for="m in createLogoCheck.messages" :key="m">{{ m }}</div>
                            </div>
                        </div>
                        
                        <b-row>
                            <b-col md="4">
                                <b-input-group prepend="Country" class="mb-3"> 
                                    <v-select 
                                        class="flex-grow-1" 
                                        :options="countries" 
                                        v-model="country"
                                    ></v-select>
                                </b-input-group>
                            </b-col>
                            <b-col md="4">
                                <b-input-group prepend="Timezone" class="mb-3"> 
                                    <v-select 
                                        class="flex-grow-1" 
                                        :options="timezones" 
                                        v-model="timezone"
                                        :reduce="option => option.value"
                                        label="text"
                                    ></v-select>
                                </b-input-group>
                            </b-col>
                            <b-col md="4">
                                <b-input-group prepend="Number of Offices" class="mb-3"> 
                                    <b-form-input 
                                        type="number" 
                                        v-model.number="numberOfOffices" 
                                        min="1" 
                                        step="1"
                                        trim
                                    />           
                                </b-input-group>
                            </b-col>
                        </b-row>
                    </b-card>
                    
                    <!-- SECTION 2: Office Details (Per Office) -->
                    <div v-for="(office, officeIndex) in offices" :key="`office-${officeIndex}`">
                        <b-card no-body class="mb-4">
                            <b-card-header 
                                header-tag="header" 
                                class="p-3" 
                                role="tab"
                                style="cursor: pointer; background-color: #f8f9fa;"
                                @click="toggleOfficeCollapse(officeIndex)"
                            >
                                <div class="d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">
                                        <i :class="office.collapsed ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="mr-2"></i>
                                        Office {{ officeIndex + 1 }} - {{ office.officeName || 'Not Named Yet' }}
                                    </h5>
                                    <b-button 
                                        v-if="officeIndex > 0" 
                                        size="sm" 
                                        variant="outline-primary"
                                        @click.stop="copyFromOffice1(officeIndex)"
                                    >
                                        <i class="fa fa-copy"></i> Copy from Office 1
                                    </b-button>
                                </div>
                            </b-card-header>
                            
                            <b-collapse :visible="!office.collapsed">
                                <b-card-body>
                                    <!-- Office Address Information -->
                                    <h6 class="text-muted mb-3">Office Details</h6>
                                    
                                    <b-input-group prepend="Office Name" class="mb-3"> 
                                        <b-form-input 
                                            type="text" 
                                            v-model="office.officeName" 
                                            :placeholder="`${customerName || 'Customer'} - Office ${officeIndex + 1}`"
                                            trim
                                        />           
                                    </b-input-group>
                                    
                                    <b-row>
                                        <b-col md="6">
                                            <b-input-group prepend="Address 1" class="mb-3"> 
                                                <b-form-input type="text" v-model="office.address1" trim/>           
                                            </b-input-group>
                                        </b-col>
                                        <b-col md="6">
                                            <b-input-group prepend="Address 2" class="mb-3"> 
                                                <b-form-input type="text" v-model="office.address2" trim/>           
                                            </b-input-group>
                                        </b-col>
                                    </b-row>
                                    
                                    <b-row>
                                        <b-col md="4">
                                            <b-input-group prepend="City" class="mb-3"> 
                                                <b-form-input type="text" v-model="office.city" trim/>           
                                            </b-input-group>
                                        </b-col>
                                        <b-col md="4">
                                            <b-input-group prepend="State" class="mb-3"> 
                                                <b-form-input type="text" v-model="office.state" trim/>           
                                            </b-input-group>
                                        </b-col>
                                        <b-col md="4">
                                            <b-input-group prepend="Zip" class="mb-3"> 
                                                <b-form-input type="text" v-model="office.zip" trim/>           
                                            </b-input-group>
                                        </b-col>
                                    </b-row>
                                    
                                    <b-input-group prepend="Office Phone" class="mb-4"> 
                                        <b-form-input type="text" v-model="office.officePhone" placeholder="(123) 456-7890" trim/>           
                                    </b-input-group>
                                    
                                    <!-- Employees for this Office -->
                                    <hr class="my-4">
                                    <h6 class="text-muted mb-3">Employees for Office {{ officeIndex + 1 }}</h6>
                                    
                                    <div v-for="(employee, empIndex) in office.employees" :key="`emp-${officeIndex}-${empIndex}`" class="mb-3">
                                        <b-card bg-variant="light">
                                            <b-row>
                                                <b-col md="3">
                                                    <label class="fw-semi-bold">Name:</label>
                                                    <b-form-input type="text" v-model="employee.name" trim/>
                                                </b-col>
                                                <b-col md="3">
                                                    <label class="fw-semi-bold">Email:</label>
                                                    <b-form-input
                                                        type="email"
                                                        v-model.trim="employee.email"
                                                        placeholder="employee@company.com"
                                                        @blur="employee.email = normalizeEmailAddress(employee.email)"
                                                    />
                                                </b-col>
                                                <b-col md="2">
                                                    <label class="fw-semi-bold">Role:</label>
                                                    <multiselect
                                                        v-if="roles"
                                                        v-model="employee.role"
                                                        :options="roles"
                                                        :multiple="false"
                                                        :taggable="false"
                                                        :show-labels="false"
                                                        label="Role"
                                                        track-by="Role"
                                                    />
                                                </b-col>
                                                <b-col md="2">
                                                    <label class="fw-semi-bold">OD User #:</label>
                                                    <b-form-input type="text" v-model="employee.odUserNum"/>
                                                </b-col>
                                                <b-col md="2" class="d-flex align-items-end">
                                                    <b-button 
                                                        variant="danger" 
                                                        size="sm" 
                                                        @click="removeEmployeeFromOffice(officeIndex, empIndex)"
                                                        class="w-100"
                                                    >
                                                        <i class="fa fa-trash"></i> Remove
                                                    </b-button>
                                                </b-col>
                                            </b-row>
                                        </b-card>
                                    </div>
                                    
                                    <b-button 
                                        variant="success" 
                                        size="sm" 
                                        @click="addEmployeeToOffice(officeIndex)"
                                        class="mb-4"
                                    >
                                        <i class="fa fa-plus"></i> Add Employee to Office {{ officeIndex + 1 }}
                                    </b-button>
                                    
                                    <!-- Responsibilities for this Office -->
                                    <hr class="my-4">
                                    <div class="d-flex justify-content-between align-items-center mb-3">
                                        <h6 class="text-muted mb-0">Responsibilities for Office {{ officeIndex + 1 }}</h6>
                                        <b-button 
                                            v-if="office.employees && office.employees.length > 0"
                                            size="sm" 
                                            variant="outline-success"
                                            @click="assignAllResponsibilities(officeIndex)"
                                        >
                                            <i class="fa fa-magic mr-1"></i> Auto-Assign First Employee
                                        </b-button>
                                    </div>
                                    
                                    <b-row>
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Overdue Recall:</label>
                                            <multiselect
                                                v-model="office.responsibilities.overdueRecall"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Unscheduled Treatment:</label>
                                            <multiselect
                                                v-model="office.responsibilities.unscheduledTreatment"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Broken Appointment:</label>
                                            <multiselect
                                                v-model="office.responsibilities.brokenAppointment"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                    </b-row>
                                    
                                    <b-row class="mt-3">
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Outstanding Claims:</label>
                                            <multiselect
                                                v-model="office.responsibilities.outstandingClaims"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Outstanding Pre-Auths:</label>
                                            <multiselect
                                                v-model="office.responsibilities.outstandingPreAuth"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Processed Pre-Auths:</label>
                                            <multiselect
                                                v-model="office.responsibilities.processedPreAuth"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                    </b-row>
                                    
                                    <b-row class="mt-3">
                                        <b-col md="4">
                                            <label class="fw-semi-bold">Patient Balances:</label>
                                            <multiselect
                                                v-model="office.responsibilities.patientBalances"
                                                :options="office.employees"
                                                :multiple="false"
                                                :taggable="false"
                                                :show-labels="false"
                                                label="name"
                                                track-by="email"
                                                placeholder="Select Employee"
                                            />
                                        </b-col>
                                    </b-row>
                                </b-card-body>
                            </b-collapse>
                        </b-card>
                    </div>
                    
                    <!-- SECTION 3: Billing & Subscriptions (Per Office) - Only show if Stripe is available -->
                    <b-card header="Billing & Subscriptions (Optional)" class="mb-4" v-if="stripe">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 class="mb-1">💳 Stripe Integration</h6>
                                <small class="text-muted">Set up billing for each office. You can skip this and add billing later.</small>
                            </div>
                            <div>
                                <b-button 
                                    variant="outline-info" 
                                    size="sm" 
                                    @click="loadStripePlans"
                                    :disabled="stripePlansLoading"
                                >
                                    <i class="fa mr-1" :class="stripePlansLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
                                    {{ stripePlansLoading ? 'Loading...' : 'Refresh Plans' }}
                                </b-button>
                            </div>
                        </div>

                        <!-- Plans Loading Error -->
                        <b-alert variant="warning" :show="!!stripePlansError" dismissible @dismissed="stripePlansError = null">
                            <i class="fa fa-exclamation-triangle mr-2"></i>
                            Failed to load Stripe plans: {{ stripePlansError }}
                            <b-button variant="outline-warning" size="sm" class="ml-2" @click="loadStripePlans">
                                <i class="fa fa-retry"></i> Retry
                            </b-button>
                        </b-alert>

                        <!-- Per-Office Billing -->
                        <div v-for="(office, officeIndex) in offices" :key="`billing-${officeIndex}`" class="office-billing-section mb-4">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h6 class="text-primary mb-0">
                                    <i class="fa fa-building mr-2"></i>{{ office.officeName || `Office ${officeIndex + 1}` }}
                                </h6>
                                <b-form-checkbox 
                                    v-model="office.skipStripeCreation" 
                                    class="text-muted"
                                    @change="resetOfficeBilling(officeIndex)"
                                >
                                    Skip billing setup
                                </b-form-checkbox>
                            </div>

                            <div v-if="!office.skipStripeCreation && stripe">
                                <b-row>
                                    <b-col md="6">
                                        <label class="fw-semi-bold">Subscription Plan:</label>
                                        <v-select 
                                            :options="subscriptionPlans"
                                            v-model="office.selectedPlan"
                                            label="name"
                                            :reduce="plan => plan.id"
                                            placeholder="Select a plan..."
                                            :disabled="stripePlansLoading || subscriptionPlans.length === 0"
                                        />
                                        <small class="text-muted">
                                            {{ subscriptionPlans.length > 0 ? 
                                               `${subscriptionPlans.length} plan(s) available` : 
                                               'No plans available - click refresh to load plans from Stripe' }}
                                        </small>
                                    </b-col>
                                    <b-col md="6" v-if="office.selectedPlan">
                                        <label class="fw-semi-bold">Plan Details:</label>
                                        <div class="plan-details p-3 bg-light rounded border">
                                            <div class="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div class="font-weight-bold text-dark">{{ getSelectedPlanDetails(office.selectedPlan).name }}</div>
                                                    <small class="text-muted">{{ getSelectedPlanDetails(office.selectedPlan).description }}</small>
                                                </div>
                                                <div class="text-right">
                                                    <div class="h5 text-success mb-1">
                                                        ${{ (getSelectedPlanDetails(office.selectedPlan).amount / 100).toFixed(2) }}
                                                    </div>
                                                    <small class="text-muted">per {{ getSelectedPlanDetails(office.selectedPlan).interval }}</small>
                                                </div>
                                            </div>
                                        </div>
                                    </b-col>
                                </b-row>
                                
                                <!-- Billing Details -->
                                <b-row class="mt-3" v-if="office.selectedPlan">
                                    <b-col md="4">
                                        <label class="fw-semi-bold">Billing Contact Email:</label>
                                        <b-form-input 
                                            type="email" 
                                            v-model="office.billingEmail"
                                            :placeholder="office.employees && office.employees[0] ? office.employees[0].email : 'billing@company.com'"
                                        />
                                        <small class="text-muted">Invoice delivery email</small>
                                    </b-col>
                                    <b-col md="4">
                                        <label class="fw-semi-bold">Trial Period:</label>
                                        <b-form-select v-model="office.trialDays">
                                            <option :value="0">No Trial - Start Billing Immediately</option>
                                            <option :value="7">7 Day Free Trial</option>
                                            <option :value="14">14 Day Free Trial</option>
                                            <option :value="30">30 Day Free Trial</option>
                                        </b-form-select>
                                    </b-col>
                                    <b-col md="4">
                                        <label class="fw-semi-bold">Billing Notes:</label>
                                        <b-form-input 
                                            v-model="office.billingNotes"
                                            placeholder="Optional notes for this subscription"
                                        />
                                    </b-col>
                                </b-row>
                            </div>
                            
                            <div v-else class="text-muted text-center p-3 bg-light rounded w-100">
                                <i class="fa fa-info-circle mr-2"></i>
                                <span v-if="!stripe">Billing setup skipped (Stripe not available). You can set up billing later from the customer management page.</span>
                                <span v-else>Billing setup skipped. You can add Stripe billing later from the site management panel.</span>
                            </div>

                            <hr v-if="officeIndex < offices.length - 1" class="my-4">
                        </div>
                        
                        <!-- Billing Summary -->
                        <div class="billing-summary mt-4 p-3 bg-primary text-white rounded" v-if="officesWithBilling.length > 0">
                            <h6 class="mb-2">
                                <i class="fa fa-credit-card mr-2"></i>Billing Summary
                            </h6>
                            <div class="d-flex justify-content-between">
                                <div>
                                    <div>Offices with billing: {{ officesWithBilling.length }}/{{ offices.length }}</div>
                                    <div class="small">Plans: {{ uniquePlansSelected.join(', ') }}</div>
                                </div>
                                <div class="text-right">
                                    <div class="h5 mb-1">${{ calculateTotalMonthly() }}/month</div>
                                    <small>Total estimated monthly</small>
                                </div>
                            </div>
                        </div>

                        <div v-else class="text-center text-muted p-3">
                            <i class="fa fa-info-circle mr-2"></i>
                            No billing configured. All offices will be created without Stripe subscriptions.
                        </div>
                    </b-card>
                    
                    <!-- Stripe Not Available Info -->
                    <b-alert variant="info" :show="!stripe && !stripeLoading" class="mb-4">
                        <i class="fa fa-info-circle mr-2"></i>
                        <strong>Billing setup unavailable</strong> - Stripe is not configured. 
                        All customers will be created without subscriptions and you can set up billing later from the customer management page.
                    </b-alert>
                    
                    <!-- Action Buttons -->
                    <div class="mt-4">
                        <b-button 
                            variant="success" 
                            size="lg" 
                            @click="_addCustomer" 
                            :disabled="addCustomerLoading"
                        >
                            <i class="fa fa-spinner fa-spin" v-if="addCustomerLoading"></i>
                            {{ addCustomerLoading ? 'Creating Customer...' : 'Add Customer' }}
                        </b-button>
                        <b-button 
                            variant="secondary" 
                            size="lg" 
                            class="ml-2" 
                            @click="showCustomerAdd = false;" 
                            :disabled="addCustomerLoading"
                        >
                            Cancel
                        </b-button>
                    </div>

                    <!-- Customer Creation Progress Display -->
                    <div v-if="customerCreationProgress.length > 0" class="mt-4 w-100">
                        <b-card header="Customer Creation Progress" border-variant="primary" class="w-100">
                            <!-- Created IDs Display -->
                            <!-- NOTE: do NOT add the .rounded class here — src/styles/_utils.scss
                                 redefines .rounded as an inline-block 1.5em badge (collides with
                                 Bootstrap's border-radius utility). Use inline style instead. -->
                            <div v-if="createdCustomerInfo.customerID" class="mb-3 p-3 bg-light border" style="border-radius: 0.25rem;">
                                <h5 class="mb-3"><i class="fa fa-id-card text-primary mr-2"></i>Created IDs</h5>

                                <!-- Customer ID -->
                                <div class="mb-3">
                                    <label class="small font-weight-bold mb-1">Customer ID:</label>
                                    <div class="input-group">
                                        <input
                                            type="text"
                                            class="form-control"
                                            :value="createdCustomerInfo.customerID"
                                            readonly
                                        >
                                        <div class="input-group-append">
                                            <button
                                                class="btn btn-outline-primary"
                                                type="button"
                                                @click="copyToClipboard(createdCustomerInfo.customerID, 'Customer ID')"
                                            >
                                                <i class="fa fa-copy"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Site IDs -->
                                <div v-if="createdCustomerInfo.siteIDs && createdCustomerInfo.siteIDs.length > 0">
                                    <label class="small font-weight-bold mb-1">Site IDs:</label>
                                    <div
                                        v-for="(site, index) in createdCustomerInfo.siteIDs"
                                        :key="site.id"
                                        class="mb-2"
                                    >
                                        <small class="text-muted d-block mb-1">Office {{ index + 1 }}: {{ site.name }}</small>
                                        <div class="input-group">
                                            <input
                                                type="text"
                                                class="form-control"
                                                :value="site.id"
                                                readonly
                                            >
                                            <div class="input-group-append">
                                                <button
                                                    class="btn btn-outline-primary"
                                                    type="button"
                                                    @click="copyToClipboard(site.id, `Site ${index + 1} ID`)"
                                                >
                                                    <i class="fa fa-copy"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Progress Steps -->
                            <div class="progress-container" style="max-height: 400px; overflow-y: auto;">
                                <div 
                                    v-for="(step, index) in customerCreationProgress" 
                                    :key="index"
                                    class="progress-step mb-2 p-2"
                                    :class="{
                                        'bg-light': step.status === 'pending',
                                        'bg-success-light': step.status === 'success',
                                        'bg-danger-light': step.status === 'error'
                                    }"
                                    style="border-left: 4px solid; border-radius: 4px;"
                                    :style="{
                                        borderLeftColor: step.status === 'success' ? '#28a745' : 
                                                        step.status === 'error' ? '#dc3545' : 
                                                        '#6c757d'
                                    }"
                                >
                                    <div class="d-flex align-items-start">
                                        <i
                                            class="mr-2 mt-1 flex-shrink-0"
                                            :class="{
                                                'fa fa-check-circle text-success': step.status === 'success',
                                                'fa fa-times-circle text-danger': step.status === 'error',
                                                'fa fa-spinner fa-spin text-primary': step.status === 'pending'
                                            }"
                                        ></i>
                                        <span v-html="step.message" class="flex-grow-1" style="min-width: 0; word-break: break-word;"></span>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </div>
                </div>  

                <div class="mt-5" v-if="showSQSRedrive">
                    <b-row>
                        <b-col md="3">
                            Select DLQ
                            <multiselect                                        
                                v-model="selectedDlq"
                                :options="dlqs"
                                :multiple="false"
                                :taggable="false"
                                :show-labels="false"
                                label="name"           
                                track-by="name"                     
                                aria-placeholder="Select a DLQ"
                            />
                        </b-col>
                        <b-col md="3">
                            Select Queue
                            <multiselect                                        
                                v-model="selectedQueue"
                                :options="queues"
                                :multiple="false"
                                :taggable="false"
                                :show-labels="false"
                                label="name"
                                track-by="name"
                                aria-placeholder="Select a Queue"
                            />
                        </b-col>
                        <b-col>
                            <b-button variant="success" type="submit" class="btn btn-bg mt-4 ml-2" @click="_sqsRedrive" :disabled="sqsRedriveLoading">
                                <i class="fa fa-spinner fa-spin" v-if="sqsRedriveLoading"></i>
                                {{ sqsRedriveLoading ? 'Processing...' : 'Redrive' }}
                            </b-button>
                            <b-button variant="success" type="submit" class="btn btn-bg mt-4 ml-2" @click="showSQSRedrive = false;" :disabled="sqsRedriveLoading">Cancel</b-button>
                        </b-col>
                    </b-row>
                </div>

                <div class="mt-5" v-if="showBulkFormCopy">
                    <h4 class="mb-3">Bulk Copy Forms Between Sites</h4>
                    <b-row>
                        <b-col md="4">
                            <label class="fw-semi-bold">Source Site:</label>
                            <b-form-select 
                                v-model="bulkFormCopy.sourceSiteID" 
                                :options="siteOptions"
                                @change="loadSourceSiteForms"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Source Site --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Target Site:</label>
                            <b-form-select 
                                v-model="bulkFormCopy.targetSiteID" 
                                :options="siteOptions"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Target Site --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Options:</label>
                            <b-form-checkbox 
                                v-model="bulkFormCopy.overwriteExisting"
                                class="mt-2"
                            >
                                Overwrite existing forms with same name
                            </b-form-checkbox>
                        </b-col>
                    </b-row>

                    <div class="mt-4" v-if="bulkFormCopy.availableForms.length > 0">
                        <label class="fw-semi-bold">Select Forms to Copy:</label>
                        <div class="form-selection-container mt-2" style="max-height: 300px; overflow-y: auto; border: 1px solid #ddd; padding: 15px; border-radius: 5px;">
                            <b-form-checkbox 
                                v-model="bulkFormCopy.selectAll"
                                @change="toggleSelectAll"
                                class="mb-3 font-weight-bold"
                            >
                                Select All ({{ bulkFormCopy.availableForms.length }} forms)
                            </b-form-checkbox>
                            <hr>
                            <b-form-checkbox-group 
                                v-model="bulkFormCopy.selectedFormIDs"
                                :options="bulkFormCopy.availableForms.map(form => ({ 
                                    text: `${form.Name} (${form.FormType || 'No Type'})`, 
                                    value: form.FormIDForWeb 
                                }))"
                                stacked
                            />
                        </div>
                        <small class="text-muted mt-2 d-block">
                            Selected: {{ bulkFormCopy.selectedFormIDs.length }} of {{ bulkFormCopy.availableForms.length }} forms
                        </small>
                    </div>

                    <!-- OD imaging category per FormType in the selection, on the TARGET site (DefNums are per site).
                         Never saved; Copy stays disabled until every type shown is picked. -->
                    <div class="mt-4 bulk-imaging-map" v-if="bulkFormCopy.targetSiteID && bulkCopyRequiredTypes.length > 0" data-test="bulk-copy-imaging-map">
                        <label class="fw-semi-bold mb-1">Open Dental imaging categories on the target site:</label>
                        <small class="text-muted d-block mb-2">Every copied form's PDF and uploaded files are saved to Open Dental under the category picked for its form type. Uploads default to the same category; change individual forms later in the form builder.</small>
                        <div v-if="bulkFormCopy.imaging.loading" class="small text-muted py-1">
                            <b-spinner small class="mr-1"></b-spinner>Loading the target site's imaging categories&hellip;
                        </div>
                        <b-alert v-else-if="bulkFormCopy.imaging.error" show variant="danger" class="mb-0">
                            {{ bulkFormCopy.imaging.error }}
                            <b-link class="ml-1" @click="loadBulkCopyImagingCategories(bulkFormCopy.targetSiteID)">Retry</b-link>
                        </b-alert>
                        <form-imaging-category-map v-else
                            v-model="bulkFormCopy.imaging.map"
                            :types="bulkCopyRequiredTypes"
                            :categories="bulkFormCopy.imaging.categories"
                            :consent-candidates="bulkFormCopy.imaging.consentCandidates"
                            :disabled="bulkFormCopy.copying"
                            id-prefix="bulk-copy-imaging" />
                    </div>

                    <div class="mt-4" v-if="bulkFormCopy.loading">
                        <b-spinner label="Loading forms..."></b-spinner>
                        <span class="ml-2">Loading forms from source site...</span>
                    </div>

                    <div class="mt-4" v-if="bulkFormCopy.error">
                        <b-alert variant="danger" show>{{ bulkFormCopy.error }}</b-alert>
                    </div>

                    <div class="mt-4">
                        <b-button 
                            variant="success" 
                            @click="executeBulkFormCopy" 
                            :disabled="!canExecuteBulkCopy || bulkFormCopy.copying"
                            class="btn btn-bg"
                        >
                            <i class="fa fa-spinner fa-spin" v-if="bulkFormCopy.copying"></i>
                            <i class="fa fa-copy" v-else></i>
                            {{ bulkCopyButtonLabel }}
                        </b-button>
                        <b-button 
                            variant="outline-secondary" 
                            @click="resetBulkFormCopy" 
                            class="btn ml-2"
                        >
                            Cancel
                        </b-button>
                    </div>

                    <div class="mt-4" v-if="bulkFormCopy.results">
                        <h5>Copy Results:</h5>
                        <b-alert 
                            :variant="getResultsAlertVariant()" 
                            show
                        >
                            <strong>Summary:</strong> 
                            {{ bulkFormCopy.results.successCount || 0 }} successful, 
                            {{ bulkFormCopy.results.failureCount || 0 }} failed, 
                            {{ bulkFormCopy.results.skippedCount || 0 }} skipped<template v-if="bulkFormCopy.results.unknownCount">,
                            {{ bulkFormCopy.results.unknownCount }} unconfirmed</template><template v-if="bulkFormCopy.results.notAttemptedCount">,
                            {{ bulkFormCopy.results.notAttemptedCount }} not attempted</template>
                        </b-alert>
                        
                        <div v-if="bulkFormCopy.results.details && bulkFormCopy.results.details.length > 0" class="results-details" style="max-height: 200px; overflow-y: auto;">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Form Name</th>
                                        <th>Status</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="detail in bulkFormCopy.results.details" :key="detail.formID || detail.formName || $index">
                                        <td>
                                            {{ detail.formName || detail.formID || 'Unknown' }}
                                            <b-badge
                                                v-if="detail.configCleared"
                                                variant="warning"
                                                class="ml-2"
                                                v-b-tooltip.hover
                                                :title="`Config fields cleared: ${detail.clearedFields ? detail.clearedFields.join(', ') : 'unknown'}`"
                                            >
                                                Config Cleared
                                            </b-badge>
                                        </td>
                                        <td>
                                            <span
                                                :class="{
                                                    'text-success': detail.status === 'created' || detail.status === 'updated',
                                                    'text-danger': detail.status === 'failed',
                                                    'text-warning': detail.status === 'skipped' || detail.status === 'unknown',
                                                    'text-muted': detail.status === 'not attempted'
                                                }"
                                            >
                                                {{ detail.status }}
                                            </span>
                                        </td>
                                        <td>
                                            {{ detail.reason || 'No details available' }}
                                            <div v-if="detail.warning" class="text-warning small mt-1">
                                                <i class="fas fa-exclamation-triangle"></i> {{ detail.warning }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Copy ONE section of a form into another form, same or another site (teamio-admin
                     COPY_FORM_SECTION, shared graftSection). Copy unlocks only after a dry run of the
                     same picks; the real run backs the target's ConfigData up to S3 before it saves. -->
                <div class="mt-5" v-if="showSectionCopy" data-test="superadmin-section-copy">
                    <h4 class="mb-3">Copy a Form Section</h4>
                    <small class="text-muted d-block mb-3">
                        Adds ONE section of the source form to the target form as a new section; nothing on the target is replaced.
                        Run a dry run first: it lists renamed fields, cleared Open Dental mappings, removed rules and, across sites,
                        remapped Open Dental options. The real copy backs the target form up to S3 before it saves.
                    </small>
                    <b-row>
                        <b-col md="4">
                            <label class="fw-semi-bold">Source Site:</label>
                            <b-form-select
                                v-model="sectionCopy.sourceSiteID"
                                :options="siteOptions"
                                :disabled="!!sectionCopy.running"
                                data-test="superadmin-section-copy-source-site"
                                @change="loadSectionCopyForms('source')"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Source Site --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Source Form:</label>
                            <b-form-select
                                v-model="sectionCopy.sourceFormID"
                                :options="sectionCopySourceFormOptions"
                                :disabled="!!sectionCopy.running || sectionCopy.loadingSource || !sectionCopy.sourceForms.length"
                                data-test="superadmin-section-copy-source-form"
                                @change="onSectionCopySourceFormChange"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Source Form --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Section:</label>
                            <b-form-select
                                v-model="sectionCopy.sectionId"
                                :options="sectionCopySectionOptions"
                                :disabled="!!sectionCopy.running || !sectionCopySourceSections.length"
                                data-test="superadmin-section-copy-section"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Section --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                    </b-row>

                    <b-alert
                        class="mt-3 mb-0"
                        show
                        variant="warning"
                        v-if="sectionCopy.sourceFormID && !sectionCopy.loadingSource && !sectionCopySourceConfig"
                    >
                        The source form's ConfigData could not be read, so its sections can't be listed.
                    </b-alert>
                    <b-alert
                        class="mt-3 mb-0"
                        show
                        variant="info"
                        v-else-if="sectionCopySourceConfig && !sectionCopySourceSections.length"
                    >
                        The source form has no sections.
                    </b-alert>

                    <!-- preview of the picked section: headline, then each field's label + control type (tabs grouped) -->
                    <div
                        class="mt-3 section-copy-preview"
                        v-if="sectionCopySelectedSection"
                        data-test="superadmin-section-copy-preview"
                        style="max-height: 240px; overflow-y: auto; border: 1px solid #ddd; padding: 10px 15px; border-radius: 5px;"
                    >
                        <div class="fw-semi-bold mb-1">
                            {{ sectionCopySelectedSection.title }}
                            <small class="text-muted">({{ sectionCopySelectedSection.fieldCount }} {{ sectionCopySelectedSection.fieldCount === 1 ? 'field' : 'fields' }})</small>
                        </div>
                        <div v-for="(group, gi) in sectionCopySelectedSection.groups" :key="'section-copy-group-' + gi">
                            <div v-if="group.tabName !== null" class="small font-weight-bold text-uppercase text-muted mt-2">{{ group.tabName }}</div>
                            <div v-for="field in group.fields" :key="field.id" class="small d-flex justify-content-between border-bottom py-1">
                                <span>{{ field.label }}</span>
                                <span class="text-muted ml-3">{{ field.typeName }}</span>
                            </div>
                            <div v-if="!group.fields.length" class="small text-muted font-italic">No fields</div>
                        </div>
                    </div>

                    <b-row class="mt-3">
                        <b-col md="4">
                            <label class="fw-semi-bold">Target Site:</label>
                            <b-form-select
                                v-model="sectionCopy.targetSiteID"
                                :options="siteOptions"
                                :disabled="!!sectionCopy.running"
                                data-test="superadmin-section-copy-target-site"
                                @change="loadSectionCopyForms('target')"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Target Site --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Target Form:</label>
                            <b-form-select
                                v-model="sectionCopy.targetFormID"
                                :options="sectionCopyTargetFormOptions"
                                :disabled="!!sectionCopy.running || sectionCopy.loadingTarget || !sectionCopy.targetForms.length"
                                data-test="superadmin-section-copy-target-form"
                                @change="onSectionCopyTargetFormChange"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Target Form --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Position:</label>
                            <b-form-select
                                v-model="sectionCopy.position"
                                :options="sectionCopyPositionOptions"
                                :disabled="!!sectionCopy.running || !sectionCopy.targetFormID"
                                data-test="superadmin-section-copy-position"
                            />
                        </b-col>
                    </b-row>

                    <div class="mt-3" v-if="sectionCopy.loadingSource || sectionCopy.loadingTarget">
                        <b-spinner small label="Loading forms..."></b-spinner>
                        <span class="ml-2">Loading forms&hellip;</span>
                    </div>

                    <b-alert class="mt-3" show variant="warning" v-if="sectionCopySameForm">
                        Source and target are the same form. Pick a different target form.
                    </b-alert>

                    <b-alert class="mt-3" show variant="danger" v-if="sectionCopy.error" data-test="superadmin-section-copy-error">
                        {{ sectionCopy.error }}
                    </b-alert>

                    <div class="mt-4">
                        <b-button
                            variant="outline-primary"
                            class="btn"
                            data-test="superadmin-section-copy-dry-run"
                            :disabled="!canDryRunSectionCopy"
                            @click="runSectionCopy(true)"
                        >
                            <i class="fa fa-spinner fa-spin" v-if="sectionCopy.running === 'dry'"></i>
                            <i class="fa fa-search" v-else></i>
                            Dry run
                        </b-button>
                        <b-button
                            variant="success"
                            class="btn btn-bg ml-2"
                            data-test="superadmin-section-copy-run"
                            :disabled="!canRunSectionCopy"
                            @click="runSectionCopy(false)"
                        >
                            <i class="fa fa-spinner fa-spin" v-if="sectionCopy.running === 'copy'"></i>
                            <i class="fa fa-clone" v-else></i>
                            Copy Section
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            class="btn ml-2"
                            data-test="superadmin-section-copy-cancel"
                            @click="resetSectionCopy"
                        >
                            Cancel
                        </b-button>
                        <small class="text-muted ml-2" v-if="canDryRunSectionCopy && !canRunSectionCopy">
                            Copy Section unlocks after a dry run of these exact picks.
                        </small>
                    </div>

                    <div class="mt-4" v-if="sectionCopy.result" data-test="superadmin-section-copy-result">
                        <h5>{{ sectionCopy.result.dryRun ? 'Dry Run Result:' : 'Copy Result:' }}</h5>
                        <b-alert
                            show
                            :variant="sectionCopy.result.status === 'copied' ? 'success' : (sectionCopy.result.status === 'dry-run' ? 'info' : 'warning')"
                        >
                            <strong>Status:</strong> {{ sectionCopy.result.status }}
                            <template v-if="sectionCopy.result.status === 'dry-run'"> &mdash; nothing was saved. Review the notes below, then Copy Section.</template>
                            <div v-if="sectionCopy.result.backupKey" class="small mt-1">
                                <strong>Backup of the previous target form:</strong> <code>{{ sectionCopy.result.backupKey }}</code>
                            </div>
                        </b-alert>

                        <div v-if="sectionCopyReportRows.length" class="results-details" style="max-height: 260px; overflow-y: auto;">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Change</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, i) in sectionCopyReportRows" :key="'section-copy-report-' + i">
                                        <td
                                            :class="{
                                                'text-success': row.kind === 'copied',
                                                'text-warning': row.kind === 'renamed' || row.kind === 'odMappingCleared' || row.kind === 'ruleDropped'
                                            }"
                                        >
                                            {{ row.label }}
                                        </td>
                                        <td>{{ row.text }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="mt-5" v-if="showBulkWikiCopy">
                    <h4 class="mb-3">Bulk Copy Wiki Pages Between Sites</h4>
                    <b-row>
                        <b-col md="4">
                            <label class="fw-semi-bold">Source Site:</label>
                            <b-form-select
                                v-model="bulkWikiCopy.sourceSiteID"
                                :options="siteOptions"
                                @change="loadSourceSiteWikiPages"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Source Site --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Target Site:</label>
                            <b-form-select
                                v-model="bulkWikiCopy.targetSiteID"
                                :options="siteOptions"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>-- Select Target Site --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label class="fw-semi-bold">Options:</label>
                            <b-form-checkbox
                                v-model="bulkWikiCopy.overwriteExisting"
                                class="mt-2"
                            >
                                Overwrite existing wiki pages with same name
                            </b-form-checkbox>
                            <b-form-checkbox
                                v-model="bulkWikiCopy.copyTags"
                                class="mt-1"
                            >
                                Copy tags (create if missing on target site)
                            </b-form-checkbox>
                        </b-col>
                    </b-row>

                    <div class="mt-4" v-if="bulkWikiCopy.availableWikiPages.length > 0">
                        <label class="fw-semi-bold">Select Wiki Pages to Copy:</label>
                        <div class="form-selection-container mt-2" style="max-height: 300px; overflow-y: auto; border: 1px solid #ddd; padding: 15px; border-radius: 5px;">
                            <b-form-checkbox
                                v-model="bulkWikiCopy.selectAll"
                                @change="toggleSelectAllWiki"
                                class="mb-3 font-weight-bold"
                            >
                                Select All ({{ bulkWikiCopy.availableWikiPages.length }} wiki pages)
                            </b-form-checkbox>
                            <hr>
                            <b-form-checkbox-group
                                v-model="bulkWikiCopy.selectedWikiPageIDs"
                                :options="bulkWikiCopy.availableWikiPages.map(page => ({
                                    text: `${page.Name}${page.Tags ? ' [' + page.Tags + ']' : ''}`,
                                    value: page.WikiPageID
                                }))"
                                stacked
                            />
                        </div>
                        <small class="text-muted mt-2 d-block">
                            Selected: {{ bulkWikiCopy.selectedWikiPageIDs.length }} of {{ bulkWikiCopy.availableWikiPages.length }} wiki pages
                        </small>
                    </div>

                    <div class="mt-4" v-if="bulkWikiCopy.loading">
                        <b-spinner label="Loading wiki pages..."></b-spinner>
                        <span class="ml-2">Loading wiki pages from source site...</span>
                    </div>

                    <div class="mt-4" v-if="bulkWikiCopy.error">
                        <b-alert variant="danger" show>{{ bulkWikiCopy.error }}</b-alert>
                    </div>

                    <div class="mt-4">
                        <b-button
                            variant="success"
                            @click="executeBulkWikiCopy"
                            :disabled="!canExecuteBulkWikiCopy || bulkWikiCopy.copying"
                            class="btn btn-bg"
                        >
                            <i class="fa fa-spinner fa-spin" v-if="bulkWikiCopy.copying"></i>
                            <i class="fa fa-copy" v-else></i>
                            {{ bulkWikiCopy.copying ? 'Copying...' : 'Copy Selected Wiki Pages' }}
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            @click="resetBulkWikiCopy"
                            class="btn ml-2"
                        >
                            Cancel
                        </b-button>
                    </div>

                    <div class="mt-4" v-if="bulkWikiCopy.results">
                        <h5>Copy Results:</h5>
                        <b-alert
                            :variant="getWikiResultsAlertVariant()"
                            show
                        >
                            <strong>Summary:</strong>
                            {{ bulkWikiCopy.results.successCount || 0 }} successful,
                            {{ bulkWikiCopy.results.failureCount || 0 }} failed,
                            {{ bulkWikiCopy.results.skippedCount || 0 }} skipped
                        </b-alert>

                        <div v-if="bulkWikiCopy.results.details && bulkWikiCopy.results.details.length > 0" class="results-details" style="max-height: 200px; overflow-y: auto;">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Wiki Page Name</th>
                                        <th>Status</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="detail in bulkWikiCopy.results.details" :key="detail.wikiPageID || detail.wikiPageName">
                                        <td>
                                            {{ detail.wikiPageName || detail.wikiPageID || 'Unknown' }}
                                            <b-badge
                                                v-if="detail.hasInternalLinks"
                                                variant="info"
                                                class="ml-2"
                                                v-b-tooltip.hover
                                                title="This page contains links to other wiki pages that may need to be updated manually"
                                            >
                                                Has Internal Links
                                            </b-badge>
                                            <b-badge
                                                v-if="detail.hasFormLinks"
                                                variant="warning"
                                                class="ml-2"
                                                v-b-tooltip.hover
                                                title="This page contains form template links that may not work on the target site"
                                            >
                                                Has Form Links
                                            </b-badge>
                                        </td>
                                        <td>
                                            <span
                                                :class="{
                                                    'text-success': detail.status === 'created' || detail.status === 'updated',
                                                    'text-danger': detail.status === 'failed',
                                                    'text-warning': detail.status === 'skipped'
                                                }"
                                            >
                                                {{ detail.status }}
                                            </span>
                                        </td>
                                        <td>
                                            {{ detail.reason || 'No details available' }}
                                            <div v-if="detail.warning" class="text-warning small mt-1">
                                                <i class="fas fa-exclamation-triangle"></i> {{ detail.warning }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- NEEDS_FORMS Modal: shown when wiki copy detects missing forms -->
                    <b-modal v-model="bulkWikiCopy.needsFormsModal"
                             title="Forms Required for Wiki Templates"
                             size="xl"
                             no-close-on-backdrop
                             no-close-on-esc
                             hide-header-close>

                        <div>
                            <div class="alert alert-warning mb-3">
                                <strong>{{ bulkWikiCopy.missingForms.length }} form(s)</strong> referenced by the selected wiki templates
                                don't exist on the target site yet. Configure and copy them now:
                            </div>

                            <!-- Apply to All Section -->
                            <div class="card mb-3">
                                <div class="card-header bg-light py-2">
                                    <strong>Apply to All Forms</strong>
                                </div>
                                <div class="card-body py-2">
                                    <div class="row align-items-end">
                                        <div class="col-md-3">
                                            <label class="mb-1 small">PDF Category</label>
                                            <select class="form-control form-control-sm"
                                                    v-model="bulkWikiCopy.applyAllConfig.pdfCategory">
                                                <option value="">--Select--</option>
                                                <option v-for="cat in bulkWikiCopy.targetCategories"
                                                        :key="'all-pdf-' + cat.DefNum"
                                                        :value="String(cat.DefNum)"
                                                        v-text="cat.ItemName"></option>
                                            </select>
                                        </div>
                                        <div class="col-md-3">
                                            <label class="mb-1 small">Attachment Category</label>
                                            <select class="form-control form-control-sm"
                                                    v-model="bulkWikiCopy.applyAllConfig.attachmentCategory">
                                                <option value="">Same as PDF category</option>
                                                <option v-for="cat in bulkWikiCopy.targetCategories"
                                                        :key="'all-att-' + cat.DefNum"
                                                        :value="String(cat.DefNum)"
                                                        v-text="cat.ItemName"></option>
                                            </select>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="mb-1 small">&nbsp;</label>
                                            <div>
                                                <label class="mr-3 mb-0"><input type="checkbox" v-model="bulkWikiCopy.applyAllConfig.enableSpanish"> Spanish</label>
                                                <label class="mr-3 mb-0"><input type="checkbox" v-model="bulkWikiCopy.applyAllConfig.autoPrefillTeeth"> Teeth #'s</label>
                                                <label class="mb-0"><input type="checkbox" v-model="bulkWikiCopy.applyAllConfig.autoPrefillDate"> Today's Date</label>
                                            </div>
                                        </div>
                                        <div class="col-md-2">
                                            <button class="btn btn-sm btn-outline-primary btn-block"
                                                    @click="applyAllFormConfig">Apply to All</button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Per-Form Configuration -->
                            <div style="max-height: 400px; overflow-y: auto;">
                                <div v-for="form in bulkWikiCopy.missingForms" :key="form.sourceFormId"
                                     class="card mb-2">
                                    <div class="card-body py-2 px-3">
                                        <div class="row align-items-center">
                                            <div class="col-md-3">
                                                <strong class="small">{{ form.formName }}</strong>
                                                <br>
                                                <small class="text-muted">
                                                    Used by: {{ form.referencedByWikis.join(', ') }}
                                                </small>
                                            </div>
                                            <div class="col-md-2">
                                                <select class="form-control form-control-sm"
                                                        v-if="bulkWikiCopy.perFormConfig[form.sourceFormId]"
                                                        v-model="bulkWikiCopy.perFormConfig[form.sourceFormId].pdfCategory"
                                                        :class="{ 'imaging-select--required': !bulkWikiCopy.perFormConfig[form.sourceFormId].pdfCategory }">
                                                    <option value="">PDF category</option>
                                                    <option v-for="cat in bulkWikiCopy.targetCategories"
                                                            :key="'pf-pdf-' + form.sourceFormId + '-' + cat.DefNum"
                                                            :value="String(cat.DefNum)"
                                                            v-text="cat.ItemName"></option>
                                                </select>
                                                <small v-if="bulkWikiCopy.perFormConfig[form.sourceFormId] && !bulkWikiCopy.perFormConfig[form.sourceFormId].pdfCategory"
                                                       class="imaging-required-text">Required</small>
                                            </div>
                                            <div class="col-md-2">
                                                <select class="form-control form-control-sm"
                                                        v-if="bulkWikiCopy.perFormConfig[form.sourceFormId]"
                                                        v-model="bulkWikiCopy.perFormConfig[form.sourceFormId].attachmentCategory">
                                                    <option value="">Same as PDF category</option>
                                                    <option v-for="cat in bulkWikiCopy.targetCategories"
                                                            :key="'pf-att-' + form.sourceFormId + '-' + cat.DefNum"
                                                            :value="String(cat.DefNum)"
                                                            v-text="cat.ItemName"></option>
                                                </select>
                                            </div>
                                            <div class="col-md-5" v-if="bulkWikiCopy.perFormConfig[form.sourceFormId]">
                                                <label class="mr-2 mb-0 small"><input type="checkbox" v-model="bulkWikiCopy.perFormConfig[form.sourceFormId].enableSpanish"> Spanish</label>
                                                <label class="mr-2 mb-0 small"><input type="checkbox" v-model="bulkWikiCopy.perFormConfig[form.sourceFormId].autoPrefillTeeth"> Teeth</label>
                                                <label class="mb-0 small"><input type="checkbox" v-model="bulkWikiCopy.perFormConfig[form.sourceFormId].autoPrefillDate"> Date</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <template #modal-footer>
                            <small v-if="!bulkWikiMissingFormsReady" class="text-danger mr-auto">
                                Pick a PDF category for every form first.
                            </small>
                            <button class="btn btn-secondary"
                                    @click="bulkWikiCopy.needsFormsModal = false"
                                    :disabled="bulkWikiCopy.copyingForms">
                                Cancel
                            </button>
                            <button class="btn btn-primary"
                                    @click="copyFormsAndRetryWiki"
                                    data-test="wiki-copy-missing-forms-submit"
                                    :disabled="bulkWikiCopy.copyingForms || !bulkWikiMissingFormsReady">
                                <span v-if="bulkWikiCopy.copyingForms">
                                    <span class="spinner-border spinner-border-sm mr-1"></span>
                                    Copying...
                                </span>
                                <span v-else>
                                    Copy {{ bulkWikiCopy.missingForms.length }} Form(s) &amp; Continue
                                </span>
                            </button>
                        </template>
                    </b-modal>
                </div>

                <div class="mt-5" v-if="showCacheManagement">
                    <h4 class="mb-3"><i class="fa fa-database text-warning mr-2"></i>Cache Management</h4>
                    <p class="text-muted">Manage application caches (Momento for follow-ups, Upstash/Redis for events & KPI)</p>

                    <!-- Cache Type Tabs -->
                    <b-nav tabs class="mb-4">
                        <b-nav-item
                            :active="cacheManagementTab === 'momento'"
                            @click="cacheManagementTab = 'momento'"
                        >
                            <i class="fa fa-cloud mr-1"></i> Momento Cache
                        </b-nav-item>
                        <b-nav-item
                            :active="cacheManagementTab === 'upstash'"
                            @click="switchToUpstashTab"
                        >
                            <i class="fa fa-server mr-1"></i> Upstash/Redis Cache
                        </b-nav-item>
                    </b-nav>

                    <!-- Momento Cache Tab -->
                    <div v-if="cacheManagementTab === 'momento'">
                    <h5 class="mb-3"><i class="fa fa-cloud text-info mr-2"></i>Momento Cache (Follow-up Tracking)</h5>
                    <b-row class="mb-4">
                        <b-col md="4">
                            <b-card header="Cache Health" border-variant="info">
                                <div v-if="cacheStats.loading" class="text-center">
                                    <b-spinner small></b-spinner> Loading...
                                </div>
                                <div v-else>
                                    <p class="mb-2"><strong>Active Follow-ups:</strong> {{ cacheStats.totalCount || 0 }}</p>
                                    <p class="mb-2"><strong>Cache Name:</strong> {{ momentoCacheName }}</p>
                                    <p class="mb-0"><strong>Set Name:</strong> fuattempts</p>
                                    <b-button 
                                        variant="outline-info" 
                                        size="sm" 
                                        @click="loadCacheStats" 
                                        :disabled="cacheStats.loading"
                                        class="mt-2"
                                    >
                                        <i class="fa fa-refresh"></i> Refresh Stats
                                    </b-button>
                                </div>
                            </b-card>
                        </b-col>
                        
                        <b-col md="8">
                            <b-card header="Quick Actions" border-variant="warning">
                                <b-button-group class="mr-3 mb-2">
                                    <b-button 
                                        variant="info" 
                                        @click="loadActiveFollowups"
                                        :disabled="cacheActions.loading"
                                    >
                                        <i class="fa fa-list"></i> View Active Follow-ups
                                    </b-button>
                                    <b-button 
                                        variant="warning" 
                                        @click="showClearAllConfirm = true"
                                        :disabled="cacheActions.loading"
                                    >
                                        <i class="fa fa-trash"></i> Clear All Follow-ups
                                    </b-button>
                                </b-button-group>
                                
                                <div class="mt-3">
                                    <label class="fw-semi-bold">Clear Specific Patient:</label>
                                    <b-input-group>
                                        <b-form-input 
                                            v-model="specificPatientId" 
                                            placeholder="Enter Patient ID or search by name..."
                                            :disabled="cacheActions.loading"
                                        />
                                        <b-input-group-append>
                                            <b-button 
                                                variant="danger" 
                                                @click="clearSpecificPatient"
                                                :disabled="!specificPatientId || cacheActions.loading"
                                            >
                                                <i class="fa fa-times"></i> Clear
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </div>
                            </b-card>
                        </b-col>
                    </b-row>

                    <!-- Active Follow-ups List -->
                    <div v-if="activeFollowups.length > 0" class="mb-4">
                        <h5><i class="fa fa-users text-info mr-2"></i>Active Follow-ups ({{ activeFollowups.length }})</h5>
                        <b-table 
                            :items="activeFollowups" 
                            :fields="activeFollowupsFields"
                            striped 
                            hover 
                            small
                            responsive
                        >
                            <template #cell(actions)="row">
                                <b-button 
                                    variant="outline-danger" 
                                    size="sm"
                                    @click="clearSpecificPatientById(row.item.patientId)"
                                    :disabled="cacheActions.loading"
                                >
                                    <i class="fa fa-times"></i> Clear
                                </b-button>
                            </template>
                        </b-table>
                    </div>

                    <!-- Results/Status Messages -->
                    <div v-if="cacheActions.message" class="mb-3">
                        <b-alert 
                            :variant="cacheActions.success ? 'success' : 'danger'" 
                            show 
                            dismissible
                            @dismissed="cacheActions.message = null"
                        >
                            {{ cacheActions.message }}
                        </b-alert>
                    </div>

                    <!-- Loading Indicator -->
                    <div v-if="cacheActions.loading" class="text-center mb-3">
                        <b-spinner></b-spinner>
                        <span class="ml-2">{{ cacheActions.loadingText || 'Processing...' }}</span>
                    </div>
                    </div>
                    <!-- End Momento Cache Tab -->

                    <!-- Upstash/Redis Cache Tab -->
                    <div v-if="cacheManagementTab === 'upstash'">
                        <h5 class="mb-3"><i class="fa fa-server text-success mr-2"></i>Upstash/Redis Cache (Events & KPI)</h5>

                        <b-row class="mb-4">
                            <b-col md="5">
                                <b-card header="Cache Statistics" border-variant="success">
                                    <div v-if="upstashStats.loading" class="text-center">
                                        <b-spinner small></b-spinner> Loading...
                                    </div>
                                    <div v-else>
                                        <p class="mb-2"><strong>Environment:</strong> {{ upstashStats.environment || 'N/A' }}</p>
                                        <p class="mb-2"><strong>Total Keys:</strong> {{ upstashStats.totalKeys || 0 }}</p>
                                        <p class="mb-0"><strong>Endpoint:</strong> {{ upstashStats.redisEndpoint || 'N/A' }}</p>
                                        <b-button
                                            variant="outline-success"
                                            size="sm"
                                            @click="loadUpstashStats"
                                            :disabled="upstashStats.loading"
                                            class="mt-2"
                                        >
                                            <i class="fa fa-refresh"></i> Refresh Stats
                                        </b-button>
                                    </div>
                                </b-card>
                            </b-col>

                            <b-col md="7">
                                <b-card header="Key Categories" border-variant="info">
                                    <div v-if="upstashStats.loading" class="text-center">
                                        <b-spinner small></b-spinner>
                                    </div>
                                    <b-table
                                        v-else-if="upstashStats.stats && upstashStats.stats.length > 0"
                                        :items="upstashStats.stats"
                                        :fields="upstashStatsFields"
                                        striped
                                        hover
                                        small
                                        responsive
                                    >
                                        <template #cell(actions)="row">
                                            <b-button
                                                variant="outline-info"
                                                size="sm"
                                                @click="viewUpstashKeys(row.item.pattern)"
                                                :disabled="upstashActions.loading"
                                                class="mr-1"
                                            >
                                                <i class="fa fa-eye"></i>
                                            </b-button>
                                            <b-button
                                                variant="outline-danger"
                                                size="sm"
                                                @click="confirmClearUpstashPattern(row.item)"
                                                :disabled="upstashActions.loading || row.item.count === 0"
                                            >
                                                <i class="fa fa-trash"></i>
                                            </b-button>
                                        </template>
                                    </b-table>
                                    <p v-else class="text-muted mb-0">No statistics available. Click "Refresh Stats" to load.</p>
                                </b-card>
                            </b-col>
                        </b-row>

                        <!-- Search/Delete by Pattern -->
                        <b-row class="mb-4">
                            <b-col md="12">
                                <b-card header="Search & Delete Keys" border-variant="warning">
                                    <b-row>
                                        <b-col md="6">
                                            <label class="fw-semi-bold">Search Pattern:</label>
                                            <b-input-group class="mb-2">
                                                <b-form-input
                                                    v-model="upstashSearchPattern"
                                                    placeholder="e.g., teamio:events:sms_sent:* or teamio:kpi:*"
                                                    :disabled="upstashActions.loading"
                                                />
                                                <b-input-group-append>
                                                    <b-button
                                                        variant="info"
                                                        @click="searchUpstashKeys"
                                                        :disabled="!upstashSearchPattern || upstashActions.loading"
                                                    >
                                                        <i class="fa fa-search"></i> Search
                                                    </b-button>
                                                </b-input-group-append>
                                            </b-input-group>
                                            <small class="text-muted">Use * as wildcard. Examples: teamio:events:sms_sent:{siteID}:*, teamio:kpi:{siteID}:*</small>
                                        </b-col>
                                        <b-col md="6">
                                            <label class="fw-semi-bold">Quick Filters:</label>
                                            <div>
                                                <b-button
                                                    v-for="filter in upstashQuickFilters"
                                                    :key="filter.pattern"
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    class="mr-1 mb-1"
                                                    @click="upstashSearchPattern = filter.pattern; searchUpstashKeys()"
                                                    :disabled="upstashActions.loading"
                                                >
                                                    {{ filter.label }}
                                                </b-button>
                                            </div>
                                        </b-col>
                                    </b-row>
                                </b-card>
                            </b-col>
                        </b-row>

                        <!-- Keys List -->
                        <div v-if="upstashKeys.length > 0" class="mb-4">
                            <h5>
                                <i class="fa fa-key text-info mr-2"></i>Keys Found ({{ upstashKeys.length }})
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    class="ml-2"
                                    @click="confirmClearAllUpstashKeys"
                                    :disabled="upstashActions.loading"
                                >
                                    <i class="fa fa-trash"></i> Delete All Matching
                                </b-button>
                            </h5>
                            <b-table
                                :items="upstashKeys"
                                :fields="upstashKeysFields"
                                striped
                                hover
                                small
                                responsive
                                :per-page="20"
                                :current-page="upstashKeysPage"
                            >
                                <template #cell(key)="row">
                                    <code class="text-dark" style="font-size: 0.85em;">{{ row.item.key }}</code>
                                    <span v-if="row.item.member" class="text-muted ml-2">(member: {{ row.item.member }})</span>
                                </template>
                                <template #cell(ttl)="row">
                                    <span v-if="row.item.ttl === -1" class="text-muted">No expiry</span>
                                    <span v-else-if="row.item.ttl === -2" class="text-danger">Expired</span>
                                    <span v-else>{{ formatTTL(row.item.ttl) }}</span>
                                </template>
                                <template #cell(actions)="row">
                                    <b-button
                                        variant="outline-danger"
                                        size="sm"
                                        @click="deleteUpstashKey(row.item)"
                                        :disabled="upstashActions.loading"
                                    >
                                        <i class="fa fa-times"></i>
                                    </b-button>
                                </template>
                            </b-table>
                            <b-pagination
                                v-if="upstashKeys.length > 20"
                                v-model="upstashKeysPage"
                                :total-rows="upstashKeys.length"
                                :per-page="20"
                                size="sm"
                                class="mt-2"
                            />
                        </div>

                        <!-- Results/Status Messages -->
                        <div v-if="upstashActions.message" class="mb-3">
                            <b-alert
                                :variant="upstashActions.success ? 'success' : 'danger'"
                                show
                                dismissible
                                @dismissed="upstashActions.message = null"
                            >
                                {{ upstashActions.message }}
                            </b-alert>
                        </div>

                        <!-- Loading Indicator -->
                        <div v-if="upstashActions.loading" class="text-center mb-3">
                            <b-spinner></b-spinner>
                            <span class="ml-2">{{ upstashActions.loadingText || 'Processing...' }}</span>
                        </div>
                    </div>
                    <!-- End Upstash/Redis Cache Tab -->

                    <div class="mt-4">
                        <b-button
                            variant="outline-secondary"
                            @click="resetCacheManagement"
                        >
                            <i class="fa fa-arrow-left"></i> Close
                        </b-button>
                    </div>
                </div>

                <!-- Upstash Clear All Confirmation Modal -->
                <b-modal
                    v-model="showUpstashClearConfirm"
                    title="Confirm Clear Cache"
                    ok-variant="danger"
                    @ok="executeClearUpstashPattern"
                    @cancel="upstashClearTarget = null"
                >
                    <p>Are you sure you want to clear the following from Upstash cache?</p>
                    <p v-if="upstashClearTarget">
                        <strong>Pattern:</strong> <code>{{ upstashClearTarget.pattern }}</code><br>
                        <strong>Count:</strong> {{ upstashClearTarget.count }} items
                    </p>
                    <p class="text-danger"><i class="fa fa-warning"></i> This action cannot be undone!</p>
                </b-modal>

                <!-- TeamIO Knowledge Base Management -->
                <div class="mt-5" v-if="showTeamioKnowledge">
                    <h4 class="mb-3">
                        <i class="fa fa-brain text-purple mr-2"></i>TeamIO Knowledge Base Management
                    </h4>
                    <p class="text-muted mb-4">Manage AI behavior, voice settings, and knowledge base for TeamIO platform support</p>
                    
                    <!-- Tab Navigation -->
                    <b-nav tabs class="mb-4">
                        <b-nav-item 
                            :active="teamioKnowledgeTab === 'ai'" 
                            @click="teamioKnowledgeTab = 'ai'"
                        >
                            <i class="fa fa-robot mr-1"></i> AI Behavior
                        </b-nav-item>
                        <b-nav-item 
                            :active="teamioKnowledgeTab === 'voice'" 
                            @click="teamioKnowledgeTab = 'voice'"
                        >
                            <i class="fa fa-microphone mr-1"></i> Voice Settings
                        </b-nav-item>
                        <b-nav-item 
                            :active="teamioKnowledgeTab === 'knowledge'" 
                            @click="teamioKnowledgeTab = 'knowledge'"
                        >
                            <i class="fa fa-brain mr-1"></i> Knowledge Base
                        </b-nav-item>
                    </b-nav>

                    <!-- AI Behavior Tab -->
                    <div v-if="teamioKnowledgeTab === 'ai'" class="tab-content">
                        <b-card>
                            <b-card-header>
                                <h5 class="mb-0"><i class="fa fa-robot text-primary mr-2"></i>AI Personality & Behavior</h5>
                            </b-card-header>
                            <b-card-body>
                                <b-row>
                                    <b-col md="6">
                                        <b-form-group label="AI Personality" label-for="ai-personality">
                                            <b-form-select 
                                                id="ai-personality" 
                                                v-model="teamioBot.aiPersonality"
                                                :options="[
                                                    { value: 'professional', text: 'Professional & Helpful' },
                                                    { value: 'friendly', text: 'Friendly & Casual' },
                                                    { value: 'technical', text: 'Technical & Detailed' },
                                                    { value: 'concise', text: 'Concise & Direct' }
                                                ]"
                                            ></b-form-select>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group label="Response Style" label-for="response-style">
                                            <b-form-select 
                                                id="response-style" 
                                                v-model="teamioBot.responseStyle"
                                                :options="[
                                                    { value: 'detailed', text: 'Detailed Explanations' },
                                                    { value: 'concise', text: 'Concise Answers' },
                                                    { value: 'step-by-step', text: 'Step-by-Step Guides' },
                                                    { value: 'examples', text: 'Include Examples' }
                                                ]"
                                            ></b-form-select>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <b-form-group label="Welcome Message" label-for="welcome-message">
                                    <b-form-textarea 
                                        id="welcome-message"
                                        v-model="teamioBot.welcomeMessage"
                                        placeholder="Hi! I'm your TeamIO Assistant. How can I help you today?"
                                        rows="3"
                                    ></b-form-textarea>
                                </b-form-group>
                                <b-form-group label="Custom System Prompt (Advanced)" label-for="system-prompt">
                                    <b-form-textarea 
                                        id="system-prompt"
                                        v-model="teamioBot.systemPrompt"
                                        placeholder="You are a helpful TeamIO assistant..."
                                        rows="4"
                                    ></b-form-textarea>
                                    <small class="text-muted">Advanced: Custom instructions for AI behavior</small>
                                </b-form-group>
                            </b-card-body>
                        </b-card>

                        <!-- Test Knowledge Base Section -->
                        <b-card class="mt-4" bg-variant="light" border-variant="info">
                            <b-card-header class="bg-info text-white">
                                <h5 class="mb-0"><i class="fa fa-flask mr-2"></i>Test Your Knowledge Base</h5>
                            </b-card-header>
                            <b-card-body>
                                <p class="mb-3">
                                    <i class="fa fa-info-circle text-info mr-2"></i>
                                    Test how well your AI assistant uses the indexed TeamIO knowledge by asking questions below.
                                </p>
                                
                                <h6 class="text-primary mb-2">
                                    <i class="fa fa-lightbulb mr-1"></i>Suggested Test Questions:
                                </h6>
                                <div class="mb-3">
                                    <div class="mb-1">
                                        <button class="btn btn-sm btn-outline-primary mr-2" @click="testBotWithQuestion('What is TeamIO?')">
                                            <i class="fa fa-play mr-1"></i>Test
                                        </button>
                                        "What is TeamIO?"
                                    </div>
                                    <div class="mb-1">
                                        <button class="btn btn-sm btn-outline-primary mr-2" @click="testBotWithQuestion('How do I add new users to TeamIO?')">
                                            <i class="fa fa-play mr-1"></i>Test
                                        </button>
                                        "How do I add new users to TeamIO?"
                                    </div>
                                    <div class="mb-1">
                                        <button class="btn btn-sm btn-outline-primary mr-2" @click="testBotWithQuestion('How does TeamIO billing work?')">
                                            <i class="fa fa-play mr-1"></i>Test
                                        </button>
                                        "How does TeamIO billing work?"
                                    </div>
                                    <div class="mb-1">
                                        <button class="btn btn-sm btn-outline-primary mr-2" @click="testBotWithQuestion('How do I set up appointments in TeamIO?')">
                                            <i class="fa fa-play mr-1"></i>Test
                                        </button>
                                        "How do I set up appointments in TeamIO?"
                                    </div>
                                </div>

                                <div class="alert alert-success">
                                    <h6 class="mb-2">
                                        <i class="fa fa-check-circle mr-1"></i>What to Look For:
                                    </h6>
                                    <div>• Specific TeamIO feature mentions</div>
                                    <div>• Accurate step-by-step instructions</div>
                                    <div>• References to your documentation</div>
                                    <div>• Relevant and helpful responses</div>
                                </div>

                                <div class="alert alert-warning">
                                    <h6 class="mb-2">
                                        <i class="fa fa-exclamation-triangle mr-1"></i>Testing Tips:
                                    </h6>
                                    <div><strong>Save settings first:</strong> Click "Save Settings" before testing to ensure changes are applied</div>
                                    <div><strong>Wait for indexing:</strong> Allow 1-2 minutes after adding new URLs for full indexing</div>
                                    <div><strong>Try variations:</strong> Ask the same question in different ways to test understanding</div>
                                    <div><strong>Check responses:</strong> Look for specific details from your indexed documentation</div>
                                </div>
                            </b-card-body>
                        </b-card>
                    </div>

                    <!-- Voice Settings Tab -->
                    <div v-if="teamioKnowledgeTab === 'voice'" class="tab-content">
                        <b-card>
                            <b-card-header>
                                <h5 class="mb-0"><i class="fa fa-microphone text-success mr-2"></i>Voice Configuration</h5>
                            </b-card-header>
                            <b-card-body>
                                <b-row>
                                    <b-col md="4">
                                        <b-form-group label="Voice ID" label-for="voice-id">
                                            <b-form-select 
                                                id="voice-id" 
                                                v-model="teamioBot.voiceId"
                                                :options="[
                                                    { value: '21m00Tcm4TlvDq8ikWAM', text: 'Rachel (Professional)' },
                                                    { value: 'AZnzlk1XvdvUeBnXmlld', text: 'Domi (Friendly)' },
                                                    { value: 'EXAVITQu4vr4xnSDxMaL', text: 'Bella (Warm)' },
                                                    { value: 'ErXwobaYiN019PkySvjV', text: 'Antoni (Clear)' }
                                                ]"
                                            ></b-form-select>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="4">
                                        <b-form-group label="Speed" label-for="voice-speed">
                                            <b-form-input 
                                                id="voice-speed"
                                                v-model="teamioBot.voiceSpeed"
                                                type="range"
                                                min="0.5"
                                                max="2.0"
                                                step="0.1"
                                            ></b-form-input>
                                            <small class="text-muted">{{ teamioBot.voiceSpeed }}x</small>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="4">
                                        <b-form-group label="Stability" label-for="voice-stability">
                                            <b-form-input 
                                                id="voice-stability"
                                                v-model="teamioBot.voiceStability"
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.1"
                                            ></b-form-input>
                                            <small class="text-muted">{{ teamioBot.voiceStability }}</small>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <b-button variant="primary" @click="testVoice" :disabled="voiceTestLoading">
                                    <i class="fa fa-play mr-1"></i>
                                    <span v-if="voiceTestLoading">Testing...</span>
                                    <span v-else>Test Voice</span>
                                </b-button>
                            </b-card-body>
                        </b-card>
                    </div>

                    <!-- Knowledge Base Tab -->
                    <div v-if="teamioKnowledgeTab === 'knowledge'" class="tab-content">
                        <b-card>
                            <b-card-header>
                                <h5 class="mb-0"><i class="fa fa-brain text-info mr-2"></i>TeamIO Platform Knowledge</h5>
                            </b-card-header>
                            <b-card-body>
                                <div class="mb-4">
                                    <label class="fw-semi-bold mb-2">
                                        <i class="fa fa-link mr-2 text-primary"></i>Documentation URLs to Index:
                                    </label>
                                    <p class="text-muted small mb-2">
                                        <i class="fa fa-info-circle mr-1"></i>
                                        Enter one URL per line. You can paste multiple URLs at once.
                                    </p>
                                    <textarea 
                                        v-model="teamioBot.knowledgeUrlsText"
                                        placeholder="https://teamioapp.notion.site/Teamio-Open-Dental-How-to-Videos-FAQs-6a84d959cc1f40bebb135ebc52d908cf

💡 TIP: Start with one main page - the system will automatically discover and crawl all linked pages up to 3 levels deep, including YouTube videos and related content!"
                                        class="form-control"
                                        rows="8"
                                        style="font-family: monospace; font-size: 14px; line-height: 1.4;"
                                    ></textarea>
                                    <div class="mt-2 d-flex justify-content-between align-items-center">
                                        <div class="d-flex gap-2">
                                            <b-button variant="outline-primary" size="sm" @click="loadDefaultTeamioUrls">
                                                <i class="fa fa-sitemap mr-1"></i> Load Root FAQ Page
                                            </b-button>
                                            <b-button variant="outline-warning" size="sm" @click="clearAllUrls">
                                                <i class="fa fa-eraser mr-1"></i> Clear All
                                            </b-button>
                                        </div>
                                        <small class="text-muted">
                                            <i class="fa fa-info-circle mr-1"></i>
                                            Auto-crawls linked pages (3 levels deep)
                                        </small>
                                    </div>
                                </div>

                                <!-- Temporarily removed to debug curved text issue -->
                                <div class="alert alert-info">
                                    <strong>📚 Documentation:</strong> Add URLs to TeamIO help documentation, user guides, or training materials.
                                </div>

                                <!-- Update Knowledge Base Button -->
                                <div class="mb-4">
                                    <b-button 
                                        variant="success" 
                                        @click="updateKnowledgeBase" 
                                        :disabled="knowledgeUpdateLoading"
                                        size="lg"
                                    >
                                        <i class="fa fa-brain mr-2"></i>
                                        <span v-if="knowledgeUpdateLoading">Updating Knowledge Base...</span>
                                        <span v-else>Update Knowledge Base</span>
                                    </b-button>
                                </div>
                            </b-card-body>
                        </b-card>

                        <!-- Knowledge Base Status Card -->
                        <b-card class="mt-4">
                            <b-card-header class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="fa fa-chart-bar mr-2"></i>Knowledge Base Status</h6>
                                <div>
                                    <b-button size="sm" variant="outline-primary" 
                                              @click="diagnosticCheck"
                                              title="Run full diagnostic check" class="mr-2">
                                        <i class="fa fa-stethoscope mr-1"></i>Diagnose
                                    </b-button>
                                    <b-button size="sm" variant="outline-danger" 
                                              @click="clearAllStatus"
                                              title="Clear all cached status">
                                        <i class="fa fa-trash mr-1"></i>Clear Status
                                    </b-button>
                                </div>
                            </b-card-header>
                            <b-card-body>
                                <b-row>
                                    <b-col md="4">
                                        <strong>Last Updated:</strong><br>
                                        <span class="text-muted">{{ teamioBot.knowledgeStatus.lastUpdated || 'Never' }}</span>
                                    </b-col>
                                    <b-col md="4">
                                        <strong>Documents Indexed:</strong><br>
                                        <span v-if="isKnowledgeStatusConsistent" :class="getDocsIndexedClass()">
                                            {{ getDocsIndexedText() }}
                                        </span>
                                        <span v-else class="text-warning">
                                            <i class="fa fa-clock mr-1"></i>Processing...
                                        </span>
                                    </b-col>
                                    <b-col md="4">
                                        <strong>Status:</strong><br>
                                        <b-badge v-if="isKnowledgeStatusConsistent" :variant="getStatusVariant()">
                                            {{ getStatusText() }}
                                        </b-badge>
                                        <b-badge v-else variant="warning">
                                            <i class="fa fa-spinner fa-spin mr-1"></i>Indexing
                                        </b-badge>
                                    </b-col>
                                </b-row>
                            </b-card-body>
                        </b-card>

                        <!-- Processing Status Card (shown when inconsistent) -->
                        <b-card bg-variant="warning" text-variant="dark" class="mt-4" v-if="!isKnowledgeStatusConsistent">
                            <b-card-body class="text-center">
                                <i class="fa fa-spinner fa-spin fa-2x mb-3"></i>
                                <h5 class="mb-2">Knowledge Base Processing</h5>
                                <p class="mb-0">
                                    Your knowledge base is currently being indexed. Document counts and URL lists will be displayed once processing is complete.
                                </p>
                                <small class="text-muted">
                                    This usually takes 2-5 minutes. You can refresh the page to check for updates.
                                </small>
                            </b-card-body>
                        </b-card>

                        <!-- Previously Indexed URLs Card -->
                        <b-card bg-variant="info" text-variant="white" class="mt-4" v-if="isKnowledgeStatusConsistent && ((teamioBot.knowledgeStatus.crawledPages && teamioBot.knowledgeStatus.crawledPages.length > 0) || (teamioBot.knowledgeStatus.urls && teamioBot.knowledgeStatus.urls.length > 0))">
                            <b-card-header>
                                <h6 class="mb-0 text-white">
                                    <i class="fa fa-list mr-2"></i>Previously Indexed URLs 
                                    ({{ teamioBot.knowledgeStatus.crawledPages?.length || teamioBot.knowledgeStatus.urls?.length || 0 }})
                                </h6>
                            </b-card-header>
                            <b-card-body>
                                <!-- Enhanced Display with Rich Information -->
                                <div v-if="teamioBot.knowledgeStatus.crawledPages && teamioBot.knowledgeStatus.crawledPages.length > 0" class="enhanced-urls-list">
                                    <div v-for="(page, index) in teamioBot.knowledgeStatus.crawledPages" :key="index" class="enhanced-url-item mb-3 p-3" 
                                         style="background: rgba(255,255,255,0.1); border-radius: 8px; border-left: 4px solid #28a745;">
                                        <div class="d-flex align-items-start">
                                            <div class="mr-3 mt-1">
                                                <i :class="getStatusIcon(page.status)" :style="getStatusColor(page.status)"></i>
                                            </div>
                                            <div class="flex-grow-1">
                                                <h6 class="mb-1 text-white">
                                                    {{ page.title || 'Untitled Page' }}
                                                </h6>
                                                <p class="mb-2 text-light small">
                                                    {{ page.description || 'No description available' }}
                                                </p>
                                                <div class="mb-2">
                                                    <a :href="page.url" target="_blank" class="text-white text-decoration-underline small">
                                                        {{ page.url }}
                                                    </a>
                                                </div>
                                                <div class="d-flex flex-wrap">
                                                    <b-badge variant="light" class="mr-2 mb-1">
                                                        <i class="fa fa-file-text mr-1"></i>{{ page.chunks || 0 }} chunks
                                                    </b-badge>
                                                    <b-badge variant="light" class="mr-2 mb-1" v-if="page.links_found > 0">
                                                        <i class="fa fa-link mr-1"></i>{{ page.links_found }} links found
                                                    </b-badge>
                                                    <b-badge variant="light" class="mr-2 mb-1" v-if="page.depth !== undefined">
                                                        <i class="fa fa-layer-group mr-1"></i>Depth {{ page.depth }}
                                                    </b-badge>
                                                    <b-badge variant="light" class="mr-2 mb-1" v-if="page.indexed_at">
                                                        <i class="fa fa-clock mr-1"></i>{{ formatDate(page.indexed_at) }}
                                                    </b-badge>
                                                </div>
                                                <div v-if="page.status !== 'success'" class="mt-2">
                                                    <small class="text-warning">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>
                                                        {{ page.reason || page.error || 'Unknown issue' }}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Fallback: Simple URL Display -->
                                <div v-else class="simple-urls-list">
                                    <div v-for="(url, index) in teamioBot.knowledgeStatus.urls" :key="index" class="indexed-url-item mb-2">
                                        <i class="fa fa-check-circle text-success mr-2"></i>
                                        <a :href="url" target="_blank" class="text-white text-decoration-underline">
                                            {{ url }}
                                        </a>
                                    </div>
                                </div>
                                
                                <small class="text-light">
                                    <i class="fa fa-info-circle mr-1"></i>
                                    These URLs have already been indexed. Adding them again will be automatically skipped.
                                </small>
                            </b-card-body>
                        </b-card>
                    </div>

                    <!-- Action Buttons -->
                    <div class="mt-4 d-flex justify-content-between">
                        <b-button variant="outline-secondary" @click="resetTeamioKnowledge">
                            <i class="fa fa-arrow-left mr-1"></i> Close
                        </b-button>
                        <b-button variant="primary" @click="saveTeamioKnowledgeSettings" :disabled="teamioKnowledgeSaving">
                            <i class="fa fa-save mr-1"></i>
                            <span v-if="teamioKnowledgeSaving">Saving...</span>
                            <span v-else>Save Settings</span>
                        </b-button>
                    </div>
                </div>

                <!-- Twilio Dashboard -->
                <div class="mt-5" v-if="showTwilioDashboard">
                    <h4 class="mb-3">
                        <i class="fa fa-phone-alt text-info mr-2"></i>Twilio Dashboard
                    </h4>

                    <b-tabs v-model="twilioActiveTab" content-class="mt-3">
                        <!-- Tab 1: Billing & Usage -->
                        <b-tab title="Billing & Usage">
                            <!-- Controls row -->
                            <div class="mb-3 d-flex justify-content-between align-items-center">
                                <div class="d-flex align-items-center">
                                    <label class="mr-2 mb-0">Month:</label>
                                    <b-form-input type="month" v-model="twilioUsageMonth"
                                        style="width:200px"></b-form-input>
                                    <b-button variant="primary" @click="loadTwilioUsage" :disabled="twilioUsageLoading" class="ml-2">
                                        <i class="fa fa-search" :class="{ 'fa-spin': twilioUsageLoading }"></i>
                                        {{ twilioUsageLoading ? 'Loading...' : 'Load' }}
                                    </b-button>
                                </div>
                                <b-button variant="outline-secondary" @click="exportTwilioReport"
                                    :disabled="!twilioUsageData">
                                    <i class="fa fa-download"></i> Export CSV
                                </b-button>
                            </div>

                            <!-- Current month warning -->
                            <b-alert show variant="info" v-if="isCurrentTwilioMonth" class="py-1">
                                Current month — data is partial
                            </b-alert>

                            <!-- Loading spinner -->
                            <div v-if="twilioUsageLoading" class="text-center my-5">
                                <b-spinner variant="primary"></b-spinner>
                                <p class="mt-2 text-muted">Fetching usage from Twilio (this may take ~10 seconds)...</p>
                            </div>

                            <!-- Summary cards -->
                            <b-row class="mb-4" v-if="twilioUsageData && !twilioUsageLoading">
                                <b-col cols="6" md="" class="mb-2 mb-md-0">
                                    <b-card class="text-center border-primary h-100">
                                        <h3 class="text-primary mb-1">${{ fmtPrice(twilioUsageData.masterTotal) }}</h3>
                                        <small>Master Account Total</small>
                                    </b-card>
                                </b-col>
                                <b-col cols="6" md="" class="mb-2 mb-md-0">
                                    <b-card class="text-center border-success h-100">
                                        <h3 class="text-success mb-1">${{ fmtPrice(twilioUsageData.siteSubtotal) }}</h3>
                                        <small>Sum of Sites</small>
                                    </b-card>
                                </b-col>
                                <b-col cols="6" md="" class="mb-2 mb-md-0">
                                    <b-card class="text-center h-100" :class="twilioUsageData.discrepancy > 1 ? 'border-danger' : 'border-secondary'">
                                        <h3 :class="twilioUsageData.discrepancy > 1 ? 'text-danger' : 'text-muted'" class="mb-1">
                                            ${{ fmtPrice(twilioUsageData.discrepancy) }}
                                        </h3>
                                        <small>
                                            Discrepancy
                                            <i class="fa fa-info-circle text-muted ml-1"
                                               style="font-size:11px; cursor:help"
                                               v-b-tooltip.hover.top
                                               title="Master total minus the sum of customer sites. This is master-level + master-direct (dev) + any orphan-subaccount spend — not attributable to a customer site."></i>
                                        </small>
                                    </b-card>
                                </b-col>
                                <b-col cols="6" md="" class="mb-2 mb-md-0">
                                    <b-card class="text-center border-info h-100">
                                        <h3 class="text-info mb-1">{{ twilioUsageData.sites.length }}</h3>
                                        <small>Customer Sites</small>
                                    </b-card>
                                </b-col>
                                <b-col cols="6" md="" class="mb-2 mb-md-0">
                                    <b-card class="text-center h-100"
                                        :class="(twilioUsageData.numberInventory && twilioUsageData.numberInventory.orphan) ? 'border-warning' : 'border-secondary'">
                                        <h3 class="mb-1"
                                            :class="(twilioUsageData.numberInventory && twilioUsageData.numberInventory.orphan) ? 'text-warning' : 'text-secondary'">
                                            {{ twilioUsageData.numberInventory ? twilioUsageData.numberInventory.owned : '—' }}
                                        </h3>
                                        <small>
                                            Owned Numbers
                                            <i class="fa fa-info-circle text-muted ml-1"
                                               style="font-size:11px; cursor:help"
                                               v-b-tooltip.hover.top
                                               :title="twilioInventoryTooltip"></i>
                                        </small>
                                    </b-card>
                                </b-col>
                            </b-row>

                            <!-- SMS overage summary banner -->
                            <b-alert v-if="twilioOverageBannerVisible"
                                show
                                :variant="twilioUsageData.overageSummary.sitesOverLimit.length > 0 ? 'danger' : 'warning'"
                                class="py-2 mb-3 small">
                                <i :class="['fa mr-2', twilioUsageData.overageSummary.sitesOverLimit.length > 0 ? 'fa-exclamation-circle' : 'fa-exclamation-triangle']"></i>
                                <strong v-if="twilioUsageData.overageSummary.sitesOverLimit.length > 0">
                                    {{ twilioUsageData.overageSummary.sitesOverLimit.length }} site{{ twilioUsageData.overageSummary.sitesOverLimit.length === 1 ? '' : 's' }} over the 5,000-message monthly limit
                                </strong>
                                <span v-if="twilioUsageData.overageSummary.sitesOverLimit.length > 0 && twilioUsageData.overageSummary.sitesAtWarning.length > 0">,</span>
                                <strong v-if="twilioUsageData.overageSummary.sitesAtWarning.length > 0">
                                    {{ twilioUsageData.overageSummary.sitesAtWarning.length }} site{{ twilioUsageData.overageSummary.sitesAtWarning.length === 1 ? '' : 's' }} approaching the limit (≥90%)
                                </strong>
                                <span v-if="twilioUsageData.overageSummary.totalOverageDollars > 0">
                                    &nbsp;·&nbsp; estimated overage charges: <strong>${{ fmtPrice(twilioUsageData.overageSummary.totalOverageDollars) }}</strong>
                                </span>
                                <small class="d-block mt-1 text-muted">
                                    A2P fee schedule: 5,000 messages/month included per site, $0.025 per overage message.
                                </small>
                            </b-alert>

                            <!-- "Charged but unused" — orphan / inactive-site / idle numbers -->
                            <b-alert v-if="twilioUsageData && twilioUsageData.wasteAlerts && twilioUsageData.wasteAlerts.length"
                                show variant="warning" class="py-2 mb-3 small">
                                <i class="fa fa-dollar-sign mr-2"></i>
                                <strong>{{ wasteNumberCount }} number(s) charged but possibly unused</strong> — review whether any can be released:
                                <ul class="mb-0 mt-1">
                                    <li v-for="(w, idx) in twilioUsageData.wasteAlerts" :key="idx">
                                        <strong>{{ w.name }}</strong> — {{ wasteReasonLabel(w.reason) }}:
                                        <span style="font-family:monospace">{{ w.numbers.join(', ') }}</span>
                                    </li>
                                </ul>
                            </b-alert>

                            <!-- Counting blind-spot: active sites that send but have no Twilio subaccount -->
                            <b-alert v-if="twilioUsageData && twilioUsageData.uncountedSites && twilioUsageData.uncountedSites.length"
                                show variant="danger" class="py-2 mb-3 small">
                                <i class="fa fa-exclamation-circle mr-2"></i>
                                <strong>{{ twilioUsageData.uncountedSites.length }} active site(s) have a phone number but no Twilio subaccount</strong>
                                — their SMS is billed on the master account and is <strong>not counted per-site</strong> below:
                                <span v-for="(s, idx) in twilioUsageData.uncountedSites" :key="s.SiteID">
                                    {{ s.CustomerName }}<span v-if="idx < twilioUsageData.uncountedSites.length - 1">, </span>
                                </span>
                            </b-alert>

                            <p v-if="twilioUsageData && !twilioUsageLoading" class="text-muted small mb-2">
                                SMS counts &amp; the 5,000/mo allowance come from Twilio per-subaccount usage.
                                <strong>Email</strong> is report-only, tracked from our own records (campaigns + appointment reminders) since SendGrid is one shared account.
                                <strong>WhatsApp</strong> is report-only and not yet validated against live traffic (no site has it enabled).
                            </p>

                            <!-- Sites table -->
                            <b-table v-if="twilioUsageData && !twilioUsageLoading"
                                :items="twilioSiteRows"
                                :fields="twilioUsageFields"
                                striped hover responsive
                                show-empty
                                empty-text="No usage data found"
                                sort-by="totalPrice"
                                :sort-desc="true"
                            >
                                <template #cell(CustomerName)="row">
                                    <strong>{{ row.item.CustomerName }}</strong>
                                </template>

                                <template #cell(totalPrice)="row">
                                    ${{ fmtPrice(row.item.totalPrice) }}
                                </template>

                                <template #cell(smsOut)="row">
                                    ${{ fmtPrice(row.item.smsOut) }}
                                </template>

                                <template #cell(smsIn)="row">
                                    ${{ fmtPrice(row.item.smsIn) }}
                                </template>

                                <template #cell(carrierFees)="row">
                                    ${{ fmtPrice(row.item.carrierFees) }}
                                </template>

                                <template #cell(phoneNumbers)="row">
                                    ${{ fmtPrice(row.item.phoneNumbers) }}
                                </template>

                                <template #cell(activePatients)="row">
                                    <span v-if="row.item.activePatients != null">{{ row.item.activePatients.toLocaleString() }}</span>
                                    <span v-else class="text-muted">—</span>
                                </template>

                                <template #cell(totalPatients)="row">
                                    <span v-if="row.item.totalPatients != null">{{ row.item.totalPatients.toLocaleString() }}</span>
                                    <span v-else class="text-muted">—</span>
                                </template>

                                <template #cell(email)="row">
                                    {{ (row.item.email || 0).toLocaleString() }}
                                </template>

                                <template #cell(whatsapp)="row">
                                    <span :class="row.item.whatsapp ? '' : 'text-muted'">{{ (row.item.whatsapp || 0).toLocaleString() }}</span>
                                </template>

                                <template #cell(usageState)="row">
                                    <div v-if="row.item.usage" class="twilio-usage-cell">
                                        <span :class="['twilio-usage-badge', 'twilio-usage-badge--' + row.item.usage.usageState]">
                                            <i v-if="row.item.usage.usageState === 'overage'" class="fa fa-exclamation-circle"></i>
                                            <i v-else-if="row.item.usage.usageState === 'warning'" class="fa fa-exclamation-triangle"></i>
                                            <i v-else class="fa fa-check"></i>
                                            {{ row.item.usage.smsCombined.toLocaleString() }} / {{ row.item.usage.allowance.toLocaleString() }}
                                            <small class="ml-1">({{ row.item.usage.usagePct }}%)</small>
                                        </span>
                                        <small v-if="row.item.usage.usageState === 'overage'" class="d-block text-danger mt-1">
                                            +{{ row.item.usage.overageMessages.toLocaleString() }} over · ${{ fmtPrice(row.item.usage.overageDollars) }}
                                        </small>
                                        <!-- Segments = the billing unit; messages alone hid the 2026-07-02
                                             Invisalign-® blast (3.3k msgs but 39.5k segments ≈ $493/day) -->
                                        <small v-if="row.item.usage.smsSegments != null" class="d-block text-muted mt-1">
                                            {{ (row.item.usage.smsSegments || 0).toLocaleString() }} segments · ${{ fmtPrice(row.item.usage.smsCostUsd || 0) }} SMS cost
                                        </small>
                                    </div>
                                    <span v-else class="text-muted">—</span>
                                </template>

                                <template #cell(actions)="row">
                                    <b-button size="sm" variant="outline-info" @click="viewTwilioSiteDetail(row.item)">
                                        Details
                                    </b-button>
                                </template>
                            </b-table>

                            <!-- Email-only sites (tracked email volume, no Twilio subaccount row above) -->
                            <b-alert show variant="info" class="py-2 mt-2 small"
                                v-if="twilioEmailOnlySites.length">
                                <i class="fa fa-envelope mr-2"></i>
                                <strong>Email-only volume (report-only)</strong> — sites with tracked email this month but no Twilio subaccount:
                                <span v-for="(e, idx) in twilioEmailOnlySites" :key="e.SiteID">
                                    {{ e.CustomerName }} ({{ e.totalEmail.toLocaleString() }})<span v-if="idx < twilioEmailOnlySites.length - 1">, </span>
                                </span>
                            </b-alert>

                            <!-- Errors -->
                            <b-alert show variant="warning" v-if="twilioUsageData && twilioUsageData.errors && twilioUsageData.errors.length">
                                Failed to fetch {{ twilioUsageData.errors.length }} site(s):
                                <span v-for="(e, idx) in twilioUsageData.errors" :key="idx">
                                    {{ e.CustomerName }}<span v-if="idx < twilioUsageData.errors.length - 1">, </span>
                                </span>
                            </b-alert>
                        </b-tab>

                        <!-- Tab 2: A2P Status (existing content) -->
                        <b-tab title="A2P Status">
                            <p class="text-muted mb-4">Monitor A2P 10DLC registration status across all sites</p>

                            <!-- Summary Cards -->
                            <b-row class="mb-4">
                                <b-col md="3">
                                    <b-card class="text-center border-success">
                                        <h3 class="text-success mb-2">{{ a2pStats.approved }}</h3>
                                        <p class="mb-0">Approved Sites</p>
                                    </b-card>
                                </b-col>
                                <b-col md="3">
                                    <b-card class="text-center border-warning">
                                        <h3 class="text-warning mb-2">{{ a2pStats.pending }}</h3>
                                        <p class="mb-0">Pending Approval</p>
                                    </b-card>
                                </b-col>
                                <b-col md="3">
                                    <b-card class="text-center border-info">
                                        <h3 class="text-info mb-2">{{ a2pStats.inProgress }}</h3>
                                        <p class="mb-0">In Progress</p>
                                    </b-card>
                                </b-col>
                                <b-col md="3">
                                    <b-card class="text-center border-danger">
                                        <h3 class="text-danger mb-2">{{ a2pStats.failed }}</h3>
                                        <p class="mb-0">Failed/Rejected</p>
                                    </b-card>
                                </b-col>
                            </b-row>

                            <!-- Controls -->
                            <div class="mb-3 d-flex justify-content-between align-items-center">
                                <div>
                                    <b-button variant="primary" @click="refreshA2PStatus" :disabled="a2pLoading">
                                        <i class="fa fa-refresh" :class="{ 'fa-spin': a2pLoading }"></i>
                                        {{ a2pLoading ? 'Refreshing...' : 'Refresh Status' }}
                                    </b-button>
                                    <b-button variant="outline-secondary" @click="exportA2PReport" class="ml-2">
                                        <i class="fa fa-download"></i> Export Report
                                    </b-button>
                                </div>
                                <div>
                                    <b-form-checkbox v-model="autoRefresh" @change="toggleAutoRefresh">
                                        Auto-refresh (30s)
                                    </b-form-checkbox>
                                </div>
                            </div>

                            <!-- A2P Registrations Table -->
                            <b-table
                                :items="a2pRegistrations"
                                :fields="a2pFields"
                                striped
                                hover
                                responsive
                                :busy="a2pLoading"
                                show-empty
                                empty-text="No A2P registrations found"
                            >
                                <template #table-busy>
                                    <div class="text-center text-danger my-2">
                                        <b-spinner class="align-middle"></b-spinner>
                                        <strong> Loading A2P registrations...</strong>
                                    </div>
                                </template>

                                <template #cell(CustomerName)="row">
                                    <strong>{{ row.item.CustomerName || 'Unknown' }}</strong>
                                </template>

                                <template #cell(Status)="row">
                                    <b-badge :variant="getStatusVariant(row.item.Status)">
                                        {{ row.item.Status || 'Unknown' }}
                                    </b-badge>
                                </template>

                                <template #cell(ProgressPercentage)="row">
                                    <div>
                                        <b-progress
                                            :value="row.item.ProgressPercentage || 0"
                                            :max="100"
                                            :variant="getProgressVariant(row.item.ProgressPercentage || 0)"
                                            class="mb-1"
                                        ></b-progress>
                                        <small>{{ row.item.ProgressPercentage || 0 }}%</small>
                                    </div>
                                </template>

                                <template #cell(CreatedAt)="row">
                                    {{ formatDate(row.item.CreatedAt) }}
                                </template>

                                <template #cell(UpdatedAt)="row">
                                    {{ formatDate(row.item.UpdatedAt) }}
                                </template>

                                <template #cell(actions)="row">
                                    <b-button-group size="sm">
                                        <b-button variant="info" @click="viewA2PDetails(row.item)">
                                            <i class="fa fa-eye"></i> Details
                                        </b-button>
                                        <b-button variant="success" @click="refreshSiteStatus(row.item)">
                                            <i class="fa fa-refresh"></i> Refresh
                                        </b-button>
                                    </b-button-group>
                                </template>
                            </b-table>
                        </b-tab>

                        <!-- Tab 3: SMS by Feature — per-site attribution from the sms_send_log ledger -->
                        <b-tab title="SMS by Feature">
                            <div class="mb-2 text-muted small">
                                Per-site SMS broken down by feature (campaign / reminders / chat / …) from the message
                                ledger — the attribution the Twilio billing total can't show. Outbound sends only.
                                <br>
                                Cost here is the <strong>fully-loaded estimate</strong>, on purpose. Twilio prices each
                                message <em>after</em> it sends, so a fleet-wide actual would be missing its most recent
                                days and would read as a cost drop. For actuals — with the coverage figure that makes them
                                safe to read — use the <strong>Credit Cost</strong> tab.
                            </div>
                            <div class="mb-3 d-flex align-items-center">
                                <label class="mr-2 mb-0">Month:</label>
                                <b-form-input type="month" v-model="smsRollupMonth" style="width:200px"></b-form-input>
                                <b-button variant="primary" @click="loadSmsFeatureRollup" :disabled="smsRollupLoading" class="ml-2">
                                    <i class="fa fa-search" :class="{ 'fa-spin': smsRollupLoading }"></i>
                                    {{ smsRollupLoading ? 'Loading...' : 'Load' }}
                                </b-button>
                            </div>

                            <div v-if="smsRollupLoading" class="text-center my-5">
                                <b-spinner variant="primary"></b-spinner>
                            </div>

                            <div v-else-if="smsRollupData">
                                <b-row class="mb-3">
                                    <b-col><b-card class="text-center py-2"><h4 class="text-primary mb-0">{{ (smsRollupData.totals.credits || 0).toLocaleString() }}</h4><small class="text-muted">Credits</small></b-card></b-col>
                                    <b-col><b-card class="text-center py-2"><h4 class="text-success mb-0">${{ fmtPrice(smsRollupData.totals.estCostUsd) }}</h4><small class="text-muted">Est. cost to Teamio</small></b-card></b-col>
                                    <b-col><b-card class="text-center py-2"><h4 class="text-info mb-0">{{ (smsRollupData.totals.messages || 0).toLocaleString() }}</h4><small class="text-muted">Messages</small></b-card></b-col>
                                    <b-col><b-card class="text-center py-2"><h4 :class="smsRollupData.totals.blocked > 0 ? 'text-danger' : 'text-muted'" class="mb-0">{{ smsRollupData.totals.blocked || 0 }}</h4><small class="text-muted">Blocked</small></b-card></b-col>
                                </b-row>

                                <b-alert show variant="light" class="py-1 small">{{ smsRollupData.note }}</b-alert>

                                <b-table v-if="smsRollupData.sites.length" :items="smsRollupData.sites" :fields="smsRollupFields" small striped responsive>
                                    <template #cell(siteName)="row">{{ row.item.siteName || row.item.siteId }}</template>
                                    <template #cell(credits)="row">{{ (row.item.credits || 0).toLocaleString() }}</template>
                                    <template #cell(estCostUsd)="row">${{ fmtPrice(row.item.estCostUsd) }}</template>
                                    <template #cell(blocked)="row"><span :class="row.item.blocked > 0 ? 'text-danger' : ''">{{ row.item.blocked }}</span></template>
                                    <template #cell(byFeature)="row">
                                        <b-badge v-for="(b, f) in row.item.byFeature" :key="f" variant="light" class="border mr-1 mb-1 font-weight-normal">{{ f }}: {{ b.credits }}</b-badge>
                                    </template>
                                    <template #cell(mode)="row">
                                        <span class="text-muted small">att {{ row.item.byMode.attended.credits }} · unatt {{ row.item.byMode.unattended.credits }}</span>
                                    </template>
                                </b-table>
                                <div v-else class="text-muted text-center my-4">No SMS sent in this month.</div>
                            </div>
                            <div v-else class="text-muted text-center my-4">Pick a month and click Load.</div>
                        </b-tab>

                        <!--
                          Tab 4: Credit Cost — the ONLY surface showing estimated vs ACTUAL cost.

                          Lives here rather than on the practice-facing usage report so internal cost
                          never renders on a page a practice loads (no screen-share exposure), and so
                          it sits beside the Twilio billing totals it exists to be reconciled against.

                          Unlike "SMS by Feature" (fleet-wide, teamio-admin rollup, estimate only,
                          outbound only) this is per-site, carries Twilio's ACTUAL price, includes
                          inbound, and is gated by a real server-side verifyIdToken.
                        -->
                        <b-tab title="Credit Cost">
                            <div class="mb-2 text-muted small">
                                Estimated vs <strong>actual</strong> cost to Teamio for one site, from the message ledger.
                                Actuals are Twilio's own per-message price. <strong>Twilio assigns that price only after
                                the message is sent</strong> (typically within an hour), and the reconcile sweep records it
                                on the next <strong>hourly</strong> run — so sends from roughly the last two hours show no
                                actual cost yet, and the current month always reads a little low. Check
                                <em>Priced&nbsp;%</em> below before comparing estimate to actual: under 100%, the actual is
                                <em>incomplete</em>, not lower. To reconcile a specific day, wait until the run after it.
                            </div>

                            <div class="mb-3 d-flex align-items-center flex-wrap">
                                <label class="mr-2 mb-0">Site:</label>
                                <b-form-select v-model="usageCostSiteId" :options="usageCostSiteOptions"
                                    style="width:260px" class="mr-3"></b-form-select>
                                <label class="mr-2 mb-0">Month:</label>
                                <b-form-input type="month" v-model="usageCostMonth" style="width:180px" class="mr-3"></b-form-input>
                                <label class="mr-2 mb-0">Channel:</label>
                                <b-form-select v-model="usageCostChannel" :options="[{value:'sms',text:'SMS & MMS'},{value:'email',text:'Email'}]"
                                    style="width:140px" class="mr-3"></b-form-select>
                                <b-button variant="primary" @click="loadUsageCost" :disabled="usageCostLoading || !usageCostSiteId">
                                    <i class="fa fa-search" :class="{ 'fa-spin': usageCostLoading }"></i>
                                    {{ usageCostLoading ? 'Loading...' : 'Load' }}
                                </b-button>
                            </div>

                            <div v-if="usageCostLoading" class="text-center my-5">
                                <b-spinner variant="primary"></b-spinner>
                            </div>

                            <b-alert show variant="danger" v-else-if="usageCostError" class="py-2">
                                {{ usageCostError }}
                            </b-alert>

                            <div v-else-if="usageCostData">
                                <b-row class="mb-3">
                                    <b-col><b-card class="text-center py-2"><h4 class="text-primary mb-0">{{ (usageCostData.totals.credits || 0).toLocaleString() }}</h4><small class="text-muted">Credits</small></b-card></b-col>
                                    <b-col><b-card class="text-center py-2"><h4 class="text-secondary mb-0">${{ fmtPrice(usageCostData.totals.estCostUsd) }}</h4><small class="text-muted">Est. cost (loaded)</small></b-card></b-col>
                                    <b-col><b-card class="text-center py-2"><h4 class="text-success mb-0">${{ fmtPrice(usageCostData.totals.actualCostUsd) }}</h4><small class="text-muted">Actual (Twilio base)</small></b-card></b-col>
                                    <b-col>
                                        <b-card class="text-center py-2">
                                            <h4 class="mb-0" :class="usageCostPricedPct === 100 ? 'text-success' : 'text-warning'">
                                                {{ usageCostPricedPct === null ? '—' : usageCostPricedPct + '%' }}
                                            </h4>
                                            <small class="text-muted">Priced</small>
                                        </b-card>
                                    </b-col>
                                </b-row>

                                <!--
                                  TRUE UNIT ECONOMICS, read live from this site's own Twilio subaccount.

                                  Exists because the loaded per-segment estimate above could not see two
                                  real costs (2026-09-08 audit): carrier pass-through VARIES BY SITE
                                  ($0.0043-0.0046/segment at seven sites, $0.0077 at one — same A2P
                                  registration, similar message shape), and fixed monthly A2P/number fees
                                  are invisible to any per-segment model.

                                  Absent when Twilio could not be read, or for the email channel — the
                                  rest of the report must still render, so everything here is v-if'd.
                                -->
                                <b-row v-if="usageCostData.margin" class="mb-3">
                                    <b-col cols="12" sm="6" md class="mb-2 mb-md-0">
                                        <b-card class="text-center py-2">
                                            <h4 class="mb-0" :class="usageCostMarginVariant">
                                                {{ usageCostData.margin.marginPct === null ? '—' : usageCostData.margin.marginPct + '%' }}
                                            </h4>
                                            <small class="text-muted">Margin vs rate card</small>
                                        </b-card>
                                    </b-col>
                                    <b-col cols="12" sm="6" md class="mb-2 mb-md-0">
                                        <b-card class="text-center py-2">
                                            <h4 class="text-dark mb-0">${{ fmtPrice(usageCostData.margin.costPerCreditUsd) }}</h4>
                                            <small class="text-muted">True cost / credit</small>
                                        </b-card>
                                    </b-col>
                                    <b-col cols="12" sm="6" md class="mb-2 mb-md-0">
                                        <b-card class="text-center py-2">
                                            <h4 class="text-dark mb-0">${{ fmtPrice(usageCostData.twilioCost && usageCostData.twilioCost.carrierPerSegmentUsd) }}</h4>
                                            <small class="text-muted">Carrier fee / segment</small>
                                        </b-card>
                                    </b-col>
                                    <b-col cols="12" sm="6" md class="mb-2 mb-md-0">
                                        <b-card class="text-center py-2">
                                            <h4 class="text-dark mb-0">${{ fmtPrice(usageCostData.margin.fixedCostFullMonthUsd) }}</h4>
                                            <small class="text-muted">Recurring fees / month</small>
                                        </b-card>
                                    </b-col>
                                    <!-- One-time A2P brand/campaign vetting fees: real money, shown so the month's
                                         Twilio invoice reconciles, but never part of margin (a registration month
                                         would otherwise read as a permanent loss). -->
                                    <b-col cols="12" sm="6" md class="mb-2 mb-md-0">
                                        <b-card class="text-center py-2">
                                            <h4 class="text-muted mb-0">${{ fmtPrice(usageCostData.twilioCost && usageCostData.twilioCost.oneTimeFeesUsd) }}</h4>
                                            <small class="text-muted">One-time fees (not in margin)</small>
                                        </b-card>
                                    </b-col>
                                </b-row>
                                <div v-else-if="usageCostChannel !== 'email' && usageCostData.twilioCostError" class="text-muted small mb-3">
                                    <i class="fa fa-info-circle mr-1"></i>{{ usageCostTwilioErrorText }}
                                </div>

                                <b-alert v-if="usageCostData.margin" show variant="secondary" class="py-2 small mb-3">
                                    <i class="fa fa-info-circle mr-1"></i>
                                    <strong>Margin is against the rate card, not money received.</strong> Every site is inside
                                    its free allowance and has been billed $0 since launch, so this is what the economics
                                    <em>would</em> be once a site crosses the tier. For the current month, recurring fees are
                                    pro-rated by the share of the month elapsed ({{ Math.round((usageCostData.margin.fixedProration || 0) * 100) }}%),
                                    because Twilio bills them on day 1 — without that, every site looks unprofitable early in
                                    the month; a completed month carries the full fee. One-time registration fees are shown
                                    but excluded from margin. Twilio cost includes inbound messages, because credits do — so
                                    the comparison is like with like. WhatsApp is excluded from margin — no WhatsApp Twilio
                                    cost is read, so its credits would count as revenue with no matching cost<span
                                        v-if="usageCostData.marginExcludes && usageCostData.marginExcludes.whatsappCredits"> ({{ usageCostData.marginExcludes.whatsappCredits.toLocaleString() }} WhatsApp credits left out this month)</span>.
                                    Fleet carrier norm is about $0.0045/segment; materially above that is destination carrier
                                    mix, which is a Twilio support question.
                                </b-alert>

                                <!--
                                  Rendered VERBATIM from the server's costCaveats. Deliberately not
                                  re-worded here: the coverage sentence carries live counts, so a
                                  hardcoded copy would go stale and quietly overstate confidence.
                                -->
                                <b-alert show variant="warning" class="py-2 small mb-3">
                                    <div v-for="(c, i) in (usageCostData.costCaveats || [])" :key="i" :class="{ 'mt-1': i > 0 }">
                                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ c }}
                                    </div>
                                </b-alert>

                                <!--
                                  Per-region rollup. Shown only when the month actually spans BOTH
                                  regions — an all-US site's single row would just restate the totals
                                  above. When Puerto Rico traffic is present, the blended est-vs-actual
                                  ratio in the caveat no longer describes either region, so we show each
                                  region's own loaded-est → Twilio-actual per-segment rate.
                                -->
                                <div v-if="usageCostRegions.length > 1" class="mb-3">
                                    <div class="text-muted small mb-1">
                                        <strong>By region</strong> — estimated (loaded) &rarr; actual (Twilio base) per segment.
                                        US runs ~${{ usageCostUsRates.loaded }} &rarr; ${{ usageCostUsRates.base }}; Puerto Rico ~$0.058 &rarr; $0.054.
                                        <span v-if="usageCostCanadaRate !== null">
                                            Canada is a cost-only row: those sends bill 1 credit and are already counted in US / mainland
                                            (do not add it), estimated here at ${{ usageCostCanadaRate.toFixed(4) }}/segment.
                                        </span>
                                    </div>
                                    <b-table :items="usageCostRegions" :fields="usageCostRegionFields" small striped responsive class="mb-0">
                                        <template #cell(label)="row">
                                            <b-badge :variant="row.item.region === 'PR' ? 'primary' : (row.item.costOnly ? 'info' : 'secondary')" class="font-weight-normal">{{ row.item.label }}</b-badge>
                                        </template>
                                        <template #cell(messages)="row">{{ (row.item.messages || 0).toLocaleString() }}</template>
                                        <template #cell(physicalSegments)="row">{{ (row.item.physicalSegments || 0).toLocaleString() }}</template>
                                        <template #cell(credits)="row">{{ (row.item.credits || 0).toLocaleString() }}</template>
                                        <template #cell(estCostUsd)="row">${{ fmtPrice(row.item.estCostUsd) }}</template>
                                        <template #cell(actualCostUsd)="row">
                                            <span :class="row.item.actualCostUsd ? '' : 'text-muted'">${{ fmtPrice(row.item.actualCostUsd) }}</span>
                                        </template>
                                        <template #cell(perSeg)="row">
                                            <span class="text-secondary">{{ row.item.estPerSeg === null ? '—' : '$' + row.item.estPerSeg.toFixed(4) }}</span>
                                            <i class="fa fa-arrow-right mx-1 text-muted" aria-hidden="true"></i>
                                            <span :class="row.item.actualPerSeg === null ? 'text-muted' : 'text-success'">{{ row.item.actualPerSeg === null ? 'unpriced' : '$' + row.item.actualPerSeg.toFixed(4) }}</span>
                                        </template>
                                    </b-table>
                                </div>

                                <b-table v-if="usageCostRows.length" :items="usageCostRows" :fields="usageCostFields" small striped responsive>
                                    <template #cell(feature)="row">{{ row.item.feature }}</template>
                                    <template #cell(direction)="row">
                                        <b-badge :variant="row.item.direction === 'in' ? 'info' : 'light'" class="border font-weight-normal">
                                            {{ row.item.direction === 'in' ? 'inbound' : 'outbound' }}
                                        </b-badge>
                                    </template>
                                    <template #cell(credits)="row">{{ (row.item.credits || 0).toLocaleString() }}</template>
                                    <template #cell(estCostUsd)="row">${{ fmtPrice(row.item.estCostUsd) }}</template>
                                    <template #cell(actualCostUsd)="row">
                                        <span :class="row.item.actualCostUsd ? '' : 'text-muted'">${{ fmtPrice(row.item.actualCostUsd) }}</span>
                                    </template>
                                </b-table>
                                <div v-else class="text-muted text-center my-4">No {{ usageCostChannel === 'email' ? 'email' : 'SMS' }} activity for this site in this month.</div>
                            </div>

                            <div v-else class="text-muted text-center my-4">Pick a site and month, then click Load.</div>
                        </b-tab>
                    </b-tabs>
                </div>

                <!-- Insurance Checks — moved out of the Twilio Dashboard tab group; insurance
                     verification is not a Twilio concern. Per-site verification counts from the
                     insurance-verification ledger. Counts ONLY (band + per-trigger split); the
                     internal cost lives on the Twilio Dashboard's Credit Cost tab, never here. -->
                <div class="mt-5" v-if="showInsuranceChecks">
                    <h4 class="mb-3">
                        <i class="fa fa-check-circle text-info mr-2"></i>Insurance Checks
                    </h4>
                    <div class="mb-2 text-muted small">
                        Per-site insurance verification checks for the month — automatic (nightly),
                        manual (on-demand), and on-booking — with the reconciliation band of what was
                        billable. Counts only; internal cost lives on the Credit Cost tab.
                    </div>
                    <div class="mb-3 d-flex align-items-center">
                        <label class="mr-2 mb-0">Month:</label>
                        <b-form-input type="month" v-model="ivRollupMonth" style="width:200px"></b-form-input>
                        <b-button variant="primary" @click="loadIvRollup" :disabled="ivRollupLoading" class="ml-2">
                            <i class="fa fa-search" :class="{ 'fa-spin': ivRollupLoading }"></i>
                            {{ ivRollupLoading ? 'Loading...' : 'Load' }}
                        </b-button>
                    </div>

                    <div v-if="ivRollupLoading" class="text-center my-5">
                        <b-spinner variant="primary"></b-spinner>
                    </div>

                    <div v-else-if="ivRollupData">
                        <b-row class="mb-3">
                            <b-col><b-card class="text-center py-2"><h4 class="text-primary mb-0">{{ ivBand(ivRollupData.totals) }}</h4><small class="text-muted">Billed checks</small></b-card></b-col>
                            <b-col><b-card class="text-center py-2"><h4 class="text-info mb-0">{{ ivBillable(ivRollupData.totals, 'nightly').toLocaleString() }} &middot; {{ ivBillable(ivRollupData.totals, 'onDemand').toLocaleString() }}</h4><small class="text-muted">Automatic &middot; Manual (billable)</small></b-card></b-col>
                            <b-col><b-card class="text-center py-2"><h4 class="text-muted mb-0">{{ ivFree(ivRollupData.totals).toLocaleString() }}</h4><small class="text-muted">Refused free</small></b-card></b-col>
                            <b-col><b-card class="text-center py-2"><h4 class="text-success mb-0">{{ ivHealthySiteCount }}</h4><small class="text-muted">Sites</small></b-card></b-col>
                        </b-row>

                        <b-alert show variant="light" class="py-1 small">{{ ivRollupData.note }}</b-alert>

                        <!-- Failed sites are NOT in the totals. Warn with the count so the band above is
                             read as "of the sites that loaded". -->
                        <b-alert v-if="ivRollupData.partial" show variant="warning" class="py-2 small">
                            <i class="fa fa-exclamation-triangle mr-1"></i>{{ (ivRollupData.errors || []).length }} site(s) failed to load — their checks are NOT included in the totals.
                        </b-alert>

                        <b-table v-if="ivRollupData.sites.length" :items="ivRollupData.sites" :fields="ivRollupFields" small striped responsive>
                            <template #cell(siteName)="row">{{ row.item.siteName || row.item.siteId }}</template>
                            <!-- Band per site, read VERBATIM from that site's reconciliation — never summed. -->
                            <template #cell(billed)="row">
                                <span v-if="row.item.counts">{{ ivBand(row.item.counts) }}</span>
                                <!-- Failed site (counts === null): its error reads across the count columns
                                     (which render an em-dash, never a misleading 0). b-table has no body
                                     colspan, so the error lives in the Billed cell and the count cells dash. -->
                                <span v-else class="text-danger">{{ row.item.error || 'Failed to load' }}</span>
                            </template>
                            <template #cell(automatic)="row">
                                <span v-if="row.item.counts">{{ ivRollupTrigger(row.item.counts, 'nightly').toLocaleString() }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template #cell(manual)="row">
                                <span v-if="row.item.counts">{{ ivRollupTrigger(row.item.counts, 'onDemand').toLocaleString() }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template #cell(onBooking)="row">
                                <span v-if="row.item.counts">{{ ivRollupTrigger(row.item.counts, 'onBooking').toLocaleString() }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template #cell(free)="row">
                                <span v-if="row.item.counts">{{ ivFree(row.item.counts).toLocaleString() }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template #cell(unknown)="row">
                                <span v-if="row.item.counts" :title="(row.item.counts.unknownChecks || 0) + ' unconfirmed + ' + (row.item.counts.unclassifiedChecks || 0) + ' unclassified'">{{ ivUnknown(row.item.counts).toLocaleString() }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                        </b-table>
                        <div v-else class="text-muted text-center my-4">No sites with insurance verification in this month.</div>
                    </div>
                    <div v-else class="text-muted text-center my-4">Pick a month and click Load.</div>
                </div>

                <!-- Clear All Confirmation Modal -->
                <b-modal 
                    id="clear-all-confirm-modal" 
                    v-model="showClearAllConfirm"
                    title="⚠️ Clear All Follow-ups" 
                    size="lg" 
                    hide-footer
                >
                    <div class="text-center">
                        <i class="fa fa-exclamation-triangle text-warning" style="font-size: 48px;"></i>
                        <h4 class="mt-3 mb-3">Are you absolutely sure?</h4>
                        <p class="lead">This will clear <strong>ALL</strong> active follow-up sessions from the cache.</p>
                        <p class="text-muted">
                            This action will allow all staff members to start new follow-ups immediately, 
                            but any staff currently in the middle of follow-ups may lose their progress.
                        </p>
                        
                        <b-alert variant="warning" show class="mt-3">
                            <strong>⚠️ Warning:</strong> This affects all sites and all patients across the entire system!
                        </b-alert>

                        <div class="mt-4">
                            <b-button variant="danger" @click="clearAllFollowups" class="mr-3">
                                <i class="fa fa-trash"></i> Yes, Clear All Follow-ups
                            </b-button>
                            <b-button variant="outline-secondary" @click="showClearAllConfirm = false">
                                <i class="fa fa-times"></i> Cancel
                            </b-button>
                        </div>
                    </div>
                </b-modal>
            </div>
            <span class="fa fa-question-circle fa-2x text-warning help"></span>
            <div class="table-responsive overdue-recall-table">
                <!-- Global Announcement Composer -->
                <div v-if="allowedToSee" class="card mb-4 p-3">
                    <h6 class="mb-2">Send Global Announcement</h6>
                    <p class="text-muted mb-2">Shows a banner for all users across all sites.</p>
                    <textarea-autosize
                        placeholder="Enter global announcement text here..."
                        class="form-control mb-2"
                        :min-height="75"
                        v-model="globalAnnouncementText"
                    />
                    <div>
                        <b-button :disabled="globalAnnouncementLoading || !globalAnnouncementText" variant="primary" size="sm" @click="sendGlobalAnnouncement">
                            <i class="fa" :class="globalAnnouncementLoading ? 'fa-spinner fa-spin' : 'fa-bullhorn'"></i>
                            <span class="ml-1">Send Global Announcement</span>
                        </b-button>
                        <b-button :disabled="globalAnnouncementLoading" variant="outline-danger" size="sm" class="ml-2" @click="clearGlobalAnnouncement">
                            <i class="fa fa-trash"></i>
                            <span class="ml-1">Clear Global Announcement</span>
                        </b-button>
                    </div>
                </div>


                <v-client-table v-if="customers" :data="customers" :columns="columns" :options="options" ref="tb">

                    <div slot="CustomerCreated" slot-scope="props">
                        {{props.row.CustomerCreated | dateformat(dateFormatStr)}}
                    </div>

                    <div slot="LastPing" slot-scope="props">
                        {{props.row.LastPing | dateformatwithtime}}
                    </div>

                    <div slot="Action" slot-scope="props">
                        <b-dropdown id="simple-select" :text="selectedAction[props.index-1]" variant="default" boundary="window">
                            <b-dropdown-item v-for="action in actions" 
                                :key="action" 
                                :value="action"
                                @click="changeSelectOption(selectedAction, props.index-1, action)"
                            >
                            {{action}}
                            </b-dropdown-item>
                        </b-dropdown>                        
                    </div>

                    <div slot="Extra" slot-scope="props">
                        <textarea-autosize
                            v-if="selectedAction[props.index-1] === 'Send Announcement'"
                            placeholder="Enetr announcement text here.."
                            class="form-control"
                            :min-height="75"
                            v-model="extra.announcementText[props.index-1]"
                        />
                    </div>

                   <div slot="SiteID" slot-scope="props">
                        {{props.row.SiteID}}
                    </div>

                    <div slot="PhoneNumber" slot-scope="props">
                        {{props.row.PhoneNumber || '-'}}
                    </div>

                    <div slot="CustomerName" slot-scope="props">
                        {{props.row.CustomerName}}
                        <!-- Inactive badge for all inactive sites -->
                        <b-badge
                            v-if="props.row.IsSiteActive === 0"
                            variant="secondary"
                            pill
                            class="ml-2"
                            style="font-size: 0.65rem;"
                        >
                            Inactive
                        </b-badge>
                        <!-- Needs Attention badge for failed deactivations requiring manual intervention -->
                        <b-badge
                            v-if="props.row.DeactivationStatus === 'failed'"
                            variant="danger"
                            pill
                            class="ml-1"
                            style="font-size: 0.65rem; cursor: pointer;"
                            @click="openSiteManagement(props.row)"
                            title="Deactivation failed - click to resume"
                        >
                            <i class="fa fa-exclamation-triangle mr-1"></i>Needs Attention
                        </b-badge>
                    </div>

                    <div slot="Manage" slot-scope="props">
                        <b-button
                            variant="outline-primary"
                            size="sm"
                            @click="openSiteManagement(props.row)"
                            :class="{'active': selectedSiteForManagement && selectedSiteForManagement.SiteID === props.row.SiteID}"
                        >
                            <i class="fa fa-cog"></i> Manage
                        </b-button>
                    </div>

                    <div slot="Send" slot-scope="props">
                        <b-button variant="success" type="submit" class="btn-sm mb-2" @click="_sendAction(props)">Send</b-button>
                        <i class="fa fa-spinner fa-spin" style="font-size:20px;" v-if="siteActionLoading[props.index-1]"/>
                        <i class="fa fa-check text-success" style="font-size:20px;" v-if="siteActionResult[props.index-1] === 'success'"/>
                        <i class="fa fa-times text-danger" style="font-size:20px;" v-if="siteActionResult[props.index-1] === 'error'"/>
                    </div>
                </v-client-table>

                <b-form-group
                    horizontal
                    label="Logs for current session"
                    label-for="autosize-textarea"

                >
                    <textarea-autosize
                        placeholder="No activity yet..."
                        class="form-control"
                        :min-height="75"
                        id="autosize-textarea"
                        v-model="logs"
                    />
                </b-form-group>
            </div>
        </div>

        <!-- Site Management Side Panel -->
        <transition name="slide-right">
            <div v-if="showSiteManagementPanel && selectedSiteForManagement" class="site-management-panel">
                <div class="panel-header bg-primary text-white p-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 class="mb-0">
                                <i class="fa fa-building mr-2"></i>{{ selectedSiteForManagement.CustomerName }}
                            </h5>
                            <small class="opacity-75">{{ selectedSiteForManagement.SiteID }}</small>
                        </div>
                        <b-button variant="light" size="sm" @click="closeSiteManagement">
                            <i class="fa fa-times"></i>
                        </b-button>
                    </div>
                </div>
                <div class="panel-body">
                    <!-- Quick Status Overview -->
                    <div class="status-overview p-3 bg-light border-bottom">
                        <b-row>
                            <b-col cols="4" class="text-center">
                                <div class="status-item">
                                    <i class="fa fa-circle" :class="selectedSiteForManagement.IsSiteActive ? 'text-success' : 'text-danger'"></i>
                                    <small class="d-block">{{ selectedSiteForManagement.IsSiteActive ? 'Active' : 'Inactive' }}</small>
                                </div>
                            </b-col>
                            <b-col cols="4" class="text-center">
                                <div class="status-item">
                                    <i class="fa fa-phone" :class="selectedSiteForManagement.PhoneNumber ? 'text-success' : 'text-muted'"></i>
                                    <small class="d-block">{{ selectedSiteForManagement.PhoneNumber ? 'Phone' : 'No Phone' }}</small>
                                </div>
                            </b-col>
                            <b-col cols="4" class="text-center">
                                <div class="status-item">
                                    <i class="fa fa-clock-o text-info"></i>
                                    <small class="d-block">v{{ selectedSiteForManagement.ExeVersion || '?' }}</small>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Tabbed Content -->
                    <b-tabs v-model="siteManagementTabIndex" pills content-class="panel-tab-content" nav-class="panel-tabs-nav">
                        <!-- Email/SendGrid Tab -->
                        <b-tab>
                            <template #title>
                                <i class="fa fa-envelope mr-1"></i> Email
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-3">SendGrid Management</h6>

                                <b-button variant="primary" size="sm" block class="mb-2" @click="listSiteDomains">
                                    <i class="fa fa-list mr-2"></i>List Authenticated Domains
                                </b-button>
                                <b-button variant="outline-primary" size="sm" block class="mb-2" @click="openDomainVerification">
                                    <i class="fa fa-plus mr-2"></i>Add Domain Authentication
                                </b-button>
                                <b-button variant="outline-secondary" size="sm" block class="mb-2" @click="openSingleSenderVerification">
                                    <i class="fa fa-user-plus mr-2"></i>Single Sender Verification
                                </b-button>
                                <b-button variant="outline-info" size="sm" block class="mb-2" @click="openVerifiedEmailsManager">
                                    <i class="fa fa-at mr-2"></i>Manage Verified Emails
                                </b-button>
                                <b-button variant="outline-secondary" size="sm" block class="mb-2" disabled>
                                    <i class="fa fa-bar-chart mr-2"></i>Email Stats <small>(Coming Soon)</small>
                                </b-button>
                                <b-button variant="outline-success" size="sm" block class="mb-3" @click="getDnsRecordsForPanel" :disabled="panelDnsLoading">
                                    <b-spinner v-if="panelDnsLoading" small class="mr-1"></b-spinner>
                                    <i v-else class="fa fa-globe mr-2"></i>Get DNS Records
                                </b-button>

                                <!-- DNS Records Display (inline in panel) -->
                                <div v-if="panelDnsRecords.length > 0" class="panel-dns-section mb-3">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <small class="text-muted font-weight-bold">DNS Records for {{ panelDnsDomain }}</small>
                                        <div>
                                            <b-button variant="outline-secondary" size="sm" class="mr-1" @click="copyDnsRecordsToClipboard" v-b-tooltip.hover title="Copy all records">
                                                <i class="fa fa-copy"></i>
                                            </b-button>
                                            <b-button variant="outline-primary" size="sm" @click="showPanelDnsEmailForm = !showPanelDnsEmailForm" v-b-tooltip.hover title="Email records">
                                                <i class="fa fa-envelope"></i>
                                            </b-button>
                                        </div>
                                    </div>

                                    <!-- Email form -->
                                    <div v-if="showPanelDnsEmailForm" class="dns-email-form mb-2 p-2 rounded">
                                        <label class="small font-weight-bold mb-1">Sends to the site's Admins. Also send to (optional, up to 5, comma-separated):</label>
                                        <div class="d-flex">
                                            <b-form-input
                                                v-model="panelDnsEmailRecipients"
                                                placeholder="admin@clinic.com, it@clinic.com"
                                                size="sm"
                                                class="mr-2"
                                                :disabled="sendingPanelDnsEmail"
                                            ></b-form-input>
                                            <b-button
                                                variant="primary"
                                                size="sm"
                                                @click="sendPanelDnsRecordsEmail"
                                                :disabled="sendingPanelDnsEmail || !panelDnsDomainId"
                                                style="white-space: nowrap;">
                                                <b-spinner v-if="sendingPanelDnsEmail" small class="mr-1"></b-spinner>
                                                <i v-else class="fa fa-paper-plane mr-1"></i>
                                                Send
                                            </b-button>
                                        </div>
                                        <small class="text-muted">From: nilay@teamio.app</small>
                                    </div>

                                    <!-- Records table -->
                                    <div v-for="(record, idx) in panelDnsRecords" :key="idx" class="dns-panel-record p-2 mb-1 rounded">
                                        <div class="d-flex justify-content-between">
                                            <small class="font-weight-bold text-muted">{{ record.type }} Record {{ idx + 1 }}</small>
                                            <b-badge :variant="record.valid ? 'success' : 'warning'" class="ml-2">{{ record.valid ? 'Verified' : 'Pending' }}</b-badge>
                                        </div>
                                        <div class="mt-1">
                                            <small class="text-muted">Host:</small>
                                            <div class="dns-panel-value" @click="copyToClipboard(record.host)">
                                                <code>{{ record.host }}</code>
                                                <i class="fa fa-copy text-muted ml-1" style="cursor:pointer; font-size: 11px;"></i>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <small class="text-muted">Value:</small>
                                            <div class="dns-panel-value" @click="copyToClipboard(record.value)">
                                                <code>{{ record.value }}</code>
                                                <i class="fa fa-copy text-muted ml-1" style="cursor:pointer; font-size: 11px;"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Verified Emails Section -->
                                <div v-if="siteVerifiedEmails.length > 0" class="mb-3">
                                    <small class="text-muted d-block mb-2 font-weight-bold">Configured Sender Emails:</small>
                                    <div class="verified-emails-list">
                                        <div v-for="(email, idx) in siteVerifiedEmails" :key="idx" class="verified-email-item d-flex justify-content-between align-items-center p-2 mb-1 bg-light rounded">
                                            <span>
                                                <i class="fa fa-envelope text-info mr-2"></i>
                                                {{ email }}
                                            </span>
                                            <b-button
                                                variant="outline-danger"
                                                size="sm"
                                                @click="removeVerifiedEmail(email)"
                                                v-b-tooltip.hover
                                                title="Remove"
                                            >
                                                <i class="fa fa-times"></i>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Domain List in Panel -->
                                <div v-if="siteEmailDomainsLoading" class="text-center py-3">
                                    <b-spinner small></b-spinner> Loading...
                                </div>
                                <div v-else-if="siteEmailDomains.length > 0">
                                    <small class="text-muted d-block mb-2 font-weight-bold">Authenticated Domains:</small>
                                    <div v-for="domain in siteEmailDomains" :key="domain.id" class="domain-card mb-2">
                                        <div class="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div class="font-weight-bold">
                                                    <i class="fa fa-globe text-primary mr-1"></i>
                                                    {{ domain.domain }}
                                                </div>
                                                <small class="text-muted">Subdomain: {{ domain.subdomain || 'mail' }}</small>
                                                <div>
                                                    <b-badge :variant="domain.valid ? 'success' : 'warning'">
                                                        {{ domain.valid ? 'Verified' : 'Pending DNS' }}
                                                    </b-badge>
                                                </div>
                                            </div>
                                            <b-button-group size="sm">
                                                <b-button
                                                    variant="outline-success"
                                                    @click="recheckDomainVerification(domain)"
                                                    :disabled="deletingDomainId === domain.id"
                                                    v-b-tooltip.hover
                                                    title="Check DNS"
                                                >
                                                    <i class="fa fa-refresh"></i>
                                                </b-button>
                                                <b-button
                                                    variant="outline-danger"
                                                    @click="confirmDeleteDomain(domain)"
                                                    :disabled="deletingDomainId === domain.id"
                                                    v-b-tooltip.hover
                                                    title="Delete"
                                                >
                                                    <i class="fa" :class="deletingDomainId === domain.id ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
                                                </b-button>
                                            </b-button-group>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Twilio/SMS Tab -->
                        <b-tab>
                            <template #title>
                                <i class="fa fa-phone mr-1"></i> Twilio
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-3">Twilio / SMS Management</h6>

                                <div class="mb-3 p-2 bg-light rounded">
                                    <small class="text-muted">Current Phone Number:</small>
                                    <div class="font-weight-bold">
                                        {{ selectedSiteForManagement.PhoneNumber || 'No phone assigned' }}
                                    </div>
                                </div>

                                <!-- A2P Registration Section -->
                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">A2P 10DLC Registration</small>

                                    <!-- A2P Status Display -->
                                    <div v-if="selectedSiteA2PLoading" class="a2p-status-box mb-2">
                                        <i class="fa fa-spinner fa-spin mr-2"></i>
                                        <span>Loading...</span>
                                    </div>
                                    <div v-else-if="selectedSiteA2PStatus" class="a2p-status-box mb-2" :class="'a2p-status-' + getA2PStatusType(selectedSiteA2PStatus.Status || selectedSiteA2PStatus.status)">
                                        <div class="a2p-status-row">
                                            <span class="a2p-status-label">Status:</span>
                                            <span class="a2p-status-value">{{ formatA2PStatus(selectedSiteA2PStatus.Status || selectedSiteA2PStatus.status) }}</span>
                                        </div>
                                        <div v-if="selectedSiteA2PStatus.ProgressPercentage !== undefined" class="a2p-status-row">
                                            <span class="a2p-status-label">Progress:</span>
                                            <span class="a2p-status-value">{{ selectedSiteA2PStatus.ProgressPercentage }}%</span>
                                        </div>
                                    </div>
                                    <div v-else class="a2p-status-box a2p-status-none mb-2">
                                        <i class="fa fa-info-circle mr-1"></i>
                                        <span>No registration started</span>
                                    </div>

                                    <!-- Live A2P Step-by-Step Status -->
                                    <div v-if="selectedSiteLiveA2P && selectedSiteLiveA2P.steps" class="a2p-live-status mb-2">
                                        <table class="table table-sm table-bordered mb-1" style="font-size: 0.8rem;">
                                            <thead class="thead-light">
                                                <tr>
                                                    <th style="width: 30px;"></th>
                                                    <th>Step</th>
                                                    <th>Twilio Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="s in selectedSiteLiveA2P.steps" :key="s.step">
                                                    <td class="text-center">{{ getLiveStepIcon(s) }}</td>
                                                    <td>{{ s.name }}</td>
                                                    <td>
                                                        <span :class="getLiveStepClass(s)">
                                                            {{ getLiveStepLabel(s) }}
                                                        </span>
                                                        <small v-if="s.error" class="text-danger d-block">{{ s.error }}</small>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <div class="text-center">
                                            <small class="font-weight-bold">{{ selectedSiteLiveA2P.progress }} steps complete</small>
                                        </div>
                                        <div v-if="selectedSiteLiveA2P.issues && selectedSiteLiveA2P.issues.length" class="mt-1">
                                            <small v-for="(issue, idx) in selectedSiteLiveA2P.issues" :key="idx" class="text-warning d-block">
                                                <i class="fa fa-exclamation-triangle mr-1"></i>{{ issue }}
                                            </small>
                                        </div>
                                    </div>

                                    <b-button
                                        variant="outline-secondary"
                                        size="sm"
                                        block
                                        class="mb-2"
                                        @click="fetchLiveA2PStatus"
                                        :disabled="selectedSiteLiveA2PLoading"
                                        v-if="selectedSiteA2PStatus"
                                    >
                                        <i :class="selectedSiteLiveA2PLoading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'" class="mr-1"></i>
                                        {{ selectedSiteLiveA2PLoading ? 'Checking Twilio...' : 'Check Live Status' }}
                                    </b-button>

                                    <!-- Dynamic A2P Button -->
                                    <b-button
                                        :variant="getA2PButtonVariant()"
                                        size="sm"
                                        block
                                        class="mb-2"
                                        @click="openA2PRegistration"
                                    >
                                        <i :class="getA2PButtonIcon()" class="mr-2"></i>
                                        {{ getA2PButtonText() }}
                                    </b-button>
                                </div>

                                <!-- Phone Number Section -->
                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Phone Numbers</small>
                                    <b-button variant="primary" size="sm" block class="mb-2" @click="openPurchaseNumber">
                                        <i class="fa fa-shopping-cart mr-2"></i>Purchase Number
                                    </b-button>
                                </div>

                                <b-button variant="outline-secondary" size="sm" block class="mb-2" disabled>
                                    <i class="fa fa-link mr-2"></i>Link Branding <small>(Coming Soon)</small>
                                </b-button>
                            </div>
                        </b-tab>

                        <!-- System Tab -->
                        <b-tab>
                            <template #title>
                                <i class="fa fa-cogs mr-1"></i> System
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-3">System Actions</h6>

                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Data Management</small>
                                    <b-button variant="outline-primary" size="sm" block class="mb-1" @click="quickAction('Schedule Data Load')">
                                        <i class="fa fa-database mr-2"></i>Schedule Data Load
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Request MySQL Dump')">
                                        <i class="fa fa-download mr-2"></i>Request MySQL Dump
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Refresh Exe Table List')">
                                        <i class="fa fa-list mr-2"></i>Refresh Exe Table List
                                    </b-button>
                                    <!-- Its own handler, not quickAction: the answer says whether the exe could be
                                         asked at all (exe 3.0.56+), and that must reach the operator verbatim. -->
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" data-test="refresh-exe-settings-btn" :disabled="exeRefreshReminder.sending" @click="refreshExeSettings()">
                                        <i class="fa fa-refresh mr-2"></i>Refresh Exe Settings
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Request Logs')">
                                        <i class="fa fa-file-text mr-2"></i>Request Logs
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Delete Logs')">
                                        <i class="fa fa-trash mr-2"></i>Delete Logs
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Get Sites File')">
                                        <i class="fa fa-file-code-o mr-2"></i>Get Sites File
                                    </b-button>
                                </div>

                                <!-- Exe table overrides — the PER-SITE delta on top of the global
                                     Open Dental ignore list. First use: `apptfield` for Insurance
                                     Verification, un-ignored for the practices that pay for it
                                     instead of for every site on the platform.
                                     ⛔ Saving sends NOTHING to the exe; the buttons above do. -->
                                <div class="action-group mb-3">
                                    <div class="d-flex align-items-center justify-content-between mb-2">
                                        <small class="text-muted font-weight-bold">Exe table overrides</small>
                                        <b-badge v-if="exeTableOverrides.loaded && !exeTableOverrides.error"
                                                 :variant="exeTableOverridesInSync ? 'success' : 'warning'" pill>
                                            {{ exeTableOverridesInSync ? 'In sync' : 'Pending — not applied' }}
                                        </b-badge>
                                    </div>

                                    <div v-if="exeTableOverrides.loading" class="text-center py-2">
                                        <b-spinner small></b-spinner>
                                        <small class="text-muted ml-2">Loading table overrides…</small>
                                    </div>

                                    <b-alert v-else-if="exeTableOverrides.error" show variant="danger" class="mb-2 exe-overrides-note">
                                        {{ exeTableOverrides.error }}
                                        <b-link class="ml-1" @click="loadExeTableOverrides()">Retry</b-link>
                                    </b-alert>

                                    <template v-else-if="exeTableOverrides.loaded">
                                        <!-- The exe on this machine is too old to read the setting at all. -->
                                        <b-alert v-if="exeTableOverridesVersionWarning" show variant="warning" class="mb-2 exe-overrides-note">
                                            <i class="fa fa-exclamation-triangle mr-1"></i>{{ exeTableOverridesVersionWarning }}
                                        </b-alert>

                                        <!-- Multi-clinic: ONE exe serves every clinic and reads the HQ row. -->
                                        <b-alert v-if="exeTableOverrides.isMultiClinic" show variant="info" class="mb-2 exe-overrides-note">
                                            <i class="fa fa-sitemap mr-1"></i>
                                            Stored on the HQ site ({{ exeTableOverrides.ownerSiteID }}). A table with no ClinicNum column (apptfield, insverify) lives in the HQ mirror only; a table that has one is split per clinic, like patient and appointment.
                                        </b-alert>

                                        <b-alert v-if="exeTableOverrides.malformed" show variant="warning" class="mb-2 exe-overrides-note">
                                            <i class="fa fa-exclamation-circle mr-1"></i>
                                            The stored value could not be read and is being treated as empty. Saving will overwrite it.
                                        </b-alert>

                                        <label class="exe-overrides-label">Un-ignore for this site</label>
                                        <input type="text" class="form-control form-control-sm mb-1"
                                               v-model="exeTableOverrides.unignoreText"
                                               :disabled="exeTableOverrides.saving"
                                               placeholder="apptfield (comma separated)">
                                        <small v-for="(p, i) in exeTableOverridesUnignoreProblems" :key="'exe-un-' + i"
                                               class="text-danger d-block">{{ p }}</small>

                                        <label class="exe-overrides-label mt-2">Also ignore for this site</label>
                                        <input type="text" class="form-control form-control-sm mb-1"
                                               v-model="exeTableOverrides.ignoreText"
                                               :disabled="exeTableOverrides.saving"
                                               placeholder="(comma separated, usually empty)">
                                        <small v-for="(p, i) in exeTableOverridesIgnoreProblems" :key="'exe-ig-' + i"
                                               class="text-danger d-block">{{ p }}</small>

                                        <b-button variant="outline-primary" size="sm" block class="mt-2 mb-1"
                                                  :disabled="exeTableOverridesSaveBlocked"
                                                  @click="saveExeTableOverrides">
                                            <b-spinner v-if="exeTableOverrides.saving" small class="mr-1"></b-spinner>
                                            <i v-else class="fa fa-save mr-1"></i>Save table overrides
                                        </b-button>

                                        <!-- What the EXE says it is really running with. Absent = it has -->
                                        <!-- never reported, which is NOT the same as "in sync". -->
                                        <div class="exe-overrides-applied mt-2">
                                            <small class="text-muted font-weight-bold d-block">Exe is running with</small>
                                            <small v-if="!exeTableOverrides.applied" class="text-muted d-block">
                                                No report from the exe yet
                                            </small>
                                            <template v-else>
                                                <small class="d-block">Un-ignored: <span v-text="exeTableOverridesAppliedUnignored || '(none)'"></span></small>
                                                <small class="d-block">Also ignored: <span v-text="exeTableOverridesAppliedIgnored || '(none)'"></span></small>
                                                <small class="text-muted d-block">
                                                    {{ exeTableOverrides.applied.reason || 'unknown' }} ·
                                                    v{{ exeTableOverrides.applied.exeVersion || '?' }} ·
                                                    {{ formatDate(exeTableOverrides.applied.appliedAt) }}
                                                </small>
                                                <small v-if="exeTableOverrides.applied.listenerInSync === false" class="text-warning d-block">
                                                    <i class="fa fa-exclamation-circle mr-1"></i>The live binlog listener has not picked this up yet.
                                                </small>
                                            </template>
                                        </div>

                                        <small class="text-muted d-block mt-2">
                                            Nothing is sent automatically. To ENABLE a table: save, then click Request MySQL Dump
                                            (the exe refreshes its list, re-attaches and dumps). To DISABLE: save, then click
                                            Refresh Exe Table List.
                                        </small>
                                    </template>
                                </div>

                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">App Control</small>
                                    <!-- Restart App (2026-10-02): its own handler, not quickAction. Before exe 3.0.60 it
                                         hangs an office exe that runs as a Windows service, so the button is enabled ONLY
                                         by teamio-admin's own verdict ('Get Exe Restart Status' — lib/exe-restart.js), is
                                         confirmed first, and the line under it says whether the exe picked it up. -->
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" data-test="restart-app-btn"
                                              :disabled="!exeRestartCanSend('restart')"
                                              @click="sendExeRestartCommand('restart')">
                                        <b-spinner v-if="exeRestart.sending === 'restart'" small class="mr-2"></b-spinner>
                                        <i v-else class="fa fa-refresh mr-2"></i>Restart App
                                    </b-button>
                                    <small v-if="exeRestartBlockedReason('restart')" class="exe-restart-note text-muted d-block mb-1" data-test="restart-app-blocked">
                                        <i class="fa fa-ban mr-1"></i>{{ exeRestartBlockedReason('restart') }}
                                        <b-link v-if="exeRestart.error" class="ml-1" @click="loadExeRestartStatus()">Retry</b-link>
                                    </small>
                                    <div v-if="exeRestartLine" class="exe-restart-status mb-2" :class="'exe-restart-status--' + exeRestartLine.variant" data-test="restart-app-status">
                                        <span data-test="restart-app-status-text">{{ exeRestartLine.text }}</span>
                                        <b-link class="ml-1" :disabled="exeRestart.loading" @click="loadExeRestartStatus()">Refresh</b-link>
                                        <small v-if="exeRestartLine.detail" class="d-block text-muted">{{ exeRestartLine.detail }}</small>
                                    </div>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Send Announcement')">
                                        <i class="fa fa-bullhorn mr-2"></i>Send Announcement
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Clear Announcement')">
                                        <i class="fa fa-times-circle mr-2"></i>Clear Announcement
                                    </b-button>
                                    <!-- Its own handler, not quickAction: the backend answers per step (setting, board,
                                         exe refresh) and a refused exe refresh must reach the operator verbatim — quickAction
                                         says "completed successfully" for any 200. force = rebuild today's board, which
                                         empties the queues, so it is confirmed first. -->
                                    <b-button variant="outline-info" size="sm" block class="mb-1" data-test="practice-pulse-init-btn" :disabled="practicePulseInit.running" @click="initializePracticePulse({ force: true })">
                                        <b-spinner v-if="practicePulseInit.running" small class="mr-2"></b-spinner>
                                        <i v-else class="fa fa-heartbeat mr-2"></i>Initialize / Rebuild Practice Ops Board
                                    </b-button>
                                    <b-alert
                                        v-if="practicePulseInitView"
                                        show
                                        dismissible
                                        :variant="practicePulseInitView.variant"
                                        class="practice-pulse-init-result small mt-2 mb-1"
                                        data-test="practice-pulse-init-result"
                                        @dismissed="dismissPracticePulseInit()"
                                    >
                                        <strong class="d-block mb-1">Practice Pulse — what was done</strong>
                                        <div v-if="practicePulseInitView.error">
                                            <i class="fa fa-times-circle mr-1"></i>{{ practicePulseInitView.error }}
                                        </div>
                                        <template v-else>
                                            <div v-for="line in practicePulseInitView.lines" :key="line.key">
                                                <i :class="line.ok ? 'fa fa-check text-success' : 'fa fa-exclamation-triangle text-warning'" class="mr-1"></i><strong>{{ line.label }}:</strong> {{ line.message }}
                                            </div>
                                            <div v-if="practicePulseInitView.hqNote" class="small mt-1">
                                                <i class="fa fa-exclamation-triangle text-warning mr-1"></i>{{ practicePulseInitView.hqNote }}
                                            </div>
                                        </template>
                                    </b-alert>
                                </div>

                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Site Status</small>
                                    <b-button variant="outline-warning" size="sm" block class="mb-1" @click="quickAction('Pause')">
                                        <i class="fa fa-pause mr-2"></i>Pause Site
                                    </b-button>
                                    <b-button variant="outline-success" size="sm" block class="mb-1" @click="quickAction('Re-Activate')">
                                        <i class="fa fa-play mr-2"></i>Reactivate Site
                                    </b-button>
                                    <b-button variant="outline-danger" size="sm" block class="mb-1" @click="quickAction('De-Activate')">
                                        <i class="fa fa-power-off mr-2"></i>Deactivate Site
                                    </b-button>
                                </div>

                                <!-- Deactivation Status Panel (Two-Phase Deactivation) -->
                                <div class="action-group mb-3" v-if="siteDeactivationStatus && siteDeactivationStatus.DeactivationStatus !== 'active'">
                                    <!-- Compact header with status badge -->
                                    <div class="d-flex align-items-center justify-content-between mb-2">
                                        <small class="text-muted font-weight-bold">Deactivation</small>
                                        <b-badge :variant="getDeactivationStatusColor(siteDeactivationStatus.DeactivationStatus)" pill>
                                            <i class="fa mr-1" :class="getDeactivationStatusIcon(siteDeactivationStatus.DeactivationStatus)"></i>
                                            {{ siteDeactivationStatus.DeactivationStatus === 'completed' ? 'Done' :
                                               siteDeactivationStatus.DeactivationStatus === 'failed' ? 'Failed' :
                                               siteDeactivationStatus.DeactivationStatus === 'phase2_scheduled' ? 'Scheduled' :
                                               siteDeactivationStatus.DeactivationStatus === 'phase2_in_progress' ? 'Running' :
                                               siteDeactivationStatus.DeactivationStatus.replace(/_/g, ' ') }}
                                        </b-badge>
                                    </div>

                                    <!-- Styled panel container -->
                                    <div class="deactivation-panel" :class="{
                                        'status-failed': siteDeactivationStatus.DeactivationStatus === 'failed',
                                        'status-completed': siteDeactivationStatus.DeactivationStatus === 'completed'
                                    }">
                                        <!-- Loading state -->
                                        <div v-if="siteDeactivationStatusLoading" class="text-center py-2">
                                            <b-spinner small></b-spinner>
                                        </div>

                                        <!-- Compact status display -->
                                        <div v-else>
                                            <!-- Progress bar -->
                                            <div class="mb-3">
                                                <div class="d-flex justify-content-between mb-1" style="font-size: 0.75rem;">
                                                    <span class="text-muted font-weight-bold">Step {{ siteDeactivationStatus.DeactivationStep || 0 }}/9</span>
                                                    <span v-if="siteDeactivationStatus.retryCount > 0" class="text-warning font-weight-bold">
                                                        Retry {{ siteDeactivationStatus.retryCount }}/{{ siteDeactivationStatus.maxRetries }}
                                                    </span>
                                                </div>
                                                <b-progress :value="siteDeactivationStatus.DeactivationStep || 0" :max="9" height="8px"
                                                    :variant="getDeactivationStatusColor(siteDeactivationStatus.DeactivationStatus)" class="mb-0"></b-progress>
                                            </div>

                                            <!-- Error message (compact) -->
                                            <div v-if="siteDeactivationStatus.DeactivationError" class="mb-3 p-2 bg-danger text-white rounded" style="font-size: 0.75rem;">
                                                <i class="fa fa-exclamation-circle mr-1"></i>
                                                {{ siteDeactivationStatus.DeactivationError.substring(0, 100) }}{{ siteDeactivationStatus.DeactivationError.length > 100 ? '...' : '' }}
                                            </div>

                                            <!-- Scheduled time (compact) -->
                                            <div v-if="siteDeactivationStatus.DeactivationScheduledAt && siteDeactivationStatus.DeactivationStatus === 'phase2_scheduled'"
                                                class="mb-3" style="font-size: 0.75rem;">
                                                <i class="fa fa-clock text-info mr-1"></i>
                                                <span class="text-muted">Runs: {{ new Date(siteDeactivationStatus.DeactivationScheduledAt).toLocaleString() }}</span>
                                            </div>

                                            <!-- Action buttons -->
                                            <div class="d-flex" style="gap: 8px;">
                                                <!-- Resume button - prominent when needed -->
                                                <b-button
                                                    v-if="siteDeactivationStatus.needsManualResume || siteDeactivationStatus.DeactivationStatus === 'failed'"
                                                    variant="warning"
                                                    size="sm"
                                                    block
                                                    :disabled="deactivationActionLoading"
                                                    @click="handleResumeDeactivation"
                                                >
                                                    <b-spinner small v-if="deactivationActionLoading"></b-spinner>
                                                    <span v-else><i class="fa fa-redo mr-1"></i>Resume</span>
                                                </b-button>

                                                <!-- Cancel button -->
                                                <b-button
                                                    v-if="siteDeactivationStatus.canCancel"
                                                    variant="outline-success"
                                                    size="sm"
                                                    block
                                                    :disabled="deactivationActionLoading"
                                                    @click="handleCancelDeactivation"
                                                >
                                                    <i class="fa fa-times mr-1"></i>Cancel
                                                </b-button>
                                            </div>

                                            <!-- Auto-retry indicator -->
                                            <div v-if="siteDeactivationStatus.autoRetryPending" class="text-center mt-2" style="font-size: 0.7rem;">
                                                <i class="fa fa-clock text-info mr-1"></i>
                                                <span class="text-muted">Auto-retry scheduled</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Debug & Testing</small>
                                    <!-- Both Set Debug Mode commands RESTART the exe, so the same gate, confirm and
                                         handler as Restart App above (never quickAction). -->
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" data-test="debug-mode-on-btn"
                                              :disabled="!exeRestartCanSend('debug')"
                                              @click="sendExeRestartCommand('debug')">
                                        <b-spinner v-if="exeRestart.sending === 'debug'" small class="mr-2"></b-spinner>
                                        <i v-else class="fa fa-bug mr-2"></i>Enable Debug Mode
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" data-test="debug-mode-off-btn"
                                              :disabled="!exeRestartCanSend('default')"
                                              @click="sendExeRestartCommand('default')">
                                        <b-spinner v-if="exeRestart.sending === 'default'" small class="mr-2"></b-spinner>
                                        <i v-else class="fa fa-check mr-2"></i>Reset Debug Mode
                                    </b-button>
                                    <small v-if="exeRestartBlockedReason('debug')" class="exe-restart-note text-muted d-block mb-1" data-test="debug-mode-blocked">
                                        <i class="fa fa-ban mr-1"></i>{{ exeRestartBlockedReason('debug') }}
                                    </small>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="quickAction('Request Upload Test')">
                                        <i class="fa fa-upload mr-2"></i>Request Upload Test
                                    </b-button>

                                    <!-- Test File Write -->
                                    <hr class="my-2">
                                    <small class="text-muted font-weight-bold d-block mb-2">Test File Write</small>
                                    <select class="form-control form-control-sm mb-1"
                                            v-model="testFileWrite.pdfCategory"
                                            :disabled="testFileWrite.categoriesLoading">
                                        <option value="">{{ testFileWrite.categoriesLoading ? 'Loading categories...' : '--OD Imaging Category--' }}</option>
                                        <option v-for="cat in testFileWrite.categories"
                                                :key="'tfw-' + cat.DefNum"
                                                :value="String(cat.DefNum)"
                                                v-text="cat.ItemName"></option>
                                    </select>
                                    <input type="text" class="form-control form-control-sm mb-1"
                                           v-model="testFileWrite.patNum"
                                           placeholder="PatNum (e.g. 1234)"
                                           maxlength="5"
                                           @keypress="$event.charCode >= 48 && $event.charCode <= 57 || $event.preventDefault()">
                                    <b-button variant="outline-info" size="sm" block class="mb-1"
                                              :disabled="!testFileWrite.pdfCategory || !testFileWrite.patNum || testFileWrite.loading"
                                              @click="sendTestFileWrite">
                                        <i class="fa fa-paper-plane mr-1"></i>
                                        {{ testFileWrite.loading ? 'Sending...' : 'Send Test File' }}
                                    </b-button>
                                </div>

                                <div class="action-group">
                                    <small class="text-muted font-weight-bold d-block mb-2">User & Security</small>
                                    <b-button variant="outline-danger" size="sm" block class="mb-1" @click="quickAction('Revoke Users')">
                                        <i class="fa fa-user-times mr-2"></i>Revoke Users
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Billing & Payments Tab -->
                        <b-tab>
                            <template #title>
                                <i class="fa fa-credit-card mr-1"></i> Billing
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-3">Stripe Billing & Payments</h6>

                                <!-- Loading indicator for Stripe initialization -->
                                <div v-if="stripeLoading" class="text-center py-3">
                                    <b-spinner small></b-spinner>
                                    <span class="ml-2">Loading Stripe...</span>
                                </div>

                                <!-- Stripe Customer Section -->
                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Customer Management</small>
                                    
                                    <!-- Current Stripe Customer Info -->
                                    <div v-if="selectedSiteStripeData.customer" class="mb-2 p-2 bg-light rounded">
                                        <div class="font-weight-bold">
                                            <i class="fa fa-user text-success mr-2"></i>{{ selectedSiteStripeData.customer.name }}
                                        </div>
                                        <small class="text-muted">{{ selectedSiteStripeData.customer.email }}</small>
                                        <br>
                                        <small class="text-muted">ID: {{ selectedSiteStripeData.customer.id }}</small>
                                    </div>
                                    <div v-else class="mb-2 p-2 bg-light rounded text-muted">
                                        <i class="fa fa-info-circle mr-2"></i>No Stripe customer found
                                    </div>
                                    
                                    <b-button variant="primary" size="sm" block class="mb-1" @click="loadStripeCustomer" :disabled="billingLoading.customer">
                                        <i class="fa mr-2" :class="billingLoading.customer ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
                                        {{ billingLoading.customer ? 'Loading...' : 'Load Customer' }}
                                    </b-button>
                                    <b-button variant="outline-primary" size="sm" block class="mb-1" @click="showCreateStripeCustomer">
                                        <i class="fa fa-plus mr-2"></i>Create/Link Customer
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-2" @click="showEditStripeCustomer" :disabled="!selectedSiteStripeData.customer">
                                        <i class="fa fa-edit mr-2"></i>Edit Customer
                                    </b-button>
                                </div>

                                <!-- Subscriptions Section -->
                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Subscriptions</small>
                                    
                                    <!-- Active Subscriptions -->
                                    <div v-if="selectedSiteStripeData.subscriptions.length > 0">
                                        <div v-for="sub in selectedSiteStripeData.subscriptions" :key="sub.id" class="subscription-card mb-2 p-2 border rounded">
                                            <div class="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div class="font-weight-bold">{{ sub.items.data[0]?.price?.nickname || 'Subscription' }}</div>
                                                    <small class="text-muted">${{ (sub.items.data[0]?.price?.unit_amount / 100).toFixed(2) }}/{{ sub.items.data[0]?.price?.recurring?.interval }}</small>
                                                    <br>
                                                    <b-badge :variant="getSubscriptionStatusVariant(sub.status)">{{ sub.status }}</b-badge>
                                                </div>
                                                <b-button-group size="sm">
                                                    <b-button variant="outline-warning" @click="cancelSubscription(sub.id)">Cancel</b-button>
                                                    <b-button variant="outline-info" @click="viewSubscriptionDetails(sub)">Details</b-button>
                                                </b-button-group>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="mb-2 p-2 bg-light rounded text-muted">
                                        <i class="fa fa-info-circle mr-2"></i>No active subscriptions
                                    </div>
                                    
                                    <b-button variant="outline-success" size="sm" block class="mb-1" @click="loadSubscriptions" :disabled="billingLoading.subscriptions">
                                        <i class="fa mr-2" :class="billingLoading.subscriptions ? 'fa-spinner fa-spin' : 'fa-list'"></i>
                                        {{ billingLoading.subscriptions ? 'Loading...' : 'Load Subscriptions' }}
                                    </b-button>
                                    <b-button variant="success" size="sm" block class="mb-2" @click="showCreateSubscription" :disabled="!selectedSiteStripeData.customer">
                                        <i class="fa fa-plus mr-2"></i>Create Subscription
                                    </b-button>
                                </div>

                                <!-- Invoices Section -->
                                <div class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Invoices & Payments</small>
                                    
                                    <b-button variant="outline-info" size="sm" block class="mb-1" @click="loadInvoices" :disabled="billingLoading.invoices">
                                        <i class="fa mr-2" :class="billingLoading.invoices ? 'fa-spinner fa-spin' : 'fa-file-text'"></i>
                                        {{ billingLoading.invoices ? 'Loading...' : 'Load Recent Invoices' }}
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-2" @click="loadPaymentMethods" :disabled="billingLoading.paymentMethods">
                                        <i class="fa mr-2" :class="billingLoading.paymentMethods ? 'fa-spinner fa-spin' : 'fa-credit-card'"></i>
                                        {{ billingLoading.paymentMethods ? 'Loading...' : 'Load Payment Methods' }}
                                    </b-button>
                                </div>

                                <!-- Recent Invoices Display -->
                                <div v-if="selectedSiteStripeData.invoices.length > 0" class="action-group mb-3">
                                    <small class="text-muted font-weight-bold d-block mb-2">Recent Invoices ({{ selectedSiteStripeData.invoices.length }})</small>
                                    <div v-for="invoice in selectedSiteStripeData.invoices.slice(0, 5)" :key="invoice.id" class="invoice-card mb-1 p-2 border rounded small">
                                        <div class="d-flex justify-content-between align-items-center">
                                            <div>
                                                <div class="font-weight-bold">${{ (invoice.amount_paid / 100).toFixed(2) }}</div>
                                                <small class="text-muted">{{ new Date(invoice.created * 1000).toLocaleDateString() }}</small>
                                            </div>
                                            <div>
                                                <b-badge :variant="getInvoiceStatusVariant(invoice.status)" class="mr-1">{{ invoice.status }}</b-badge>
                                                <b-button variant="outline-primary" size="sm" @click="viewInvoice(invoice.hosted_invoice_url)" target="_blank">
                                                    <i class="fa fa-external-link"></i>
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Quick Actions -->
                                <div class="action-group">
                                    <small class="text-muted font-weight-bold d-block mb-2">Quick Actions</small>
                                    <b-button variant="outline-info" size="sm" block class="mb-1" @click="openStripePortal" :disabled="!selectedSiteStripeData.customer">
                                        <i class="fa fa-external-link mr-2"></i>Open Customer Portal
                                    </b-button>
                                    <b-button variant="outline-secondary" size="sm" block class="mb-1" @click="syncStripeData">
                                        <i class="fa fa-sync mr-2"></i>Sync All Data
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Stripe Mapping Tab -->
                        <b-tab>
                            <template #title>
                                <i class="fa fa-link mr-1"></i> Stripe Mapping
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-3">Link TeamIO Sites to Stripe Customers</h6>

                                <!-- Load All Sites & Stripe Customers -->
                                <div class="action-group mb-3">
                                    <b-button variant="primary" size="sm" class="mr-2" @click="loadAllSitesForMapping" :disabled="mappingLoading.sites">
                                        <i class="fa mr-2" :class="mappingLoading.sites ? 'fa-spinner fa-spin' : 'fa-building'"></i>
                                        {{ mappingLoading.sites ? 'Loading...' : 'Load All Sites' }}
                                    </b-button>
                                    <b-button variant="outline-primary" size="sm" @click="loadAllStripeCustomers" :disabled="mappingLoading.customers">
                                        <i class="fa mr-2" :class="mappingLoading.customers ? 'fa-spinner fa-spin' : 'fa-users'"></i>
                                        {{ mappingLoading.customers ? 'Loading...' : 'Load Stripe Customers' }}
                                    </b-button>
                                </div>

                                <!-- Error Messages -->
                                <b-alert variant="danger" :show="!!mappingError" dismissible @dismissed="mappingError = null">
                                    {{ mappingError }}
                                </b-alert>

                                <!-- Success Messages -->
                                <b-alert variant="success" :show="!!mappingSuccess" dismissible @dismissed="mappingSuccess = null">
                                    {{ mappingSuccess }}
                                </b-alert>

                                <!-- Sites & Mapping Table -->
                                <div v-if="allSitesForMapping.length > 0 && allStripeCustomers.length > 0" class="mapping-table-container">
                                    <div class="d-flex align-items-center justify-content-between mb-2">
                                        <small class="text-muted">
                                            <strong>{{ activeSitesForMapping.length }}</strong> active sites,
                                            <strong>{{ inactiveSitesForMapping.length }}</strong> inactive sites,
                                            <strong>{{ allStripeCustomers.length }}</strong> Stripe customers available
                                        </small>
                                        <b-button variant="outline-info" size="sm" @click="autoMatchSuggestions" :disabled="autoMatchRunning">
                                            <i class="fa mr-1" :class="autoMatchRunning ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
                                            {{ autoMatchRunning ? 'Matching...' : 'Auto-Match Suggestions' }}
                                        </b-button>
                                    </div>

                                    <div class="table-responsive">
                                        <table class="table table-sm table-hover">
                                            <thead class="thead-light">
                                                <tr>
                                                    <th>Status</th>
                                                    <th>Site ID</th>
                                                    <th>Customer Name</th>
                                                    <th>Site Name</th>
                                                    <th>Current Stripe Customer</th>
                                                    <th>Link to Stripe Customer</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <!-- Active Sites -->
                                            <tbody>
                                                <tr v-for="site in activeSitesForMapping" :key="site.SiteID" :class="{ 'table-success': site.stripe_customer_id }">
                                                    <td>
                                                        <i class="fa fa-circle text-success" v-b-tooltip.hover title="Active"></i>
                                                    </td>
                                                    <td class="font-weight-bold">{{ site.SiteID }}</td>
                                                    <td>{{ site.CustomerName }}</td>
                                                    <td>{{ site.SiteName || '-' }}</td>
                                                    <td>
                                                        <div v-if="site.stripe_customer_id" class="d-flex align-items-center">
                                                            <i class="fa fa-check-circle text-success mr-2"></i>
                                                            <span class="small">{{ getStripeCustomerNameById(site.stripe_customer_id) }}</span>
                                                            <br>
                                                            <small class="text-muted">{{ site.stripe_customer_id }}</small>
                                                        </div>
                                                        <div v-else class="text-muted">
                                                            <i class="fa fa-circle-o mr-2"></i>Not linked
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div class="d-flex align-items-center">
                                                            <v-select
                                                                :options="allStripeCustomers"
                                                                v-model="site.selectedStripeCustomer"
                                                                label="name"
                                                                :reduce="customer => customer.id"
                                                                placeholder="Choose Stripe customer..."
                                                                :disabled="savingMapping === site.SiteID"
                                                                class="mapping-select flex-grow-1"
                                                            >
                                                                <template #option="option">
                                                                    <div>
                                                                        <div class="font-weight-bold">{{ option.name }}</div>
                                                                        <small class="text-muted">{{ option.email }} &bull; {{ option.id }}</small>
                                                                    </div>
                                                                </template>
                                                            </v-select>
                                                            <span v-if="site.autoMatchSuggested" class="badge badge-info ml-2" v-b-tooltip.hover :title="'Match: ' + site.autoMatchReason">
                                                                <i class="fa fa-magic"></i> suggested
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <b-button-group size="sm">
                                                            <b-button
                                                                variant="success"
                                                                @click="saveSiteStripeMapping(site)"
                                                                :disabled="!site.selectedStripeCustomer || savingMapping === site.SiteID"
                                                                v-b-tooltip.hover
                                                                title="Save mapping"
                                                            >
                                                                <i class="fa" :class="savingMapping === site.SiteID ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                                                            </b-button>
                                                            <b-button
                                                                v-if="site.stripe_customer_id"
                                                                variant="outline-danger"
                                                                @click="unlinkSiteStripeMapping(site)"
                                                                :disabled="savingMapping === site.SiteID"
                                                                v-b-tooltip.hover
                                                                title="Unlink"
                                                            >
                                                                <i class="fa fa-unlink"></i>
                                                            </b-button>
                                                        </b-button-group>
                                                    </td>
                                                </tr>
                                            </tbody>
                                            <!-- Inactive Sites Separator -->
                                            <tbody v-if="inactiveSitesForMapping.length > 0">
                                                <tr class="inactive-separator">
                                                    <td colspan="7" class="text-center py-2">
                                                        <small class="text-muted font-weight-bold"><i class="fa fa-ban mr-1"></i> Inactive Sites ({{ inactiveSitesForMapping.length }})</small>
                                                    </td>
                                                </tr>
                                                <tr v-for="site in inactiveSitesForMapping" :key="site.SiteID" class="inactive-site-row">
                                                    <td>
                                                        <i class="fa fa-circle text-danger" v-b-tooltip.hover title="Inactive"></i>
                                                    </td>
                                                    <td class="font-weight-bold">{{ site.SiteID }}</td>
                                                    <td>{{ site.CustomerName }}</td>
                                                    <td>{{ site.SiteName || '-' }}</td>
                                                    <td>
                                                        <div v-if="site.stripe_customer_id" class="d-flex align-items-center">
                                                            <i class="fa fa-check-circle text-success mr-2"></i>
                                                            <span class="small">{{ getStripeCustomerNameById(site.stripe_customer_id) }}</span>
                                                            <br>
                                                            <small class="text-muted">{{ site.stripe_customer_id }}</small>
                                                        </div>
                                                        <div v-else class="text-muted">
                                                            <i class="fa fa-circle-o mr-2"></i>Not linked
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <v-select
                                                            :options="allStripeCustomers"
                                                            v-model="site.selectedStripeCustomer"
                                                            label="name"
                                                            :reduce="customer => customer.id"
                                                            placeholder="Choose Stripe customer..."
                                                            :disabled="savingMapping === site.SiteID"
                                                            class="mapping-select"
                                                        >
                                                            <template #option="option">
                                                                <div>
                                                                    <div class="font-weight-bold">{{ option.name }}</div>
                                                                    <small class="text-muted">{{ option.email }} &bull; {{ option.id }}</small>
                                                                </div>
                                                            </template>
                                                        </v-select>
                                                    </td>
                                                    <td>
                                                        <b-button-group size="sm">
                                                            <b-button
                                                                variant="success"
                                                                @click="saveSiteStripeMapping(site)"
                                                                :disabled="!site.selectedStripeCustomer || savingMapping === site.SiteID"
                                                                v-b-tooltip.hover
                                                                title="Save mapping"
                                                            >
                                                                <i class="fa" :class="savingMapping === site.SiteID ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                                                            </b-button>
                                                            <b-button
                                                                v-if="site.stripe_customer_id"
                                                                variant="outline-danger"
                                                                @click="unlinkSiteStripeMapping(site)"
                                                                :disabled="savingMapping === site.SiteID"
                                                                v-b-tooltip.hover
                                                                title="Unlink"
                                                            >
                                                                <i class="fa fa-unlink"></i>
                                                            </b-button>
                                                        </b-button-group>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <!-- Summary Stats -->
                                    <div class="mapping-summary mt-3 p-3 bg-light rounded">
                                        <div class="row text-center">
                                            <div class="col-md-3">
                                                <div class="h4 text-success">{{ linkedSitesCount }}</div>
                                                <small class="text-muted">Active Linked</small>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="h4 text-warning">{{ unlinkedSitesCount }}</div>
                                                <small class="text-muted">Active Unlinked</small>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="h4 text-info">{{ allStripeCustomers.length }}</div>
                                                <small class="text-muted">Stripe Customers</small>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="h4 text-primary">{{ usedStripeCustomersCount }}</div>
                                                <small class="text-muted">Customers Used</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Empty States -->
                                <div v-else-if="allSitesForMapping.length === 0 && !mappingLoading.sites" class="text-center text-muted p-4">
                                    <i class="fa fa-building fa-3x mb-3"></i>
                                    <p>Click "Load All Sites" to get started</p>
                                </div>

                                <div v-else-if="allStripeCustomers.length === 0 && !mappingLoading.customers" class="text-center text-muted p-4">
                                    <i class="fa fa-users fa-3x mb-3"></i>
                                    <p>Click "Load Stripe Customers" to see available customers</p>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Features Tab -->
                        <b-tab @click="loadSiteFeatures">
                            <template #title>
                                <i class="fa fa-toggle-on mr-1"></i> Features
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-3">Site Feature Toggles</h6>

                                <div v-if="featuresLoading" class="text-center py-3">
                                    <b-spinner small></b-spinner>
                                    <span class="ml-2">Loading features...</span>
                                </div>

                                <div v-else class="feature-toggles">

                                    <!-- Refresh-exe reminder (2026-09-24). Schedule View live updates and Practice Pulse are
                                         read by the Teamio application on the PRACTICE'S server, which only re-reads them on
                                         "Refresh Exe Settings", its own 10-minute re-read (exe 3.0.56+), or a restart. So
                                         flipping one of those switches and walking away is not enough — this says what to
                                         do next and does it in one click. Per site; cleared only by a successful send.
                                         List: @/utils/exeLiveSettings.js. -->
                                    <b-alert
                                        v-if="exeRefreshReminderVisible"
                                        show
                                        :variant="exeRefreshReminder.result && !exeRefreshReminder.result.sent ? 'warning' : 'info'"
                                        class="exe-refresh-reminder mb-3"
                                        data-test="exe-refresh-reminder"
                                    >
                                        <div class="d-flex align-items-start">
                                            <i class="fa fa-refresh mr-2 mt-1"></i>
                                            <div class="flex-grow-1">
                                                <strong>Next step: click Refresh Exe Settings.</strong>
                                                <div class="small">
                                                    You changed {{ exeRefreshReminderLabel }}. The office's Teamio application
                                                    applies this on its own server — until it is refreshed it keeps the old
                                                    setting (up to 10 minutes on exe {{ exeSettingsMinVersion }}+, until its next
                                                    restart on anything older).
                                                </div>
                                                <div v-if="exeRefreshReminder.result" class="small mt-1 font-weight-bold" data-test="exe-refresh-result">
                                                    {{ exeRefreshReminder.result.message }}
                                                </div>
                                            </div>
                                            <b-button
                                                size="sm"
                                                variant="primary"
                                                class="ml-2 text-nowrap"
                                                data-test="exe-refresh-reminder-btn"
                                                :disabled="exeRefreshReminder.sending"
                                                @click="refreshExeSettings()"
                                            >
                                                <b-spinner v-if="exeRefreshReminder.sending" small class="mr-1"></b-spinner>
                                                <i v-else class="fa fa-refresh mr-1"></i>Refresh Exe Settings
                                            </b-button>
                                        </div>
                                    </b-alert>

                                    <!-- One-click Practice Pulse enablement result (2026-09-25): the backend's answer per
                                         step, verbatim — see initializePracticePulse. Same markup under the System-tab
                                         Initialize / Rebuild button (Vue 2 has no in-file fragment reuse). -->
                                    <b-alert
                                        v-if="practicePulseInitView"
                                        show
                                        dismissible
                                        :variant="practicePulseInitView.variant"
                                        class="practice-pulse-init-result small mb-3"
                                        data-test="practice-pulse-init-result"
                                        @dismissed="dismissPracticePulseInit()"
                                    >
                                        <strong class="d-block mb-1">Practice Pulse — what was done</strong>
                                        <div v-if="practicePulseInitView.error">
                                            <i class="fa fa-times-circle mr-1"></i>{{ practicePulseInitView.error }}
                                        </div>
                                        <template v-else>
                                            <div v-for="line in practicePulseInitView.lines" :key="line.key">
                                                <i :class="line.ok ? 'fa fa-check text-success' : 'fa fa-exclamation-triangle text-warning'" class="mr-1"></i><strong>{{ line.label }}:</strong> {{ line.message }}
                                            </div>
                                            <div v-if="practicePulseInitView.hqNote" class="small mt-1">
                                                <i class="fa fa-exclamation-triangle text-warning mr-1"></i>{{ practicePulseInitView.hqNote }}
                                            </div>
                                        </template>
                                    </b-alert>

                                    <!-- Practice logo (teamio.sites.LogoUrl). Until 2026-08-27 the only way to change it
                                         after onboarding was SQL — meanwhile it renders on every patient form, reminder
                                         email, treatment plan, review page and My Account. Saved through sendAction
                                         SET_SITE_LOGO (teamio-admin lib/siteLogo.js), which probes the URL as image/*
                                         before writing; blank clears it. -->
                                    <div class="feature-card site-logo-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #fdf2e8;"><i class="fa fa-picture-o" style="color: #e67e22;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Practice logo</div>
                                                <div class="feature-desc">Shown on patient forms, reminder emails, treatment plans, review pages and My Account</div>
                                            </div>
                                        </div>
                                        <div class="site-logo-body">
                                            <div class="logo-check" :class="'logo-check--' + siteLogoCheck.level">
                                                <img v-if="siteLogoEdit.url" v-show="siteLogoCheck.level !== 'error'" class="logo-check-img" :src="siteLogoEdit.url" alt="Practice logo preview"
                                                     @load="onLogoPreview('site', $event)" @error="onLogoPreview('site', null)">
                                                <div class="logo-check-msgs">
                                                    <div v-for="m in siteLogoCheck.messages" :key="m">{{ m }}</div>
                                                </div>
                                            </div>
                                            <b-input-group prepend="Logo URL" size="sm" class="mt-2">
                                                <b-form-input v-model="siteLogoEdit.url" type="text" trim :disabled="siteLogoEdit.saving"
                                                              placeholder="https://practice-site.com/logo.png  (blank = no logo)"></b-form-input>
                                                <b-input-group-append>
                                                    <b-button variant="primary" size="sm" :disabled="siteLogoEdit.saving || !siteLogoDirty || siteLogoBlocksSave" @click="saveSiteLogo">
                                                        <b-spinner v-if="siteLogoEdit.saving" small class="mr-1"></b-spinner>Save logo
                                                    </b-button>
                                                </b-input-group-append>
                                            </b-input-group>
                                            <small v-if="siteLogoEdit.error" class="text-danger d-block mt-1">{{ siteLogoEdit.error }}</small>
                                            <small v-else-if="siteLogoDirty && !siteLogoBlocksSave" class="text-muted d-block mt-1">Unsaved — click Save logo to apply everywhere.</small>
                                        </div>
                                    </div>
                                    <!-- Forms -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #e8f4fd;"><i class="fa fa-file-text" style="color: #3498db;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Forms</div>
                                                <div class="feature-desc">Patient intake forms, consent forms</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.FORMS_PAID_FOR" switch @change="toggleFeature('FORMS_PAID_FOR', $event)" :disabled="featureSaving.FORMS_PAID_FOR"></b-form-checkbox>
                                        </div>
                                        <transition name="slide-down">
                                            <div v-if="siteFeatures.FORMS_PAID_FOR" class="feature-sub-option">
                                                <b-form-checkbox
                                                    v-model="siteFeatures.AI_FORMS_PAID_FOR"
                                                    switch
                                                    @change="toggleFeature('AI_FORMS_PAID_FOR', $event)"
                                                    :disabled="featureSaving.AI_FORMS_PAID_FOR"
                                                    class="mb-0"
                                                >
                                                    <span class="feature-sub-label">AI form builder</span>
                                                    <span class="feature-sub-desc">&mdash; enable &ldquo;Create with AI&rdquo; &amp; &ldquo;Edit with AI&rdquo;</span>
                                                </b-form-checkbox>
                                            </div>
                                        </transition>
                                        <transition name="checklist-slide">
                                            <div v-if="siteFeatures.FORMS_PAID_FOR" class="feature-sub-option feature-checklist">
                                                <!-- OD imaging category per form type — asked on every run, never saved. Only "Apply to
                                                     existing forms" uses it and stays disabled until all three are picked (the backend
                                                     refuses an incomplete map). Load Form Email & SMS Templates copies no forms. -->
                                                <div class="forms-imaging-map" data-test="forms-imaging-map">
                                                    <div class="checklist-label">Open Dental imaging categories</div>
                                                    <small class="text-muted d-block mb-1">Used by Apply to existing forms: each form's PDF and uploaded files are saved to Open Dental under the category picked for its form type. Uploads default to the same category; change individual forms later in the form builder.</small>
                                                    <div v-if="formsImaging.loading" class="small text-muted py-1">
                                                        <b-spinner small class="mr-1"></b-spinner>Loading imaging categories&hellip;
                                                    </div>
                                                    <b-alert v-else-if="formsImaging.error" show variant="danger" class="mb-0 seed-results">
                                                        {{ formsImaging.error }}
                                                        <b-link class="ml-1" @click="loadFormsImagingCategories()">Retry</b-link>
                                                    </b-alert>
                                                    <form-imaging-category-map v-else
                                                        v-model="formsImaging.map"
                                                        :categories="formsImaging.categories"
                                                        :consent-candidates="formsImaging.consentCandidates"
                                                        :disabled="formTemplatesSeed.seeding || formsImagingApply.previewing || formsImagingApply.applying"
                                                        id-prefix="forms-imaging" />
                                                </div>
                                                <div v-for="item in formsChecklistItems" :key="item.key" class="checklist-item">
                                                    <b-form-checkbox v-if="item.checkbox" v-model="wikiTemplateChecks[item.key]" class="checklist-checkbox"></b-form-checkbox>
                                                    <i v-else class="fa" :class="item.ok ? 'fa-check-circle text-success' : 'fa-circle-o text-muted'"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">
                                                            {{ item.label }}
                                                            <b-badge v-if="item.badge" variant="light" class="ml-1">{{ item.badge }}</b-badge>
                                                        </div>
                                                        <small v-if="item.detail" class="text-muted d-block">{{ item.detail }}</small>
                                                        <small v-if="item.note && !wikiTemplateChecks[item.key]" class="text-danger d-block font-weight-bold"><i class="fa fa-exclamation-circle mr-1"></i>{{ item.note }}</small>
                                                        <div v-if="item.list && item.list.length" class="checklist-sublist">
                                                            <small class="text-warning d-block"><i class="fa fa-exclamation-triangle mr-1"></i>{{ item.listLabel }}</small>
                                                            <small v-for="n in item.list.slice(0, 8)" :key="n" class="d-block text-muted">&bull; {{ n }}</small>
                                                            <small v-if="item.list.length > 8" class="d-block text-muted">&hellip;and {{ item.list.length - 8 }} more</small>
                                                        </div>
                                                    </div>
                                                    <!-- Each Forms checklist action names its own handler (formsChecklistItems → action.handler). -->
                                                    <b-button v-if="item.action" size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="item.action.disabled"
                                                              :title="item.action.tooltip"
                                                              :data-test="'forms-checklist-action-' + item.key"
                                                              @click="runFormsChecklistAction(item.action)">
                                                        <b-spinner v-if="item.action.busy" small class="mr-1"></b-spinner>{{ item.action.label }}
                                                    </b-button>
                                                    <b-link v-else-if="item.jump" class="checklist-action" @click="siteManagementTabIndex = item.jump.tab">
                                                        {{ item.jump.label }} <i class="fa fa-arrow-right"></i>
                                                    </b-link>
                                                </div>

                                                <b-alert v-if="formTemplatesSeed.results" show :variant="seedResultsVariant" class="mt-2 mb-0 seed-results">
                                                    <strong>{{ formTemplatesSeed.results.wikiTemplates.successCount || 0 }}</strong> template(s) copied,
                                                    <strong>{{ formTemplatesSeed.results.wikiTemplates.skippedCount || 0 }}</strong> already on site,
                                                    <strong>{{ formTemplatesSeed.results.wikiTemplates.failureCount || 0 }}</strong> failed
                                                    <div v-if="formTemplatesSeed.results.emailSignatureSeeded" class="small mt-1">
                                                        <i class="fa fa-check mr-1"></i>Email signature seeded from the practice profile and turned on<span v-if="!formTemplatesSeed.results.emailSignatureLogoIncluded"> (without a logo)</span>
                                                    </div>
                                                    <div v-for="w in formTemplatesSeed.results.warnings" :key="w" class="small text-warning mt-1">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                                    </div>
                                                </b-alert>
                                                <b-alert v-if="formTemplatesSeed.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ formTemplatesSeed.error }}</b-alert>
                                                <b-alert v-if="formsImagingApply.error && !formsImagingApply.preview" show variant="danger" class="mt-2 mb-0 seed-results">{{ formsImagingApply.error }}</b-alert>
                                                <b-alert v-if="procMappingsSeed.error && !procMappingsSeed.preview" show variant="danger" class="mt-2 mb-0 seed-results">{{ procMappingsSeed.error }}</b-alert>
                                                <b-alert v-if="formMessagesSeed.error && !formMessagesSeed.preview" show variant="danger" class="mt-2 mb-0 seed-results" data-test="seed-fm-error">{{ formMessagesSeed.error }}</b-alert>
                                            </div>
                                        </transition>
                                    </div>

                                    <!-- Forms → "Apply to existing forms": dryRun preview first, then the real apply. -->
                                    <b-modal id="apply-form-imaging-modal"
                                             title="Apply imaging defaults to existing forms"
                                             size="lg"
                                             scrollable
                                             ok-variant="success"
                                             :ok-title="formsImagingApplyOkTitle"
                                             :ok-disabled="formsImagingApplyOkDisabled"
                                             :cancel-disabled="formsImagingApply.applying"
                                             :no-close-on-backdrop="formsImagingApply.applying"
                                             :no-close-on-esc="formsImagingApply.applying"
                                             :hide-header-close="formsImagingApply.applying"
                                             @ok="onApplyFormImagingOk"
                                             @hidden="onApplyFormImagingHidden">
                                        <template v-if="formsImagingApply.preview">
                                            <p class="small mb-2">
                                                <strong>{{ formsImagingApplyRows.length }}</strong> change(s) across
                                                <strong>{{ formsImagingApply.preview.items.length }}</strong> form(s). Only blank file names and blank or
                                                invalid categories are filled; values already valid on this site are kept.
                                            </p>
                                            <b-alert v-if="formsImagingApply.preview.unparseable && formsImagingApply.preview.unparseable.length" show variant="warning" class="small py-2">
                                                <i class="fa fa-exclamation-triangle mr-1"></i>{{ formsImagingApply.preview.unparseable.length }} form(s) have unreadable configuration and will be skipped:
                                                {{ formsImagingApply.preview.unparseable.join(', ') }}
                                            </b-alert>
                                            <div class="table-responsive">
                                                <table class="table table-sm apply-imaging-table mb-0">
                                                    <thead><tr><th>Form</th><th>Field</th><th>From &rarr; To</th></tr></thead>
                                                    <tbody>
                                                        <tr v-for="row in formsImagingApplyRows" :key="row.key">
                                                            <td>{{ row.form }}</td>
                                                            <td class="text-nowrap">{{ row.field }}</td>
                                                            <td><span class="text-muted">{{ row.from }}</span> &rarr; <strong>{{ row.to }}</strong></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </template>
                                        <b-alert v-if="formsImagingApply.error && formsImagingApply.preview" show variant="danger" class="mt-2 mb-0 small">{{ formsImagingApply.error }}</b-alert>
                                    </b-modal>

                                    <!-- Forms → "Load default procedure mappings" (SEED_PROCEDURE_MAPPINGS): dryRun preview of the rows it would add, then the real run.
                                         Never flips the auto-send switch; codes already mapped are never changed. -->
                                    <b-modal id="seed-procedure-mappings-modal"
                                             title="Load default procedure mappings"
                                             size="xl"
                                             scrollable
                                             ok-variant="success"
                                             :ok-title="procMappingsOkTitle"
                                             :ok-disabled="procMappingsOkDisabled"
                                             :cancel-disabled="procMappingsSeed.applying"
                                             :no-close-on-backdrop="procMappingsSeed.applying"
                                             :no-close-on-esc="procMappingsSeed.applying"
                                             :hide-header-close="procMappingsSeed.applying"
                                             @ok="onSeedProcedureMappingsOk"
                                             @hidden="onSeedProcedureMappingsHidden">
                                        <template v-if="procMappingsSeed.preview">
                                            <p class="small mb-2" data-test="seed-pm-summary">
                                                <strong>{{ procMappingsSeed.preview.rowsAdded }}</strong> row(s) will be added to
                                                <strong>{{ procMappingsSeed.preview.siteName || "this site" }}</strong>. Forms are matched to each group by NAME among this office's own forms;
                                                a cell with no match, or several, is left blank and flagged <span class="badge badge-warning">Pick a form</span> for the office to choose.
                                                Codes that are already in a row are never changed.
                                            </p>
                                            <b-alert v-for="w in (procMappingsSeed.preview.warnings || [])" :key="w" show variant="warning" class="small py-2 mb-2">
                                                <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                            </b-alert>
                                            <div v-if="procMappingsPreviewRows.length" class="table-responsive">
                                                <table class="table table-sm apply-imaging-table mb-2" data-test="seed-pm-table">
                                                    <thead><tr><th>Group</th><th>Codes</th><th>Consent form</th><th>Post-op form</th></tr></thead>
                                                    <tbody>
                                                        <tr v-for="row in procMappingsPreviewRows" :key="row.label">
                                                            <td class="text-nowrap">{{ row.label }}</td>
                                                            <td class="small">{{ row.codes }}</td>
                                                            <td><template v-if="row.consent">{{ row.consent }}</template><span v-else class="badge badge-warning">Pick a form</span></td>
                                                            <td><template v-if="row.postOp">{{ row.postOp }}</template><span v-else class="badge badge-warning">Pick a form</span></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div v-if="(procMappingsSeed.preview.toReview || []).length" class="small mb-2" data-test="seed-pm-review">
                                                <strong>To review ({{ procMappingsSeed.preview.toReview.length }})</strong>
                                                <div v-for="(t, i) in procMappingsSeed.preview.toReview" :key="i" class="text-muted">
                                                    &bull; {{ t.label }} &mdash; {{ t.cell === "postOp" ? "post-op form" : "consent form" }}:
                                                    <template v-if="(t.candidates || []).length">several matches &ndash; {{ t.candidates.join(", ") }}</template>
                                                    <template v-else>no form matched</template>
                                                </div>
                                            </div>
                                            <div v-if="(procMappingsSeed.preview.codesSkippedExisting || []).length" class="small text-muted mb-1" data-test="seed-pm-skipped">
                                                {{ procMappingsSeed.preview.codesSkippedExisting.length }} code(s) already mapped &mdash; left as they are:
                                                {{ procMappingsSeed.preview.codesSkippedExisting.map(c => c.procCode).join(", ") }}
                                            </div>
                                            <div v-if="(procMappingsSeed.preview.codesMissingOnSite || []).length" class="small text-muted mb-1" data-test="seed-pm-missing">
                                                {{ procMappingsSeed.preview.codesMissingOnSite.length }} default code(s) are not in this office's procedure list &mdash; skipped:
                                                {{ procMappingsSeed.preview.codesMissingOnSite.map(c => c.procCode).join(", ") }}
                                            </div>
                                            <div class="small mt-2" data-test="seed-pm-settings">
                                                <i class="fa fa-info-circle mr-1"></i>
                                                <template v-if="procMappingsSeed.preview.wouldSeedPostOpSettings">Also saves the default post-op text &amp; email. Auto-send stays <strong>OFF</strong> &mdash; the office turns it on in Forms &rarr; &#128279;.</template>
                                                <template v-else>Post-op message settings are already saved on this site &mdash; left as they are. Auto-send is not touched.</template>
                                            </div>
                                        </template>
                                        <b-alert v-if="procMappingsSeed.error && procMappingsSeed.preview" show variant="danger" class="mt-2 mb-0 small">{{ procMappingsSeed.error }}</b-alert>
                                    </b-modal>

                                    <!-- Forms → "Load default form messages" (SEED_FORM_MESSAGES): dryRun preview of the two blobs it would write (New Patients +
                                         Medical History Update, whole-blob set-if-absent), then the real run. Never turns either on. The row shows only while the
                                         Forms Settings gate is open (formsSettingsGateOpen); the classic "Form email & SMS templates" row stays untouched. -->
                                    <b-modal id="seed-form-messages-modal"
                                             title="Load default form messages"
                                             size="lg"
                                             scrollable
                                             ok-variant="success"
                                             :ok-title="formMessagesOkTitle"
                                             :ok-disabled="formMessagesOkDisabled"
                                             :cancel-disabled="formMessagesSeed.applying"
                                             :no-close-on-backdrop="formMessagesSeed.applying"
                                             :no-close-on-esc="formMessagesSeed.applying"
                                             :hide-header-close="formMessagesSeed.applying"
                                             @ok="onSeedFormMessagesOk"
                                             @hidden="onSeedFormMessagesHidden">
                                        <template v-if="formMessagesPreview">
                                            <p class="small mb-2" data-test="seed-fm-summary">
                                                Saves the default New Patients and Medical History messages on
                                                <strong>{{ formMessagesPreview.siteName || "this site" }}</strong>, with the form link pre-picked from the office's own forms
                                                when exactly one fits. A setting that is already saved is never changed.
                                            </p>
                                            <b-alert v-for="w in formMessagesPreview.warnings" :key="w" show variant="warning" class="small py-2 mb-2">
                                                <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                            </b-alert>
                                            <div v-for="card in formMessagesPreview.cards" :key="card.key" class="border seed-fm-card p-2 mb-2" :data-test="'seed-fm-card-' + card.key">
                                                <div class="d-flex align-items-center">
                                                    <strong>{{ card.title }}</strong>
                                                    <span class="badge ml-2" :class="card.status === 'write' ? 'badge-success' : (card.status === 'corrupt' ? 'badge-warning' : 'badge-secondary')">{{ card.statusText }}</span>
                                                </div>
                                                <template v-if="card.status === 'write'">
                                                    <div v-for="m in card.messages" :key="m.label" class="small text-muted">&bull; {{ m.label }} &mdash; {{ m.linkText }}</div>
                                                    <div v-if="card.reason" class="small mt-1"><span class="badge badge-warning">Pick a form</span> {{ card.reason }}</div>
                                                    <div v-if="card.triggerForms.length" class="small text-muted mt-1" :data-test="'seed-fm-triggers-' + card.key">
                                                        What counts as an update ({{ card.triggerForms.length }}): {{ card.triggerForms.join(", ") }}
                                                    </div>
                                                </template>
                                            </div>
                                            <div class="small mt-2" data-test="seed-fm-off">
                                                <i class="fa fa-info-circle mr-1"></i>Both stay <strong>OFF</strong> &mdash; turn them on in Forms &rarr; Settings after review.
                                            </div>
                                        </template>
                                        <b-alert v-if="formMessagesSeed.error && formMessagesSeed.preview" show variant="danger" class="mt-2 mb-0 small">{{ formMessagesSeed.error }}</b-alert>
                                    </b-modal>

                                    <!-- Online Appointments -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #e8fdf0;"><i class="fa fa-calendar-check-o" style="color: #27ae60;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Online Appointments</div>
                                                <div class="feature-desc">Patient self-scheduling via web</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.ONLINE_SCHEDULING_PAID_FOR" switch @change="toggleFeature('ONLINE_SCHEDULING_PAID_FOR', $event)" :disabled="featureSaving.ONLINE_SCHEDULING_PAID_FOR"></b-form-checkbox>
                                        </div>
                                    </div>

                                    <!-- Appointment Reminders -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #fef6e8;"><i class="fa fa-bell" style="color: #f39c12;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Appointment Reminders</div>
                                                <div class="feature-desc">Automated SMS/email reminders</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.APPT_REMINDER_PAID_FOR" switch @change="toggleApptReminderAccess($event)" :disabled="featureSaving.APPT_REMINDER_PAID_FOR"></b-form-checkbox>
                                        </div>
                                        <transition name="slide-down">
                                            <div v-if="siteFeatures.APPT_REMINDER_PAID_FOR" class="feature-sub-option">
                                                <b-form-checkbox
                                                    v-model="siteFeatures.APPT_REMINDER_ACTIVATED"
                                                    switch
                                                    @change="onToggleApptReminderActivated($event)"
                                                    data-test="appt-reminder-activate-switch"
                                                    :disabled="featureSaving.APPT_REMINDER_ACTIVATED"
                                                    class="mb-0"
                                                >
                                                    <span class="feature-sub-label">Activate sending</span>
                                                    <span class="feature-sub-desc">— start sending reminders to patients</span>
                                                </b-form-checkbox>
                                            </div>
                                        </transition>
                                        <transition name="checklist-slide">
                                            <div v-if="siteFeatures.APPT_REMINDER_PAID_FOR" class="feature-sub-option feature-checklist">
                                                <div v-for="item in aptRemindersChecklistItems" :key="item.key" class="checklist-item" :data-test="'apt-checklist-' + item.key">
                                                    <i class="fa" :class="item.ok ? 'fa-check-circle text-success' : (item.bad ? 'fa-times-circle text-danger' : (item.warn ? 'fa-exclamation-circle text-warning' : 'fa-circle-o text-muted'))"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">
                                                            {{ item.label }}
                                                            <b-badge v-if="item.badge" variant="light" class="ml-1">{{ item.badge }}</b-badge>
                                                        </div>
                                                        <small v-if="item.detail" class="d-block" :class="item.bad ? 'text-danger' : (item.warn ? 'checklist-detail-warn' : 'text-muted')">{{ item.detail }}</small>
                                                        <!-- Patients with WORDS in Open Dental's cell-phone field (junkCells row only) -->
                                                        <div v-if="item.patients && item.patients.length" class="junk-cells-tools">
                                                            <b-link class="small" data-test="junk-cells-toggle" @click="junkCellsExpanded = !junkCellsExpanded">
                                                                <i class="fa mr-1" :class="junkCellsExpanded ? 'fa-caret-down' : 'fa-caret-right'"></i>{{ junkCellsExpanded ? 'Hide patients' : 'Show patients' }}
                                                            </b-link>
                                                            <b-link class="small ml-3" data-test="junk-cells-copy" @click="copyJunkCellsList(item.patients)">
                                                                <i class="fa fa-clipboard mr-1"></i>Copy list
                                                            </b-link>
                                                        </div>
                                                        <div v-if="item.patients && item.patients.length && junkCellsExpanded" class="junk-cells-list" data-test="junk-cells-list">
                                                            <div v-for="p in item.patients" :key="p.patNum" class="junk-cells-line" :class="{ 'junk-cells-line-urgent': p.urgent }">{{ junkCellLine(p) }}</div>
                                                            <div v-if="item.truncated" class="junk-cells-more" data-test="junk-cells-truncated">(first {{ item.patients.length }} shown)</div>
                                                        </div>
                                                    </div>
                                                    <b-button v-if="item.action" size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="item.action.disabled || reminderTemplatesSeed.seeding"
                                                              :title="item.action.tooltip"
                                                              @click="confirmSeedReminderTemplates">
                                                        <b-spinner v-if="reminderTemplatesSeed.seeding" small class="mr-1"></b-spinner>{{ item.action.label }}
                                                    </b-button>
                                                    <b-link v-else-if="item.jump" class="checklist-action" @click="siteManagementTabIndex = item.jump.tab">
                                                        {{ item.jump.label }} <i class="fa fa-arrow-right"></i>
                                                    </b-link>
                                                </div>

                                                <b-alert v-if="reminderTemplatesSeed.results" show :variant="reminderSeedResultsVariant" class="mt-2 mb-0 seed-results">
                                                    <span v-if="reminderTemplatesSeed.results.wikiPagesCopied !== false">
                                                        <strong>{{ reminderTemplatesSeed.results.successCount }}</strong> copied,
                                                        <strong>{{ reminderTemplatesSeed.results.skippedCount }}</strong> skipped,
                                                        <strong>{{ reminderTemplatesSeed.results.failureCount }}</strong> failed
                                                        <span v-if="reminderTemplatesSeed.results.logoRemovedCount"> &mdash; logo removed from {{ reminderTemplatesSeed.results.logoRemovedCount }} template(s)</span>
                                                    </span>
                                                    <div v-if="reminderTemplatesSeed.results.wikiPagesCopied === false" class="small" data-test="reminder-no-copy-note">
                                                        <i class="fa fa-info-circle mr-1"></i>No reminder wiki pages were copied &mdash; the offices edit the default messages directly in Appointment Reminders → Settings.
                                                    </div>
                                                    <div v-if="reminderTemplatesSeed.results.inlineSlotsWritten > 0" class="small mt-1" data-test="reminder-inline-written">
                                                        <i class="fa fa-check mr-1"></i>Filled {{ reminderTemplatesSeed.results.inlineSlotsWritten }} message(s) with the default wording in Appointment Reminders → Settings<span v-if="reminderTemplatesSeed.results.settingsRowCreated"> (settings created &mdash; nothing turned on)</span>
                                                    </div>
                                                    <div v-if="reminderTemplatesSeed.results.emailSignatureSeeded" class="small mt-1">
                                                        <i class="fa fa-check mr-1"></i>Email signature seeded from the practice profile and turned on<span v-if="!reminderTemplatesSeed.results.emailSignatureLogoIncluded"> (without a logo)</span>
                                                    </div>
                                                    <div v-for="w in (reminderTemplatesSeed.results.warnings || [])" :key="w" class="small text-warning mt-1">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                                    </div>
                                                </b-alert>
                                                <b-alert v-if="reminderTemplatesSeed.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ reminderTemplatesSeed.error }}</b-alert>
                                            </div>
                                        </transition>
                                    </div>

                                    <!-- Workflow Builder -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #f3e8fd;"><i class="fa fa-sitemap" style="color: #8e44ad;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Workflow Builder</div>
                                                <div class="feature-desc">Automated workflow engine</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.WORKFLOW_ACTIVATED" switch @change="toggleFeature('WORKFLOW_ACTIVATED', $event)" :disabled="featureSaving.WORKFLOW_ACTIVATED"></b-form-checkbox>
                                        </div>
                                    </div>

                                    <!-- Practice Pulse -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #fde8e8;"><i class="fa fa-heartbeat" style="color: #e74c3c;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Practice Ops</div>
                                                <div class="feature-desc">Patient check-in and flow tracking</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.PRACTICE_PULSE_PAID_FOR" switch @change="toggleFeature('PRACTICE_PULSE_PAID_FOR', $event)" :disabled="featureSaving.PRACTICE_PULSE_PAID_FOR"></b-form-checkbox>
                                        </div>
                                    </div>

                                    <!--
                                        Schedule - Chart Audit (was "Schedule View live updates", Phase 3Lb).

                                        THE per-office switch for the page (release plan 2026-10-28, D3):
                                        ON = the page for the office's staff (nav entry + route) AND live
                                        updates, because the office's own Teamio application reads this
                                        same row and pushes a signal the instant Open Dental changes.
                                        Absent = OFF. The service refuses every data route while it is off.

                                        ⛔ Needs the office's Teamio app (exe) at 3.0.61+ (D4). Below that the
                                        switch cannot be turned ON from here (scheduleSwitchGate, src/utils/
                                        scheduleView/access.js) — and the service refuses the page anyway, so
                                        this is the early warning, not the gate. Turning it OFF is always
                                        allowed.

                                        ⚠️ MULTI-CLINIC: set this on the HQ site. The office
                                        application reads one settings row — the HQ's — so on a
                                        multi-clinic group this switch covers EVERY clinic at
                                        once, and setting it on an individual clinic's site is
                                        written but never read. Each clinic still gets its own
                                        signal; it is only the on/off that is group-wide.
                                    -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #e6f4ee;"><i class="fa fa-bolt" style="color: #0f7259;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Schedule - Chart Audit</div>
                                                <div class="feature-desc">The day's schedule with a chart audit on every appointment, live from Open Dental. Needs Teamio app 3.0.61+.</div>
                                                <small v-if="!scheduleSwitchGate.allowed" class="d-block text-danger" data-test="schedule-switch-needs-exe">{{ scheduleSwitchGate.message }}</small>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.SCHEDULE_VIEW_ENABLED" switch @change="toggleFeature('SCHEDULE_VIEW_ENABLED', $event)" :disabled="featureSaving.SCHEDULE_VIEW_ENABLED || scheduleSwitchBlocked" data-test="schedule-switch"></b-form-checkbox>
                                        </div>
                                    </div>

                                    <!-- Open Dental API (OD-API Phase 0.7). Its own component: it reads and saves
                                         through /get-od-api-config + /save-od-api-config (teamio.sites columns,
                                         not teamio.settings), so it is NOT part of siteFeatures/toggleFeature. -->
                                    <od-api-settings v-if="selectedSiteForManagement" :key="'od-api-' + selectedSiteForManagement.SiteID" :site-id="selectedSiteForManagement.SiteID" />

                                    <!-- Tx Presenter -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #e6f1ef;"><i class="fa fa-stethoscope" style="color: #0d5f54;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Tx Presenter</div>
                                                <div class="feature-desc">Treatment plan presentation, chairside walkthrough, patient-facing plan link</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.TX_PRESENTER_ENABLED" switch @change="toggleFeature('TX_PRESENTER_ENABLED', $event)" :disabled="featureSaving.TX_PRESENTER_ENABLED"></b-form-checkbox>
                                        </div>
                                        <!-- Cherry patient financing. Nested under Tx Presenter the same way the
                                             AI form builder nests under Forms: it is an add-on to that feature and
                                             has no meaning without it. Turning it ON only reveals the connection
                                             panel in Tx Presenter Settings → Payment Options — it starts no vendor
                                             spend and moves no patient data on its own. The practice still has to
                                             paste their own Cherry Business API Key there. -->
                                        <transition name="slide-down">
                                            <div v-if="siteFeatures.TX_PRESENTER_ENABLED" class="feature-sub-option">
                                                <b-form-checkbox
                                                    v-model="siteFeatures.CHERRY_ENABLED"
                                                    switch
                                                    @change="toggleFeature('CHERRY_ENABLED', $event)"
                                                    :disabled="featureSaving.CHERRY_ENABLED"
                                                    class="mb-0"
                                                >
                                                    <span class="feature-sub-label">Cherry financing</span>
                                                    <span class="feature-sub-desc">&mdash; let the practice connect their Cherry account for real patient financing</span>
                                                </b-form-checkbox>
                                            </div>
                                        </transition>
                                        <transition name="checklist-slide">
                                            <div v-if="siteFeatures.TX_PRESENTER_ENABLED" class="feature-sub-option feature-checklist">
                                                <div v-for="item in txPresenterChecklistItems" :key="item.key" class="checklist-item">
                                                    <i class="fa" :class="item.ok ? 'fa-check-circle text-success' : (item.warn ? 'fa-exclamation-circle text-warning' : 'fa-circle-o text-muted')"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">
                                                            {{ item.label }}
                                                            <b-badge v-if="item.badge" variant="light" class="ml-1">{{ item.badge }}</b-badge>
                                                        </div>
                                                        <small v-if="item.detail" class="text-muted d-block">{{ item.detail }}</small>
                                                        <small v-if="item.note" class="text-danger d-block"><i class="fa fa-exclamation-circle mr-1"></i>{{ item.note }}</small>
                                                    </div>
                                                    <b-button v-if="item.action" size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="item.action.disabled || txPresenterSeed.seeding"
                                                              :title="item.action.tooltip"
                                                              @click="confirmSeedTxPresenter">
                                                        <b-spinner v-if="txPresenterSeed.seeding" small class="mr-1"></b-spinner>{{ item.action.label }}
                                                    </b-button>
                                                    <b-link v-else-if="item.jump" class="checklist-action" @click="siteManagementTabIndex = item.jump.tab">
                                                        {{ item.jump.label }} <i class="fa fa-arrow-right"></i>
                                                    </b-link>
                                                </div>

                                                <b-alert v-if="txPresenterSeed.results" show :variant="txPresenterSeedResultsVariant" class="mt-2 mb-0 seed-results">
                                                    <strong>{{ txPresenterSeed.results.seeded.length }}</strong> section(s) seeded,
                                                    <strong>{{ txPresenterSeed.results.skipped.length }}</strong> skipped
                                                    <span v-if="txPresenterSeed.results.imagingItemName"> &mdash; imaging category: {{ txPresenterSeed.results.imagingItemName }}</span>
                                                    <div v-for="w in (txPresenterSeed.results.warnings || [])" :key="w" class="small text-warning mt-1">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                                    </div>
                                                </b-alert>
                                                <b-alert v-if="txPresenterSeed.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ txPresenterSeed.error }}</b-alert>

                                                <!-- Patient email sender.
                                                     A practice can only email patients if it has a verified sender,
                                                     which needs three DNS records many offices won't publish. This
                                                     lets such a site send as <slug>@mail.teamio.app with the practice
                                                     name as the display name and the office's own address as Reply-To.

                                                     Deliberately NO client-side precondition logic: the backend is the
                                                     single source of truth for whether a site can be enabled, and its
                                                     refusal reasons are already plain-language and actionable. Copying
                                                     those rules here would be a second implementation free to drift. -->
                                                <div class="checklist-item mt-2">
                                                    <i class="fa" :class="txPresenterEmailSender.icon"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">Patient email sender</div>
                                                        <small class="text-muted d-block">{{ txPresenterEmailSender.detail }}</small>
                                                    </div>
                                                    <b-button v-if="txPresenterEmailSender.canEnable" size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="platformSender.enabling"
                                                              title="Lets this site email patients as Teamio — no DNS setup required"
                                                              @click="confirmEnablePlatformSender">
                                                        <b-spinner v-if="platformSender.enabling" small class="mr-1"></b-spinner>Send through Teamio
                                                    </b-button>
                                                </div>

                                                <b-alert v-if="platformSender.results" show variant="success" class="mt-2 mb-0 seed-results">
                                                    Patients will now receive email from
                                                    <strong>{{ platformSender.results.practiceName }} &lt;{{ platformSender.results.address }}&gt;</strong>,
                                                    replying to <strong>{{ platformSender.results.replyTo }}</strong>.
                                                    <div v-for="w in (platformSender.results.warnings || [])" :key="w" class="small text-warning mt-1">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                                    </div>
                                                </b-alert>
                                                <b-alert v-if="platformSender.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ platformSender.error }}</b-alert>
                                            </div>
                                        </transition>
                                    </div>

                                    <!-- Insurance Verification.
                                         ⚠ Unlike every other card, the checklist shows ALWAYS, not only
                                         when the feature is ON. It is a PRE-enable checklist — the whole
                                         point is to be read before the toggle is flipped, because that
                                         flip starts real per-click Stedi spend. -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #e6f1ef;"><i class="fa fa-shield" style="color: #0f7259;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Insurance Verification</div>
                                                <div class="feature-desc">Real-time benefits &amp; eligibility prechecks, dashboard, and printable patient handout</div>
                                            </div>
                                            <b-form-checkbox
                                                v-model="siteFeatures.INSURANCE_VERIFICATION_ENABLED"
                                                switch
                                                @change="onToggleInsuranceVerification($event)"
                                                :disabled="featureSaving.INSURANCE_VERIFICATION_ENABLED || ivToggleBlocked"
                                            ></b-form-checkbox>
                                        </div>

                                        <div class="feature-sub-option feature-checklist">
                                            <!-- Readiness could not be computed (IV service unreachable, or
                                                 the site has no OD database yet). Say so — never imply PASS. -->
                                            <b-alert v-if="!ivReadiness" show variant="secondary" class="mb-0 seed-results">
                                                <i class="fa fa-question-circle mr-1"></i>
                                                Readiness could not be checked for this site. The toggle stays blocked
                                                until it can — reopen the Features tab to retry.
                                            </b-alert>

                                            <template v-else>
                                                <div v-for="item in insuranceVerificationChecklistItems" :key="item.key" class="checklist-item">
                                                    <i class="fa" :class="item.ok ? 'fa-check-circle text-success' : (item.warn ? 'fa-exclamation-circle text-warning' : 'fa-circle-o text-muted')"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">
                                                            {{ item.label }}
                                                            <b-badge v-if="item.badge" variant="light" class="ml-1">{{ item.badge }}</b-badge>
                                                        </div>
                                                        <small v-if="item.detail" class="text-muted d-block">{{ item.detail }}</small>
                                                        <small v-if="item.note" class="text-danger d-block"><i class="fa fa-exclamation-circle mr-1"></i>{{ item.note }}</small>

                                                        <!-- Inline billing-provider picker. The blocker is fixed HERE
                                                             rather than by sending the operator to the practice's own
                                                             Precheck settings page mid-onboarding. -->
                                                        <div v-if="item.key === 'billingProvider'" class="iv-provider-picker mt-2">
                                                            <select class="iv-provider-select" v-model="ivBillingProvider.provNum" :disabled="ivBillingProvider.saving">
                                                                <option :value="null">Select a billing provider…</option>
                                                                <option v-for="p in ivProviderOptions" :key="p.value" :value="p.value">{{ p.text }}</option>
                                                            </select>
                                                            <b-button size="sm" variant="outline-primary"
                                                                      :disabled="ivBillingProvider.saving || ivBillingProvider.provNum === null || ivBillingProvider.provNum === ivSavedBillingProviderNum"
                                                                      @click="saveIvBillingProvider">
                                                                <b-spinner v-if="ivBillingProvider.saving" small class="mr-1"></b-spinner>Save
                                                            </b-button>
                                                            <small v-if="!ivProviderOptions.length" class="text-muted d-block mt-1">
                                                                No providers loaded for this practice.
                                                            </small>
                                                            <small v-if="ivBillingProvider.error" class="text-danger d-block mt-1">{{ ivBillingProvider.error }}</small>
                                                        </div>

                                                        <!-- Provider-name override (2026-10-29). Stedi fills the 270's provider
                                                             name from NPPES when we send the NPI alone, so this is empty for
                                                             every site. Super Admin only BY PLACEMENT: it lives here, never in
                                                             the office-facing Precheck settings modal. Collapsed by default; a
                                                             set override stays visible as a badge on the collapsed header. -->
                                                        <div v-if="item.key === 'billingProviderNpi'" class="iv-override mt-2" data-test="iv-override">
                                                            <a href="#" class="iv-override-toggle" data-test="iv-override-toggle" @click.prevent="ivOverride.open = !ivOverride.open">
                                                                <i class="fa" :class="ivOverride.open ? 'fa-caret-down' : 'fa-caret-right'"></i>
                                                                Override provider name (rare)
                                                            </a>
                                                            <b-badge v-if="ivProviderNameOverrideText" variant="warning" class="ml-1" data-test="iv-override-current">{{ ivProviderNameOverrideText }}</b-badge>

                                                            <div v-if="ivOverride.open" class="iv-override-body mt-2">
                                                                <small class="text-muted d-block mb-2">
                                                                    Leave empty. Set only when a payer is known to keep a different name on file than NPPES.
                                                                </small>
                                                                <small v-if="ivProviderNameOverrideText" class="d-block mb-2" data-test="iv-override-saved">
                                                                    Currently sent on the 270: <strong>{{ ivProviderNameOverrideText }}</strong>
                                                                </small>
                                                                <small v-else class="text-muted d-block mb-2">
                                                                    No override — the 270 sends the NPI only and Stedi fills the name from NPPES.
                                                                </small>

                                                                <div class="iv-override-modes mb-2">
                                                                    <label class="mr-3 mb-0">
                                                                        <input type="radio" value="organization" v-model="ivOverride.mode" :disabled="ivOverride.saving"> Organization
                                                                    </label>
                                                                    <label class="mb-0">
                                                                        <input type="radio" value="person" v-model="ivOverride.mode" :disabled="ivOverride.saving"> Person
                                                                    </label>
                                                                </div>

                                                                <div class="iv-provider-picker">
                                                                    <input v-if="ivOverride.mode === 'organization'" type="text" class="iv-override-input"
                                                                           v-model="ivOverride.organizationName" maxlength="60" placeholder="Organization name"
                                                                           :disabled="ivOverride.saving" data-test="iv-override-org">
                                                                    <template v-else>
                                                                        <input type="text" class="iv-override-input" v-model="ivOverride.firstName" maxlength="60"
                                                                               placeholder="First name" :disabled="ivOverride.saving" data-test="iv-override-first">
                                                                        <input type="text" class="iv-override-input" v-model="ivOverride.lastName" maxlength="60"
                                                                               placeholder="Last name" :disabled="ivOverride.saving" data-test="iv-override-last">
                                                                    </template>
                                                                    <b-button size="sm" variant="outline-primary" data-test="iv-override-save"
                                                                              :disabled="ivOverride.saving || !ivOverrideDraft || ivOverrideDraftIsSaved"
                                                                              @click="saveIvProviderNameOverride">
                                                                        <b-spinner v-if="ivOverride.saving" small class="mr-1"></b-spinner>Save
                                                                    </b-button>
                                                                    <b-button size="sm" variant="outline-secondary" data-test="iv-override-clear"
                                                                              :disabled="ivOverride.saving || !ivProviderNameOverride"
                                                                              @click="clearIvProviderNameOverride">
                                                                        Clear
                                                                    </b-button>
                                                                </div>
                                                                <small v-if="ivOverride.error" class="text-danger d-block mt-1">{{ ivOverride.error }}</small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <b-button v-if="item.action" size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="item.action.disabled || ivSeed.seeding"
                                                              :title="item.action.tooltip"
                                                              @click="confirmSeedInsuranceVerification">
                                                        <b-spinner v-if="ivSeed.seeding" small class="mr-1"></b-spinner>{{ item.action.label }}
                                                    </b-button>
                                                </div>

                                                <!-- Appointment fields (apptfield) are on the GLOBAL Open Dental
                                                     ignore list, so they never reach the mirror until this site
                                                     is given a per-site override AND its exe is new enough. -->
                                                <div class="checklist-item">
                                                    <i class="fa fa-table text-muted"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">Appointment fields in the mirror</div>
                                                        <small class="text-muted d-block">
                                                            Needs exe v3.0.55+ and the <code>apptfield</code> table override
                                                            (System tab → Exe table overrides card → Request MySQL Dump).
                                                            Multi-clinic customers: un-ignore <code>insverify</code> too — the clinics
                                                            build ignores it by default, so "Eligibility Last Verified" never reaches the mirror.
                                                        </small>
                                                    </div>
                                                </div>

                                                <b-alert v-if="ivSeed.results" show :variant="ivSeed.results.skipped ? 'info' : 'success'" class="mt-2 mb-0 seed-results">
                                                    <span v-if="ivSeed.results.skipped">Settings already configured — nothing was changed.</span>
                                                    <span v-else>Default Insurance Verification settings loaded (all automation OFF).</span>
                                                </b-alert>
                                                <b-alert v-if="ivSeed.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ ivSeed.error }}</b-alert>

                                                <!-- Insurance-book analysis (read-only, zero cost) -->
                                                <div class="checklist-item">
                                                    <i class="fa fa-search text-muted"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">Insurance book analysis</div>
                                                        <small class="text-muted d-block">
                                                            Read-only and free — no eligibility checks are sent. Shows who will verify,
                                                            who would fail <em>and still be billed</em>, and which member IDs need fixing.
                                                        </small>
                                                    </div>
                                                    <b-button size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="ivCensus.loading"
                                                              @click="runInsuranceCensus">
                                                        <b-spinner v-if="ivCensus.loading" small class="mr-1"></b-spinner>Analyze insurance book
                                                    </b-button>
                                                </div>

                                                <b-alert v-if="ivCensus.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ ivCensus.error }}</b-alert>

                                                <b-alert v-if="ivCensus.results" show variant="info" class="mt-2 mb-0 seed-results">
                                                    <div>
                                                        <strong>{{ ivCensus.results.headline.insuredPatients }}</strong> insured patients ·
                                                        <strong>{{ ivCensus.results.headline.appointmentsInWindow }}</strong> appointments in the next
                                                        {{ ivCensus.results.generatedFor.windowDays }} days
                                                        <span v-if="ivCensus.results.headline.appointmentsWithNoVerifiablePlan">
                                                            (<strong>{{ ivCensus.results.headline.appointmentsWithNoVerifiablePlan }}</strong> with no verifiable plan)
                                                        </span>
                                                    </div>
                                                    <div class="mt-1">
                                                        <span class="text-success">{{ ivCensus.results.patients.willVerify }} will verify</span>
                                                        · {{ ivCensus.results.patients.unmapped }} unmapped
                                                        · {{ ivCensus.results.patients.noEligibility }} no electronic eligibility
                                                    </div>

                                                    <!--
                                                      NOT a money bucket. Stedi bills only when a check REACHES THE PAYER
                                                      (confirmed by Stedi 2026-07-21), and an id no payer carries is rejected
                                                      before transmission — so these fail for FREE. This block said "fails AND
                                                      is still billed" until 2026-08-11; that was our own retracted inference,
                                                      never Stedi's. Do not reinstate it. It is still red because these
                                                      patients cannot be verified at all until the id is fixed.
                                                    -->
                                                    <div v-if="ivCensus.results.patients.unknownId" class="small text-danger mt-2">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>
                                                        <strong>{{ ivCensus.results.patients.unknownId }} patients</strong> are on payer IDs that no insurer
                                                        recognises. These are rejected before reaching a payer, so they cost nothing — but those patients
                                                        <strong>cannot be verified</strong> until the ID is fixed in Open Dental:
                                                        <div v-for="p in ivCensus.results.unknownIdPayers.slice(0, 5)" :key="p.payerId" class="ml-3">
                                                            {{ p.payerId }} — {{ p.carrierNames.join(', ') }} ({{ p.patientCount }})
                                                        </div>
                                                    </div>

                                                    <div v-if="ivCensus.results.headline.unusableMemberIdPatients" class="small text-warning mt-2">
                                                        <i class="fa fa-exclamation-circle mr-1"></i>
                                                        {{ ivCensus.results.headline.unusableMemberIdPatients }} member IDs are blank or placeholders.
                                                        These are skipped for free, but those patients stay unverified.
                                                    </div>

                                                    <!--
                                                      Size of what we could NOT measure, kept deliberately visible.
                                                      ⛔ Do NOT call this bucket harmless. It said "not a problem, just
                                                      unmeasured" until 2026-08-11, and on Mint prod it was hiding the
                                                      single largest anomaly in the book: 93 patients on DDS01, which the
                                                      full census classifies as an id no payer carries. Census-lite is
                                                      DB-only — an unpopulated capability table lands real problems here,
                                                      so the honest framing is "unknown", never "fine".
                                                    -->
                                                    <div v-if="ivCensusUnmeasured" class="small text-muted mt-2">
                                                        <i class="fa fa-question-circle mr-1"></i>
                                                        {{ ivCensusUnmeasured }} patients could not be classified (payer not yet synced, or a stale
                                                        capability record). This is <strong>unknown, not clear</strong> — real problems can hide here.
                                                        Run the full carrier census to classify them.
                                                    </div>

                                                    <div class="small text-muted mt-2">
                                                        <i class="fa fa-info-circle mr-1"></i>{{ ivCensus.results.disclaimer }}
                                                    </div>
                                                </b-alert>

                                                <div v-if="ivToggleBlocked" class="checklist-item iv-blocked-row">
                                                    <i class="fa fa-lock text-danger"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label text-danger">Enabling is blocked</div>
                                                        <small class="text-muted d-block">Clear the required items above first.</small>
                                                    </div>
                                                    <b-link class="checklist-action text-danger" @click="confirmEnableInsuranceVerificationAnyway">
                                                        Enable anyway <i class="fa fa-arrow-right"></i>
                                                    </b-link>
                                                </div>
                                            </template>
                                        </div>
                                    </div>

                                    <!-- Review Management -->
                                    <div class="feature-card">
                                        <div class="feature-card-header">
                                            <div class="feature-icon" style="background: #fff4e0;"><i class="fa fa-star" style="color: #f0a500;"></i></div>
                                            <div class="feature-info">
                                                <div class="feature-name">Review Management</div>
                                                <div class="feature-desc">Auto-send review requests after completed appointments, nudged rating page, private-feedback inbox &amp; Google reviews</div>
                                            </div>
                                            <b-form-checkbox v-model="siteFeatures.REVIEWS_PAID_FOR" switch @change="toggleFeature('REVIEWS_PAID_FOR', $event)" :disabled="featureSaving.REVIEWS_PAID_FOR"></b-form-checkbox>
                                        </div>
                                        <transition name="checklist-slide">
                                            <div v-if="siteFeatures.REVIEWS_PAID_FOR" class="feature-sub-option feature-checklist">
                                                <div v-for="item in reviewsChecklistItems" :key="item.key" class="checklist-item">
                                                    <i class="fa" :class="item.ok ? 'fa-check-circle text-success' : (item.warn ? 'fa-exclamation-circle text-warning' : 'fa-circle-o text-muted')"></i>
                                                    <div class="checklist-body">
                                                        <div class="checklist-label">
                                                            {{ item.label }}
                                                            <b-badge v-if="item.badge" variant="light" class="ml-1">{{ item.badge }}</b-badge>
                                                        </div>
                                                        <small v-if="item.detail" class="text-muted d-block">{{ item.detail }}</small>
                                                        <small v-if="item.note" class="text-danger d-block"><i class="fa fa-exclamation-circle mr-1"></i>{{ item.note }}</small>
                                                    </div>
                                                    <b-button v-if="item.action" size="sm" variant="outline-primary" class="checklist-action"
                                                              :disabled="item.action.disabled || reviewsSeed.seeding"
                                                              :title="item.action.tooltip"
                                                              @click="confirmSeedReviews">
                                                        <b-spinner v-if="reviewsSeed.seeding" small class="mr-1"></b-spinner>{{ item.action.label }}
                                                    </b-button>
                                                </div>

                                                <b-alert v-if="reviewsSeed.results" show :variant="reviewsSeedResultsVariant" class="mt-2 mb-0 seed-results">
                                                    <template v-if="reviewsSeed.results.skipped">
                                                        Reviews settings already configured &mdash; nothing to load.
                                                    </template>
                                                    <template v-else>
                                                        Reviews defaults loaded.
                                                        <span v-if="reviewsSeed.results.templatesSeeded"> Review-request templates copied.</span>
                                                        <span v-else> Message templates still need to be selected in Reviews &rarr; Settings.</span>
                                                    </template>
                                                    <div v-for="w in (reviewsSeed.results.warnings || [])" :key="w" class="small text-warning mt-1">
                                                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ w }}
                                                    </div>
                                                </b-alert>
                                                <b-alert v-if="reviewsSeed.error" show variant="danger" class="mt-2 mb-0 seed-results">{{ reviewsSeed.error }}</b-alert>
                                            </div>
                                        </transition>
                                    </div>

                                    <!-- Last saved indicator -->
                                    <div v-if="featuresLastSaved" class="text-center mt-3">
                                        <small class="text-muted"><i class="fa fa-check text-success mr-1"></i>Last updated: {{ featuresLastSaved }}</small>
                                    </div>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Accounts Tab — Super Admin: change a user's login email -->
                        <b-tab>
                            <template #title>
                                <i class="fa fa-key mr-1"></i> Accounts
                            </template>
                            <div class="tab-pane-content p-3">
                                <h6 class="mb-2">Change a user's login email</h6>
                                <p class="text-muted small mb-3">
                                    Switch which Google/Microsoft account a user signs in with (e.g. when Microsoft admin-consent is pending).
                                    Their tasks, role, and everything else stay intact — only the login changes. Recorded in the audit log.
                                </p>

                                <b-button variant="outline-primary" size="sm" block class="mb-3" @click="loadSiteUsersForEmailChange" :disabled="changeEmail.loadingUsers">
                                    <b-spinner v-if="changeEmail.loadingUsers" small class="mr-1"></b-spinner>
                                    <i v-else class="fa fa-users mr-2"></i>Load this site's users
                                </b-button>

                                <div v-if="changeEmail.users.length > 0">
                                    <b-form-group label="User" label-size="sm" class="mb-2">
                                        <b-form-select v-model="changeEmail.employeeID" size="sm" @change="onChangeEmailUserSelected">
                                            <b-form-select-option :value="null">-- Select a user --</b-form-select-option>
                                            <b-form-select-option v-for="u in changeEmail.users" :key="u.EmployeeID" :value="u.EmployeeID">
                                                {{ u.Name }} — {{ u.Username }}
                                            </b-form-select-option>
                                        </b-form-select>
                                    </b-form-group>

                                    <div v-if="changeEmail.employeeID">
                                        <b-form-group label="Current login email" label-size="sm" class="mb-2">
                                            <b-form-input :value="changeEmail.currentEmail" size="sm" disabled></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="New login email" label-size="sm" class="mb-2">
                                            <b-form-input v-model="changeEmail.newEmail" type="email" size="sm" placeholder="new.login@gmail.com"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Reason (optional)" label-size="sm" class="mb-2">
                                            <b-form-input v-model="changeEmail.reason" size="sm" placeholder="e.g. M365 admin-consent pending"></b-form-input>
                                        </b-form-group>

                                        <b-button variant="primary" size="sm" block @click="submitChangeLoginEmail" :disabled="changeEmail.saving || !changeEmail.newEmail">
                                            <b-spinner v-if="changeEmail.saving" small class="mr-1"></b-spinner>
                                            <i v-else class="fa fa-exchange mr-2"></i>Change login email
                                        </b-button>

                                        <b-alert :show="!!changeEmail.error" variant="danger" class="mt-2 mb-0 py-2">{{ changeEmail.error }}</b-alert>
                                        <b-alert :show="!!changeEmail.success" variant="success" class="mt-2 mb-0 py-2">{{ changeEmail.success }}</b-alert>
                                    </div>
                                </div>
                                <b-alert v-else :show="!!changeEmail.error" variant="danger" class="mt-2 mb-0 py-2">{{ changeEmail.error }}</b-alert>
                            </div>
                        </b-tab>
                    </b-tabs>
                </div>
            </div>
        </transition>
        <!-- Panel Overlay -->
        <div
            v-if="showSiteManagementPanel"
            class="panel-overlay"
            @click="closeSiteManagement"
        ></div>

        <!-- Purchase Number Modal -->
        <b-modal id="purchase-number-modal" title="Purchase Twilio Number" size="lg" hide-footer @hidden="resetPurchaseModal">
            <div v-if="purchaseModalBusy" class="text-center">
                <b-spinner label="Loading..."></b-spinner>
            </div>
            <div v-else>
                <b-alert variant="danger" :show="!!purchaseModalError" dismissible @dismissed="purchaseModalError=''">{{ purchaseModalError }}</b-alert>

                <p>Purchasing for Site ID: <strong>{{ siteIdForPurchase }}</strong></p>

                <div v-if="existingPhoneNumber">
                    <p class="text-info">This site already has a phone number: <strong>{{ existingPhoneNumber }}</strong>. Cannot purchase another.</p>
                </div>

                <b-form @submit.prevent="searchTwilioNumbers" v-if="!existingPhoneNumber">
                    <b-input-group prepend="Area Code (e.g., 415)" class="mb-3">
                        <b-form-input v-model="areaCodeInput" type="number" required></b-form-input>
                        <b-input-group-append>
                            <b-button type="submit" variant="primary">Search Available Numbers</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form>

                <div v-if="availableNumbers.length > 0 && !existingPhoneNumber">
                    <h5>Available Numbers:</h5>
                    <b-form-group label="Select a number to purchase:">
                        <b-form-radio-group
                            v-model="selectedNumberToPurchase"
                            :options="availableNumbers.map(num => ({ text: num, value: num }))"
                            name="radio-available-numbers"
                            stacked
                        ></b-form-radio-group>
                    </b-form-group>
                    <b-button 
                        variant="success" 
                        @click="purchaseTwilioNumber" 
                        :disabled="!selectedNumberToPurchase" 
                        class="mt-3">
                        Purchase Selected Number
                    </b-button>
                </div>
                <div v-else-if="!purchaseModalError && areaCodeInput && !existingPhoneNumber">
                    <p>No numbers found for area code {{ areaCodeInput }}.</p>
                </div>

            </div>
            <template #modal-footer="{ cancel }">
                 <b-button size="sm" variant="outline-secondary" @click="cancel()">
                    Cancel
                 </b-button>
            </template>
        </b-modal>

        <!-- Email Verification Modal -->
        <b-modal id="verify-email-modal" title="Verify Email Address for Sending" size="xl" hide-footer @hidden="resetEmailVerificationModal">
            <div v-if="verificationModalBusy" class="text-center">
                <b-spinner label="Processing..."></b-spinner>
            </div>
            <div v-else>
                <b-alert variant="danger" :show="!!verificationModalError" dismissible @dismissed="verificationModalError=''">{{ verificationModalError }}</b-alert>
                <b-alert variant="success" :show="verificationStatus === 'success'" dismissible>{{ verificationSuccessMessage }}</b-alert>

                <p>Setting up email verification for: <strong>{{ customerNameForEmailVerification }}</strong></p>
                <p class="text-muted mb-3">Site ID: {{ siteIdForEmailVerification }}</p>

                <div v-if="currentVerifiedEmails.length > 0" class="mb-3">
                    <h6>Currently Verified:</h6>
                    <ul class="list-unstyled">
                        <li v-for="email in currentVerifiedEmails" :key="email" class="text-success">
                            <i class="fa fa-check-circle"></i> {{ email }}
                        </li>
                    </ul>
                </div>

                <!-- DNS Records Display (when domain verification is set up) -->
                <div v-if="dnsRecordsToShow.length > 0" class="mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="mb-0"><i class="fa fa-cog"></i> DNS Records Required</h5>
                        <div>
                            <b-button
                                variant="outline-primary"
                                size="sm"
                                class="mr-2"
                                @click="showDnsEmailForm = !showDnsEmailForm">
                                <i class="fa fa-envelope mr-1"></i>
                                Email Records
                            </b-button>
                            <b-button
                                variant="outline-success"
                                size="sm"
                                @click="checkDomainVerification"
                                :disabled="checkingVerification">
                                <i class="fa fa-sync" :class="{ 'fa-spin': checkingVerification }"></i>
                                {{ checkingVerification ? 'Checking...' : 'Check Verification Status' }}
                            </b-button>
                        </div>
                    </div>

                    <!-- Email DNS Records Form -->
                    <div v-if="showDnsEmailForm" class="dns-email-form mb-3 p-3 rounded">
                        <label class="small font-weight-bold mb-1">Sends to the site's Admins. Also send to (optional, up to 5, comma-separated):</label>
                        <div class="d-flex">
                            <b-form-input
                                v-model="dnsEmailRecipients"
                                placeholder="e.g., admin@clinic.com, it@clinic.com"
                                size="sm"
                                class="mr-2"
                                :disabled="sendingDnsEmail"
                            ></b-form-input>
                            <b-button
                                variant="primary"
                                size="sm"
                                @click="sendDnsRecordsEmail"
                                :disabled="sendingDnsEmail || !domainId"
                                style="white-space: nowrap;">
                                <b-spinner v-if="sendingDnsEmail" small class="mr-1"></b-spinner>
                                <i v-else class="fa fa-paper-plane mr-1"></i>
                                Send
                            </b-button>
                        </div>
                        <small class="text-muted mt-1 d-block">Email will be sent from nilay@teamio.app</small>
                    </div>
                    
                    <b-alert variant="info" show>
                        <strong><i class="fa fa-info-circle"></i> Instructions for Customer:</strong><br>
                        Please add the following DNS records to your domain <strong>{{ domainBeingVerified }}</strong>:
                    </b-alert>

                    <div class="dns-records-container">
                        <div v-for="(record, index) in dnsRecordsToShow" :key="index" class="dns-record-card mb-3">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center">
                                    <span class="font-weight-bold">{{ record.type }} Record {{ index + 1 }}</span>
                                    <small class="text-muted">{{ record.description }}</small>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <label class="small font-weight-bold">Host/Name:</label>
                                            <div class="input-group">
                                                <input type="text" class="form-control" :value="record.host" readonly>
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="copyToClipboard(record.host)">
                                                        <i class="fa fa-copy"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <label class="small font-weight-bold">Value/Points to:</label>
                                            <div class="input-group">
                                                <input type="text" class="form-control" :value="record.value" readonly>
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="copyToClipboard(record.value)">
                                                        <i class="fa fa-copy"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="dns-important-notes p-3 rounded mb-3">
                        <strong><i class="fa fa-clock-o mr-1"></i> Important Notes:</strong>
                        <ul class="mb-0 mt-2">
                            <li>DNS changes can take up to 48 hours to propagate globally</li>
                            <li>Contact your domain provider (GoDaddy, Namecheap, etc.) if you need help adding DNS records</li>
                            <li>Click "Check Verification Status" above after adding the records</li>
                        </ul>
                    </div>

                    <div class="d-flex justify-content-between mt-3">
                        <b-button variant="outline-secondary" @click="resetForNewVerification">
                            <i class="fa fa-arrow-left"></i> Start Over
                        </b-button>
                        <b-button variant="outline-secondary" @click="$bvModal.hide('verify-email-modal')">
                            Close
                        </b-button>
                    </div>
                </div>

                <!-- Single Sender Verification Result -->
                <div v-else-if="showSingleSenderInstructions" class="mb-4">
                    <b-alert variant="success" show>
                        <h5><i class="fa fa-envelope"></i> Verification Email Sent!</h5>
                        <p class="mb-2">A verification email has been sent to <strong>{{ emailBeingVerified }}</strong></p>
                        <p class="mb-0">Please ask the customer to check their email and click the verification link.</p>
                    </b-alert>
                    
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>Verification Status:</span>
                        <b-button 
                            variant="outline-success" 
                            size="sm" 
                            @click="checkSenderVerification"
                            :disabled="checkingVerification">
                            <i class="fa fa-sync" :class="{ 'fa-spin': checkingVerification }"></i>
                            {{ checkingVerification ? 'Checking...' : 'Check Verification Status' }}
                        </b-button>
                    </div>

                    <div class="d-flex justify-content-between mt-3">
                        <b-button variant="outline-secondary" @click="resetForNewVerification">
                            <i class="fa fa-arrow-left"></i> Start Over
                        </b-button>
                        <b-button variant="outline-secondary" @click="$bvModal.hide('verify-email-modal')">
                            Close
                        </b-button>
                    </div>
                </div>

                <!-- Initial Verification Setup Form -->
                <div v-else>
                    <!-- Verification Type Selection -->
                    <b-form-group label="Choose Verification Method:" class="mb-3">
                        <b-form-radio-group v-model="verificationType" name="verification-type" stacked>
                            <b-form-radio value="domain" class="mb-2">
                                <strong>Domain Authentication (Recommended)</strong>
                                <br><small class="text-muted">Verify entire domain (e.g., clinicname.com) - allows any email from that domain</small>
                            </b-form-radio>
                            <b-form-radio value="single_sender">
                                <strong>Single Sender Verification</strong>
                                <br><small class="text-muted">For personal emails (Gmail, Yahoo, etc.) - verifies one specific address</small>
                            </b-form-radio>
                        </b-form-radio-group>
                    </b-form-group>

                    <!-- Domain Authentication Form -->
                    <div v-if="verificationType === 'domain'" class="mb-3">
                        <b-input-group prepend="Domain">
                            <b-form-input 
                                v-model="domainToVerify" 
                                placeholder="clinicname.com"
                                @input="domainToVerify = domainToVerify.toLowerCase()"
                            ></b-form-input>
                        </b-input-group>
                        <small class="text-muted mt-1 d-block">
                            <i class="fa fa-info-circle"></i> 
                            Customer will need to add DNS records to their domain. This allows any email address from this domain to send emails.
                        </small>
                        <b-alert variant="info" show class="mt-2">
                            <strong>Benefits:</strong> Professional appearance, better deliverability, supports multiple email addresses from the same domain.
                        </b-alert>
                    </div>

                    <!-- Single Sender Form -->
                    <div v-if="verificationType === 'single_sender'" class="mb-3">
                        <b-input-group prepend="Email Address">
                            <b-form-input 
                                v-model="emailToVerify" 
                                type="email"
                                placeholder="doctor@gmail.com"
                            ></b-form-input>
                        </b-input-group>
                        <small class="text-muted mt-1 d-block">
                            <i class="fa fa-info-circle"></i> 
                            Customer will receive a verification email and must click the link to confirm.
                        </small>
                        <b-alert variant="warning" show class="mt-2">
                            <strong>Note:</strong> Single sender verification may have lower deliverability rates and appear less professional. 
                            Consider recommending a business domain for better results.
                        </b-alert>
                    </div>

                    <!-- Action Buttons -->
                    <div class="d-flex justify-content-between">
                        <b-button 
                            variant="primary" 
                            @click="initiateEmailVerification" 
                            :disabled="!canInitiateVerification">
                            {{ verificationType === 'domain' ? 'Set Up Domain Authentication' : 'Send Verification Email' }}
                        </b-button>
                        <b-button variant="outline-secondary" @click="$bvModal.hide('verify-email-modal')">
                            Cancel
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- A2P Registration Wizard Modal -->
        <b-modal
            id="a2p-registration-wizard-modal"
            size="xl"
            :title="'A2P Registration - ' + (a2pWizardSiteName || 'Site')"
            hide-footer
            no-close-on-backdrop
            scrollable
            @hidden="resetA2PWizard"
        >
            <A2PRegistrationWizard
                v-if="showA2PWizard"
                :targetSiteID="a2pWizardSiteID"
                :targetSiteName="a2pWizardSiteName"
                :targetSiteCountry="a2pWizardSiteCountry"
                @close="closeA2PWizard"
            />
        </b-modal>

        <!-- A2P Details Modal -->
        <b-modal id="a2p-details-modal" size="xl" title="A2P Registration Details" hide-footer @hidden="resetA2PDetails">
            <div v-if="selectedA2PRegistration">
                <!-- Site Information -->
                <b-card class="mb-3">
                    <b-card-header>
                        <h5 class="mb-0"><i class="fa fa-building text-primary mr-2"></i>Site Information</h5>
                    </b-card-header>
                    <b-card-body>
                        <b-row>
                            <b-col md="6">
                                <p><strong>Business Name:</strong> {{ selectedA2PRegistration.BusinessName }}</p>
                                <p><strong>Site ID:</strong> {{ selectedA2PRegistration.SiteID }}</p>
                                <p><strong>Customer Name:</strong> {{ selectedA2PRegistration.CustomerName }}</p>
                            </b-col>
                            <b-col md="6">
                                <p><strong>Status:</strong> 
                                    <b-badge :variant="getStatusVariant(selectedA2PRegistration.Status)">
                                        {{ selectedA2PRegistration.Status || 'Unknown' }}
                                    </b-badge>
                                </p>
                                <p><strong>Progress:</strong> {{ selectedA2PRegistration.ProgressPercentage || 0 }}%</p>
                                <p><strong>Last Updated:</strong> {{ formatDate(selectedA2PRegistration.UpdatedAt) }}</p>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-card>

                <!-- Twilio Resources -->
                <b-card class="mb-3">
                    <b-card-header>
                        <h5 class="mb-0"><i class="fa fa-cloud text-info mr-2"></i>Twilio Resources</h5>
                    </b-card-header>
                    <b-card-body>
                        <b-table 
                            :items="twilioResources" 
                            :fields="twilioResourceFields"
                            small
                            striped
                        >
                            <template #cell(status)="row">
                                <b-badge :variant="getResourceStatusVariant(row.item.status)">
                                    {{ row.item.status }}
                                </b-badge>
                            </template>
                        </b-table>
                    </b-card-body>
                </b-card>

                <!-- Status Details -->
                <b-card class="mb-3" v-if="selectedA2PRegistration.StatusDetails">
                    <b-card-header>
                        <h5 class="mb-0"><i class="fa fa-info-circle text-warning mr-2"></i>Status Details</h5>
                    </b-card-header>
                    <b-card-body>
                        <pre class="bg-light p-3 rounded">{{ formatStatusDetails(selectedA2PRegistration.StatusDetails) }}</pre>
                    </b-card-body>
                </b-card>

                <!-- Actions -->
                <div class="d-flex justify-content-between">
                    <div>
                        <b-button variant="primary" @click="retryA2PRegistration" v-if="canRetryA2P">
                            <i class="fa fa-refresh"></i> Retry Registration
                        </b-button>
                        <b-button variant="info" @click="viewTwilioConsole" class="ml-2">
                            <i class="fa fa-external-link"></i> View in Twilio Console
                        </b-button>
                    </div>
                    <b-button variant="outline-secondary" @click="$bvModal.hide('a2p-details-modal')">
                        Close
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Twilio Site Detail Modal -->
        <b-modal id="twilio-site-detail-modal" size="lg" ok-only
            :title="twilioSelectedSite ? twilioSelectedSite.CustomerName + ' — Usage Detail' : 'Site Detail'">
            <div v-if="twilioSelectedSite">
                <p class="text-muted mb-2">
                    <strong>{{ twilioSelectedSite.CustomerName }}</strong>
                    &nbsp;|&nbsp; Subaccount: <code>{{ twilioSelectedSite.SubaccountSID }}</code>
                    &nbsp;|&nbsp; Total: <strong>${{ fmtPrice(twilioSelectedSite.totalPrice) }}</strong>
                </p>
                <b-alert show variant="info" class="py-1 small">
                    These charges are for this subaccount only, not the master account.
                </b-alert>

                <!-- Hierarchical tree breakdown (if available) -->
                <table v-if="twilioSelectedSite.tree && twilioSelectedSite.tree.items" class="table table-sm table-striped">
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th class="text-right" style="width:80px">Count</th>
                            <th class="text-right" style="width:100px">Price ($)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="item in twilioSelectedSite.tree.items">
                            <!-- Top-level row -->
                            <tr :key="item.category"
                                :class="['twilio-tree-row', twilioHasVisibleChildren(item) ? 'font-weight-bold twilio-tree-row--clickable' : '']"
                                @click="twilioHasVisibleChildren(item) && toggleTwilioCategory(item.category)">
                                <td>
                                    <i v-if="twilioHasVisibleChildren(item)"
                                       :class="['fa', isTwilioCategoryExpanded(item.category) ? 'fa-caret-down' : 'fa-caret-right', 'twilio-tree-caret']"></i>
                                    <span v-else class="twilio-tree-caret-spacer"></span>
                                    {{ item.label }}
                                    <button v-if="item.category === 'phonenumbers'"
                                            class="btn btn-sm btn-link twilio-view-numbers-btn"
                                            @click.stop="viewTwilioSiteNumbers()">
                                        <i class="fa fa-list"></i> View list
                                    </button>
                                </td>
                                <td class="text-right">{{ item.count || '' }}</td>
                                <td class="text-right">${{ fmtPrice(item.price) }}</td>
                            </tr>
                            <!-- Children (level 1) — only when parent is expanded, redundant children skipped -->
                            <template v-if="isTwilioCategoryExpanded(item.category)">
                                <template v-for="child in twilioVisibleChildren(item)">
                                    <tr :key="child.category" class="text-muted">
                                        <td style="padding-left: 32px">
                                            <small>{{ child.label }}</small>
                                        </td>
                                        <td class="text-right"><small>{{ child.count || '' }}</small></td>
                                        <td class="text-right"><small>${{ fmtPrice(child.price) }}</small></td>
                                    </tr>
                                    <!-- Grandchildren (level 2) — same redundancy filter applied at the child level -->
                                    <tr v-for="gc in twilioVisibleChildren(child)" :key="gc.category" class="text-muted">
                                        <td style="padding-left: 54px">
                                            <small><em>{{ gc.label }}</em></small>
                                        </td>
                                        <td class="text-right"><small>{{ gc.count || '' }}</small></td>
                                        <td class="text-right"><small>${{ fmtPrice(gc.price) }}</small></td>
                                    </tr>
                                </template>
                            </template>
                        </template>
                    </tbody>
                    <tfoot>
                        <tr class="font-weight-bold border-top">
                            <td>Total</td>
                            <td></td>
                            <td class="text-right">${{ fmtPrice(twilioSelectedSite.totalPrice) }}</td>
                        </tr>
                    </tfoot>
                </table>

                <!-- Fallback flat table if tree not available -->
                <b-table v-else
                    :items="twilioSiteDetailRows"
                    :fields="twilioSiteDetailFields"
                    striped small responsive
                    sort-by="price"
                    :sort-desc="true"
                >
                    <template #cell(price)="row">
                        ${{ fmtPrice(row.item.price) }}
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Twilio Phone Numbers Sub-Modal (drilldown from "Phone Numbers" row) -->
        <b-modal id="twilio-site-numbers-modal" size="xl" ok-only ok-title="Close"
            :title="twilioSelectedSite ? twilioSelectedSite.CustomerName + ' — Phone Numbers' : 'Phone Numbers'">
            <div v-if="twilioSelectedSite">
                <p class="text-muted mb-3 small">
                    Subaccount: <code>{{ twilioSelectedSite.SubaccountSID }}</code>
                    <span v-if="twilioNumbersFetchedForSid && !twilioNumbersLoading">
                        &nbsp;|&nbsp; <strong>{{ twilioNumbersList.length }}</strong> number{{ twilioNumbersList.length === 1 ? '' : 's' }}
                    </span>
                </p>
                <div v-if="twilioNumbersLoading" class="text-center text-muted py-4">
                    <i class="fa fa-spinner fa-spin mr-2"></i> Loading phone numbers&hellip;
                </div>
                <b-alert v-else-if="twilioNumbersError" show variant="danger" class="small">
                    {{ twilioNumbersError }}
                </b-alert>
                <div v-else-if="!twilioNumbersList.length" class="text-muted small py-3">
                    No phone numbers found on this subaccount.
                </div>
                <table v-else class="table table-sm table-striped">
                    <thead>
                        <tr>
                            <th>Phone Number</th>
                            <th>Friendly Name</th>
                            <th>Capabilities</th>
                            <th>Origin</th>
                            <th>Created</th>
                            <th>SMS Webhook</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="n in twilioNumbersList" :key="n.sid">
                            <td><code>{{ n.phoneNumber }}</code></td>
                            <td>{{ n.friendlyName || '—' }}</td>
                            <td><small>{{ formatTwilioNumberCapabilities(n.capabilities) }}</small></td>
                            <td><small>{{ n.origin || '—' }}</small></td>
                            <td><small>{{ n.dateCreated ? n.dateCreated.substring(0, 10) : '—' }}</small></td>
                            <td>
                                <small v-if="n.smsUrl" :title="n.smsUrl" class="text-muted twilio-webhook-cell">{{ n.smsUrl }}</small>
                                <small v-else class="text-muted">—</small>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </b-modal>

        <!-- PDF Import to Forms Modal -->
        <b-modal id="pdf-import-modal" size="xl" title="Import PDF to Forms"
                 v-model="showPdfImport" @hidden="resetPdfImport" hide-footer scrollable>
            
            <!-- Step 1: Site Selection -->
            <div v-if="pdfImport.step === 1" class="pdf-import-step">
                <h5 class="mb-3">Step 1: Select Target Site</h5>
                <p class="text-muted mb-3">
                    Choose the site where you want to import the forms from the PDF files.
                </p>
                <label class="fw-semi-bold">Target Site:</label>
                <b-form-select 
                    v-model="pdfImport.targetSiteID" 
                    :options="siteOptions"
                    class="mb-3"
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled>-- Select Target Site --</b-form-select-option>
                    </template>
                </b-form-select>
                <div class="d-flex justify-content-end">
                    <b-button @click="pdfImport.step = 2" :disabled="!pdfImport.targetSiteID" 
                              variant="primary">
                        Next: Upload PDFs <i class="fa fa-arrow-right ml-1"></i>
                    </b-button>
                </div>
            </div>
            
            <!-- Step 2: File Upload -->
            <div v-if="pdfImport.step === 2" class="pdf-import-step">
                <h5 class="mb-3">Step 2: Upload PDF Files</h5>
                <p class="text-muted mb-3">
                    Select one or more PDF files to convert into TeamIO forms. 
                    Fillable PDF forms and scanned PDFs (with OCR) are supported.
                </p>
                
                <b-form-file 
                    v-model="pdfImport.files" 
                    multiple 
                    accept=".pdf"
                    :state="pdfImport.files && pdfImport.files.length > 0"
                    placeholder="Choose PDF files or drag and drop here"
                    drop-placeholder="Drop PDF files here"
                    class="mb-3"
                />
                
                <small class="text-muted d-block mb-3">
                    <i class="fa fa-info-circle"></i>
                    Supported: Fillable PDF forms and scanned PDFs (OCR will be applied).
                    Max 10MB per file, 50MB total. Multiple pages will be combined into one form.
                </small>
                
                <!-- File list with sizes -->
                <div v-if="pdfImport.files && pdfImport.files.length > 0" class="mt-3 mb-3">
                    <h6>Selected Files:</h6>
                    <ul class="list-unstyled">
                        <li v-for="(file, index) in pdfImport.files" :key="index" class="mb-2">
                            <i class="fa fa-file-pdf text-danger mr-2"></i>
                            {{ file.name }} ({{ formatFileSize(file.size) }})
                            <span v-if="file.size > 10 * 1024 * 1024" class="text-danger ml-2">
                                <i class="fa fa-exclamation-triangle"></i> Exceeds 10MB limit
                            </span>
                        </li>
                    </ul>
                    <p class="text-muted">
                        <strong>Total:</strong> {{ formatFileSize(totalPdfFileSize) }} / 50MB
                        <span v-if="totalPdfFileSize > 50 * 1024 * 1024" class="text-danger ml-2">
                            <i class="fa fa-exclamation-triangle"></i> Exceeds batch limit
                        </span>
                    </p>
                </div>
                
                <div class="d-flex justify-content-between">
                    <b-button @click="pdfImport.step = 1" variant="secondary">
                        <i class="fa fa-arrow-left mr-1"></i> Back
                    </b-button>
                    <b-button @click="processPdfUpload" :disabled="!canProceedToProcessing" 
                              variant="primary">
                        Process PDFs <i class="fa fa-arrow-right ml-1"></i>
                    </b-button>
                </div>
            </div>
            
            <!-- Step 3: Processing -->
            <div v-if="pdfImport.step === 3" class="pdf-import-step">
                <!-- Error State -->
                <div v-if="pdfImport.error">
                    <h5 class="mb-3 text-danger"><i class="fa fa-exclamation-triangle mr-2"></i>Import Failed</h5>
                    <b-alert variant="danger" show>
                        {{ pdfImport.error }}
                    </b-alert>
                    <div v-if="pdfImport.processingDetails.length > 0" class="mt-3 mb-3">
                        <h6>Details:</h6>
                        <div v-for="(detail, index) in pdfImport.processingDetails" :key="index"
                             class="processing-item mb-2">
                            <i :class="getProcessingIcon(detail.status)" class="mr-2"></i>
                            <strong>{{ detail.fileName }}:</strong> {{ detail.message }}
                        </div>
                    </div>
                    <div class="d-flex justify-content-end">
                        <b-button @click="pdfImport.step = 2; pdfImport.error = null" variant="primary">
                            <i class="fa fa-arrow-left mr-1"></i> Try Again
                        </b-button>
                    </div>
                </div>

                <!-- Normal Processing State -->
                <div v-else>
                    <h5 class="mb-3">Processing PDFs...</h5>
                    <b-progress :value="pdfImport.progress" :max="100" :animated="pdfImport.processing" class="mb-3" />
                    <p class="text-center text-muted">{{ pdfImport.statusMessage }}</p>

                    <div v-if="pdfImport.processingDetails.length > 0" class="mt-3">
                        <h6>Processing Details:</h6>
                        <div v-for="(detail, index) in pdfImport.processingDetails" :key="index"
                             class="processing-item mb-2">
                            <i :class="getProcessingIcon(detail.status)" class="mr-2"></i>
                            <strong>{{ detail.fileName }}:</strong> {{ detail.message }}
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Step 4: Preview & Edit -->
            <div v-if="pdfImport.step === 4" class="pdf-import-step">
                <h5 class="mb-3">Step 3: Review & Edit Extracted Forms</h5>
                <p class="text-muted mb-3">
                    Review the extracted forms below. You can edit form names, field types, 
                    and other settings before saving.
                </p>
                
                <div v-for="(form, index) in pdfImport.results" :key="index" 
                     class="extracted-form-preview mb-4">
                    <b-card>
                        <b-card-header>
                            <b-row>
                                <b-col md="8">
                                    <b-form-input 
                                        v-model="form.formName" 
                                        placeholder="Enter form name"
                                        class="mb-2"
                                    />
                                    <b-form-select
                                        v-model="form.formType"
                                        :options="[
                                            { value: 'NP_FORM', text: 'New Patient Form' },
                                            { value: 'MED_HX_UPDATE', text: 'Medical History Update' },
                                            { value: 'OTHER', text: 'Other' }
                                        ]"
                                        placeholder="Select form type"
                                    />
                                </b-col>
                                <b-col md="4" class="text-right">
                                    <b-badge :variant="form.status === 'success' ? 'success' : 'danger'">
                                        {{ form.status }}
                                    </b-badge>
                                </b-col>
                            </b-row>
                        </b-card-header>
                        
                        <b-card-body>
                            <div v-if="form.status === 'success'">
                                <p><strong>Source:</strong> {{ form.fileName }}</p>
                                <p><strong>Sections:</strong> {{ form.sectionCount }}</p>
                                <p><strong>Fields:</strong> {{ form.fieldCount }}</p>
                                
                                <!-- Expandable field list -->
                                <b-button v-b-toggle="`pdf-fields-${index}`" variant="link" size="sm" class="p-0">
                                    <i class="fa fa-chevron-down"></i> View/Edit Fields
                                </b-button>
                                <b-collapse :id="`pdf-fields-${index}`" class="mt-2">
                                    <!-- Wiki Control Display -->
                                    <div v-if="form.fields && form.fields.length > 0 && form.fields[0].type === 'wiki'" class="mb-3">
                                        <b-alert variant="info" show>
                                            <strong><i class="fa fa-file-text"></i> Wiki Control</strong><br>
                                            This form contains a Wiki control with the PDF content. 
                                            You can edit the content after saving the form.
                                        </b-alert>
                                        <div v-if="form.fields[0].blockCount" class="text-muted small">
                                            <i class="fa fa-info-circle"></i> Contains {{ form.fields[0].blockCount }} content blocks
                                        </div>
                                    </div>
                                    
                                    <!-- Regular Fields Table -->
                                    <div v-else class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                                        <table class="table table-sm table-bordered">
                                            <thead class="thead-light">
                                                <tr>
                                                    <th>Field Name</th>
                                                    <th>Label</th>
                                                    <th>Type</th>
                                                    <th>Required</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="field in form.fields" :key="field.uniqueId">
                                                    <td>
                                                        <b-form-input v-model="field.name" size="sm" />
                                                    </td>
                                                    <td>
                                                        <b-form-input v-model="field.label" size="sm" />
                                                    </td>
                                                    <td>
                                                        <b-form-select 
                                                            v-model="field.type" 
                                                            :options="fieldTypeOptions"
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td class="text-center">
                                                        <b-form-checkbox v-model="field.required" />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </b-collapse>
                            </div>
                            <div v-else>
                                <b-alert variant="danger" show>
                                    <strong>Error:</strong> {{ form.error }}
                                </b-alert>
                            </div>
                        </b-card-body>
                    </b-card>
                </div>
                
                <div class="d-flex justify-content-between mt-3">
                    <b-button @click="pdfImport.step = 2" variant="secondary">
                        <i class="fa fa-arrow-left mr-1"></i> Back
                    </b-button>
                    <b-button @click="saveImportedForms" :disabled="!canSavePdfForms" 
                              variant="success">
                        <i class="fa fa-save mr-1"></i> Save {{ successfulPdfFormsCount }} Form(s)
                    </b-button>
                </div>
            </div>
            
            <!-- Step 5: Success -->
            <div v-if="pdfImport.step === 5" class="pdf-import-step">
                <h5 class="mb-3">Forms Imported Successfully!</h5>
                <b-alert variant="success" show>
                    Successfully imported {{ pdfImport.savedForms.length }} form(s).
                </b-alert>
                <div v-for="form in pdfImport.savedForms" :key="form.formID" class="mb-2">
                    <router-link :to="`/app/forms/edit-form/${form.formID}`" target="_blank">
                        <i class="fa fa-external-link"></i> Edit {{ form.formName }}
                    </router-link>
                </div>
                <div class="d-flex justify-content-end mt-3">
                    <b-button @click="resetPdfImport" variant="primary">
                        Import More Forms
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
/* eslint-disable */

import Vue from "vue";
import Multiselect from "vue-multiselect";
import Widget from "@/components/Widget/Widget";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import vSelect from 'vue-select';
import mixin from '../../mixin.js';
import A2PRegistrationWizard from '@/pages/MyAccount/A2P/A2PRegistrationWizard.vue';
import NudgeManager from './NudgeManager.vue';
import WhatsAppOnboarding from './components/WhatsAppOnboarding.vue';
import MessagingSafety from './components/MessagingSafety.vue';
import CustomerHealth from './components/CustomerHealth.vue';
import OutreachEmailEditor from './components/OutreachEmailEditor.vue';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import { assessLogoUrl, logoBlocksSave } from '@/utils/logoUrlCheck';
import { extractApiError } from '@/utils/apiError';
import { COST_PER_SEGMENT_USD, TWILIO_BASE_COST_PER_SEGMENT_USD } from '@/utils/smsSegments';
// Forms Settings onboarding (WP3): the "New patient & medical history messages" checklist row + its dry-run preview. The row shows only
// while the Forms Settings release gate is open for this Super Admin (the gate is read through gate.js only, with the userKey).
import { formsSettingsUiOn } from '@/utils/formsSettings/gate';
import {
    FORM_MESSAGES_ACTION, formMessagesReadinessOf, summarizeFormMessages, describeFormMessagesPreview, describeFormMessagesResult,
} from '@/utils/formMessagesSeed';
// Release stage of the plain reminder message boxes: once every office has them ('default' / 'retired') "Load
// reminder templates" stops copying the template host's wiki pages (reminderWikiCopyRetired below).
import { STAGE } from '@/utils/followups/uiVersion';
// OD imaging category per FormType for seeded / copied forms — pure, import-free, executed by
// cypress/node-tests/superadmin-forms-imaging-map.test.js.
import {
    FORM_IMAGING_TYPES, FORM_IMAGING_TYPE_LABELS, IMAGING_FIELD_LABELS, emptyCategoryMap, isCategoryMapComplete,
    mergeImagingMap, requiredTypesForSelection, categoryName, describeImagingValue, perFormPdfCategoriesComplete,
} from '@/utils/formImagingMap';
// Bulk Form Copy sends ONE form per request (API Gateway's 29s cap) — see the file header.
import { runBulkFormCopy } from '@/utils/bulkFormCopyRunner';
// Copy Form Section: a 502/503/504 or network error means "no answer" — the copy may still have run.
import { isNoAnswer } from '@/utils/bulkFormCopyRunner';
// Copy Form Section: section previews + report wording, shared with the builder's SectionCopyModal.
import { describeSections, describeReportEntry } from '@/pages/Forms/libraries/section-copy-describe';
import { CONTROLS } from '@/pages/Forms/configs/controls';
import FormImagingCategoryMap from './components/FormImagingCategoryMap.vue';
import OdApiSettings from './components/OdApiSettings.vue';
// Insurance Verification usage rollup — the band formatter and per-site count helpers are the SAME
// pure functions the practice-facing Credits tab uses, so the band a practice audits and the band
// SuperAdmin sees can't drift.
import { usageMeterView, ivRollupTriggerCount, ivRollupUnknownCount, isValidRollupPayload } from '@/pages/InsuranceVerification/components/constants';
// Switches the office's exe applies itself (Schedule View live updates, Practice Pulse) — the ones
// that need "Refresh Exe Settings" after a change. Same list as teamio-exe + teamio-admin.
import {
    EXE_SETTINGS_MIN_VERSION, needsExeRefresh, addPendingExeSetting, describePendingExeSettings,
} from '@/utils/exeLiveSettings';
// Schedule - Chart Audit needs the office's Teamio app at 3.0.61+: the switch cannot be turned ON below it.
import { canSwitchOn, SCHEDULE_ACCESS_SETTING } from '@/utils/scheduleView/access';
// One-click Practice Pulse enablement — reads teamio-admin's per-step answer (flag, board, exe).
// Import-free, executed by cypress/node-tests/practice-pulse-init-superadmin.test.js.
import {
    PRACTICE_PULSE_INIT_ACTION, describeInitializeSteps, initializeNeedsExeReminder, initializeFlagSaved,
} from '@/utils/practicePulseInit';

// ── Per-site Open Dental table overrides for the on-prem exe (EXE_OD_TABLE_OVERRIDES) ──────────
// ⛔ These rules MUST match teamio-admin/lib/exe-table-overrides.js (which is itself kept in sync
// with teamio-exe/main-process/app/ignored-tables.js). The backend re-validates every save — this
// copy only exists so the operator sees the bad name before clicking Save. Guarded by
// cypress/node-tests/exe-table-overrides-superadmin.test.js.
const EXE_TABLE_NAME_RE = /^[a-z0-9_]+$/;
const EXE_TABLE_NAME_MAX_LENGTH = 64;
const EXE_TABLE_LIST_MAX = 50;
// Keep in sync with teamio-admin/lib/exe-table-overrides.js MIN_EXE_VERSION. It must sit ABOVE every
// build already in the field: the exe's version is typed at build time, not tracked in git, so a
// number below a shipped build makes this warning claim an exe supports the setting when it does not
// (3.0.52 did — dev was already on 3.0.54). The backend's verdict wins; this is the fallback.
const EXE_MIN_VERSION = '3.0.55';
// Never ignorable: ignoring one of these silently stops a practice's mirror. Un-ignoring is fine.
const EXE_PROTECTED_TABLES = Object.freeze([
    'teamio', 'patient', 'appointment', 'procedurelog', 'claimproc', 'adjustment', 'claim',
    'paysplit', 'payment', 'commlog', 'claimpayment', 'patfield', 'recall', 'insplan', 'inssub',
    'patplan', 'carrier', 'provider', 'operatory', 'schedule', 'scheduleop', 'definition',
    'procedurecode', 'treatplan', 'proctp', 'fee', 'feesched', 'employee', 'userod', 'clinic',
    'benefit', 'insverify',
]);

/** Comma/whitespace-separated text → normalised, de-duplicated table names (blanks dropped). */
function exeTableNamesFromText(text) {
    const seen = new Set();
    const out = [];
    String(text || '').split(/[\s,]+/).forEach((raw) => {
        const name = raw.trim().toLowerCase();
        if (!name || seen.has(name)) return;
        seen.add(name);
        out.push(name);
    });
    return out;
}

/** Names the backend would REFUSE, with the reason — shown inline so Save is never a surprise. */
function exeTableNameProblems(names, listKey) {
    const problems = [];
    names.forEach((name) => {
        if (name.length > EXE_TABLE_NAME_MAX_LENGTH) {
            problems.push(`"${name.slice(0, 24)}…" is longer than ${EXE_TABLE_NAME_MAX_LENGTH} characters`);
        } else if (!EXE_TABLE_NAME_RE.test(name)) {
            problems.push(`"${name}" — lowercase letters, digits and underscores only`);
        } else if (listKey === 'ignore' && EXE_PROTECTED_TABLES.indexOf(name) !== -1) {
            problems.push(`"${name}" is a protected table and can never be ignored`);
        }
    });
    if (names.length > EXE_TABLE_LIST_MAX) {
        problems.push(`${names.length} tables listed — the limit is ${EXE_TABLE_LIST_MAX}`);
    }
    return problems;
}

/** Order-insensitive set comparison — "in sync" must not depend on the order the exe reports. */
function exeTableListsEqual(a, b) {
    const left = Array.isArray(a) ? [...a].sort() : [];
    const right = Array.isArray(b) ? [...b].sort() : [];
    return left.length === right.length && left.every((v, i) => v === right[i]);
}

// ── Restart App / Set Debug Mode (System tab, 2026-10-02) ──────────────────────────────────────
// ⛔ Before exe 3.0.60 these commands hang an office exe that runs as a Windows service. The rule lives
// in ONE place — teamio-admin/lib/exe-restart.js — and this page only shows its verdict: the buttons
// are enabled by 'Get Exe Restart Status' (restartAllowed / debugModeAllowed), never by a version
// compare here. Guarded by cypress/node-tests/exe-restart-superadmin.test.js.
const EXE_RESTART_STATUS_ACTION = 'Get Exe Restart Status';
const EXE_RESTART_COMMANDS = Object.freeze({
    restart: Object.freeze({
        action: 'Restart App',
        title: 'Restart the Teamio application',
        okTitle: 'Yes, restart it',
        confirm: (practice, version) => `Restart the Teamio application at ${practice} (exe ${version || 'unknown'})? `
            + `It finishes what it is doing, exits, and its Windows service starts it again — normally within a minute. `
            + `Nothing syncs with Open Dental while it restarts.`,
    }),
    debug: Object.freeze({
        action: 'Set Debug Mode to Debug',
        title: 'Enable Debug Mode',
        okTitle: 'Yes, enable debug logging',
        confirm: (practice, version) => `Switch the Teamio application at ${practice} (exe ${version || 'unknown'}) to DEBUG logging? `
            + `This restarts the application: its Windows service starts it again, normally within a minute.`,
    }),
    default: Object.freeze({
        action: 'Set Debug Mode to Default',
        title: 'Reset Debug Mode',
        okTitle: 'Yes, back to default logging',
        confirm: (practice, version) => `Switch the Teamio application at ${practice} (exe ${version || 'unknown'}) back to DEFAULT logging? `
            + `This restarts the application: its Windows service starts it again, normally within a minute.`,
    }),
});
// After a send, re-read the status line until the exe answers — the settings-row route can take up to
// 10 minutes, so watch a little longer than that.
const EXE_RESTART_POLL_EVERY_MS = 20000;
const EXE_RESTART_POLL_FOR_MS = 12 * 60 * 1000;
// The exe's refusal codes (EXE_RESTART_STATUS.reason), in words.
const EXE_RESTART_REFUSAL_REASONS = Object.freeze({
    stale: 'the request was too old when the exe saw it',
    appexit: 'the Windows service is not set to restart the app when it exits',
});

/** A ms epoch → the viewer's local time ("8:20 PM"), with the date when it is not today. '' if unknown. */
function exeRestartClock(ms) {
    if (typeof ms !== 'number' || !isFinite(ms) || ms <= 0) return '';
    const d = new Date(ms);
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return d.toDateString() === new Date().toDateString()
        ? time
        : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}

/**
 * The line under Restart App, from teamio-admin's describeStatus answer (`view`). `clock(ms)` renders a
 * time. Pure (no `this`, no Date) — executed by cypress/node-tests/exe-restart-superadmin.test.js.
 * @returns {{text: string, variant: 'muted'|'info'|'success'|'warning'|'danger', detail: string}}
 */
function exeRestartStatusText(view, clock) {
    const v = (view && typeof view === 'object') ? view : {};
    const at = (ms) => clock(ms) || 'an unknown time';
    const requested = `Requested ${at(v.requestedAt)}`;
    const u = v.unmatchedStatus;
    const detail = (u && typeof u === 'object' && u.state)
        ? `The exe's last report is about another restart: ${u.state} ${at(u.at)}${u.source ? ` (${u.source})` : ''}.`
        : '';
    if (v.state === 'restarted') return { text: `${requested} — restarted ${at(v.at)}`, variant: 'success', detail: '' };
    if (v.state === 'exiting') {
        return { text: `${requested} — exiting ${at(v.at)}, waiting for the new copy to report`, variant: 'info', detail: '' };
    }
    if (v.state === 'refused') {
        const code = typeof v.reason === 'string' && v.reason ? v.reason : '';
        const words = code && Object.prototype.hasOwnProperty.call(EXE_RESTART_REFUSAL_REASONS, code) ? ` (${EXE_RESTART_REFUSAL_REASONS[code]})` : '';
        return { text: `Refused: ${code || 'no reason given'}${words}${v.at ? ` — ${at(v.at)}` : ''}`, variant: 'danger', detail: '' };
    }
    if (v.state === 'pending') {
        return v.expired
            ? { text: `${requested} — not picked up, and now too old for the exe to act on. Send it again if it is still needed.`, variant: 'warning', detail }
            : { text: `${requested} — not picked up yet`, variant: 'warning', detail };
    }
    return { text: 'No restart requested from SuperAdmin yet.', variant: 'muted', detail };
}

// ── Copy Form Section (teamio-admin COPY_FORM_SECTION) ──────────────────────────────────────────
// ONE section of a source form is added to a target form (same or another site) by the shared
// graftSection routine on the server. Copy unlocks only after a dry run of the SAME picks
// (sectionCopy.dryRunKey); the real run backs the target's ConfigData up to S3 before it saves.
function emptySectionCopyState() {
    return {
        sourceSiteID: null,
        sourceFormID: null,
        sectionId: null,
        targetSiteID: null,
        targetFormID: null,
        position: 'end', // 'end' or the target section key to insert after
        sourceForms: [],
        targetForms: [],
        loadingSource: false,
        loadingTarget: false,
        running: false, // false | 'dry' | 'copy'
        dryRunDone: false,
        dryRunKey: '', // sectionCopySelectionKey of the last successful dry run
        error: '',
        result: null, // { dryRun, status, report, backupKey }
    };
}

function sectionCopyTypeName(type) {
    return (CONTROLS[type] && CONTROLS[type].name) || type;
}

/** The parsed ConfigData of one form in a forms/getForms list (null when absent or unreadable). */
function sectionCopyConfigOf(forms, formID) {
    if (!formID || !Array.isArray(forms)) return null;
    const form = forms.find(f => f && f.FormIDForWeb === formID);
    if (!form || !form.ConfigData) return null;
    try {
        const parsed = typeof form.ConfigData === 'string' ? JSON.parse(form.ConfigData) : form.ConfigData;
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
        return null;
    }
}

export default {
  name: 'SuperAdmin',
  components: { Multiselect, Widget, ErrorDisplay, vSelect, A2PRegistrationWizard, NudgeManager, WhatsAppOnboarding, MessagingSafety, CustomerHealth, OutreachEmailEditor, FormImagingCategoryMap, OdApiSettings },
  mixins: [mixin],

    created() {
        if(this.allSettings) {
            this.init();
        }
        // Load Stripe plans only when needed, not on component creation
    },

    beforeDestroy() {
        // The Restart App status poll must not keep calling the backend after the page is gone.
        this.stopExeRestartPoll();
    },

    data() {
        return {
            pageLoading: true, // Loading state for initial page load
            customerName: '',
            logoUrl: '',
            numberOfOffices: 1,
            offices: [], // Array of office objects
            timezones: [
                { value: 'America/New_York', text: 'Eastern Time (ET) - New York, Florida, Georgia, North Carolina, etc.' },
                { value: 'America/Chicago', text: 'Central Time (CT) - Texas, Illinois, Louisiana, Minnesota, etc.' },
                { value: 'America/Denver', text: 'Mountain Time (MT) - Colorado, Utah, Montana, Wyoming, etc.' },
                { value: 'America/Phoenix', text: 'Mountain Time (MST) - Arizona (no daylight saving)' },
                { value: 'America/Los_Angeles', text: 'Pacific Time (PT) - California, Washington, Oregon, Nevada, etc.' },
                { value: 'America/Anchorage', text: 'Alaska Time (AKT) - Alaska' },
                { value: 'Pacific/Honolulu', text: 'Hawaii Time (HST) - Hawaii' },
                { value: 'America/Toronto', text: 'Eastern Time (Canada) - Ontario, Quebec' },
                { value: 'America/Winnipeg', text: 'Central Time (Canada) - Manitoba, Saskatchewan' },
                { value: 'America/Edmonton', text: 'Mountain Time (Canada) - Alberta' },
                { value: 'America/Vancouver', text: 'Pacific Time (Canada) - British Columbia' },
                { value: 'America/Halifax', text: 'Atlantic Time (Canada) - Nova Scotia, New Brunswick' },
                { value: 'America/St_Johns', text: 'Newfoundland Time (Canada) - Newfoundland' },
                { value: 'Africa/Johannesburg', text: 'South Africa Time (SAST) - South Africa' },
                { value: 'America/Barbados', text: 'Atlantic Time (AST) - Barbados' }
            ],
            countries: ['USA', 'CANADA', 'SOUTH_AFRICA', 'BARBADOS'],
            timezone: '',
            country: 'USA',
            showCustomerAdd: false,
            showSQSRedrive: false,
            showBulkFormCopy: false,
            showSectionCopy: false,
            showBulkWikiCopy: false,
            showPdfImport: false,
            showCacheManagement: false,
            showNudgeManager: false,
            showWhatsAppOnboarding: false,
            showMessagingSafety: false,
            showCustomerHealth: false,
            showTeamioKnowledge: false,
            addCustomerLoading: false,
            customerCreationProgress: [], // Track progress steps during customer creation
            createdCustomerInfo: { // Store created IDs for display
                customerID: null,
                siteIDs: []
            },
            sqsRedriveLoading: false,
            logs: '',
            siteActionLoading: [],
            siteActionResult: [],
            customers: [],
            customerEmployees: [], // CRITICAL: Array for managing employees during customer creation
            extra: {
                announcementText: []
            },
            // ---- Global Announcement ----
            globalAnnouncementText: '',
            globalAnnouncementLoading: false,
            // ---- Modal State for Phone Purchase ----
            siteIdForPurchase: null,
            areaCodeInput: '',
            availableNumbers: [],
            selectedNumberToPurchase: null,
            purchaseModalBusy: false,
            purchaseModalError: '',
            existingPhoneNumber: null,
            // -----------------------------------------
            // ---- Modal State for Email Verification ----
            siteIdForEmailVerification: null,
            customerNameForEmailVerification: null,
            verificationType: 'domain', // 'domain' or 'single_sender'
            domainToVerify: '',
            emailToVerify: '',
            verificationModalBusy: false,
            verificationModalError: '',
            currentVerifiedEmails: [],
            verificationStatus: null,
            verificationSuccessMessage: '',
            // DNS Records Display
            dnsRecordsToShow: [],
            domainBeingVerified: '',
            domainId: null,
            // Single Sender Display
            showSingleSenderInstructions: false,
            emailBeingVerified: '',
            // Verification Checking
            checkingVerification: false,
            // DNS Email (modal)
            showDnsEmailForm: false,
            dnsEmailRecipients: '',
            sendingDnsEmail: false,
            // DNS Records Panel
            panelDnsLoading: false,
            panelDnsRecords: [],
            panelDnsDomain: '',
            panelDnsDomainId: null,
            showPanelDnsEmailForm: false,
            panelDnsEmailRecipients: '',
            sendingPanelDnsEmail: false,
            // -----------------------------------------
            // ---- A2P Registration Wizard State ----
            showA2PWizard: false,
            a2pWizardSiteID: null,
            a2pWizardSiteName: null,
            a2pWizardSiteCountry: null,
            // -----------------------------------------
            // ---- Site Management UI Mode ----
            siteManagementUIMode: 'panel', // 'expandable' or 'panel'
            expandedSiteId: null, // For expandable row mode
            showSiteManagementPanel: false, // For side panel mode
            selectedSiteForManagement: null, // Currently selected site data
            // Super Admin: change a user's login email (Phase 2 audit tool)
            changeEmail: {
                loadingUsers: false,
                users: [],
                employeeID: null,
                currentEmail: '',
                newEmail: '',
                reason: '',
                saving: false,
                error: '',
                success: '',
            },
            selectedSiteA2PStatus: null, // A2P registration status for selected site
            selectedSiteA2PLoading: false, // Loading state for A2P status fetch
            // Test File Write state
            testFileWrite: {
                patNum: '',
                pdfCategory: '',
                categories: [],
                loading: false,
                categoriesLoaded: false,
                categoriesLoading: false,
            },
            // ---- Exe per-site OD table overrides (System tab) --------------------------------
            // The desired delta on top of the GLOBAL S3 ignore list, plus the exe's own report of
            // what it is ACTUALLY running with. Stored on the OWNER site (HQ for multi-clinic) —
            // `ownerSiteID` comes back from the backend; the FE never derives it.
            // ⛔ Saving sends NOTHING to the exe. The operator clicks Request MySQL Dump (enable)
            // or Refresh Exe Table List (disable) by hand, off-hours.
            exeTableOverrides: {
                siteID: null,          // the site the card was loaded FOR (stale-response guard)
                loading: false,
                saving: false,
                loaded: false,
                error: '',
                ownerSiteID: null,
                isMultiClinic: false,
                exeVersion: null,
                minExeVersion: EXE_MIN_VERSION,
                // Backend's numeric verdict (a string compare would put 3.0.9 ABOVE 3.0.52).
                // null while unknown — the warning shows until the backend says `true`.
                exeSupportsOverrides: null,
                malformed: false,      // the stored row was hand-edited into something unreadable
                saved: { unignore: [], ignore: [] },  // what is in teamio.settings right now
                applied: null,         // EXE_OD_TABLE_OVERRIDES_APPLIED, or null = no report yet
                unignoreText: '',
                ignoreText: '',
            },
            exeTableOverridesSeq: 0,
            // ---- Restart App / Set Debug Mode (System tab) ----------------------------------------
            // teamio-admin's answer to 'Get Exe Restart Status' for the managed site: the gate verdict
            // (exe 3.0.60+ — the buttons are enabled ONLY by restartAllowed / debugModeAllowed, so they
            // start disabled and stay disabled on any doubt) and `view` = describeStatus of the
            // EXE_RESTART_REQUEST / EXE_RESTART_STATUS rows. `sending` = the command in flight.
            exeRestart: {
                siteID: null, loading: false, loaded: false, error: '', sending: '',
                ownerSiteID: null, isMultiClinic: false, exeVersion: null, minExeVersion: null,
                restartAllowed: false, restartMessage: '', debugModeAllowed: false, debugModeMessage: '',
                view: null,
            },
            exeRestartSeq: 0,
            exeRestartPollTimer: null,
            siteManagementTab: 'email', // Active tab in management UI: 'email', 'twilio', 'system'
            siteManagementTabIndex: 0, // Tab index for b-tabs
            // SendGrid Management State
            siteEmailDomains: [], // List of authenticated domains for selected site
            siteEmailDomainsLoading: false,
            deletingDomainId: null,
            // Verified Emails Management
            siteVerifiedEmails: [], // List of configured sender emails for selected site
            siteVerifiedEmailsLoading: false,
            // Deactivation Status Tracking (Two-Phase Deactivation)
            siteDeactivationStatus: null, // Current deactivation status for selected site
            siteDeactivationStatusLoading: false,
            deactivationActionLoading: false, // Loading state for resume/cancel actions
            // Feature Toggles State
            featuresLoading: false,
            featuresLastSaved: null,
            siteFeatures: {
                FORMS_PAID_FOR: false,
                AI_FORMS_PAID_FOR: false,
                ONLINE_SCHEDULING_PAID_FOR: false,
                APPT_REMINDER_PAID_FOR: false,
                APPT_REMINDER_ACTIVATED: false,
                WORKFLOW_ACTIVATED: false,
                PRACTICE_PULSE_PAID_FOR: false,
                SCHEDULE_VIEW_ENABLED: false,
                TX_PRESENTER_ENABLED: false,
                // Cherry patient financing — an add-on to Tx Presenter. Declared here
                // (not added later) because Vue 2 only makes properties reactive if
                // they exist when the component is created.
                CHERRY_ENABLED: false,
                INSURANCE_VERIFICATION_ENABLED: false,
                REVIEWS_PAID_FOR: false,
            },
            featureSaving: {
                FORMS_PAID_FOR: false,
                AI_FORMS_PAID_FOR: false,
                ONLINE_SCHEDULING_PAID_FOR: false,
                APPT_REMINDER_PAID_FOR: false,
                APPT_REMINDER_ACTIVATED: false,
                WORKFLOW_ACTIVATED: false,
                PRACTICE_PULSE_PAID_FOR: false,
                SCHEDULE_VIEW_ENABLED: false,
                TX_PRESENTER_ENABLED: false,
                CHERRY_ENABLED: false,
                INSURANCE_VERIFICATION_ENABLED: false,
                REVIEWS_PAID_FOR: false,
            },
            // "Refresh Exe Settings" reminder (see @/utils/exeLiveSettings.js). `siteID` scopes it: a
            // reminder raised for one practice must never be shown — or sent — against another.
            // `pending` = setting keys changed since the last SUCCESSFUL send; `result` = the backend's
            // answer ({ sent, message, exeVersion }), shown verbatim.
            exeRefreshReminder: { siteID: null, pending: [], sending: false, result: null },
            exeSettingsMinVersion: EXE_SETTINGS_MIN_VERSION,
            // One-click Practice Pulse enablement (initializePracticePulse). Scoped to `siteID` like the
            // reminder above; `data` = the backend's per-step answer, `error` = why the call itself failed.
            practicePulseInit: { siteID: null, running: false, data: null, error: null },
            featureReadiness: null, // per-feature readiness payload from GET_SITE_FEATURE_STATUS
            junkCellsExpanded: false, // Appointment Reminders checklist: "Show patients" list under the cell-phone row
            // Practice-logo entry point (see src/utils/logoUrlCheck.js). `loaded` is what the preview
            // <img> reported: undefined = pending, true = @load, false = @error.
            createLogoPreview: { loaded: undefined, naturalWidth: 0, naturalHeight: 0 },
            siteLogoEdit: { url: '', saving: false, error: '', preview: { loaded: undefined, naturalWidth: 0, naturalHeight: 0 } },
            formTemplatesSeed: { seeding: false, results: null, error: '' },
            // Forms card → Open Dental imaging category per form type (GET_FORM_IMAGING_CATEGORIES).
            // Per site (DefNums differ between sites) and never saved — picked again on every run.
            formsImaging: { siteID: null, loading: false, error: '', categories: [], consentCandidates: [], map: { OTHER: '', NP_FORM: '', MED_HX_UPDATE: '' } },
            formsImagingRequestSeq: 0,
            // Forms card → "Apply to existing forms" (APPLY_FORM_IMAGING_DEFAULTS): dryRun preview → apply.
            formsImagingApply: { previewing: false, applying: false, preview: null, error: '' },
            // Forms card → "Load default procedure mappings" (SEED_PROCEDURE_MAPPINGS): dryRun preview → apply. Per site; reset on a site change.
            procMappingsSeed: { previewing: false, applying: false, preview: null, error: '' },
            // "Load default form messages" (SEED_FORM_MESSAGES): the dry-run answer, per site (reset with the site like procMappingsSeed).
            formMessagesSeed: { previewing: false, applying: false, preview: null, error: '' },
            reminderTemplatesSeed: { seeding: false, results: null, error: '' },
            txPresenterSeed: { seeding: false, results: null, error: '' },
            // Platform email sender enablement (ENABLE_PLATFORM_SENDER).
            platformSender: { enabling: false, results: null, error: '' },
            reviewsSeed: { seeding: false, results: null, error: '' },
            // Insurance Verification onboarding (pre-enable). `ivBillingProvider.provNum`
            // mirrors the saved setting so the inline picker can Save only a real change.
            ivSeed: { seeding: false, results: null, error: '' },
            ivCensus: { loading: false, results: null, error: '' },
            ivBillingProvider: { provNum: null, saving: false, error: '' },
            // Provider-name override form (2026-10-29): `mode` picks which inputs apply; the draft sent is
            // built by ivOverrideDraft. Seeded from the saved value on every loadSiteFeatures.
            ivOverride: { open: false, mode: 'organization', organizationName: '', firstName: '', lastName: '', saving: false, error: '' },
            wikiTemplateChecks: {}, // non-persisting per-template "form linked" acknowledgement checkboxes
            panelTabs: Object.freeze({ EMAIL: 0, TWILIO: 1, SYSTEM: 2, BILLING: 3, STRIPE: 4, FEATURES: 5 }),
            // -----------------------------------------
            roles: [
                {
                    RoleID: 1,
                    Role: 'Super Admin'
                },
                {
                    RoleID: 2,
                    Role: 'Admin'
                },
                {
                    RoleID: 3,
                    Role: 'Manager'
                },
                {
                    RoleID: 4,
                    Role: 'Employee'
                },
            ],           
            columns: ['CustomerCreated', 'IsCustomerActive', 'IsSiteActive', 'DefaultDB', 'ExeVersion', 'LastPing', 'PhoneNumber', 'Extra', 'SiteID', 'CustomerName', 'Manage', 'Action', 'Send'],
            options: {
                skin:'table',
                sortByColumn: false,
                sortable:[],
                perPage: 50,                
                pagination: { chunk: 10, dropdown: false },
            },
            // ⛔ Restart App and both Set Debug Mode actions are NOT in this generic list (2026-10-02): they
            // are version-gated per site (exe 3.0.60+), need a confirm, and their answer — including "not sent,
            // this exe is too old" — must reach the operator. This dropdown says "completed successfully" for
            // any 200. They live on the site panel's System tab (sendExeRestartCommand).
            actions: ['Request Upload Test', 'Schedule Data Load', 'Request MySQL Dump', 'Request Logs', 'SQS Redrive', 'Send Announcement',
                      'Clear Announcement', 'Pause', 'De-Activate', 'Re-Activate', 'Delete Logs',
                      'Revoke Users', 'Get Sites File', 'Purchase Number', 'Verify Email Address',
                      'Refresh Exe Table List'
                    ],
            selectedAction: [''],
            queues: [
                { name: 'prod-teamio-qprocessor-messages.fifo'},
                { name: 'prod-teamio-utility-misc-msgs-queue.fifo'},
                { name: 'prod-workflow-service-workflow'},
                { name: 'dev-teamio-utility-misc-msgs-queue.fifo'}
                
            ],

            dlqs: [
                { name: 'prod-teamio-qprocessor-messages-dlq.fifo'},
                { name: 'prod-teamio-utility-misc-msgs-queue-dlq.fifo'},
                { name: 'prod-teamio-utility-scheduler-dlq'},
                { name: 'prod-workflow-service-workflow-dlq'},
                { name: 'dev-teamio-utility-misc-msgs-queue-dlq.fifo'}
            ],
            selectedDlq: null,
            selectedQueue: null,
            // ---- Bulk Form Copy State ----
            bulkFormCopy: {
                sourceSiteID: null,
                targetSiteID: null,
                overwriteExisting: false,
                availableForms: [],
                selectedFormIDs: [],
                selectAll: false,
                loading: false,
                copying: false,
                // { done, total, name } while a copy runs (one form per request — runBulkFormCopy).
                progress: null,
                error: '',
                results: null,
                // TARGET site's OD imaging categories + the per-FormType pick (see loadBulkCopyImagingCategories).
                imaging: { siteID: null, loading: false, error: '', categories: [], consentCandidates: [], map: { OTHER: '', NP_FORM: '', MED_HX_UPDATE: '' } },
            },
            bulkCopyImagingRequestSeq: 0,
            // ---- Copy Form Section State (see emptySectionCopyState) ----
            sectionCopy: emptySectionCopyState(),
            // per-picker request counters: a site switched mid-load drops the stale forms list
            sectionCopySeq: { source: 0, target: 0 },
            // ---- Bulk Wiki Copy State ----
            bulkWikiCopy: {
                sourceSiteID: null,
                targetSiteID: null,
                overwriteExisting: false,
                copyTags: true,
                availableWikiPages: [],
                selectedWikiPageIDs: [],
                selectAll: false,
                loading: false,
                copying: false,
                error: '',
                results: null,
                // NEEDS_FORMS modal state
                needsFormsModal: false,
                missingForms: [],
                formIDsToCopy: [],
                targetCategories: [],
                applyAllConfig: {
                    pdfCategory: '',
                    attachmentCategory: '',
                    enableSpanish: true,
                    autoPrefillTeeth: true,
                    autoPrefillDate: true
                },
                perFormConfig: {},
                copyingForms: false,
            },
            // ---- PDF Import State ----
            pdfImport: {
                step: 1, // 1=site, 2=upload, 3=processing, 4=preview, 5=success
                targetSiteID: null,
                files: [],
                processing: false,
                progress: 0,
                statusMessage: '',
                processingDetails: [],
                results: [], // Array of {fileName, formName, formType, formData, status, error, sectionCount, fieldCount, fields}
                savedForms: [], // Array of {formID, formName} after saving
                jobId: null,
                pollTimer: null,
                error: null,
            },
            cacheStats: {
                loading: false,
                totalCount: 0,
                momentoCacheName: ''
            },
            cacheActions: {
                loading: false,
                loadingText: '',
                success: false,
                message: null
            },
            activeFollowups: [],
            activeFollowupsFields: [
                { key: 'patientId', label: 'Patient ID' },
                { key: 'name', label: 'Name' },
                { key: 'actions', label: 'Actions' }
            ],
            specificPatientId: '',
            showClearAllConfirm: false,
            momentoWebClient: null,
            cacheManagementTab: 'momento', // 'momento' or 'upstash'

            // Upstash/Redis Cache Management
            upstashStats: {
                loading: false,
                totalKeys: 0,
                environment: '',
                redisEndpoint: '',
                stats: [] // Array of { name, pattern, type, count }
            },
            upstashStatsFields: [
                { key: 'name', label: 'Category' },
                { key: 'count', label: 'Count' },
                { key: 'actions', label: 'Actions' }
            ],
            upstashActions: {
                loading: false,
                loadingText: '',
                success: false,
                message: null
            },
            upstashKeys: [],
            upstashKeysFields: [
                { key: 'key', label: 'Key' },
                { key: 'ttl', label: 'TTL' },
                { key: 'actions', label: 'Actions' }
            ],
            upstashKeysPage: 1,
            upstashSearchPattern: '',
            upstashQuickFilters: [
                { label: 'DB Reload Queue', pattern: 'teamio:events:db_needs_reloading' },
                { label: 'SMS Events', pattern: 'teamio:events:sms_sent:*' },
                { label: 'Email Events', pattern: 'teamio:events:email_sent:*' },
                { label: 'Task Events', pattern: 'teamio:events:task_assigned:*' },
                { label: 'KPI Data', pattern: 'teamio:kpi:*' },
                { label: 'Email Report Locks', pattern: 'email_report_*' }
            ],
            showUpstashClearConfirm: false,
            upstashClearTarget: null, // { pattern, count, type }

            // TeamIO Knowledge Base Management
            teamioKnowledgeTab: 'ai',
            teamioKnowledgeSaving: false,
            voiceTestLoading: false,
            knowledgeUpdateLoading: false,
            busy: false, // Full-screen loading overlay
            busyAction: '', // Track what action is causing the busy state
            teamioBot: {
                // AI Behavior
                aiPersonality: 'professional',
                responseStyle: 'detailed',
                welcomeMessage: 'Hi! I\'m your TeamIO Assistant. I\'m here to help you understand and use the TeamIO platform effectively.',
                systemPrompt: '',
                
                // Voice Settings
                voiceId: '21m00Tcm4TlvDq8ikWAM',
                voiceSpeed: 1.0,
                voiceStability: 0.5,
                
                // Knowledge Base
                knowledgeUrlsText: '', // Textarea field for bulk URL entry
                knowledgeStatus: {
                    lastUpdated: null,
                    docsIndexed: 0,
                    crawledPages: [] // Enhanced: Store rich page information
                    // Note: We don't track URLs in frontend - backend handles deduplication via Supabase
                }
            },

            // Twilio Dashboard (combined Billing + A2P)
            showTwilioDashboard: false,
            twilioActiveTab: 0,
            twilioUsageData: null,
            twilioUsageLoading: false,
            twilioUsageMonth: '',
            // SMS-by-feature rollup (from the sms_send_log ledger via SMS_FEATURE_ROLLUP action)
            smsRollupData: null,
            smsRollupLoading: false,
            smsRollupMonth: new Date().toISOString().slice(0, 7), // default = current YYYY-MM
            smsRollupFields: [
                { key: 'siteName', label: 'Site', sortable: true },
                { key: 'credits', label: 'Credits', sortable: true },
                { key: 'estCostUsd', label: 'Est. cost', sortable: true },
                { key: 'messages', label: 'Messages', sortable: true },
                { key: 'blocked', label: 'Blocked', sortable: true },
                { key: 'byFeature', label: 'By feature' },
                { key: 'mode', label: 'Attended / Unattended' },
            ],

            // ---- Insurance Checks (own top-level section; toggled by its button, like Twilio Dashboard) ----
            showInsuranceChecks: false,
            // ---- Insurance Checks rollup (per-site verification counts; COUNTS ONLY, no cost) ----
            ivRollupData: null,
            ivRollupLoading: false,
            ivRollupMonth: new Date().toISOString().slice(0, 7), // default = current YYYY-MM
            // Count columns are computed cells (off counts.byTriggerTotal), not real item keys, so
            // they're not sortable — only the real siteName key is.
            ivRollupFields: [
                { key: 'siteName', label: 'Site', sortable: true },
                { key: 'billed', label: 'Billed checks' },
                { key: 'automatic', label: 'Automatic' },
                { key: 'manual', label: 'Manual' },
                { key: 'onBooking', label: 'On booking' },
                { key: 'free', label: 'Free' },
                { key: 'unknown', label: 'Unknown' },
            ],

            // ---- Credit Cost tab (SuperAdmin-only internal cost) --------------------------------
            usageCostSiteId: null,
            usageCostMonth: new Date().toISOString().slice(0, 7),
            usageCostChannel: 'sms',
            usageCostData: null,
            usageCostLoading: false,
            usageCostError: null,
            usageCostFields: [
                { key: 'feature', label: 'Feature', sortable: true },
                { key: 'direction', label: 'Direction' },
                { key: 'credits', label: 'Credits', sortable: true },
                { key: 'estCostUsd', label: 'Est. cost', sortable: true },
                { key: 'actualCostUsd', label: 'Actual (Twilio)', sortable: true },
            ],
            usageCostRegionFields: [
                { key: 'label', label: 'Region' },
                { key: 'messages', label: 'Messages' },
                { key: 'physicalSegments', label: 'Segments' },
                { key: 'credits', label: 'Credits' },
                { key: 'estCostUsd', label: 'Est. cost' },
                { key: 'actualCostUsd', label: 'Actual' },
                { key: 'perSeg', label: 'Est → actual / seg' },
            ],

            twilioSelectedSite: null,
            twilioExpandedCategories: [],
            twilioNumbersLoading: false,
            twilioNumbersError: null,
            twilioNumbersList: [],
            twilioNumbersFetchedForSid: null,
            twilioUsageFields: [
                { key: 'CustomerName', label: 'Site', sortable: true },
                { key: 'totalPrice', label: 'Total ($)', sortable: true },
                { key: 'smsOut', label: 'SMS Out ($)', sortable: true },
                { key: 'smsIn', label: 'SMS In ($)', sortable: true },
                { key: 'carrierFees', label: 'Carrier Fees ($)', sortable: true },
                { key: 'phoneNumbers', label: 'Phone #s ($)', sortable: true },
                { key: 'smsOutCount', label: 'SMS Sent', sortable: true },
                { key: 'smsInCount', label: 'SMS Received', sortable: true },
                { key: 'activePatients', label: 'Active Pts (5yr)', sortable: true },
                { key: 'totalPatients', label: 'Total Pts', sortable: true },
                { key: 'email', label: 'Email (tracked)', sortable: true },
                { key: 'whatsapp', label: 'WhatsApp', sortable: true },
                { key: 'usageState', label: 'Usage', sortable: true, sortByFormatted: false },
                { key: 'actions', label: '' }
            ],
            twilioSiteDetailFields: [
                { key: 'category', label: 'Category', sortable: true },
                { key: 'description', label: 'Description' },
                { key: 'count', label: 'Count', sortable: true },
                { key: 'price', label: 'Price ($)', sortable: true }
            ],
            a2pRegistrations: [],
            a2pLoading: false,
            autoRefresh: false,
            autoRefreshInterval: null,
            selectedA2PRegistration: null,
            a2pStats: {
                approved: 0,
                pending: 0,
                inProgress: 0,
                failed: 0
            },
            selectedSiteLiveA2P: null,
            selectedSiteLiveA2PLoading: false,
            a2pFields: [
                { key: 'CustomerName', label: 'Customer Name', sortable: true },
                { key: 'SiteID', label: 'Site ID', sortable: true },
                { key: 'Status', label: 'Status', sortable: true },
                { key: 'ProgressPercentage', label: 'Progress', sortable: true },
                { key: 'BusinessName', label: 'Business Name', sortable: true },
                { key: 'CreatedAt', label: 'Created', sortable: true },
                { key: 'UpdatedAt', label: 'Last Updated', sortable: true },
                { key: 'actions', label: 'Actions' }
            ],
            twilioResourceFields: [
                { key: 'name', label: 'Resource Type' },
                { key: 'sid', label: 'SID' },
                { key: 'status', label: 'Status' }
            ],

            // ===== STRIPE BILLING & PAYMENTS =====
            stripe: null, // Stripe instance
            stripeLoading: false,
            selectedSiteStripeData: {
                customer: null,
                subscriptions: [],
                invoices: [],
                paymentMethods: []
            },
            // Billing tab loading states
            billingLoading: {
                customer: false,
                subscriptions: false,
                invoices: false,
                paymentMethods: false
            },
            // Create/Edit customer modal
            showStripeCustomerModal: false,
            stripeCustomerForm: {
                name: '',
                email: '',
                phone: '',
                address: {
                    line1: '',
                    line2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    country: 'US'
                }
            },
            // Subscription management
            showCreateSubscriptionModal: false,
            subscriptionForm: {
                priceId: '',
                quantity: 1,
                trialDays: 0
            },
            // Dynamic subscription plans from Stripe
            subscriptionPlans: [],
            stripePlansLoading: false,
            stripePlansError: null,
            stripePlansLastLoaded: null,

            // ===== STRIPE MAPPING (Sites to Customers) =====
            allSitesForMapping: [],
            allStripeCustomers: [],
            mappingLoading: {
                sites: false,
                customers: false
            },
            mappingError: null,
            mappingSuccess: null,
            savingMapping: null, // SiteID currently being saved
            autoMatchRunning: false
        }
    },

    computed: {
        // ── Schedule - Chart Audit: the switch needs Teamio app 3.0.61+ ───────
        // The version of the app that serves the managed office — the OWNER's (HQ for a multi-clinic
        // group), which is what the two cards above (Restart App, table overrides) already read from
        // teamio-admin for this very site on open. The site row's own ExeVersion is the fallback
        // (identical for every single-site customer, i.e. all of prod today). null = unknown.
        scheduleOwnerExeVersion() {
            const site = this.selectedSiteForManagement;
            if (!site) return null;
            const r = this.exeRestart;
            if (r.siteID === site.SiteID && r.loaded && r.exeVersion) return r.exeVersion;
            const t = this.exeTableOverrides;
            if (t.siteID === site.SiteID && t.loaded && t.exeVersion) return t.exeVersion;
            return site.ExeVersion || null;
        },
        scheduleSwitchGate() {
            return canSwitchOn(this.scheduleOwnerExeVersion);
        },
        // Disabled only for turning ON: a switch that is already on can always be turned off.
        scheduleSwitchBlocked() {
            return !this.siteFeatures[SCHEDULE_ACCESS_SETTING] && !this.scheduleSwitchGate.allowed;
        },

        // ── "Refresh Exe Settings" reminder ───────────────────────────────────
        // Only for the practice it was raised for, and only while something is still unsent.
        exeRefreshReminderVisible() {
            const site = this.selectedSiteForManagement;
            const r = this.exeRefreshReminder;
            return !!site && r.siteID === site.SiteID && r.pending.length > 0;
        },
        exeRefreshReminderLabel() {
            return describePendingExeSettings(this.exeRefreshReminder.pending);
        },

        // ── Restart App status line ───────────────────────────────────────────
        // Only for the practice it was read for, and only once the backend has answered.
        exeRestartLine() {
            const site = this.selectedSiteForManagement;
            const r = this.exeRestart;
            if (!site || r.siteID !== site.SiteID || !r.view) return null;
            return exeRestartStatusText(r.view, exeRestartClock);
        },

        // ── One-click Practice Pulse enablement result ────────────────────────
        // Only for the practice it ran for. null = nothing to show.
        practicePulseInitView() {
            const site = this.selectedSiteForManagement;
            const p = this.practicePulseInit;
            if (!site || p.siteID !== site.SiteID || !(p.data || p.error)) return null;
            if (p.error) return { variant: 'danger', lines: [], hqNote: null, error: p.error };
            const lines = describeInitializeSteps(p.data);
            return {
                variant: lines.every(l => l.ok) ? 'success' : 'warning',
                lines,
                hqNote: typeof p.data.hqNote === 'string' && p.data.hqNote.trim() ? p.data.hqNote : null,
                error: null,
            };
        },

        // ── Practice logo entry point ─────────────────────────────────────────
        createLogoCheck() {
            return assessLogoUrl({ url: this.logoUrl, ...this.createLogoPreview });
        },
        siteLogoCurrent() {
            return ((this.featureReadiness && this.featureReadiness.site && this.featureReadiness.site.logoUrl) || '').trim();
        },
        siteLogoCheck() {
            return assessLogoUrl({ url: this.siteLogoEdit.url, ...this.siteLogoEdit.preview });
        },
        siteLogoDirty() {
            return (this.siteLogoEdit.url || '').trim() !== this.siteLogoCurrent;
        },
        siteLogoBlocksSave() {
            return logoBlocksSave(this.siteLogoCheck);
        },
        // Insurance Checks rollup: how many sites actually loaded (counts !== null). Failed sites
        // are excluded from the totals, so the "Sites" card counts only the healthy ones.
        ivHealthySiteCount() {
            const sites = (this.ivRollupData && this.ivRollupData.sites) || [];
            return sites.filter((s) => s && s.counts).length;
        },

        // Forms onboarding checklist rows (Features tab, under the Forms toggle).
        // Each future feature adds its own computed in this shape and reuses the
        // same v-for block — readiness payload is keyed per feature.
        formsChecklistItems() {
            const r = (this.featureReadiness && this.featureReadiness.forms) || {};
            const tl = r.templateLibrary;
            const unmapped = (r.configMapping && r.configMapping.unmapped) || [];
            const attention = (r.prefill && r.prefill.needingAttention) || [];
            const formsCount = r.formsCount || 0;
            const companion = (r.wikiTemplates && r.wikiTemplates.items) || [];
            const companionOnHost = companion.filter(it => it.availableOnHost).length;
            const companionOnSite = companion.filter(it => it.present).length;
            // The procedure-code -> consent-form map is a single per-site blob
            // (TX_PRESENTER_SETTINGS_CONSENT) shared by Tx Presenter and Forms, so
            // both cards read the same readiness flag.
            const consentConfigured = !!((this.featureReadiness && this.featureReadiness.txPresenter) || {}).consentConfigured;
            // readiness.forms.procedureMappings = { rows, withConsent, withPostOp, toReview, postOpEnabled } (teamio-admin seed-procedure-mappings.js)
            const pm = (r.procedureMappings && typeof r.procedureMappings === 'object') ? r.procedureMappings : null;
            // readiness.forms.formMessages = { newPatient: { present, corrupt, linksPicked, linksNeeded, enabled }, medHx: { present, corrupt, inline,
            // linksPicked, linksNeeded, activated } } (teamio-admin seed-form-messages.js). null while an older teamio-admin is deployed.
            const fm = formMessagesReadinessOf(r);
            const fmSummary = summarizeFormMessages(fm);
            // Forms-companion wiki templates (New Patient + MedHx-Update): one item
            // each, shown only once the template exists on the site, with a
            // non-persisting checkbox + red note for the manual "link the form"
            // follow-up step (form links are copied as text and must be re-linked
            // by the office to the client's own New Patient / Medical History forms).
            const wikiItems = (((r.wikiTemplates && r.wikiTemplates.items) || [])
                .filter(it => it.present)
                .map(it => ({
                    key: `wiki-${it.name}`,
                    checkbox: true,
                    label: it.name,
                    badge: it.type === 'email' ? 'email template' : 'sms template',
                    note: 'Office to manually link the form in this template during onboarding',
                })));
            return [
                {
                    // Copies ONLY the Forms-companion email/SMS wiki templates (2026-10-01) — no
                    // forms, so no imaging map is needed. The office's own forms are converted per client.
                    key: 'messageTemplates',
                    ok: companionOnHost > 0 && companionOnSite >= companionOnHost,
                    label: 'Form email & SMS templates',
                    badge: `${companionOnSite}/${companion.length} on site`,
                    detail: tl ? `${companionOnHost} templates available from ${tl.hostName} (no forms are copied)` : 'Template host not configured for this stage',
                    action: {
                        handler: 'confirmSeedFormTemplates',
                        label: 'Load Form Email & SMS Templates',
                        busy: this.formTemplatesSeed.seeding,
                        disabled: !tl || companionOnHost === 0 || this.formTemplatesSeed.seeding,
                        tooltip: !tl ? 'TEMPLATE_HOST_SITE_ID not configured'
                            : (companionOnHost === 0 ? 'No form email/SMS templates on the host site'
                            : 'Copies the New Patient + MedHx-Update email/SMS templates (no forms); same-name templates are skipped — safe to re-run'),
                    },
                },
                {
                    key: 'mapping',
                    ok: formsCount > 0 && unmapped.length === 0,
                    label: 'Form Configuration and mapping',
                    badge: formsCount ? `${formsCount - unmapped.length}/${formsCount} mapped` : 'no forms yet',
                    list: unmapped,
                    listLabel: 'Need a file name or category (form builder → Form Configurations, or Apply to existing forms):',
                    action: {
                        handler: 'previewApplyFormImaging',
                        label: 'Apply to existing forms',
                        busy: this.formsImagingApply.previewing || this.formsImagingApply.applying,
                        disabled: formsCount === 0 || this.formTemplatesSeed.seeding
                            || !this.formsImagingMapComplete || this.formsImaging.loading || !!this.formsImaging.error
                            || this.formsImagingApply.previewing || this.formsImagingApply.applying,
                        tooltip: formsCount === 0 ? 'No forms on this site yet'
                            : (!this.formsImagingMapComplete ? 'Pick an Open Dental imaging category for all three form types first.'
                            : 'Preview, then fill blank file names and blank or invalid categories on this site\'s forms'),
                    },
                },
                {
                    key: 'prefill',
                    ok: attention.length === 0,
                    label: "Prefill teeth #'s, Patient Names, Date & Skip validation",
                    badge: attention.length ? `${attention.length} need attention` : 'all set',
                    list: attention,
                    listLabel: 'Consent/authorization forms missing prefill or skip-verification:',
                },
                {
                    // Procedure -> consent form AND post-op form rows: the one TX_PRESENTER_SETTINGS_CONSENT blob Tx Presenter, Forms (Get Form QR) and the
                    // post-op sender share. Replaces the old consent-only "consentMapping" row (readiness.txPresenter.consentConfigured). "Load default
                    // procedure mappings" pre-fills it from the office's OWN forms by name (dry-run preview first) — the office reviews the amber cells.
                    // Falls back to the old consent-only readiness while an older teamio-admin is deployed (the admin ships first, so this is a transition only).
                    key: 'procedureMappings',
                    ok: pm ? (pm.rows > 0 && pm.toReview === 0) : !!consentConfigured,
                    label: 'Procedure mappings & post-ops',
                    badge: pm ? (pm.rows > 0 ? `${pm.rows} rows · ${pm.toReview} to review · auto-send ${pm.postOpEnabled ? 'ON' : 'OFF'}` : 'no rows yet') : '',
                    detail: pm
                        ? (pm.rows > 0 ? `${pm.withConsent} with a consent form · ${pm.withPostOp} with a post-op form` : 'Not set')
                        : (consentConfigured ? 'Configured' : 'Not set'),
                    note: (pm ? pm.rows === 0 : !consentConfigured)
                        ? 'Without this, Get Form QR lists the appointment\'s procedures as "No consent form mapped", consent forms prefill every tooth on the appointment, and post-op instructions cannot auto-send. Load the defaults below, or the office maps codes in Forms → 🔗.'
                        : null,
                    action: {
                        handler: 'previewSeedProcedureMappings',
                        label: 'Load default procedure mappings',
                        busy: this.procMappingsSeed.previewing || this.procMappingsSeed.applying,
                        disabled: !this.selectedSiteForManagement || formsCount === 0 || this.formTemplatesSeed.seeding
                            || this.procMappingsSeed.previewing || this.procMappingsSeed.applying,
                        tooltip: formsCount === 0 ? 'Load the office\'s forms first — each group is matched to the office\'s own consent / post-op forms by name'
                            : 'Preview, then add the default procedure → consent form / post-op form rows (matched by form name). Codes already mapped are never changed; auto-send stays OFF.',
                    },
                },
                // The Forms Settings messages (New Patients + Medical History Update), Super Admins only while the gate is at 'preview'. The classic
                // "Form email & SMS templates" row above and its wiki rows are untouched: office staff still use the classic Medical History popup and the
                // workflows, which need those wiki pages, until the stage moves to default / retired. An older teamio-admin (no formMessages readiness and
                // no SEED_FORM_MESSAGES action) shows "readiness unavailable" with the button off - deploy teamio-admin first.
                ...(this.formsSettingsGateOpen ? [{
                    key: 'formMessages',
                    ok: !!(fmSummary && fmSummary.ok),
                    label: 'New patient & medical history messages',
                    badge: fmSummary ? fmSummary.badge : 'readiness unavailable',
                    detail: fmSummary ? fmSummary.detail : 'teamio-admin does not report this yet — deploy teamio-admin first',
                    action: {
                        handler: 'previewSeedFormMessages',
                        label: 'Load default form messages',
                        busy: this.formMessagesSeed.previewing || this.formMessagesSeed.applying,
                        disabled: !this.selectedSiteForManagement || !fmSummary || formsCount === 0 || this.formTemplatesSeed.seeding
                            || this.formMessagesSeed.previewing || this.formMessagesSeed.applying,
                        tooltip: !fmSummary ? 'teamio-admin does not report form message readiness yet - deploy teamio-admin first'
                            : (formsCount === 0 ? 'Load the office\'s forms first - the form link in each message is pre-picked from the office\'s own forms'
                            : 'Preview, then save the default New Patients and Medical History messages with the form link pre-picked. A setting already saved is never changed; both stay OFF.'),
                    },
                }] : []),
                {
                    key: 'sms',
                    ok: !!r.smsReady,
                    label: 'Text messaging (Twilio number)',
                    detail: r.generalPhone || 'No number assigned',
                    jump: { label: 'Set up', tab: this.panelTabs.TWILIO },
                },
                {
                    key: 'email',
                    ok: !!r.emailReady,
                    label: 'Email sending (verified sender)',
                    detail: (r.verifiedEmails || []).join(', ') || 'No verified emails',
                    jump: { label: 'Set up', tab: this.panelTabs.EMAIL },
                },
                this.emailSignatureChecklistItem(r.emailSignature, 'Load Form Email & SMS Templates'),
                ...wikiItems,
            ];
        },

        seedResultsVariant() {
            const res = this.formTemplatesSeed.results;
            if (!res) return 'info';
            const w = res.wikiTemplates || {};
            if (w.failureCount > 0) return w.successCount > 0 ? 'warning' : 'danger';
            // e.g. a template whose form link the office still has to set — keep it visible
            if ((res.warnings || []).length > 0) return 'warning';
            if (w.successCount > 0) return 'success';
            return 'info'; // everything skipped — already loaded
        },

        // Appointment Reminders onboarding checklist rows (same shape as formsChecklistItems).
        aptRemindersChecklistItems() {
            const ar = (this.featureReadiness && this.featureReadiness.aptReminders) || {};
            const f = (this.featureReadiness && this.featureReadiness.forms) || {};
            const tl = ar.templateLibrary;
            const hostTotal = tl ? (tl.hostAptCount + tl.hostRecallCount + (tl.hostBookedCount || 0)) : 0;
            // Inline (plain-text) reminder messages that are blank (the text, or an email's subject) —
            // summarizeTemplateSlots in teamio-admin. Any of the 18 slots (text or email) can be inline.
            const inlineBlank = ar.inlineSlotsBlank || 0;
            // Once every office sees the plain message boxes the action stops copying wiki pages (STAGE-driven),
            // so this row stops counting wiki templates and reports the messages instead.
            const noCopy = this.reminderWikiCopyRetired();
            const loadLabel = noCopy ? 'Load default reminder messages' : 'Load reminder templates';
            const slotsSet = ar.templateSlotsSet || 0;
            const slotsTotal = ar.templateSlotsTotal || 18;
            // Onboarding order (docs/plans/2026-10-19-online-scheduling-link-token-plan.md, WP4): the seeder picks the recall
            // text's default by ONLINE_SCHEDULING_PAID_FOR when it runs, so Online Appointments goes on FIRST for an office
            // that will use it. Shown only while that switch is off.
            const osHint = this.siteFeatures && this.siteFeatures.ONLINE_SCHEDULING_PAID_FOR === false
                ? ' — if this office will use Online Appointments, turn it on above first so the recall text starts with the booking link'
                : '';
            const templatesRow = noCopy
                ? {
                    key: 'templates',
                    ok: !!ar.settingsConfigured && slotsSet >= slotsTotal && inlineBlank === 0,
                    label: 'Reminder messages',
                    badge: ar.settingsConfigured ? `${slotsSet}/${slotsTotal} messages filled` : '',
                    detail: 'Default text and email messages, ready for the office to edit in Appointment Reminders → Settings' + osHint,
                    action: {
                        label: loadLabel,
                        disabled: !tl,
                        tooltip: !tl ? 'TEMPLATE_HOST_SITE_ID not configured' : 'Fills the empty reminder messages (text and email) with the default wording; no wiki pages are copied. A message the office already wrote or picked is never replaced — safe to re-run',
                    },
                }
                : {
                    key: 'templates',
                    ok: (ar.aptTemplateCount || 0) > 0 && (ar.recallTemplateCount || 0) > 0,
                    label: 'Reminder templates',
                    badge: `${ar.aptTemplateCount || 0} apt / ${ar.recallTemplateCount || 0} recall / ${ar.bookedTemplateCount || 0} booked`,
                    detail: (tl ? `${hostTotal} templates available from the template library` : 'Template host not configured for this stage') + osHint,
                    action: {
                        label: loadLabel,
                        disabled: !tl || hostTotal === 0,
                        tooltip: !tl ? 'TEMPLATE_HOST_SITE_ID not configured' : (hostTotal === 0 ? 'No reminder-tagged templates on the host site' : 'Copies all apt-reminder/recall-reminder wiki templates, fills the empty reminder messages (text and email) with the default wording; existing page names are skipped — safe to re-run'),
                    },
                };
            return [
                templatesRow,
                {
                    // "Load reminder templates" can create a template-only row, so a configured row alone
                    // is not "set up" — green once a channel (text/email/recall/booked…) is switched on.
                    key: 'settings',
                    ok: !!ar.settingsConfigured && !!ar.sendingEnabled && inlineBlank === 0,
                    label: 'Reminder settings',
                    badge: ar.settingsConfigured ? `${ar.templateSlotsSet || 0}/${ar.templateSlotsTotal || 18} templates selected` : '',
                    detail: inlineBlank > 0
                        ? `${inlineBlank} message(s) are blank — the office must fill them in Appointment Reminders → Settings`
                        : (!ar.settingsConfigured
                            ? 'Office sets templates & timing in Appointment Reminders → Settings, then flip "Activate sending" above'
                            : (ar.sendingEnabled
                                ? 'APPT_REMINDER_SETTINGS configured'
                                : 'Templates pre-selected — office sets timing and turns channels on in Appointment Reminders → Settings, then flip "Activate sending" above')),
                },
                this.emailSignatureChecklistItem(ar.emailSignature, loadLabel),
                {
                    key: 'sms',
                    ok: !!ar.smsReady,
                    label: 'Text messaging (Twilio number)',
                    detail: f.generalPhone || 'No number assigned',
                    jump: { label: 'Set up', tab: this.panelTabs.TWILIO },
                },
                {
                    key: 'email',
                    ok: !!ar.emailReady,
                    label: 'Email sending (verified sender)',
                    detail: (f.verifiedEmails || []).join(', ') || 'No verified emails',
                    jump: { label: 'Set up', tab: this.panelTabs.EMAIL },
                },
                // Patients with WORDS in Open Dental's cell-phone field - they crash an un-updated office exe's whole reminder
                // cycle. `junkCells` is null when GET_SITE_FEATURE_STATUS could not check (or the older API has no such key):
                // then there is no row at all, never a green "all clear".
                ...(ar.junkCells ? [this.junkCellsChecklistItem(ar.junkCells)] : []),
            ];
        },

        reminderSeedResultsVariant() {
            const res = this.reminderTemplatesSeed.results;
            if (!res) return 'info';
            // No wiki pages were copied (the default messages only): green when it filled something, amber on a
            // warning, grey when everything was already in place.
            if (res.wikiPagesCopied === false) {
                if ((res.warnings || []).length > 0) return 'warning';
                return (res.inlineSlotsWritten || 0) > 0 ? 'success' : 'info';
            }
            if ((res.failureCount || 0) > 0) return (res.successCount || 0) > 0 ? 'warning' : 'danger';
            if ((res.successCount || 0) > 0) return 'success';
            return 'info'; // all skipped — already loaded
        },

        /**
         * Which email sender this site uses, for the Tx Presenter card.
         *
         * THE FLIP RULE: a site's own verified sender always wins, so a site that has one
         * is never offered the platform sender — enabling it would store a setting that
         * could never take effect (and the backend refuses it outright for that reason).
         * The offer only appears for a site with no sender at all, which is the situation
         * the platform sender exists for.
         */
        txPresenterEmailSender() {
            const tx = (this.featureReadiness && this.featureReadiness.txPresenter) || {};
            if (tx.hasVerifiedEmail) {
                return {
                    icon: 'fa-check-circle text-success',
                    canEnable: false,
                    detail: 'Sends from the practice\'s own verified address. Nothing to do — a site\'s own sender always takes precedence over Teamio\'s.',
                };
            }
            // Just enabled in this session — reflect it without waiting for a readiness refresh.
            if (this.platformSender.results) {
                return {
                    icon: 'fa-check-circle text-success',
                    canEnable: false,
                    detail: `Sending through Teamio as ${this.platformSender.results.address}.`,
                };
            }
            return {
                icon: 'fa-exclamation-circle text-warning',
                canEnable: true,
                detail: 'No verified sender — this site cannot email patients at all today. Send through Teamio instead (no DNS setup), or have the practice verify its own domain.',
            };
        },

        txPresenterChecklistItems() {
            const tx = (this.featureReadiness && this.featureReadiness.txPresenter) || {};
            const f = (this.featureReadiness && this.featureReadiness.forms) || {};
            const seeded = tx.sectionsSeeded || {};
            const seededCount = ['general', 'presentation', 'payment', 'delivery'].filter(s => seeded[s]).length;
            return [
                {
                    key: 'settings',
                    ok: seededCount === 4,
                    label: 'Default settings',
                    badge: `${seededCount}/4 sections`,
                    detail: tx.hostAvailable ? 'General, Presentation, Payment & Delivery house-style defaults' : 'Template host has no Tx Presenter defaults configured',
                    action: {
                        label: 'Load Tx Presenter defaults',
                        disabled: !tx.hostAvailable,
                        tooltip: !tx.hostAvailable ? 'No Tx Presenter house-style on the template host (curate it first)' : 'Seeds General/Presentation/Payment/Delivery; sections already saved are preserved — safe to re-run',
                    },
                },
                {
                    key: 'imaging',
                    ok: !!tx.imagingMatched,
                    warn: seeded.general && !tx.imagingMatched,
                    label: 'Signed-plan imaging category',
                    detail: tx.imagingMatched ? 'Mapped to an Open Dental imaging category' : 'Not auto-matched',
                    note: (seeded.general && !tx.imagingMatched) ? 'Set the imaging category for signed plans in Tx Presenter → Settings → General.' : null,
                },
                {
                    key: 'finance',
                    ok: !!tx.financePayTypeMapped,
                    warn: seeded.payment && !tx.financePayTypeMapped,
                    label: 'Finance payment → OD PayType',
                    detail: tx.financePayTypeMapped ? 'Mapped' : 'Unmapped (per-clinic — set during onboarding if posting to OD)',
                    note: (seeded.payment && !tx.financePayTypeMapped) ? 'Map the "No Down Payment" option to an Open Dental PayType in Tx Presenter → Settings → Payment Options.' : null,
                },
                // Treatment-plan deposits. Like the finance PayType above this is a
                // per-clinic Open Dental DefNum that CANNOT be seeded — someone has to
                // pick it from the practice's own Cat-29 definitions. Unset, deposits
                // are refused outright (payment-service now blocks them rather than
                // charging a card it can't post), so they go silently missing from Tx
                // Presenter chairside, the at-home patient view AND Patient Profile.
                // Only warns when it's actually load-bearing — Moolah + OD posting on.
                {
                    key: 'prepayment',
                    ok: !!(tx.prepayment && tx.prepayment.defNumSet),
                    warn: !!(tx.prepayment && tx.prepayment.required && !tx.prepayment.defNumSet),
                    label: 'Treatment plan deposit → OD Unearned Type',
                    badge: (tx.prepayment && tx.prepayment.defNumSet) ? `DefNum ${tx.prepayment.defNum}` : null,
                    detail: (tx.prepayment && tx.prepayment.defNumSet)
                        ? 'Deposits post to Open Dental as a prepayment'
                        : (tx.prepayment && tx.prepayment.required
                            ? 'Not set — deposits cannot be collected anywhere until it is'
                            : 'Unset (per-clinic — needed once this practice collects deposits through Moolah)'),
                    note: (tx.prepayment && tx.prepayment.required && !tx.prepayment.defNumSet)
                        ? 'Pick the practice\'s Cat-29 Unearned Type in Settings → Payments → Treatment Plan Prepayment. Until then every deposit is refused — chairside, at-home and Patient Profile.'
                        : null,
                },
                {
                    key: 'consent',
                    ok: !!tx.consentConfigured,
                    label: 'Consent-form mappings',
                    detail: tx.consentConfigured ? 'Configured' : 'Not set',
                    // Same blob as the Forms card's 'consentMapping' row — it now also
                    // drives Patient Profile → Get Form QR, so both entry points are named.
                    note: !tx.consentConfigured ? 'Office maps procedure codes to consent forms in Tx Presenter → Settings → Consent Forms (or Forms → 🔗). Also drives Patient Profile → Get Form QR.' : null,
                },
                {
                    key: 'email',
                    ok: !!tx.hasVerifiedEmail,
                    label: 'Email sending (verified sender)',
                    detail: (f.verifiedEmails || []).join(', ') || 'No verified emails — post-sign email, comment notifications & plan-link email need one',
                    jump: { label: 'Set up', tab: this.panelTabs.EMAIL },
                },
                // Cherry rows appear ONLY when the site is entitled — the backend sends
                // readiness.txPresenter.cherry as null otherwise, so the great majority of
                // practices never see a permanently-unchecked box for a product they don't use.
                ...(tx.cherry ? [
                    {
                        key: 'cherryConnection',
                        ok: !!tx.cherry.connected,
                        warn: tx.cherry.keyConnected && !tx.cherry.practiceSelected,
                        label: 'Cherry account connected',
                        detail: tx.cherry.connected
                            ? `${tx.cherry.businessName} → ${tx.cherry.practiceName}`
                            : (tx.cherry.keyConnected
                                ? 'Key verified, but no Cherry practice location chosen yet'
                                : 'The office pastes their own Cherry Business API Key — we never hold it for them'),
                        note: !tx.cherry.connected
                            ? 'Office connects Cherry in Tx Presenter → Settings → Payment Options → Financing partner.'
                            : null,
                    },
                    {
                        key: 'cherryOption',
                        ok: !!tx.cherry.optionLinked,
                        warn: tx.cherry.connected && !tx.cherry.optionLinked,
                        label: 'Financing option backed by Cherry',
                        detail: tx.cherry.optionLinked
                            ? 'A finance option is set to Cherry'
                            : 'No finance option points at Cherry yet — until one does, patients still see the practice\'s typed estimates; once one does, they see Cherry\'s real terms (or no figures when Cherry can\'t price the amount)',
                        note: (tx.cherry.connected && !tx.cherry.optionLinked)
                            ? 'Set a financing option\'s partner to Cherry in Tx Presenter → Settings → Payment Options.'
                            : null,
                    },
                ] : []),
            ];
        },

        txPresenterSeedResultsVariant() {
            const res = this.txPresenterSeed.results;
            if (!res) return 'info';
            if ((res.seeded || []).length > 0) return 'success';
            return 'info'; // all skipped — already configured
        },

        // Reviews onboarding checklist — built from GET_SITE_FEATURE_STATUS
        // readiness.reviews.items (buildReviewsReadiness owns ok/warn/label/detail/
        // note); the frontend only attaches the "Load Reviews defaults" action.
        reviewsChecklistItems() {
            const rv = (this.featureReadiness && this.featureReadiness.reviews) || {};
            const baseItems = Array.isArray(rv.items) ? rv.items : [];
            return baseItems.map((it) => {
                const item = { ...it };
                if (it.key === 'settings') {
                    item.action = {
                        label: 'Load Reviews defaults',
                        disabled: false,
                        tooltip: 'Seeds timing, channels, gate threshold, blank review links & private-feedback reasons. A saved config is never overwritten — safe to run.',
                    };
                }
                return item;
            });
        },

        // ---- Insurance Verification onboarding -------------------------------------------
        // readiness.insuranceVerification is null when the probe could not run (IV service
        // unreachable, or the site has no OD database yet). Null must read as "unknown",
        // never as "ready" — ivToggleBlocked below treats it as blocked.
        ivReadiness() {
            return (this.featureReadiness && this.featureReadiness.insuranceVerification) || null;
        },

        ivSavedBillingProviderNum() {
            const r = this.ivReadiness;
            return r && r.billingProviderNum != null ? r.billingProviderNum : null;
        },

        /** OD providers for the inline picker, richest-first, with the NPI + entity type visible. */
        ivProviderOptions() {
            const list = (this.ivReadiness && this.ivReadiness.providers) || [];
            return list.map((p) => {
                const name = `${p.FName || ''} ${p.LName || ''}`.trim() || '(unnamed)';
                const abbr = p.Abbr ? ` (${p.Abbr})` : '';
                const npi = p.NationalProvID ? `NPI ${p.NationalProvID}` : 'NO NPI';
                // No "· individual/organization" suffix since 2026-10-29: the 270 sends the NPI only and
                // Stedi takes the entity type from NPPES, so OD's IsNotPerson flag no longer matters here.
                return { value: p.ProvNum, text: `${p.ProvNum} — ${name}${abbr} · ${npi}` };
            });
        },

        // ---- Provider-name override (2026-10-29) -------------------------------------------
        /** The SAVED override from readiness — `{ organizationName }` | `{ firstName, lastName }` | null. */
        ivProviderNameOverride() {
            const o = this.ivReadiness && this.ivReadiness.providerNameOverride;
            return o && typeof o === 'object' ? o : null;
        },

        /** Human text for the saved override, or '' when none. */
        ivProviderNameOverrideText() {
            const o = this.ivProviderNameOverride;
            if (!o) return '';
            if (o.organizationName) return `Organization: ${o.organizationName}`;
            if (o.firstName || o.lastName) return `Person: ${o.firstName || ''} ${o.lastName || ''}`.trim();
            return '';
        },

        /**
         * What Save would post — EXACTLY `{ organizationName }` or `{ firstName, lastName }` (non-empty,
         * trimmed, <= 60 chars: X12 NM103), or null while the active mode's inputs are incomplete. The
         * service re-validates; this only keeps Save disabled for an incomplete form.
         */
        ivOverrideDraft() {
            const f = this.ivOverride;
            const ok = (v) => v.length > 0 && v.length <= 60;
            if (f.mode === 'organization') {
                const organizationName = String(f.organizationName || '').trim();
                return ok(organizationName) ? { organizationName } : null;
            }
            const firstName = String(f.firstName || '').trim();
            const lastName = String(f.lastName || '').trim();
            return ok(firstName) && ok(lastName) ? { firstName, lastName } : null;
        },

        /** True when the draft equals what is already saved — Save stays disabled for a no-op. */
        ivOverrideDraftIsSaved() {
            const d = this.ivOverrideDraft;
            const s = this.ivProviderNameOverride;
            if (!d || !s) return false;
            return d.organizationName !== undefined
                ? d.organizationName === s.organizationName
                : (d.firstName === s.firstName && d.lastName === s.lastName);
        },

        // The backend owns ok/warn/label/detail/note (buildInsuranceVerificationReadiness);
        // the frontend only attaches the "Load defaults" action to the settings-blob row.
        insuranceVerificationChecklistItems() {
            const iv = this.ivReadiness || {};
            const items = Array.isArray(iv.items) ? iv.items.map((it) => ({ ...it })) : [];

            // Surface the settings blob as its own row — billingProviderNum lives inside it,
            // so a missing blob is the real first step, not the billing provider.
            const blobRow = {
                key: 'settingsBlob',
                ok: !!iv.settingsRowPresent,
                warn: false,
                label: 'Precheck settings',
                detail: iv.settingsRowPresent
                    ? 'Configured'
                    : 'Not created yet — load the defaults to create it (all automation stays OFF)',
                note: null,
                action: iv.settingsRowPresent ? null : {
                    label: 'Load defaults',
                    disabled: false,
                    tooltip: 'Creates INSURANCE_VERIFICATION_SETTINGS with the conservative code defaults — nightly precheck and verify-on-booking both OFF. A saved config is never overwritten.',
                },
            };
            return [blobRow, ...items];
        },

        /**
         * Block the toggle until the site can actually verify. Unknown readiness blocks too:
         * "we could not check" is not "it is fine". The 'Enable anyway' link overrides.
         */
        ivToggleBlocked() {
            if (this.siteFeatures.INSURANCE_VERIFICATION_ENABLED) return false; // never block turning it OFF
            const iv = this.ivReadiness;
            return !iv || iv.readyToEnable !== true;
        },

        // ---- Exe per-site OD table overrides ---------------------------------------------
        // Mirrors teamio-admin/lib/exe-table-overrides.js. The backend re-validates every save;
        // these computeds exist so a bad name is visible BEFORE Save, never after.

        /** Normalised names currently typed into the two boxes. */
        exeTableOverridesUnignoreNames() {
            return exeTableNamesFromText(this.exeTableOverrides.unignoreText);
        },

        exeTableOverridesIgnoreNames() {
            return exeTableNamesFromText(this.exeTableOverrides.ignoreText);
        },

        exeTableOverridesUnignoreProblems() {
            return exeTableNameProblems(this.exeTableOverridesUnignoreNames, 'unignore');
        },

        exeTableOverridesIgnoreProblems() {
            const problems = exeTableNameProblems(this.exeTableOverridesIgnoreNames, 'ignore');
            // A table in BOTH lists is contradictory — the backend refuses it, so block it here too.
            this.exeTableOverridesIgnoreNames
                .filter(n => this.exeTableOverridesUnignoreNames.indexOf(n) !== -1)
                .forEach(n => problems.push(`"${n}" is in both lists — pick one`));
            return problems;
        },

        exeTableOverridesSaveBlocked() {
            const o = this.exeTableOverrides;
            if (!o.loaded || o.loading || o.saving || o.error) return true;
            return this.exeTableOverridesUnignoreProblems.length > 0
                || this.exeTableOverridesIgnoreProblems.length > 0;
        },

        /**
         * "In sync" = what is SAVED equals what the exe REPORTS (order-insensitive) AND the exe
         * did not tell us its live listener is lagging. No report at all is never "in sync" — that
         * is the state where an operator would otherwise assume the dump already happened.
         */
        exeTableOverridesInSync() {
            const o = this.exeTableOverrides;
            const applied = o.applied;
            if (!applied) return false;
            if (applied.listenerInSync === false) return false;
            return exeTableListsEqual(o.saved.unignore, applied.unignored)
                && exeTableListsEqual(o.saved.ignore, applied.extraIgnored);
        },

        /** Old exes never read the setting — say so rather than leaving a saved row looking live. */
        exeTableOverridesVersionWarning() {
            const o = this.exeTableOverrides;
            if (!o.loaded) return '';
            if (o.exeSupportsOverrides === true) return '';
            const shown = o.exeVersion || 'unknown';
            return `This exe (v${shown}) does not support per-site table overrides — the setting has no effect until it is upgraded to v${o.minExeVersion || EXE_MIN_VERSION}+.`;
        },

        exeTableOverridesAppliedUnignored() {
            const a = this.exeTableOverrides.applied;
            return (a && Array.isArray(a.unignored)) ? a.unignored.join(', ') : '';
        },

        exeTableOverridesAppliedIgnored() {
            const a = this.exeTableOverrides.applied;
            return (a && Array.isArray(a.extraIgnored)) ? a.extraIgnored.join(', ') : '';
        },

        /** Patients census-lite could not classify — shown so "unmeasured" never reads as healthy. */
        ivCensusUnmeasured() {
            const p = this.ivCensus.results && this.ivCensus.results.patients;
            if (!p) return 0;
            return (p.notCached || 0) + (p.stale || 0) + (p.lookupErrored || 0);
        },

        reviewsSeedResultsVariant() {
            const res = this.reviewsSeed.results;
            if (!res) return 'info';
            if (res.skipped) return 'info'; // already configured — nothing loaded
            return res.seeded ? 'success' : 'info';
        },

        ...mapState('customer', ['siteID', 'user', 'sites']),

        // ---- Credit Cost tab ----------------------------------------------------------------
        /** Every site, current one first so the common case is one click. */
        usageCostSiteOptions() {
            const list = (this.sites || []).map((s) => ({
                value: s.SiteID,
                text: s.Name || s.SiteID,
            }));
            list.sort((a, b) => {
                if (a.value === this.siteID) return -1;
                if (b.value === this.siteID) return 1;
                return String(a.text).localeCompare(String(b.text));
            });
            return list;
        },
        /**
         * Rows sorted by ESTIMATED cost, not actual — actual is incomplete by construction while
         * Twilio is still pricing, so sorting on it would reshuffle the table as the sweep runs.
         */
        usageCostRows() {
            const rows = (this.usageCostData && this.usageCostData.breakdown) || [];
            return [...rows].sort((a, b) => (b.estCostUsd || 0) - (a.estCostUsd || 0));
        },
        /** null when there is nothing priceable at all — rendered as an em dash, never "0%". */
        usageCostPricedPct() {
            const c = this.usageCostData && this.usageCostData.coverage;
            return c && c.pricedPct != null ? c.pricedPct : null;
        },
        /**
         * Colour for the margin figure. Thresholds mirror the server-side alert floor (25%) so the
         * screen and the Slack alert never disagree about what "thin" means — a site shown green here
         * must never be one the daily margin-drift task is paging about.
         */
        usageCostMarginVariant() {
            const m = this.usageCostData && this.usageCostData.margin;
            const pct = m && m.marginPct;
            if (pct === null || pct === undefined) return 'text-muted';
            if (pct < 25) return 'text-danger';
            if (pct < 40) return 'text-warning';
            return 'text-success';
        },
        /**
         * Per-region rollup with the per-segment rates the disclosure needs. A blended est-vs-actual
         * ratio is only honest for an all-US site: a Puerto Rico segment costs Teamio ~$0.058 loaded
         * ($0.054 Twilio base) and bills the practice 5 credits, against ~$0.0125 / 1 credit for US.
         *
         * est/seg divides by ALL segments (EstimatedCostToTeamioUsd is populated at send time);
         * actual/seg divides by the CAPTURED segments only (ActualCostToTeamioUsd is null until the
         * reconcile sweep prices a row), so the actual rate isn't diluted by not-yet-priced sends.
         * Sorted by est cost so the bigger region leads. Sorting is non-mutating (spread first).
         */
        usageCostRegions() {
            const rows = (this.usageCostData && this.usageCostData.regionBreakdown) || [];
            const base = [...rows]
                .map((r) => {
                    const seg = Number(r.physicalSegments) || 0;
                    const capSeg = Number(r.capturedSegments) || 0;
                    const est = Number(r.estCostUsd) || 0;
                    const act = Number(r.actualCostUsd) || 0;
                    const priceable = Number(r.priceable) || 0;
                    return {
                        region: r.region,
                        label: r.region === 'PR' ? 'Puerto Rico' : 'US / mainland',
                        messages: Number(r.messages) || 0,
                        physicalSegments: seg,
                        credits: Number(r.credits) || 0,
                        estCostUsd: est,
                        actualCostUsd: act,
                        estPerSeg: seg > 0 ? est / seg : null,
                        actualPerSeg: capSeg > 0 ? act / capSeg : null,
                        pricedPct: priceable > 0 ? Math.round((Number(r.captured) || 0) / priceable * 100) : null,
                    };
                })
                .sort((a, b) => (b.estCostUsd || 0) - (a.estCostUsd || 0));
            // COST-ONLY rows (2026-09-15: Canada) are a SUBSET of a region above — a Canadian send bills
            // 1 credit and is counted in US / mainland — so they go LAST and are never sorted in among
            // the credit-bearing rows. Est cost is at the Canada rate the server computed.
            const costOnly = ((this.usageCostData && this.usageCostData.costOnlyRegions) || []).map((r) => {
                const seg = Number(r.physicalSegments) || 0;
                const capSeg = Number(r.capturedSegments) || 0;
                const est = Number(r.estCostAtCaRateUsd) || 0;
                const act = Number(r.actualCostUsd) || 0;
                const priceable = Number(r.priceable) || 0;
                return {
                    region: r.region,
                    costOnly: true,
                    label: 'Canada (cost only, within US / mainland)',
                    messages: Number(r.messages) || 0,
                    physicalSegments: seg,
                    credits: Number(r.credits) || 0,
                    estCostUsd: est,
                    actualCostUsd: act,
                    estPerSeg: seg > 0 ? est / seg : null,
                    actualPerSeg: capSeg > 0 ? act / capSeg : null,
                    pricedPct: priceable > 0 ? Math.round((Number(r.captured) || 0) / priceable * 100) : null,
                };
            });
            return [...base, ...costOnly];
        },
        /**
         * Why live Twilio cost is missing, worded per error CLASS: the site's subaccount mapping was
         * refused, the service lacks master Twilio credentials (a config problem, not the site's), or
         * Twilio usage could not be read (names the failed categories). Mirrored in
         * cypress/node-tests/superadmin-credit-cost.test.js.
         */
        usageCostTwilioErrorText() {
            const d = this.usageCostData;
            const err = d && d.twilioCostError;
            if (!err) return null;
            const mappingErrors = ['no_subaccount_for_site', 'ambiguous_subaccount', 'ambiguous_site_prefix', 'missing_site_id'];
            if (mappingErrors.includes(err)) {
                return `Live Twilio cost unavailable: this site's Twilio subaccount could not be matched unambiguously by site ID (${err}), so no margin is shown rather than a guessed one.`;
            }
            if (err === 'missing_master_credentials') {
                return 'Live Twilio cost unavailable: the cost service has no master Twilio credentials configured for this stage.';
            }
            const cats = (d.twilioCostFailedCategories || []).join(', ');
            return `Live Twilio cost unavailable: Twilio usage could not be read (${err}${cats ? `: ${cats}` : ''}). Margin is hidden rather than shown with missing costs — try again shortly.`;
        },
        /** The US estimated (loaded) and actual (Twilio base) per-segment rates for the hint text, from the shared
         *  date-aware constants (Twilio's base rate changes on 2027-01-01) — never typed into the template. */
        usageCostUsRates() {
            return { loaded: COST_PER_SEGMENT_USD.toFixed(4), base: TWILIO_BASE_COST_PER_SEGMENT_USD.toFixed(4) };
        },
        /** The Canada per-segment cost from the payload (never hardcoded), or null when no CA row. */
        usageCostCanadaRate() {
            const ca = ((this.usageCostData && this.usageCostData.costOnlyRegions) || []).find((r) => r.region === 'CA');
            return ca && Number.isFinite(Number(ca.caCostPerSegmentUsd)) ? Number(ca.caCostPerSegmentUsd) : null;
        },

        allowedToSee() {
            return this.user && (this.user.Username === 'shahsaumil@gmail.com' || this.user.Username === 'nilay613@gmail.com' || this.user.Username === 'nilay@teamio.app'
            || this.user.Username === 'webprojects3064@gmail.com' || this.user.Username === 'nilay@dentalstartupacademy.com'
            || this.user.Username === 'reachyourgoals80@gmail.com');
        },

        // WhatsApp Tech Provider onboarding panel is hidden unless the build flag is on
        // AND the operator passes the existing allowlist gate.
        whatsAppTechProviderEnabled() {
            return this.allowedToSee && process.env.VUE_APP_WHATSAPP_TECH_PROVIDER === 'true';
        },

        addCustomerDisabled() {
            return this.customerName === "" || this.timezone === "" || this.customerEmployees.length === 0 || (this.customerEmployees.length === 1 && !this.customerEmployees[0].name);
        },

        responsibilitiesDisabled() {
            return this.customerEmployees.length === 0 || (this.customerEmployees.length === 1 && !this.customerEmployees[0].name);
        },

        dateFormatStr() {
            return 'MM/DD/YYYY';
        },

        // Check if knowledge status is consistent
        isKnowledgeStatusConsistent() {
            const status = this.teamioBot.knowledgeStatus;
            
            const docsIndexed = status.docsIndexed || 0;
            
            // Debug logging
            console.log('🔍 isKnowledgeStatusConsistent check:', {
                status: status.status,
                docsIndexed,
                knowledgeStatus: status
            });
            
            // Consider it consistent if:
            // 1. Status is explicitly "completed", "cleared", or "error"
            // 2. Status is not "indexing"
            
            // If processing is complete, always consider it consistent
            if (status.status === 'completed' || status.status === 'error' || status.status === 'cleared') {
                console.log('✅ Status is completed/error/cleared - showing as consistent');
                return true;
            }
            
            // If status is "indexing", always show as inconsistent
            if (status.status === 'indexing') {
                console.log('🔄 Status is indexing - showing as inconsistent (Processing...)');
                return false;
            }
            
            // If no docs and no explicit status, consider consistent (empty/not configured)
            if (docsIndexed === 0 && !status.status) {
                console.log('📭 Empty with no status - showing as consistent (Not Configured)');
                return true;
            }
            
            // Default to consistent for any other state
            console.log('✅ Default case - showing as consistent');
            return true;
        },


        bulkCopyButtonLabel() {
            const { copying, progress } = this.bulkFormCopy;
            if (!copying) return 'Copy Selected Forms';
            if (!progress || !progress.total) return 'Copying...';
            return `Copying ${Math.min(progress.done + 1, progress.total)} of ${progress.total}...`;
        },
        canExecuteBulkCopy() {
            const imaging = this.bulkFormCopy.imaging;
            return this.bulkFormCopy.sourceSiteID &&
                   this.bulkFormCopy.targetSiteID &&
                   this.bulkFormCopy.selectedFormIDs.length > 0 &&
                   !this.bulkFormCopy.loading &&
                   !this.bulkFormCopy.copying &&
                   // Target-site imaging category for every FormType in the selection — BULK_COPY_FORMS refuses otherwise.
                   imaging.siteID === this.bulkFormCopy.targetSiteID &&
                   !imaging.loading &&
                   !imaging.error &&
                   this.bulkCopyImagingMapComplete;
        },

        // Normalized FormTypes (blank/unknown → OTHER) of the selected forms.
        bulkCopyRequiredTypes() {
            return requiredTypesForSelection(this.bulkFormCopy.availableForms, this.bulkFormCopy.selectedFormIDs);
        },

        bulkCopyImagingMapComplete() {
            return isCategoryMapComplete(this.bulkFormCopy.imaging.map, this.bulkCopyRequiredTypes, this.bulkFormCopy.imaging.categories);
        },

        // ---- Copy Form Section ----
        sectionCopySourceFormOptions() {
            return this.sectionCopy.sourceForms.map(f => ({ value: f.FormIDForWeb, text: `${f.Name} (${f.FormType || 'No Type'})` }));
        },
        sectionCopyTargetFormOptions() {
            return this.sectionCopy.targetForms.map(f => ({ value: f.FormIDForWeb, text: `${f.Name} (${f.FormType || 'No Type'})` }));
        },
        sectionCopySourceConfig() {
            return sectionCopyConfigOf(this.sectionCopy.sourceForms, this.sectionCopy.sourceFormID);
        },
        sectionCopyTargetConfig() {
            return sectionCopyConfigOf(this.sectionCopy.targetForms, this.sectionCopy.targetFormID);
        },
        sectionCopySourceSections() {
            return this.sectionCopySourceConfig ? describeSections(this.sectionCopySourceConfig, sectionCopyTypeName) : [];
        },
        sectionCopySectionOptions() {
            return this.sectionCopySourceSections.map(s => ({
                value: s.id,
                text: `${s.title} (${s.fieldCount} ${s.fieldCount === 1 ? 'field' : 'fields'}${s.isTab ? ', tabs' : ''})`,
            }));
        },
        sectionCopySelectedSection() {
            return this.sectionCopySourceSections.find(s => s.id === this.sectionCopy.sectionId) || null;
        },
        sectionCopyPositionOptions() {
            const targetSections = this.sectionCopyTargetConfig ? describeSections(this.sectionCopyTargetConfig, sectionCopyTypeName) : [];
            return [{ value: 'end', text: 'At the end' }]
                .concat(targetSections.map(s => ({ value: s.id, text: `After "${s.title}"` })));
        },
        sectionCopySameForm() {
            const sc = this.sectionCopy;
            return !!(sc.sourceSiteID && sc.sourceSiteID === sc.targetSiteID && sc.sourceFormID && sc.sourceFormID === sc.targetFormID);
        },
        // Copy is enabled only while the picks equal the ones the last dry run checked.
        sectionCopySelectionKey() {
            const sc = this.sectionCopy;
            return JSON.stringify([sc.sourceSiteID, sc.sourceFormID, sc.sectionId, sc.targetSiteID, sc.targetFormID, sc.position]);
        },
        canDryRunSectionCopy() {
            const sc = this.sectionCopy;
            return !!(sc.sourceSiteID && sc.sourceFormID && sc.sectionId && sc.targetSiteID && sc.targetFormID) &&
                !!this.sectionCopySelectedSection &&
                !this.sectionCopySameForm &&
                !sc.running && !sc.loadingSource && !sc.loadingTarget;
        },
        canRunSectionCopy() {
            return this.canDryRunSectionCopy &&
                this.sectionCopy.dryRunDone &&
                this.sectionCopy.dryRunKey === this.sectionCopySelectionKey;
        },
        sectionCopyReportRows() {
            const result = this.sectionCopy.result;
            if (!result || !Array.isArray(result.report)) return [];
            return result.report.map(entry => describeReportEntry(entry, { sourceConfig: this.sectionCopySourceConfig }));
        },

        // Bulk Wiki Copy missing-forms modal: every form needs a PDF category (uploads fall back to it on the backend).
        bulkWikiMissingFormsReady() {
            return perFormPdfCategoriesComplete(this.bulkWikiCopy.perFormConfig);
        },

        formsImagingMapComplete() {
            // Only for the site on screen — never a map left over from another site.
            const site = this.selectedSiteForManagement;
            return !!site && this.formsImaging.siteID === site.SiteID
                && isCategoryMapComplete(this.formsImaging.map, FORM_IMAGING_TYPES, this.formsImaging.categories);
        },

        // Flattened "Apply to existing forms" preview: Form · Field · From → To (category DefNums shown by name).
        formsImagingApplyRows() {
            const preview = this.formsImagingApply.preview;
            if (!preview || !Array.isArray(preview.items)) return [];
            const cats = this.formsImaging.categories;
            const rows = [];
            preview.items.forEach(item => {
                (item.changes || []).forEach(ch => {
                    rows.push({
                        key: `${item.formID}-${ch.field}`,
                        form: item.name,
                        field: IMAGING_FIELD_LABELS[ch.field] || ch.field,
                        from: describeImagingValue(ch.field, ch.from, cats),
                        to: describeImagingValue(ch.field, ch.to, cats),
                    });
                });
            });
            return rows;
        },

        formsImagingApplyOkTitle() {
            if (this.formsImagingApply.applying) return 'Applying…';
            const n = this.formsImagingApplyRows.length;
            return `Apply ${n} change${n === 1 ? '' : 's'}`;
        },

        formsImagingApplyOkDisabled() {
            const preview = this.formsImagingApply.preview;
            return this.formsImagingApply.applying || !preview || !!preview.needsMap
                || this.formsImagingApplyRows.length === 0 || !this.formsImagingMapComplete;
        },

        // "Load default procedure mappings" preview: Group · Codes · Consent form · Post-op form (a null name = the cell the office has to pick).
        procMappingsPreviewRows() {
            const preview = this.procMappingsSeed.preview;
            if (!preview || !Array.isArray(preview.preview)) return [];
            return preview.preview.map(row => ({
                label: row.label,
                codes: (row.procCodes || []).join(', '),
                consent: row.consentFormName || null,
                postOp: row.postOpFormName || null,
            }));
        },

        procMappingsOkTitle() {
            if (this.procMappingsSeed.applying) return 'Loading…';
            const n = this.procMappingsSeed.preview ? this.procMappingsSeed.preview.rowsAdded : 0;
            return n > 0 ? `Add ${n} row${n === 1 ? '' : 's'}` : 'Save post-op defaults';
        },

        procMappingsOkDisabled() {
            const preview = this.procMappingsSeed.preview;
            return this.procMappingsSeed.applying || !preview || (preview.rowsAdded === 0 && !preview.wouldSeedPostOpSettings);
        },

        // The Forms Settings release gate (uiVersion.js area 'formsSettings', read ONLY through gate.js, with the employee ID as the userKey).
        // Open for a Super Admin unless they chose "old" for themselves. The "New patient & medical history messages" row follows it, so it
        // only appears where the new editor it feeds can actually be opened.
        formsSettingsGateOpen() {
            return formsSettingsUiOn({ isSuperAdmin: this.isSuperAdmin, userKey: this.userID });
        },

        // "Load default form messages" preview, worded by src/utils/formMessagesSeed.js (null until a dry run has answered).
        formMessagesPreview() {
            return this.formMessagesSeed.preview ? describeFormMessagesPreview(this.formMessagesSeed.preview) : null;
        },

        formMessagesOkTitle() {
            if (this.formMessagesSeed.applying) return 'Loading…';
            const n = this.formMessagesPreview ? this.formMessagesPreview.willWriteCount : 0;
            return n > 0 ? `Save ${n} message setting${n === 1 ? '' : 's'}` : 'Nothing to save';
        },

        formMessagesOkDisabled() {
            const preview = this.formMessagesPreview;
            return this.formMessagesSeed.applying || !preview || preview.nothingToWrite;
        },

        canExecuteBulkWikiCopy() {
            return this.bulkWikiCopy.sourceSiteID &&
                   this.bulkWikiCopy.targetSiteID &&
                   this.bulkWikiCopy.selectedWikiPageIDs.length > 0 &&
                   !this.bulkWikiCopy.loading &&
                   !this.bulkWikiCopy.copying;
        },

        siteOptions() {
            if (!this.customers || this.customers.length === 0) {
                return [];
            }
            
            return this.customers
                .filter(customer => customer.IsSiteActive === 1) // Only show active sites
                .map(customer => ({
                    value: customer.SiteID,
                    text: `${customer.CustomerName} (${customer.SiteID})`
                }))
                .sort((a, b) => a.text.localeCompare(b.text)); // Sort alphabetically by customer name
        },

        momentoCacheName() {
            return process.env.VUE_APP_MOMENTO_CACHE;
        },

        // A2P computed properties
        twilioResources() {
            if (!this.selectedA2PRegistration) return [];
            
            const reg = this.selectedA2PRegistration;
            return [
                { name: 'Business Profile', sid: reg.BusinessProfileSID, status: reg.BusinessProfileSID ? 'Created' : 'Not Created' },
                { name: 'Trust Product', sid: reg.TrustProductSID, status: reg.TrustProductSID ? 'Created' : 'Not Created' },
                { name: 'Brand Registration', sid: reg.BrandRegistrationSID, status: reg.BrandRegistrationSID ? 'Created' : 'Not Created' },
                { name: 'Campaign', sid: reg.CampaignSID, status: reg.CampaignSID ? 'Created' : 'Not Created' },
                { name: 'Messaging Service', sid: reg.MessagingServiceSID, status: reg.MessagingServiceSID ? 'Created' : 'Not Created' },
                { name: 'Twilio Account', sid: reg.TwilioAccountSID, status: reg.TwilioAccountSID ? 'Created' : 'Not Created' }
            ];
        },

        canRetryA2P() {
            if (!this.selectedA2PRegistration) return false;
            const status = this.selectedA2PRegistration.Status;
            return status === 'failed' || status === 'rejected' || status === 'error';
        },

        // Twilio Billing computed properties
        isCurrentTwilioMonth() {
            const now = new Date();
            const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            return this.twilioUsageMonth === currentMonth;
        },

        twilioOverageBannerVisible() {
            const summary = this.twilioUsageData && this.twilioUsageData.overageSummary;
            if (!summary) return false;
            return (summary.sitesAtWarning && summary.sitesAtWarning.length > 0)
                || (summary.sitesOverLimit && summary.sitesOverLimit.length > 0);
        },

        twilioSiteRows() {
            if (!this.twilioUsageData || !this.twilioUsageData.sites) return [];
            return this.twilioUsageData.sites.map(site => ({
                ...site,
                smsOut: (site.breakdown['sms-outbound'] || {}).price || 0,
                smsIn: (site.breakdown['sms-inbound'] || {}).price || 0,
                carrierFees: ((site.breakdown['sms-messages-carrierfees'] || {}).price || 0) +
                             ((site.breakdown['mms-messages-carrierfees'] || {}).price || 0),
                phoneNumbers: (site.breakdown['phonenumbers'] || {}).price || 0,
                smsOutCount: (site.breakdown['sms-outbound'] || {}).count || 0,
                smsInCount: (site.breakdown['sms-inbound'] || {}).count || 0,
                // Report-only channels (no allowance/alert): email from our DB, WhatsApp from Twilio.
                email: site.email || 0,
                whatsapp: (site.whatsapp && site.whatsapp.messages) || 0,
                // Usage column key — sorts by usage % via the underlying number
                usageState: site.usage ? site.usage.usagePct : 0
            }));
        },

        // Tooltip for the Owned Numbers card — breaks the inventory into buckets.
        twilioInventoryTooltip() {
            const inv = this.twilioUsageData && this.twilioUsageData.numberInventory;
            if (!inv) return 'Owned phone numbers (from Twilio).';
            const billed = this.twilioUsageData.masterPhoneNumberCount;
            return `Owned now: ${inv.owned} total — ${inv.activeSubaccount} in customer subaccounts, `
                + `${inv.masterDirect} master-direct (dev), ${inv.inactiveSite} on inactive sites, `
                + `${inv.orphan} orphan (no site).`
                + (billed != null ? ` (Billed this month: ${billed}.)` : '');
        },

        // Total count of numbers flagged as "charged but unused".
        wasteNumberCount() {
            const w = this.twilioUsageData && this.twilioUsageData.wasteAlerts;
            if (!w) return 0;
            return w.reduce((n, x) => n + (x.numbers ? x.numbers.length : 0), 0);
        },

        // Sites with tracked email volume that have NO Twilio subaccount row above
        // (e.g. email-only practices) — surfaced separately so email isn't hidden.
        twilioEmailOnlySites() {
            if (!this.twilioUsageData || !this.twilioUsageData.emailUsage) return [];
            const twilioSiteIds = new Set((this.twilioUsageData.sites || []).map(s => s.SiteID));
            return this.twilioUsageData.emailUsage.filter(e => e.totalEmail > 0 && !twilioSiteIds.has(e.SiteID));
        },

        twilioSiteDetailRows() {
            if (!this.twilioSelectedSite || !this.twilioSelectedSite.breakdown) return [];
            return Object.entries(this.twilioSelectedSite.breakdown).map(([category, data]) => ({
                category,
                description: data.description || category,
                count: data.count,
                price: data.price
            }));
        },

        // PDF Import computed properties
        canProceedToProcessing() {
            if (!this.pdfImport.files || this.pdfImport.files.length === 0) return false;
            
            // Check file size limits
            const totalSize = this.totalPdfFileSize;
            if (totalSize > 50 * 1024 * 1024) return false; // 50MB batch limit
            
            // Check individual file sizes
            for (const file of this.pdfImport.files) {
                if (file.size > 10 * 1024 * 1024) return false; // 10MB per file
            }
            
            return true;
        },

        totalPdfFileSize() {
            if (!this.pdfImport.files || this.pdfImport.files.length === 0) return 0;
            return this.pdfImport.files.reduce((total, file) => total + (file.size || 0), 0);
        },

        successfulPdfFormsCount() {
            if (!this.pdfImport.results) return 0;
            return this.pdfImport.results.filter(r => r.status === 'success').length;
        },

        canSavePdfForms() {
            return this.successfulPdfFormsCount > 0 && !this.pdfImport.processing;
        },

        fieldTypeOptions() {
            return [
                { value: 'input', text: 'Text Input' },
                { value: 'text', text: 'Textarea' },
                { value: 'date', text: 'Date' },
                { value: 'number', text: 'Number' },
                { value: 'checkbox', text: 'Checkbox' },
                { value: 'radio', text: 'Radio' },
                { value: 'dropDown', text: 'Dropdown' },
                { value: 'signature', text: 'Signature' }
            ];
        },

        // ===== BILLING COMPUTED PROPERTIES =====
        
        officesWithBilling() {
            return this.offices.filter(office => !office.skipStripeCreation && office.selectedPlan);
        },

        uniquePlansSelected() {
            const plans = this.officesWithBilling.map(office => {
                const plan = this.getSelectedPlanDetails(office.selectedPlan);
                return plan ? plan.name : 'Unknown Plan';
            });
            return [...new Set(plans)];
        },

        calculateTotalMonthly() {
            let total = 0;
            this.officesWithBilling.forEach(office => {
                const plan = this.getSelectedPlanDetails(office.selectedPlan);
                if (plan && plan.interval === 'month') {
                    total += (plan.amount / 100);
                }
            });
            return total.toFixed(2);
        },

        // ===== STRIPE MAPPING COMPUTED PROPERTIES =====

        activeSitesForMapping() {
            return this.allSitesForMapping.filter(site => site.IsSiteActive === 1);
        },

        inactiveSitesForMapping() {
            return this.allSitesForMapping.filter(site => site.IsSiteActive !== 1);
        },

        linkedSitesCount() {
            return this.activeSitesForMapping.filter(site => site.stripe_customer_id).length;
        },

        unlinkedSitesCount() {
            return this.activeSitesForMapping.filter(site => !site.stripe_customer_id).length;
        },

        usedStripeCustomersCount() {
            const usedCustomerIds = new Set(
                this.allSitesForMapping
                    .filter(site => site.stripe_customer_id)
                    .map(site => site.stripe_customer_id)
            );
            return usedCustomerIds.size;
        }
    },

    async mounted() {
        // Initialize Momento Web SDK
        if (typeof window !== 'undefined' && !window.momentoWebSdk) {
            try {
                // Dynamically import Momento Web SDK
                const { CacheClient, CredentialProvider, Configurations } = await import('@gomomento/sdk-web');
                window.momentoWebSdk = { CacheClient, CredentialProvider, Configurations };
                console.log('[SuperAdmin] Momento Web SDK loaded successfully');
            } catch (error) {
                console.error('[SuperAdmin] Failed to load Momento Web SDK:', error);
            }
        }
    },

    methods: {
        // Once the plain reminder message boxes are released to every office (STAGE.reminderMessages 'default' /
        // 'retired') they already hold the default wording, so "Load reminder templates" stops copying the template
        // host's wiki pages and asks the admin for the default messages only (copyReminderWikiPages: false). Before
        // that (preview / invite) it is exactly the old copy. A method, not a computed: STAGE is a plain object.
        reminderWikiCopyRetired() {
            return STAGE.reminderMessages === 'default' || STAGE.reminderMessages === 'retired';
        },

        // ── Practice logo entry point ─────────────────────────────────────────
        onLogoPreview(which, ev) {
            const target = which === 'site' ? this.siteLogoEdit.preview : this.createLogoPreview;
            if (!ev || !ev.target) {
                target.loaded = false; target.naturalWidth = 0; target.naturalHeight = 0;
                return;
            }
            target.loaded = true;
            target.naturalWidth = ev.target.naturalWidth || 0;
            target.naturalHeight = ev.target.naturalHeight || 0;
        },
        async saveSiteLogo() {
            if (!this.selectedSiteForManagement || this.siteLogoEdit.saving) return;
            if (logoBlocksSave(this.siteLogoCheck)) {
                this.siteLogoEdit.error = 'That is not an image — fix the link, or clear the field to remove the logo.';
                return;
            }
            this.siteLogoEdit.saving = true;
            this.siteLogoEdit.error = '';
            try {
                const siteID = this.selectedSiteForManagement.SiteID;
                const logoUrl = (this.siteLogoEdit.url || '').trim();
                const res = await this.sendAction({ siteID, action: 'SET_SITE_LOGO', logoUrl });
                const ok = res && res.status === 200 && res.data && typeof res.data.logoUrl === 'string';
                if (!ok) {
                    // teamio-admin's createErrorResponse(500, msg) sends the bare message string as the
                    // body (not { message }), so accept both shapes.
                    const d = res && res.data;
                    this.siteLogoEdit.error = (typeof d === 'string' ? d : (d && (d.message || d.error))) || 'Could not save the logo. Please try again.';
                    return;
                }
                this.$toast.success(logoUrl ? 'Practice logo saved — it now shows on forms, emails and My Account.' : 'Practice logo removed.');
                await this.loadSiteFeatures();   // re-seeds siteLogoEdit.url from readiness → not dirty
            } catch (e) {
                this.siteLogoEdit.error = (e && e.message) || 'Could not save the logo.';
            } finally {
                this.siteLogoEdit.saving = false;
            }
        },
        ...mapActions('customer', ['getCustomers']),
        ...mapActions('superadmin', ['sendAction', 'addCustomer', 'getDeactivationStatus', 'resumeDeactivation', 'cancelDeactivation']),
        ...mapActions('a2p', ['getAllA2PRegistrations', 'getA2PRegistrationStatus', 'getLiveA2PStatus']),
        ...mapActions('myaccount', ['getMyAccount', 'changeLoginEmail', 'getSiteUsageCost']),
        ...mapMutations('alert', ['setErrored', 'setMessage']),
        ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),

        // ---- Super Admin: change a user's login email (Phase 2 audit tool) ----
        async loadSiteUsersForEmailChange() {
            this.changeEmail.error = '';
            this.changeEmail.success = '';
            this.changeEmail.employeeID = null;
            this.changeEmail.currentEmail = '';
            this.changeEmail.users = [];
            if (!this.selectedSiteForManagement) return;
            this.changeEmail.loadingUsers = true;
            try {
                const siteID = this.selectedSiteForManagement.SiteID;
                const res = await this.getMyAccount({ siteID });
                // getMyAccount returns multiple result sets; the employees set is the one
                // whose rows have EmployeeID + Username (active employees for the site).
                let employees = [];
                const data = (res && res.data) ? res.data : [];
                if (Array.isArray(data)) {
                    for (const rs of data) {
                        if (Array.isArray(rs) && rs.length && rs[0] && rs[0].EmployeeID && rs[0].Username !== undefined) {
                            employees = rs;
                            break;
                        }
                    }
                }
                this.changeEmail.users = employees;
                if (employees.length === 0) {
                    this.changeEmail.error = 'No users found for this site.';
                }
            } catch (e) {
                console.log('loadSiteUsersForEmailChange error:', e);
                this.changeEmail.error = 'Could not load users for this site.';
            } finally {
                this.changeEmail.loadingUsers = false;
            }
        },

        onChangeEmailUserSelected() {
            const u = this.changeEmail.users.find(x => x.EmployeeID === this.changeEmail.employeeID);
            this.changeEmail.currentEmail = u ? u.Username : '';
            this.changeEmail.newEmail = '';
            this.changeEmail.error = '';
            this.changeEmail.success = '';
        },

        async submitChangeLoginEmail() {
            this.changeEmail.error = '';
            this.changeEmail.success = '';
            const newEmail = (this.changeEmail.newEmail || '').trim().toLowerCase();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
                this.changeEmail.error = 'Please enter a valid email address.';
                return;
            }
            if (newEmail === (this.changeEmail.currentEmail || '').trim().toLowerCase()) {
                this.changeEmail.error = 'New email is the same as the current login email.';
                return;
            }
            this.changeEmail.saving = true;
            try {
                const siteID = this.selectedSiteForManagement.SiteID;
                await this.changeLoginEmail({
                    siteID,
                    employeeID: this.changeEmail.employeeID,
                    newEmail,
                    reason: this.changeEmail.reason,
                });
                this.changeEmail.success = `Login email changed to ${newEmail}. Tell the user to sign in with the Google or Microsoft button that matches the new email.`;
                // Reflect the change locally so the dropdown + current-email field stay accurate.
                const u = this.changeEmail.users.find(x => x.EmployeeID === this.changeEmail.employeeID);
                if (u) u.Username = newEmail;
                this.changeEmail.currentEmail = newEmail;
                this.changeEmail.newEmail = '';
                this.changeEmail.reason = '';
            } catch (e) {
                const data = e && e.response && e.response.data;
                this.changeEmail.error = (typeof data === 'string' && data) ? data : ((e && e.message) ? e.message : 'Failed to change login email.');
            } finally {
                this.changeEmail.saving = false;
            }
        },

        async sendGlobalAnnouncement() {
            try {
                if (!this.globalAnnouncementText || this.globalAnnouncementText.trim().length === 0) {
                    this.$toast.error('Please enter an announcement message');
                    return;
                }
                this.globalAnnouncementLoading = true;
                this.logActivity(`Initiating GLOBAL announcement`);
                const payload = {
                    action: 'Send Global Announcement',
                    announcementText: this.globalAnnouncementText
                };
                const res = await this.sendAction(payload);
                if (res && (!res.status || res.status === 200)) {
                    this.$toast.success('Global announcement sent');
                    this.logActivity('✅ Global announcement sent successfully', 'SUCCESS');
                    this.globalAnnouncementText = '';
                } else {
                    const errorMsg = (res && res.data && res.data.message) || (res && res.message) || 'Unknown error';
                    this.$toast.error('Failed to send global announcement');
                    this.logActivity(`❌ Global announcement failed: ${errorMsg}`, 'ERROR');
                }
            } catch (err) {
                const errorMsg = (err && err.message) || 'Unknown error';
                this.$toast.error('Failed to send global announcement');
                this.logActivity(`❌ Global announcement failed: ${errorMsg}`, 'ERROR');
            } finally {
                this.globalAnnouncementLoading = false;
            }
        },

        async clearGlobalAnnouncement() {
            try {
                this.globalAnnouncementLoading = true;
                this.logActivity('Initiating CLEAR GLOBAL announcement');
                const payload = { action: 'Clear Global Announcement' };
                const res = await this.sendAction(payload);
                if (res && (!res.status || res.status === 200)) {
                    this.$toast.success('Global announcement cleared');
                    this.logActivity('✅ Global announcement cleared', 'SUCCESS');
                } else {
                    const errorMsg = (res && res.data && res.data.message) || (res && res.message) || 'Unknown error';
                    this.$toast.error('Failed to clear global announcement');
                    this.logActivity(`❌ Clear global announcement failed: ${errorMsg}`, 'ERROR');
                }
            } catch (err) {
                const errorMsg = (err && err.message) || 'Unknown error';
                this.$toast.error('Failed to clear global announcement');
                this.logActivity(`❌ Clear global announcement failed: ${errorMsg}`, 'ERROR');
            } finally {
                this.globalAnnouncementLoading = false;
            }
        },

        async getMomentoWebClient() {
            if (!window.momentoWebSdk) {
                throw new Error('Momento Web SDK not loaded');
            }

            if (!this.momentoWebClient) {
                const { CacheClient, CredentialProvider, Configurations } = window.momentoWebSdk;
                
                // Use environment variable for API key
                const apiKey = process.env.VUE_APP_MOMENTO_API_KEY;
                
                if (!apiKey) {
                    throw new Error('VUE_APP_MOMENTO_API_KEY environment variable is not set');
                }
                
                console.log('[getMomentoWebClient] Creating Momento Web client...');
                console.log('[getMomentoWebClient] API Key length:', apiKey.length);
                console.log('[getMomentoWebClient] API Key first 20 chars:', apiKey.substring(0, 20));
                
                // Try multiple configuration approaches
                const configsToTry = [
                    { name: 'Laptop', config: () => Configurations.Laptop.latest() },
                    { name: 'Browser', config: () => Configurations.Browser?.latest() || Configurations.Laptop.latest() },
                    { name: 'InRegion', config: () => Configurations.InRegion?.latest() || Configurations.Laptop.latest() }
                ];
                
                let lastError = null;
                
                for (const configOption of configsToTry) {
                    try {
                        console.log(`[getMomentoWebClient] Trying ${configOption.name} configuration...`);
                        const configuration = configOption.config();
                        
                        this.momentoWebClient = await CacheClient.create({
                            configuration: configuration,
                            credentialProvider: CredentialProvider.fromString({
                                apiKey: apiKey
                            }),
                            defaultTtlSeconds: 3600
                        });
                        
                        console.log(`[getMomentoWebClient] Success with ${configOption.name} configuration!`);
                        
                        // Test the connection with a simple operation
                        console.log('[getMomentoWebClient] Testing connection...');
                        const testResponse = await this.momentoWebClient.createCache('test-connection');
                        console.log('[getMomentoWebClient] Test response:', testResponse.type);
                        
                        break; // Success, exit loop
                        
                    } catch (error) {
                        console.error(`[getMomentoWebClient] ${configOption.name} configuration failed:`, error.message);
                        lastError = error;
                        this.momentoWebClient = null; // Reset for next attempt
                    }
                }
                
                if (!this.momentoWebClient) {
                    console.error('[getMomentoWebClient] All configurations failed. Last error:', lastError);
                    throw lastError || new Error('Failed to create Momento client with any configuration');
                }
            }
            
            return this.momentoWebClient;
        },

        getStatusVariant() {
            const status = this.teamioBot.knowledgeStatus;
            const urlCount = status.crawledPages?.length || status.urls?.length || 0;
            const docsIndexed = status.docsIndexed || 0;
            
            // If nothing has been attempted
            if (urlCount === 0 && docsIndexed === 0) {
                return 'secondary'; // Not Configured
            }
            
            // If processing was attempted
            if (status.status === 'error') {
                return 'danger'; // Error
            }
            
            if (docsIndexed > 0) {
                return 'success'; // Successfully indexed content
            } else {
                return 'warning'; // Attempted but no content indexed
            }
        },

        getStatusText() {
            const status = this.teamioBot.knowledgeStatus;
            const urlCount = status.crawledPages?.length || status.urls?.length || 0;
            const docsIndexed = status.docsIndexed || 0;
            
            // If nothing has been attempted
            if (urlCount === 0 && docsIndexed === 0) {
                return 'Not Configured';
            }
            
            // If processing was attempted
            if (status.status === 'error') {
                return 'Error';
            }
            
            if (docsIndexed > 0) {
                return 'Active';
            } else {
                return 'No Content Found'; // Attempted but no content indexed
            }
        },

        getDocsIndexedClass() {
            const docsIndexed = this.teamioBot.knowledgeStatus.docsIndexed || 0;
            const urlCount = this.teamioBot.knowledgeStatus.crawledPages?.length || this.teamioBot.knowledgeStatus.urls?.length || 0;
            
            if (docsIndexed > 0) {
                return 'text-success'; // Green for successful indexing
            } else if (urlCount > 0) {
                return 'text-warning'; // Orange for attempted but no content
            } else {
                return 'text-muted'; // Gray for not configured
            }
        },

        getDocsIndexedText() {
            const docsIndexed = this.teamioBot.knowledgeStatus.docsIndexed || 0;
            const urlCount = this.teamioBot.knowledgeStatus.crawledPages?.length || this.teamioBot.knowledgeStatus.urls?.length || 0;
            
            if (docsIndexed > 0) {
                return `${docsIndexed} documents`;
            } else if (urlCount > 0) {
                return `0 (${urlCount} URL${urlCount === 1 ? '' : 's'} processed)`;
            } else {
                return '0';
            }
        },

        async init() {
            // Only proceed if user is authorized to see this page
            if (!this.allowedToSee) {
                console.warn('❌ User not authorized to view SuperAdmin page');
                this.pageLoading = false;
                return;
            }

            this.pageLoading = true;

            let employee = {role : {RoleID: 4, Role: 'Employee'}};
            this.customerEmployees.push(employee);

            try {
                console.log('🔄 Calling getCustomers...');
                let res = await this.getCustomers();

                console.log('📦 getCustomers response:', {
                    status: res?.status,
                    dataType: typeof res?.data,
                    isArray: Array.isArray(res?.data),
                    dataLength: Array.isArray(res?.data) ? res.data.length : 'N/A',
                    firstItem: Array.isArray(res?.data) && res.data.length > 0 ? res.data[0] : null,
                    fullResponse: res
                });

                if (res && res.status === 200) {
                    // Handle both possible response structures
                    if (res.data && Array.isArray(res.data)) {
                        this.customers = res.data;
                        console.log('✅ Customers loaded successfully:', this.customers.length);
                    } else if (res.data && res.data.result && Array.isArray(res.data.result)) {
                        this.customers = res.data.result;
                        console.log('✅ Customers loaded from result property:', this.customers.length);
                    } else {
                        console.error('❌ Unexpected response structure:', res.data);
                        this.customers = [];
                    }
                } else {
                    console.error('❌ Non-200 status code:', res?.status);
                    this.customers = [];
                }
            } catch (error) {
                console.error('❌ Failed to load customers:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response,
                    stack: error.stack
                });
                // Continue initialization even if customers fail to load
                this.customers = [];
            } finally {
                this.pageLoading = false;
            }
        },

        changeSelectOption(field, index, value) {
            Vue.set(field, index, value);
        },

        async startCustomerAdd() {
            this.showCustomerAdd = true;
            
            // Initialize with one office by default
            if (this.offices.length === 0) {
                this.offices.push(this.createEmptyOffice(1));
            }
            
            // Try to initialize Stripe
            await this.initializeStripe();
            
            // If Stripe is not available, automatically skip billing for all offices
            if (!this.stripe) {
                this.offices.forEach(office => {
                    office.skipStripeCreation = true;
                });
            }
        },

        createEmptyOffice(officeNumber) {
            return {
                officeName: this.customerName ? `${this.customerName} - Office ${officeNumber}` : `Office ${officeNumber}`,
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                officePhone: '',
                employees: [],
                responsibilities: {
                    overdueRecall: null,
                    unscheduledTreatment: null,
                    brokenAppointment: null,
                    outstandingClaims: null,
                    outstandingPreAuth: null,
                    processedPreAuth: null,
                    patientBalances: null
                },
                // Billing fields
                skipStripeCreation: false,
                selectedPlan: null,
                billingEmail: '',
                trialDays: 14, // Default 14-day trial
                billingNotes: '',
                collapsed: false // For UI collapsible state
            };
        },

        addEmployeeToOffice(officeIndex) {
            let employee = {
                name: '',
                email: '',
                role: {RoleID: 4, Role: 'Employee'},
                odUserNum: ''
            };
            this.offices[officeIndex].employees.push(employee);
        },

        removeEmployeeFromOffice(officeIndex, employeeIndex) {
            this.offices[officeIndex].employees.splice(employeeIndex, 1);
        },

        copyFromOffice1(targetOfficeIndex) {
            if (targetOfficeIndex === 0 || this.offices.length === 0) return;
            
            const office1 = this.offices[0];
            const targetOffice = this.offices[targetOfficeIndex];
            
            // Copy address information
            targetOffice.address1 = office1.address1;
            targetOffice.address2 = office1.address2;
            targetOffice.city = office1.city;
            targetOffice.state = office1.state;
            targetOffice.zip = office1.zip;
            targetOffice.officePhone = office1.officePhone;
            
            // Copy employees (deep copy to avoid reference issues)
            targetOffice.employees = JSON.parse(JSON.stringify(office1.employees));
            
            // Copy responsibilities
            targetOffice.responsibilities = JSON.parse(JSON.stringify(office1.responsibilities));
            
            this.$toast.success(`Copied information from Office 1 to Office ${targetOfficeIndex + 1}`);
        },

        assignAllResponsibilities(officeIndex) {
            const office = this.offices[officeIndex];
            
            if (!office.employees || office.employees.length === 0) {
                this.$toast.error(`Please add at least one employee to Office ${officeIndex + 1} first`);
                return;
            }
            
            // Assign first employee to all responsibilities
            const firstEmployee = office.employees[0];
            
            office.responsibilities.overdueRecall = firstEmployee;
            office.responsibilities.unscheduledTreatment = firstEmployee;
            office.responsibilities.brokenAppointment = firstEmployee;
            office.responsibilities.outstandingClaims = firstEmployee;
            office.responsibilities.outstandingPreAuth = firstEmployee;
            office.responsibilities.processedPreAuth = firstEmployee;
            office.responsibilities.patientBalances = firstEmployee;
            
            this.$toast.success(`Assigned ${firstEmployee.name} to all responsibilities for Office ${officeIndex + 1}`);
        },

        toggleOfficeCollapse(officeIndex) {
            this.offices[officeIndex].collapsed = !this.offices[officeIndex].collapsed;
        },

        copyToClipboard(text, label) {
            // Modern clipboard API
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$toast.success(`${label} copied to clipboard!`);
                }).catch(err => {
                    console.error('Failed to copy:', err);
                    this.$toast.error('Failed to copy to clipboard');
                });
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    document.execCommand('copy');
                    this.$toast.success(`${label} copied to clipboard!`);
                } catch (err) {
                    console.error('Failed to copy:', err);
                    this.$toast.error('Failed to copy to clipboard');
                }
                
                document.body.removeChild(textArea);
            }
        },

        addEmployee() {
            // Legacy method - kept for backwards compatibility
            let employee = {role : {RoleID: 4, Role: 'Employee'}};
            if (this.offices.length > 0) {
                this.offices[0].employees.push(employee);
            }
        },

        async _addCustomer() {
            // Validate customer-level info
            if(this.customerName === "" || this.timezone === "") {                
                this.$toast.error('Please enter customer name and timezone');
                return false;
            }
            
            // Validate offices
            if(this.offices.length === 0) {
                this.$toast.error('Please add at least one office');
                return false;
            }
            
            // Initialize billing defaults for all offices
            this.initializeOfficeDefaults();
            
            // Validate each office
            for (let i = 0; i < this.offices.length; i++) {
                const office = this.offices[i];
                
                // Check if office has employees
                if (!office.employees || office.employees.length === 0) {
                    this.$toast.error(`Office ${i + 1} must have at least one employee`);
                    return false;
                }
                
                // Validate and normalize employee emails
                for (const emp of office.employees) {
                    if (!emp.name || !emp.email) {
                        this.$toast.error(`Please fill in all employee information for Office ${i + 1}`);
                        return false;
                    }
                    
                    // Validate role is assigned
                    if (!emp.role || !emp.role.RoleID) {
                        this.$toast.error(`Please assign a role for employee "${emp.name}" in Office ${i + 1}`);
                        return false;
                    }
                    
                    emp.email = this.normalizeEmailAddress(emp.email);
                    if (!this.isValidEmail(emp.email)) {
                        this.$toast.error(`Please enter a valid email for employee "${emp.name}" in Office ${i + 1}`);
                        return false;
                    }
                }
                
                // Validate responsibilities are assigned
                const resp = office.responsibilities;
                if (!resp.overdueRecall || !resp.unscheduledTreatment || !resp.brokenAppointment ||
                    !resp.outstandingClaims || !resp.outstandingPreAuth || !resp.processedPreAuth || !resp.patientBalances) {
                    this.$toast.error(`Please assign all responsibilities for Office ${i + 1}`);
                    return false;
                }

                // Validate billing setup if not skipped and Stripe is available
                if (!office.skipStripeCreation && this.stripe) {
                    if (!office.selectedPlan) {
                        this.$toast.error(`Office ${i + 1}: Please select a subscription plan or check "Skip billing setup". If you don't see billing options, Stripe may not be configured.`);
                        return false;
                    }
                    
                    // Validate billing email
                    const billingEmail = office.billingEmail || (office.employees && office.employees[0] ? office.employees[0].email : '');
                    if (!billingEmail) {
                        this.$toast.error(`Please provide a billing email for Office ${i + 1}`);
                        return false;
                    }
                    
                    if (!this.isValidEmail(billingEmail)) {
                        this.$toast.error(`Please enter a valid billing email for Office ${i + 1}`);
                        return false;
                    }

                    // Set the billing email if it's empty
                    if (!office.billingEmail && office.employees && office.employees[0]) {
                        office.billingEmail = office.employees[0].email;
                    }
                }
            }
            
            // A logo URL that is definitely not an image (or a web page) must not ride into
            // teamio.sites — it would render as a broken image on every patient surface.
            if (logoBlocksSave(this.createLogoCheck)) {
                this.$toast.error('Logo URL is not an image — fix the link, or clear the field to create the customer without a logo.');
                return;
            }

            // Clear previous progress and set loading state
            this.customerCreationProgress = [];
            this.addCustomerLoading = true;
            
            try {
                let payload = {
                    customerName: this.customerName,
                    logoUrl: this.logoUrl,
                    numberOfOffices: this.numberOfOffices,
                    timezone: this.timezone,
                    country: this.country,
                    offices: this.offices
                };
                
                console.log(`payload: ${JSON.stringify(payload)}`);
                let custAddResults = await this.addCustomer(payload);
                console.log(`Results: ${JSON.stringify(custAddResults)}`);
                
                // Parse and display progress from backend response
                if (custAddResults && custAddResults.data) {
                    let progressMessage = '';
                    
                    // Handle different response structures
                    if (typeof custAddResults.data === 'string') {
                        progressMessage = custAddResults.data;
                    } else if (custAddResults.data.message) {
                        progressMessage = custAddResults.data.message;
                    }
                    
                    // Parse the HTML-formatted progress message
                    if (progressMessage) {
                        const steps = progressMessage.split('<br>').filter(step => step.trim());
                        
                        // Extract IDs from progress message
                        this.createdCustomerInfo.customerID = null;
                        this.createdCustomerInfo.siteIDs = [];
                        
                        steps.forEach(step => {
                            // Extract Customer ID
                            const customerMatch = step.match(/Customer ID: ([a-f0-9-]+)/i);
                            if (customerMatch) {
                                this.createdCustomerInfo.customerID = customerMatch[1];
                            }
                            
                            // Extract Site IDs - look for patterns like "Site 1 ID: xxx" or "Office 1 - SiteID: xxx"
                            const siteMatch = step.match(/(?:Site|Office)\s+(\d+).*?(?:ID|SiteID):\s*([a-f0-9-]+)/i);
                            if (siteMatch) {
                                const officeNum = parseInt(siteMatch[1]);
                                const siteID = siteMatch[2];
                                
                                // Try to get office name from the offices array
                                const officeName = this.offices[officeNum - 1]?.name || `Office ${officeNum}`;
                                
                                this.createdCustomerInfo.siteIDs.push({
                                    id: siteID,
                                    name: officeName,
                                    officeNum: officeNum
                                });
                            }
                        });
                        
                        this.customerCreationProgress = steps.map(step => {
                            // Determine status based on content
                            let status = 'success';
                            if (step.includes('❌') || step.includes('ERROR') || step.includes('FAILED')) {
                                status = 'error';
                            } else if (step.includes('⚠️') || step.includes('WARNING')) {
                                status = 'pending';
                            }
                            
                            return {
                                message: step,
                                status: status
                            };
                        });
                    }
                }
                
                if(custAddResults.status === 200) {
                    this.logs = `${this.logs}\n\n************************************************\nSuccess\n\n`;
                    this.$toast.success(`Customer added successfully! Created ${this.numberOfOffices} site(s).`);
                    
                    // Clear form inputs but keep progress visible for review
                    this.customerName = '';
                    this.logoUrl = '';
                    this.numberOfOffices = 1;
                    this.offices = [];
                    
                    // Refresh customer list
                    try {
                        let res = await this.getCustomers();
                        if (res && res.status === 200) {
                            if (Array.isArray(res.data)) {
                                this.customers = res.data;
                            } else if (res.data && res.data.result && Array.isArray(res.data.result)) {
                                this.customers = res.data.result;
                            }
                        }
                    } catch (error) {
                        console.error('Failed to refresh customer list:', error);
                    }
                } else {
                    // Handle error response
                    this.$toast.error('Failed to add customer. Please check the progress details below.');
                }
                
                this.logs = `${this.logs} Results from Customer Add: ${JSON.stringify(custAddResults.data)}`;
            } catch (error) {
                console.error('Error adding customer:', error);
                this.$toast.error('Failed to add customer. Please try again.');
                this.logs = `${this.logs}\n\nError adding customer: ${error.message || error}`;
                
                // Add error to progress display
                this.customerCreationProgress.push({
                    message: `❌ Error: ${error.message || error}`,
                    status: 'error'
                });
            } finally {
                // Clear loading state
                this.addCustomerLoading = false;
            }
        },

        async _sqsRedrive() {
            this.sqsRedriveLoading = true;
            try {
                this.logs = `${this.logs} <P> ********************************************Starting Redrive from ${this.selectedDlq} to ${this.selectedQueue}`;
                let res = await this.sendAction(
                    {
                        siteID: this.siteID, 
                        action: "SQS Redrive",
                        selectedDlq: this.selectedDlq,
                        selectedQueue: this.selectedQueue,
                    });
                this.logs = `${this.logs} <P> ********************************************Results from last action: <P>${JSON.stringify(res)}`;
                this.$toast.success('SQS Redrive completed successfully!');
            } catch(error) {
                console.error(`Error with SQS Redrive: ${error}`);
                this.logs = `${this.logs} <P> ********************************************Results from last action: <P>${JSON.stringify(error)}`;
                this.$toast.error('SQS Redrive failed. Please try again.');
            } finally {
                this.sqsRedriveLoading = false;
            }
        },

        removeEmployee(index) {
            this.customerEmployees.splice(index,1);
        },

        checkArray(my_arr) {
            for(var i=0;i<my_arr.length;i++) {
                if(my_arr[i] === "")   
                    return false;
            }
            return true;
        },

        logActivity(message, level = 'INFO') {
            const timestamp = moment().format('YYYY-MM-DD HH:mm:ss');
            const formattedMessage = `[${timestamp}] [${level}] ${message}`;
            this.logs = `${this.logs}${this.logs ? '\n' : ''}${formattedMessage}`; // Add newline if logs not empty
        },

        normalizeEmailAddress(email) {
            if (!email || typeof email !== 'string') return '';
            return email.trim().toLowerCase();
        },
        isValidEmail(email) {
            if (!email || typeof email !== 'string') return false;
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },

        async _sendAction(props) {
            const action = this.selectedAction[props.index-1];
            const siteID = props.row.SiteID;
            const i = props.index - 1;

            if(!action || action === '') {
                this.$toast.error('Please select an action to perform before submitting');
                return false;
            }

            // Handle Purchase Number action separately by showing modal
            if (action === 'Purchase Number') {
                const customerData = this.customers.find(c => c.SiteID === siteID);
                this.existingPhoneNumber = customerData ? customerData.PhoneNumber : null;
                this.siteIdForPurchase = siteID;
                this.$bvModal.show('purchase-number-modal');
                return; 
            }

            // Handle Verify Email Address action separately by showing modal
            if (action === 'Verify Email Address') {
                this.siteIdForEmailVerification = siteID;
                this.customerNameForEmailVerification = props.row.CustomerName;
                this.$bvModal.show('verify-email-modal');
                // Try to get existing verified emails
                this.checkExistingVerifiedEmails();
                return;
            }

            // Handle Pause action with confirmation
            if (action === 'Pause') {
                const customerData = this.customers.find(c => c.SiteID === siteID);
                const isActive = customerData ? customerData.IsSiteActive : null;
                
                if (isActive === 0) {
                    this.$toast.warning(`Site ${siteID} is already inactive. No action needed.`);
                    this.logActivity(`Pause skipped for Site ID ${siteID} - site is already inactive`, 'WARNING');
                    return;
                }

                // Show confirmation dialog for pause
                this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to PAUSE site ${siteID} (${props.row.CustomerName})?\n\nPAUSE will:\n• Mark site and customer as inactive (prevents login)\n• Delete SQS queues (saves AWS costs)\n• PRESERVE all data: MySQL schema, Firestore, SendGrid, Twilio\n\nThis is DIFFERENT from De-Activate which DELETES everything permanently.\n\nUse PAUSE for temporary suspension (customer can be re-activated later with data intact).\nUse DE-ACTIVATE for permanent removal (all data deleted).`,
                    {
                        title: 'Confirm Site Pause (Temporary)',
                        size: 'lg',
                        buttonSize: 'sm',
                        okVariant: 'warning',
                        okTitle: 'Yes, Pause Site',
                        cancelTitle: 'Cancel',
                        footerClass: 'p-2',
                        hideHeaderClose: false,
                        centered: true
                    }
                ).then(value => {
                    if (value) {
                        // User confirmed, proceed with pause
                        this.logActivity(`User confirmed PAUSE for Site ID ${siteID}. Proceeding...`);
                        this.performGenericAction(props, action, siteID, i);
                    } else {
                        this.logActivity(`User cancelled PAUSE for Site ID ${siteID}`, 'INFO');
                    }
                }).catch(err => {
                    console.error('Error with confirmation dialog:', err);
                });
                return;
            }

            // Handle Re-Activate action with confirmation
            if (action === 'Re-Activate') {
                const customerData = this.customers.find(c => c.SiteID === siteID);
                const isActive = customerData ? customerData.IsSiteActive : null;
                
                if (isActive === 1) {
                    this.$toast.warning(`Site ${siteID} is already active. No action needed.`);
                    this.logActivity(`Re-Activate skipped for Site ID ${siteID} - site is already active`, 'WARNING');
                    return;
                }

                // Show confirmation dialog for reactivation
                this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to reactivate site ${siteID} (${props.row.CustomerName})?\n\nThis will:\n• Reactivate the site and all employees\n• Recreate the database schema (empty)\n• Recreate SQS queues\n\nNote: All historical data will be permanently lost.`,
                    {
                        title: 'Confirm Site Reactivation',
                        size: 'lg',
                        buttonSize: 'sm',
                        okVariant: 'success',
                        okTitle: 'Yes, Reactivate',
                        cancelTitle: 'Cancel',
                        footerClass: 'p-2',
                        hideHeaderClose: false,
                        centered: true
                    }
                ).then(value => {
                    if (value) {
                        // User confirmed, proceed with reactivation
                        this.logActivity(`User confirmed reactivation for Site ID ${siteID}. Proceeding...`);
                        this.performGenericAction(props, action, siteID, i);
                    } else {
                        this.logActivity(`User cancelled reactivation for Site ID ${siteID}`, 'INFO');
                    }
                }).catch(err => {
                    console.error('Error with confirmation dialog:', err);
                });
                return;
            }

            // Original logic for other actions with added logging
            this.performGenericAction(props, action, siteID, i);
        },

        async performGenericAction(props, action, siteID, i) {
            this.logActivity(`Initiating action '${action}' for Site ID: ${siteID}, Name: ${props.row.CustomerName}`);
            Vue.set(this.siteActionLoading, i, true);
            Vue.set(this.siteActionResult, i, null); 

            try {
                const payload = {
                    siteID: siteID,
                    action: action,
                    ...(action === 'Send Announcement' && this.extra.announcementText.length > i && { announcementText: this.extra.announcementText[i] })
                };
                console.log('Frontend: Sending generic action request', payload);
                let res = await this.sendAction(payload);

                Vue.set(this.siteActionLoading, i, null);
                
                // Enhanced logging - display detailed Lambda logs for BOTH success and error responses
                if (res && res.data && res.data.detailedLogs) {
                    this.logActivity(`\n=== DETAILED LAMBDA EXECUTION LOGS ===`);
                    res.data.detailedLogs.forEach(logEntry => {
                        this.logs += `\n${logEntry}`;
                    });
                    
                    if (res.data.executionSummary) {
                        this.logActivity(`\n=== EXECUTION SUMMARY ===`);
                        this.logActivity(`Action: ${res.data.executionSummary.action}`);
                        this.logActivity(`Site ID: ${res.data.executionSummary.siteID}`);
                        this.logActivity(`Status: ${res.data.executionSummary.status}`);
                        this.logActivity(`Timestamp: ${res.data.executionSummary.timestamp}`);
                        this.logActivity(`Total Log Entries: ${res.data.executionSummary.logCount}`);
                    }
                    this.logActivity(`=== END LAMBDA LOGS ===\n`);
                }
                
                if(res && (!res.status || res.status === 200)) { // Consider successful if status OK or missing
                    Vue.set(this.siteActionResult, i, "success");
                    this.logActivity(`✅ Action '${action}' for Site ID ${siteID} completed successfully!`, 'SUCCESS');
                    
                    // Special handling for Re-Activate success
                    if (action === 'Re-Activate') {
                        this.$toast.success(`Site ${siteID} has been successfully reactivated!`);
                        // Refresh the customer list to show updated status
                        setTimeout(() => {
                            this.init();
                        }, 1000);
                    }
                } else {
                    Vue.set(this.siteActionResult, i, "error");
                    this.logActivity(`❌ Action '${action}' for Site ID ${siteID} failed!`, 'ERROR');
                    
                    // Display error details if available
                    const errorMsg = (res && res.data && res.data.error) || (res && res.data && res.data.message) || (res && res.message) || 'Unknown error';
                    this.logActivity(`Error Details: ${errorMsg}`, 'ERROR');
                    // Let global error display handle this one unless specifically told otherwise
                }                
            } catch (err) {
                Vue.set(this.siteActionLoading, i, null);
                Vue.set(this.siteActionResult, i, "error");
                const errorMsg = (err.response && err.response.data && err.response.data.message) || err.message || 'Network or unknown error';
                this.logActivity(`Action '${action}' for Site ID ${siteID} failed. Error: ${errorMsg}`, 'ERROR');
                console.error(`Error sending super admin action: ${err}`);                         
            }
        },

        resetPurchaseModal() {
            this.siteIdForPurchase = null;
            this.areaCodeInput = '';
            this.availableNumbers = [];
            this.selectedNumberToPurchase = null;
            this.purchaseModalBusy = false;
            this.purchaseModalError = '';
            this.existingPhoneNumber = null;
        },

        async searchTwilioNumbers() {
            const payload = { siteID: this.siteIdForPurchase, action: 'SEARCH_PHONE_NUMBER', areaCode: this.areaCodeInput };
            this.logActivity(`Initiating Twilio number search for Site ID: ${this.siteIdForPurchase}, Area Code: ${this.areaCodeInput}`);
            console.log('Frontend: Sending search request', payload);

            if (!this.areaCodeInput) return;
            this.purchaseModalBusy = true;
            this.purchaseModalError = '';
            this.availableNumbers = [];
            this.selectedNumberToPurchase = null;
            try {
                const res = await this.sendAction(payload);
                console.log('Frontend: Received response from sendAction:', res); // Log the raw response

                // Check if the response structure indicates a handled error from the backend
                // Adjust these checks based on the actual structure returned by your Vuex action on error
                let backendErrorMessage = null;
                if (res && res.status && res.status !== 200 && res.data && res.data.message) { // Check for non-200 status with message
                    backendErrorMessage = res.data.message;
                } else if (res && res.data && res.data.message && (!res.status || res.status === 200)) { // Check for 200 status but with an error message in data
                    backendErrorMessage = res.data.message;
                } else if (res && typeof res.message === 'string' && res.message.length > 0 && (!res.data || !res.data.numbers)) { // Check for a direct message property (and not a success response)
                    backendErrorMessage = res.message;
                }

                if (backendErrorMessage) {
                    console.warn('Frontend: Backend indicated an error:', backendErrorMessage);
                    this.logActivity(`Search failed: ${backendErrorMessage}`, 'ERROR');
                    this.purchaseModalError = backendErrorMessage;
                    // Log the error, the global display should be handled by the Vuex action
                    this.logActivity(`Search failed: ${this.purchaseModalError}`, 'ERROR');
                } else {
                    // Assuming backend returns { success: true, data: { numbers: [...] } } on actual success
                     if (res && res.data && Array.isArray(res.data.numbers)) { // Check if numbers is an array
                         this.availableNumbers = res.data.numbers;
                         this.logActivity(`Search successful. Found ${this.availableNumbers.length} numbers.`);
                    } 
                    // If no numbers found, log that
                    else if (res && res.data && Array.isArray(res.data.numbers) && res.data.numbers.length === 0) {
                        this.logActivity(`Search successful. No available numbers found for area code ${this.areaCodeInput}.`);
                    }
                }

            } catch (err) { // This catch block now handles network errors or truly unhandled backend exceptions
                console.error('Error searching Twilio numbers:', err);
                console.error('Full error object in catch:', JSON.stringify(err, Object.getOwnPropertyNames(err))); // Log full error structure
                console.error('Error response object:', err.response); // Log response object
                // Try to get specific message from backend response
                if (err.response && err.response.data && err.response.data.message) {
                    this.purchaseModalError = err.response.data.message;
                } else {
                    this.purchaseModalError = err.message || 'An error occurred while searching for numbers.';
                }
                // Log the error, the global display should be handled by the Vuex action
                this.logActivity(`Search failed: ${this.purchaseModalError}`, 'ERROR');
            } finally {
                this.purchaseModalBusy = false;
            }
        },

        async purchaseTwilioNumber() {
            const payload = { siteID: this.siteIdForPurchase, action: 'PURCHASE_PHONE_NUMBER', phoneNumber: this.selectedNumberToPurchase };
            this.logActivity(`Initiating purchase of ${this.selectedNumberToPurchase} for Site ID: ${this.siteIdForPurchase}`);
            console.log('Frontend: Sending purchase request', payload);

            if (!this.selectedNumberToPurchase) return;
            this.purchaseModalBusy = true;
            this.purchaseModalError = '';
            try {
                const res = await this.sendAction(payload);
                this.$toast.success(`Successfully purchased ${this.selectedNumberToPurchase}`);
                this.logActivity(`Successfully purchased ${this.selectedNumberToPurchase} for Site ID: ${this.siteIdForPurchase}`);
                this.$bvModal.hide('purchase-number-modal');
                // Simple refresh:
                this.init(); // Re-fetch customers to show the new number

            } catch (err) {
                console.error('Error purchasing Twilio number:', err);
                console.error('Full error object in catch:', JSON.stringify(err, Object.getOwnPropertyNames(err))); // Log full error structure
                console.error('Error response object:', err.response); // Log response object
                // Try to get specific message from backend response
                if (err.response && err.response.data && err.response.data.message) {
                    this.purchaseModalError = err.response.data.message;
                } else {
                     this.purchaseModalError = err.message || 'An error occurred during purchase.';
                }
                // Log the error, the global display should be handled by the Vuex action
                this.logActivity(`Purchase failed: ${this.purchaseModalError}`, 'ERROR');
            } finally {
                this.purchaseModalBusy = false;
            }
        },

                 resetEmailVerificationModal() {
             this.siteIdForEmailVerification = null;
             this.customerNameForEmailVerification = null;
             this.verificationType = 'domain';
             this.domainToVerify = '';
             this.emailToVerify = '';
             this.verificationModalBusy = false;
             this.verificationModalError = '';
             this.currentVerifiedEmails = [];
             this.verificationStatus = null;
             this.verificationSuccessMessage = '';
             this.dnsRecordsToShow = [];
             this.domainBeingVerified = '';
             this.domainId = null;
             this.showSingleSenderInstructions = false;
             this.emailBeingVerified = '';
             this.showDnsEmailForm = false;
             this.dnsEmailRecipients = '';
             this.sendingDnsEmail = false;
         },

         resetForNewVerification() {
             this.verificationType = 'domain';
             this.domainToVerify = '';
             this.emailToVerify = '';
             this.verificationModalError = '';
             this.verificationStatus = null;
             this.verificationSuccessMessage = '';
             this.dnsRecordsToShow = [];
             this.domainBeingVerified = '';
             this.domainId = null;
             this.showSingleSenderInstructions = false;
             this.emailBeingVerified = '';
         },

        // ========== Site Management UI Methods ==========
        openSiteManagement(site) {
            this.selectedSiteForManagement = site;
            this.siteEmailDomains = [];
            this.selectedSiteA2PStatus = null;
            this.selectedSiteLiveA2P = null;
            this.selectedSiteLiveA2PLoading = false;
            this.siteDeactivationStatus = null;
            this.siteManagementTabIndex = 0;
            this.showSiteManagementPanel = true;
            // Reset test file write state for new site and auto-load categories
            this.testFileWrite = { patNum: '', pdfCategory: '', categories: [], loading: false, categoriesLoaded: false, categoriesLoading: false };
            this.loadTestFileWriteCategories();
            // Exe table overrides are per site (and stored on the HQ row for multi-clinic) — reset
            // before loading so the previous practice's lists can never be seen against this one.
            this.resetExeTableOverrides();
            this.loadExeTableOverrides();
            // Restart App / Set Debug Mode: disabled until this site's gate verdict arrives.
            this.resetExeRestart();
            this.loadExeRestartStatus();
            this.logActivity(`Opened management for site: ${site.CustomerName} (${site.SiteID})`);
            
            // Reset Stripe data when switching sites
            this.resetStripeData();
            
            // Initialize Stripe if not already done
            this.initializeStripe();
            
            // Fetch A2P status for this site
            this.fetchSiteA2PStatus(site.SiteID);
            // Fetch deactivation status for this site
            this.fetchDeactivationStatus(site.SiteID);
        },

        closeSiteManagement() {
            this.selectedSiteForManagement = null;
            this.selectedSiteA2PStatus = null;
            this.siteDeactivationStatus = null;
            this.showSiteManagementPanel = false;
            this.expandedSiteId = null;
            this.siteEmailDomains = [];
            this.siteVerifiedEmails = [];
            this.featuresLastSaved = null;
            this.panelDnsRecords = [];
            this.panelDnsDomain = '';
            this.panelDnsDomainId = null;
            this.showPanelDnsEmailForm = false;
            this.panelDnsEmailRecipients = '';
            this.resetExeTableOverrides();
            this.resetExeRestart();
            this.resetSiteFeatures();
            this.exeRefreshReminder = { siteID: null, pending: [], sending: false, result: null };
            this.practicePulseInit = { siteID: null, running: false, data: null, error: null };
        },

        resetSiteFeatures() {
            const keys = Object.keys(this.siteFeatures);
            keys.forEach(k => {
                this.siteFeatures[k] = false;
                this.featureSaving[k] = false;
            });
            this.featureReadiness = null;
            this.junkCellsExpanded = false;
            this.formTemplatesSeed = { seeding: false, results: null, error: '' };
            // Imaging map: reset ONLY when the site actually changes (DefNums are per site). "Asked each time"
            // means per site visit — a same-site reload (after Load / Apply, a Features-tab click) keeps the
            // operator's picks, and loadFormsImagingCategories merges them with the fresh list. Bumping the
            // sequence drops any in-flight category load so its DefNums can't land on the next site's card.
            const managedSiteID = this.selectedSiteForManagement ? this.selectedSiteForManagement.SiteID : null;
            if (!managedSiteID || this.formsImaging.siteID !== managedSiteID) {
                this.formsImaging = { siteID: null, loading: false, error: '', categories: [], consentCandidates: [], map: emptyCategoryMap() };
                this.formsImagingApply = { previewing: false, applying: false, preview: null, error: '' };
                this.formsImagingRequestSeq++;
            }
            this.formMessagesSeed = { previewing: false, applying: false, preview: null, error: '' };
            this.procMappingsSeed = { previewing: false, applying: false, preview: null, error: '' };
            this.reminderTemplatesSeed ={ seeding: false, results: null, error: '' };
            this.txPresenterSeed = { seeding: false, results: null, error: '' };
            // Must reset with the seed state: a stale result carries a per-site sending
            // ADDRESS, so leaving it would show one practice's sender against another's card.
            this.platformSender = { enabling: false, results: null, error: '' };
            this.reviewsSeed = { seeding: false, results: null, error: '' };
            // Insurance Verification: census results and the provider selection are per-site
            // and MUST NOT bleed across sites — a stale census would misreport another
            // practice's billing risk.
            this.ivSeed = { seeding: false, results: null, error: '' };
            this.ivCensus = { loading: false, results: null, error: '' };
            this.ivBillingProvider = { provNum: null, saving: false, error: '' };
            // Same reason: one practice's override text must never sit in another practice's form.
            this.ivOverride = { open: false, mode: 'organization', organizationName: '', firstName: '', lastName: '', saving: false, error: '' };
            this.wikiTemplateChecks = {}; // ephemeral; clear so checks never bleed across sites
            // "Refresh Exe Settings" reminder: dropped only when the SITE changes. loadSiteFeatures runs
            // this on every Features-tab click, and the reminder must survive that until it is sent.
            if (!managedSiteID || this.exeRefreshReminder.siteID !== managedSiteID) {
                this.exeRefreshReminder = { siteID: null, pending: [], sending: false, result: null };
            }
            // Practice Pulse enablement result: same rule — it explains the reminder above it.
            if (!managedSiteID || this.practicePulseInit.siteID !== managedSiteID) {
                this.practicePulseInit = { siteID: null, running: false, data: null, error: null };
            }
            this.siteLogoEdit = { url: '', saving: false, error: '', preview: { loaded: undefined, naturalWidth: 0, naturalHeight: 0 } };
        },

        async loadSiteFeatures() {
            if (!this.selectedSiteForManagement) return;
            this.featuresLoading = true;
            this.resetSiteFeatures();
            try {
                const siteID = this.selectedSiteForManagement.SiteID;
                const response = await this.sendAction({ siteID, action: 'GET_SITE_FEATURE_STATUS' });
                if (response && response.data) {
                    const features = response.data.features || {};
                    for (const key of Object.keys(this.siteFeatures)) {
                        if (Object.prototype.hasOwnProperty.call(features, key)) {
                            this.siteFeatures[key] = features[key] === true;
                        }
                    }
                    this.featureReadiness = response.data.readiness || null;
                    // Forms card shown → (re)load this site's OD imaging categories. Same site: the operator's
                    // picks are kept and merged with the fresh list. The FORMS_PAID_FOR watcher covers switching
                    // Forms on (ensureFormsImagingLoaded no-ops because this call already claimed the site).
                    if (this.siteFeatures.FORMS_PAID_FOR) this.loadFormsImagingCategories();
                    // Seed the Practice-logo editor from the LIVE row (readiness.site.logoUrl) —
                    // getCustomers/selectedSiteForManagement never carries LogoUrl.
                    this.siteLogoEdit.url = this.siteLogoCurrent;
                    this.siteLogoEdit.error = '';

                    // Pre-seed the (ephemeral) companion-wiki checkboxes as a fresh
                    // reactive object so v-model + the red-note v-if stay reactive
                    // (Vue 2 won't track keys added after the fact).
                    const wikiItems = (this.featureReadiness && this.featureReadiness.forms
                        && this.featureReadiness.forms.wikiTemplates
                        && this.featureReadiness.forms.wikiTemplates.items) || [];
                    const checks = {};
                    wikiItems.filter(it => it.present).forEach(it => { checks[`wiki-${it.name}`] = false; });
                    this.wikiTemplateChecks = checks;

                    // Seed the inline billing-provider picker from the SAVED value so the
                    // Save button stays disabled until the operator actually changes it.
                    const ivReady = this.featureReadiness && this.featureReadiness.insuranceVerification;
                    this.ivBillingProvider.provNum = (ivReady && ivReady.billingProviderNum != null)
                        ? ivReady.billingProviderNum
                        : null;

                    // Same for the provider-name override form: show what is SAVED (blank when none).
                    // (resetSiteFeatures collapses the block on every reload, so a save collapses it;
                    // the header badge then shows the saved value.)
                    const savedOverride = (ivReady && ivReady.providerNameOverride) || null;
                    this.ivOverride.mode = savedOverride && savedOverride.firstName ? 'person' : 'organization';
                    this.ivOverride.organizationName = (savedOverride && savedOverride.organizationName) || '';
                    this.ivOverride.firstName = (savedOverride && savedOverride.firstName) || '';
                    this.ivOverride.lastName = (savedOverride && savedOverride.lastName) || '';
                    this.ivOverride.error = '';
                }
            } catch (err) {
                console.error('Error loading site features:', err);
            } finally {
                this.featuresLoading = false;
            }
        },

        async toggleFeature(property, newValue) {
            if (!this.selectedSiteForManagement) return;
            // Schedule - Chart Audit cannot be switched ON for an office below Teamio app 3.0.61 (the
            // switch is disabled; this covers any other path to it). OFF is never refused.
            if (property === SCHEDULE_ACCESS_SETTING && newValue === true && !this.scheduleSwitchGate.allowed) {
                this.siteFeatures[property] = false;
                this.$bvToast.toast(this.scheduleSwitchGate.message, { title: 'Not turned on', variant: 'warning', solid: true, autoHideDelay: 5000 });
                return;
            }
            // Practice Pulse ON = the one-click enablement: the backend saves the flag, builds today's
            // board and tells the exe, answering per step. A bare /upsert-settings here would leave the
            // board unbuilt and the exe on the old setting. OFF saves through the generic path below.
            if (property === 'PRACTICE_PULSE_PAID_FOR' && newValue === true) {
                this.featureSaving[property] = true;
                try {
                    const ok = await this.initializePracticePulse();
                    // Revert on failure. null = the answer landed after a site switch: the switch on
                    // screen now belongs to another practice, so leave it alone.
                    if (ok === false) this.siteFeatures[property] = !newValue;
                } finally {
                    this.featureSaving[property] = false;
                }
                return;
            }
            this.featureSaving[property] = true;
            try {
                const siteID = this.selectedSiteForManagement.SiteID;
                const payload = {
                    siteID: siteID,
                    settings: {
                        [property]: newValue ? 'TRUE' : 'FALSE'
                    }
                };
                await axios.post('/upsert-settings', payload, { crossdomain: true });
                this.featuresLastSaved = new Date().toLocaleTimeString();
                // Saved — but the office's exe applies this one itself. Say what to do next.
                if (needsExeRefresh(property)) {
                    this.noteExeSettingChanged(siteID, property);
                }
            } catch (err) {
                console.error(`Error toggling ${property}:`, err);
                // Revert on failure
                this.siteFeatures[property] = !newValue;
                // The server's own wording when it gave one (extractApiError): /upsert-settings refuses
                // Schedule - Chart Audit ON below Teamio app 3.0.61 with a 400 and a message, and axios's
                // "Request failed with status code 400" tells the operator nothing. No server message
                // (a network error, an HTML body) keeps the old text.
                this.$bvToast.toast(extractApiError(err, `Failed to update ${property}: ${err && err.message}`), {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 3000
                });
            } finally {
                this.featureSaving[property] = false;
            }
        },

        /**
         * A switch the office's exe applies itself (Schedule View live updates, Practice Pulse) was
         * just saved. Raise the reminder for THIS site and say so at once — the banner sits at the top
         * of a long tab, and the switch that was flipped may be far below it.
         */
        noteExeSettingChanged(siteID, property) {
            const r = this.exeRefreshReminder;
            const pending = r.siteID === siteID ? r.pending : [];
            this.exeRefreshReminder = {
                siteID,
                pending: addPendingExeSetting(pending, property),
                sending: false,
                result: null,
            };
            this.$bvToast.toast(
                `Saved. The office's Teamio application applies this itself — click "Refresh Exe Settings" (top of this tab) so it takes effect now.`,
                { title: 'Next step: Refresh Exe Settings', variant: 'info', solid: true, autoHideDelay: 10000 }
            );
        },

        /**
         * Ask the office's RUNNING exe to re-apply Schedule View / Practice Pulse (teamio-admin
         * 'Refresh Exe Settings'). The backend decides whether it may be sent at all — an exe older
         * than 3.0.56 does not know the command — and its message is shown verbatim either way.
         * The reminder clears ONLY on a successful send.
         */
        async refreshExeSettings() {
            const site = this.selectedSiteForManagement;
            if (!site || this.exeRefreshReminder.sending) return;
            const siteID = site.SiteID;
            const customerName = site.CustomerName;
            const keep = this.exeRefreshReminder.siteID === siteID ? this.exeRefreshReminder.pending : [];
            this.exeRefreshReminder = { siteID, pending: keep, sending: true, result: null };
            // The operator may switch practice while this is in flight; never paint another site.
            const isStale = () => !this.selectedSiteForManagement || this.selectedSiteForManagement.SiteID !== siteID;
            this.logActivity(`Executing "Refresh Exe Settings" for ${customerName}...`);
            try {
                const response = await this.sendAction({ siteID, action: 'Refresh Exe Settings' });
                if (!(response && (response.status === 200 || !response.status) && response.data && typeof response.data === 'object')) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from Refresh Exe Settings'));
                }
                const d = response.data;
                const result = {
                    sent: d.sent === true,
                    message: d.message || (d.sent === true ? 'Sent.' : 'Not sent.'),
                    exeVersion: d.exeVersion || null,
                };
                if (isStale()) return;
                this.exeRefreshReminder = { siteID, pending: result.sent ? [] : keep, sending: false, result };
                if (result.sent) {
                    this.$toast.success(result.message);
                    this.logActivity(`✅ Refresh Exe Settings sent for ${customerName} (exe ${result.exeVersion})`);
                } else {
                    this.$toast.warning(result.message);
                    this.logActivity(`⚠️ Refresh Exe Settings NOT sent for ${customerName}: ${result.message}`, 'WARNING');
                }
            } catch (err) {
                const message = `Couldn't send Refresh Exe Settings: ${extractApiError(err, err && err.message ? err.message : 'Unknown error')}`;
                if (!isStale()) {
                    this.exeRefreshReminder = { siteID, pending: keep, sending: false, result: { sent: false, message, exeVersion: null } };
                }
                this.$toast.error(message);
                this.logActivity(`❌ ${message} (${customerName})`, 'ERROR');
            }
        },

        /**
         * One-click Practice Pulse enablement (2026-09-25) — teamio-admin 'Initialize Practice Pulse'
         * runs three steps in order and answers for each (see @/utils/practicePulseInit.js): the
         * PRACTICE_PULSE_PAID_FOR flag, today's board (built, or left alone if it exists unless
         * `force`), and Refresh Exe Settings (refused on exe < 3.0.56 — a normal answer, not an error).
         * Every step's answer is shown verbatim (practicePulseInitView). Own handler, not quickAction:
         * that one says "completed successfully" for any 200, including a refused exe step.
         *   force=false — the Features-tab switch turned ON (toggleFeature).
         *   force=true  — the System-tab Initialize / Rebuild button; confirmed first, because a
         *                 rebuild empties today's check-in and checkout queues.
         * Returns true on success, false on failure (the switch reverts), null when the answer landed
         * after the operator switched practice (nothing on screen belongs to it any more).
         */
        async initializePracticePulse({ force = false } = {}) {
            const site = this.selectedSiteForManagement;
            if (!site) return false;
            const siteID = site.SiteID;
            if (this.practicePulseInit.running && this.practicePulseInit.siteID === siteID) return false;
            const customerName = site.CustomerName;
            // The operator may switch practice while this is in flight; never paint another site.
            const isStale = () => !this.selectedSiteForManagement || this.selectedSiteForManagement.SiteID !== siteID;

            if (force) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Rebuild today's Practice Pulse board for ${customerName}? This empties the current check-in and checkout queues (saved settings are kept).`,
                    {
                        title: 'Rebuild Practice Pulse board',
                        size: 'md',
                        buttonSize: 'sm',
                        okVariant: 'warning',
                        okTitle: 'Yes, Rebuild',
                        cancelTitle: 'Cancel',
                        footerClass: 'p-2',
                        centered: true
                    }
                );
                if (!confirmed) return false;
                if (isStale()) return null;
            }

            this.practicePulseInit = { siteID, running: true, data: null, error: null };
            this.logActivity(`Executing "${PRACTICE_PULSE_INIT_ACTION}"${force ? ' (rebuild)' : ''} for ${customerName}...`);
            try {
                const payload = { siteID, action: PRACTICE_PULSE_INIT_ACTION };
                if (force) payload.force = true;
                const response = await this.sendAction(payload);
                if (!(response && (response.status === 200 || !response.status) && response.data
                    && typeof response.data === 'object' && response.data.steps && typeof response.data.steps === 'object')) {
                    throw new Error(this.seedActionError(response, `Unexpected response from ${PRACTICE_PULSE_INIT_ACTION}`));
                }
                if (isStale()) return null;
                const data = response.data;
                const lines = describeInitializeSteps(data);
                const exe = lines[2];
                const exeVersion = (data.steps.exe && data.steps.exe.exeVersion) || null;
                const flagSaved = initializeFlagSaved(data);
                const summary = (typeof data.message === 'string' && data.message.trim())
                    ? data.message
                    : lines.map(l => `${l.label}: ${l.message}`).join(' · ');
                this.practicePulseInit = { siteID, running: false, data, error: null };

                if (flagSaved) {
                    this.siteFeatures.PRACTICE_PULSE_PAID_FOR = true;
                    this.featuresLastSaved = new Date().toLocaleTimeString();
                    // The exe applies Practice Pulse itself: keep the reminder up (with the backend's
                    // reason, and its Refresh button) unless this run actually got the refresh sent.
                    const r = this.exeRefreshReminder;
                    const keep = r.siteID === siteID ? r.pending : [];
                    if (initializeNeedsExeReminder(data)) {
                        this.exeRefreshReminder = {
                            siteID,
                            pending: addPendingExeSetting(keep, 'PRACTICE_PULSE_PAID_FOR'),
                            sending: false,
                            result: { sent: false, message: exe.message, exeVersion },
                        };
                    } else {
                        this.exeRefreshReminder = { siteID, pending: [], sending: false, result: { sent: true, message: exe.message, exeVersion } };
                    }
                }

                if (lines.every(l => l.ok)) {
                    this.$toast.success(summary);
                    this.logActivity(`✅ ${PRACTICE_PULSE_INIT_ACTION} for ${customerName}: ${summary}`);
                } else {
                    this.$toast.warning(summary);
                    this.logActivity(`⚠️ ${PRACTICE_PULSE_INIT_ACTION} for ${customerName}: ${summary}`, 'WARNING');
                }
                return flagSaved;
            } catch (err) {
                if (isStale()) return null;
                const message = `Couldn't initialize Practice Pulse: ${extractApiError(err, err && err.message ? err.message : 'Unknown error')}`;
                this.practicePulseInit = { siteID, running: false, data: null, error: message };
                this.$toast.error(message);
                this.logActivity(`❌ ${message} (${customerName})`, 'ERROR');
                return false;
            } finally {
                if (this.practicePulseInit.siteID === siteID) this.practicePulseInit.running = false;
            }
        },

        dismissPracticePulseInit() {
            this.practicePulseInit = { ...this.practicePulseInit, data: null, error: null };
        },

        async toggleApptReminderAccess(newValue) {
            if (!newValue) {
                // Turning off access — also turn off activated
                this.siteFeatures.APPT_REMINDER_ACTIVATED = false;
                const siteID = this.selectedSiteForManagement.SiteID;
                this.featureSaving.APPT_REMINDER_PAID_FOR = true;
                try {
                    await axios.post('/upsert-settings', {
                        siteID: siteID,
                        settings: {
                            APPT_REMINDER_PAID_FOR: 'FALSE',
                            APPT_REMINDER_ACTIVATED: 'FALSE'
                        }
                    }, { crossdomain: true });
                    this.featuresLastSaved = new Date().toLocaleTimeString();
                } catch (err) {
                    console.error('Error toggling appointment reminder access:', err);
                    this.siteFeatures.APPT_REMINDER_PAID_FOR = true;
                    this.siteFeatures.APPT_REMINDER_ACTIVATED = true;
                } finally {
                    this.featureSaving.APPT_REMINDER_PAID_FOR = false;
                }
            } else {
                // Turning on access only — don't auto-activate
                await this.toggleFeature('APPT_REMINDER_PAID_FOR', true);
            }
        },

        /**
         * The "Activate sending" switch. Turning it ON while patients have WORDS in Open Dental's cell-phone field
         * ("None", "NA", "DO NOT TEXT", "0") asks first: an office exe that has not had the fix crashes its whole
         * reminder cycle on the first one (Brester, 2026-11-02), and those patients can't be texted anyway.
         *   - ON with featureReadiness.aptReminders.junkCells.total > 0 -> confirm. Cancel / close writes NOTHING and
         *     puts the switch back to OFF; OK goes on to the normal save.
         *   - ON with total 0, or junkCells null (could not check / older API) -> exactly the normal save, no prompt.
         *   - OFF never prompts. No other feature toggle goes through here.
         */
        async onToggleApptReminderActivated(newValue) {
            if (newValue) {
                const ar = (this.featureReadiness && this.featureReadiness.aptReminders) || {};
                const jc = ar.junkCells;
                if (jc && Number(jc.total) > 0) {
                    const confirmed = await this.$bvModal.msgBoxConfirm(
                        `${jc.total} patient(s) have words instead of a phone number in Open Dental's cell-phone field ` +
                        `(${Number(jc.urgentCount) || 0} with an appointment in the next 31 days). ` +
                        `Appointment reminders can stop for this office until they are cleared. Activate anyway?`,
                        {
                            title: 'Patients with words in the cell-phone field',
                            size: 'md', buttonSize: 'sm', okVariant: 'warning',
                            okTitle: 'Activate anyway', cancelTitle: 'Not yet',
                            footerClass: 'p-2', centered: true,
                        });
                    if (!confirmed) {
                        this.siteFeatures.APPT_REMINDER_ACTIVATED = false; // put the switch back; nothing is written
                        return;
                    }
                }
            }
            await this.toggleFeature('APPT_REMINDER_ACTIVATED', newValue);
        },

        /** The "Cell-phone values that are words" checklist row, from readiness.aptReminders.junkCells (never null here). */
        junkCellsChecklistItem(jc) {
            const total = Number(jc.total) || 0;
            const urgent = Number(jc.urgentCount) || 0;
            const other = Number(jc.otherCount) || Math.max(total - urgent, 0);
            const base = {
                key: 'junkCells',
                label: 'Patient cell-phone values',
                patients: Array.isArray(jc.patients) ? jc.patients : [],
                truncated: !!jc.truncated,
            };
            if (total === 0) {
                return { ...base, ok: true, detail: 'No cell-phone values that are words' };
            }
            if (urgent > 0) {
                return {
                    ...base, ok: false, bad: true,
                    detail: `${urgent} patient(s) with an appointment in the next 31 days have words in the cell-phone field — ask the office to clear them (Open Dental → patient → Wireless Phone). They stop appointment reminders until the exe update is installed, and those patients can't be texted.`,
                };
            }
            return {
                ...base, ok: false, warn: true,
                detail: `${other} active patient(s) have words in the cell-phone field (no appointment in the next 31 days) — worth cleaning up; those patients can't be texted.`,
            };
        },

        /** One line per patient - shown in the expanded list AND copied, so the two always match. */
        junkCellLine(p) {
            const name = `${p.fName || ''} ${p.lName || ''}`.trim();
            return `PatNum ${p.patNum} — ${name} — "${p.value}" — next appt ${p.nextAppt || 'none'}${p.urgent ? ' — URGENT (within 31 days)' : ''}`;
        },

        copyJunkCellsList(patients) {
            const lines = (Array.isArray(patients) ? patients : []).map((p) => this.junkCellLine(p));
            if (!lines.length) return;
            this.copyToClipboard(lines.join('\n'), 'Patient list');
        },

        /**
         * Why the onboarding actions below don't just read `response.data.message`.
         *
         * Two layers each hide the server's own wording, and together they turned a
         * perfectly clear backend refusal into "Unexpected response from
         * SEED_TX_PRESENTER_DEFAULTS" (seen on prod 2026-08-15, clicking Load Tx
         * Presenter defaults on the template host itself — the seeder had said
         * "Cannot seed the template host site into itself" and nobody could see it):
         *
         *  1. `superadmin/sendAction` CATCHES the axios error and returns a plain
         *     `{ status, data }` object, so there is no axios Error here at all and
         *     `err.response` — which `extractApiError` reads — does not exist.
         *  2. teamio-admin answers with `common.createErrorResponse(500, err.message)`,
         *     and that serialiser is `body: JSON.stringify(message)`. The body is a
         *     BARE JSON STRING, so `data.message` is always undefined. See the header
         *     comment in src/utils/apiError.js.
         *
         * So: rebuild the axios-error shape and let `extractApiError` do the rest
         * (it handles the bare-string case, refuses HTML/oversized bodies, and logs
         * the full body + status + stack to the console and LogRocket either way).
         * `fallback` is the call site's old hardcoded string, so a response that
         * genuinely carries no message reads exactly as it did before.
         */
        seedActionError(response, fallback) {
            return extractApiError({
                response: {
                    status: response && response.status,
                    data: response && response.data,
                },
            }, fallback);
        },

        // Forms checklist buttons: each action names its handler. Whitelisted so a computed can't call anything else.
        runFormsChecklistAction(action) {
            const allowed = ['confirmSeedFormTemplates', 'previewApplyFormImaging', 'previewSeedProcedureMappings', 'applySeedProcedureMappings',
                'previewSeedFormMessages', 'applySeedFormMessages'];
            if (!action || action.disabled || allowed.indexOf(action.handler) === -1) return;
            this[action.handler]();
        },

        /**
         * GET_FORM_IMAGING_CATEGORIES response → imaging state (categories, consent candidates, map).
         * prevMap = the operator's picks on a same-site reload (null for a new site). mergeImagingMap keeps
         * picks still in the list, blanks ones that aren't, and suggests only into blank NP_FORM /
         * MED_HX_UPDATE rows — OTHER is always asked. An empty category list is an error: the backend
         * refuses to seed/copy onto a site with none (picks are kept so a Retry doesn't lose them; the
         * actions stay disabled because the list is empty).
         */
        imagingStateFromResponse(response, prevMap = null) {
            if (!(response && response.status === 200 && response.data && typeof response.data === 'object')) {
                throw new Error(this.seedActionError(response, 'Unexpected response from GET_FORM_IMAGING_CATEGORIES'));
            }
            const d = response.data;
            const categories = Array.isArray(d.categories) ? d.categories : [];
            const error = d.error || (categories.length === 0
                ? 'No Open Dental imaging categories found for this site (has the exe synced its definitions yet?).'
                : '');
            return {
                loading: false,
                error,
                categories,
                consentCandidates: Array.isArray(d.consentCandidates) ? d.consentCandidates : [],
                map: error ? { ...emptyCategoryMap(), ...(prevMap || {}) } : mergeImagingMap(prevMap, categories, d.suggestions),
            };
        },

        ensureFormsImagingLoaded() {
            const site = this.selectedSiteForManagement;
            if (!site || !this.siteFeatures.FORMS_PAID_FOR) return;
            if (this.formsImaging.siteID === site.SiteID) return; // already loaded / in flight for this site
            this.loadFormsImagingCategories();
        },

        async loadFormsImagingCategories() {
            const site = this.selectedSiteForManagement;
            if (!site) return;
            const siteID = site.SiteID;
            const seq = ++this.formsImagingRequestSeq;
            // Same site → keep the operator's picks (merged with the fresh list below); new site → start blank.
            const prevMap = this.formsImaging.siteID === siteID ? { ...this.formsImaging.map } : null;
            this.formsImaging = { siteID, loading: true, error: '', categories: [], consentCandidates: [], map: prevMap || emptyCategoryMap() };
            // Stale-response guard: a slow answer for a site the operator has since left (or a superseded
            // request) must never paint its DefNums here — DefNums are per site.
            const isStale = () => seq !== this.formsImagingRequestSeq
                || !this.selectedSiteForManagement
                || this.selectedSiteForManagement.SiteID !== siteID
                || this.formsImaging.siteID !== siteID;
            try {
                const response = await this.sendAction({ siteID, action: 'GET_FORM_IMAGING_CATEGORIES' });
                if (isStale()) return;
                this.formsImaging = { siteID, ...this.imagingStateFromResponse(response, prevMap) };
            } catch (err) {
                if (isStale()) return;
                this.formsImaging = {
                    siteID, loading: false, categories: [], consentCandidates: [], map: prevMap || emptyCategoryMap(),
                    error: `Couldn't load Open Dental imaging categories: ${err.message || 'Unknown error'}`,
                };
            }
        },

        // "Apply to existing forms" step 1: dryRun preview. Nothing is written.
        async previewApplyFormImaging() {
            if (!this.selectedSiteForManagement) return;
            if (!this.formsImagingMapComplete || this.formsImaging.loading || this.formsImaging.error) {
                this.$toast.error('Pick an Open Dental imaging category for all three form types first.');
                return;
            }
            const siteID = this.selectedSiteForManagement.SiteID;
            const categoryMap = { ...this.formsImaging.map };
            this.formsImagingApply = { previewing: true, applying: false, preview: null, error: '' };
            try {
                const response = await this.sendAction({ siteID, action: 'APPLY_FORM_IMAGING_DEFAULTS', categoryMap, dryRun: true });
                if (!this.selectedSiteForManagement || this.selectedSiteForManagement.SiteID !== siteID) return;
                if (!(response && response.status === 200 && response.data && Array.isArray(response.data.items))) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from APPLY_FORM_IMAGING_DEFAULTS'));
                }
                const preview = response.data;
                const unparseable = Array.isArray(preview.unparseable) ? preview.unparseable : [];
                if (preview.items.length === 0) {
                    this.$toast.info('All forms already have a file name and categories.');
                    if (unparseable.length) this.$toast.warning(`${unparseable.length} form(s) have unreadable configuration: ${unparseable.join(', ')}`);
                    return;
                }
                // Keep the exact map that produced the preview — the apply sends the same one.
                this.formsImagingApply.preview = { ...preview, unparseable, categoryMap };
                this.$bvModal.show('apply-form-imaging-modal');
            } catch (error) {
                this.formsImagingApply.error = `Preview failed: ${error.message || 'Unknown error'}`;
                this.$toast.error(this.formsImagingApply.error);
            } finally {
                this.formsImagingApply.previewing = false;
            }
        },

        async onApplyFormImagingOk(bvModalEvt) {
            bvModalEvt.preventDefault(); // stay open until the apply finishes (or fails, showing the error)
            await this.applyFormImaging();
        },

        onApplyFormImagingHidden() {
            if (this.formsImagingApply.applying) return;
            this.formsImagingApply.preview = null;
            this.formsImagingApply.error = '';
        },

        // "Apply to existing forms" step 2: the real write, only after the dryRun preview above.
        async applyFormImaging() {
            const preview = this.formsImagingApply.preview;
            if (!this.selectedSiteForManagement || !preview || !this.formsImagingMapComplete) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.formsImagingApply.applying = true;
            this.formsImagingApply.error = '';
            try {
                const response = await this.sendAction({
                    siteID,
                    action: 'APPLY_FORM_IMAGING_DEFAULTS',
                    categoryMap: { ...preview.categoryMap },
                    dryRun: false,
                });
                if (!(response && response.status === 200 && response.data && typeof response.data.updated === 'number')) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from APPLY_FORM_IMAGING_DEFAULTS'));
                }
                const n = response.data.updated;
                this.$toast.success(`Updated ${n} form${n === 1 ? '' : 's'} with file names and imaging categories.`);
                this.logActivity(`Applied form imaging defaults on site ${siteID}: ${n} form(s) updated`);
                this.formsImagingApply.applying = false;
                this.$bvModal.hide('apply-form-imaging-modal');
                if (this.selectedSiteForManagement && this.selectedSiteForManagement.SiteID === siteID) {
                    await this.loadSiteFeatures();
                }
            } catch (error) {
                this.formsImagingApply.error = `Apply failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Apply form imaging defaults failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.formsImagingApply.error);
            } finally {
                this.formsImagingApply.applying = false;
            }
        },

        // "Load default procedure mappings" step 1: dryRun preview. Nothing is written.
        async previewSeedProcedureMappings() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.procMappingsSeed = { previewing: true, applying: false, preview: null, error: '' };
            try {
                const response = await this.sendAction({ siteID, action: 'SEED_PROCEDURE_MAPPINGS', dryRun: true });
                if (!this.selectedSiteForManagement || this.selectedSiteForManagement.SiteID !== siteID) return;
                if (!(response && response.status === 200 && response.data && Array.isArray(response.data.rows))) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_PROCEDURE_MAPPINGS'));
                }
                const preview = response.data;
                if (preview.rowsAdded === 0 && !preview.wouldSeedPostOpSettings) {
                    this.$toast.info('Nothing to add — every default procedure code is already mapped (or missing on this site).');
                    (preview.warnings || []).forEach(w => this.$toast.warning(w));
                    return;
                }
                this.procMappingsSeed.preview = preview;
                this.$bvModal.show('seed-procedure-mappings-modal');
            } catch (error) {
                this.procMappingsSeed.error = `Preview failed: ${error.message || 'Unknown error'}`;
                this.$toast.error(this.procMappingsSeed.error);
            } finally {
                this.procMappingsSeed.previewing = false;
            }
        },

        async onSeedProcedureMappingsOk(bvModalEvt) {
            bvModalEvt.preventDefault(); // stay open until the run finishes (or fails, showing the error)
            await this.applySeedProcedureMappings();
        },

        onSeedProcedureMappingsHidden() {
            if (this.procMappingsSeed.applying) return;
            this.procMappingsSeed.preview = null;
            this.procMappingsSeed.error = '';
        },

        // "Load default procedure mappings" step 2: the real write, only after the dryRun preview above. Never flips the auto-send switch.
        async applySeedProcedureMappings() {
            const preview = this.procMappingsSeed.preview;
            if (!this.selectedSiteForManagement || !preview) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.procMappingsSeed.applying = true;
            this.procMappingsSeed.error = '';
            try {
                const response = await this.sendAction({ siteID, action: 'SEED_PROCEDURE_MAPPINGS', dryRun: false });
                if (!(response && response.status === 200 && response.data && typeof response.data.rowsAdded === 'number')) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_PROCEDURE_MAPPINGS'));
                }
                const d = response.data;
                const review = Array.isArray(d.toReview) ? d.toReview.length : 0;
                this.$toast.success(`Added ${d.rowsAdded} procedure mapping row${d.rowsAdded === 1 ? '' : 's'}${review ? ` — ${review} cell(s) for the office to review in Forms → 🔗` : ''}.`);
                (d.warnings || []).forEach(w => this.$toast.warning(w));
                this.logActivity(`Loaded default procedure mappings on site ${siteID}: ${d.rowsAdded} row(s) added, ${review} cell(s) to review`);
                this.procMappingsSeed.applying = false;
                this.$bvModal.hide('seed-procedure-mappings-modal');
                if (this.selectedSiteForManagement && this.selectedSiteForManagement.SiteID === siteID) {
                    await this.loadSiteFeatures();
                }
            } catch (error) {
                this.procMappingsSeed.error = `Load failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Load default procedure mappings failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.procMappingsSeed.error);
            } finally {
                this.procMappingsSeed.applying = false;
            }
        },

        // "Load default form messages" step 1: dryRun preview. Nothing is written. (Forms Settings WP3; whole-blob set-if-absent.)
        async previewSeedFormMessages() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.formMessagesSeed = { previewing: true, applying: false, preview: null, error: '' };
            try {
                const response = await this.sendAction({ siteID, action: FORM_MESSAGES_ACTION, dryRun: true });
                if (!this.selectedSiteForManagement || this.selectedSiteForManagement.SiteID !== siteID) return;
                const preview = response && response.status === 200 ? describeFormMessagesPreview(response.data) : null;
                if (!preview) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_FORM_MESSAGES'));
                }
                if (preview.nothingToWrite) {
                    this.$toast.info('Nothing to add — both message settings are already saved on this site (left as they are).');
                    preview.warnings.forEach(w => this.$toast.warning(w));
                    return;
                }
                this.formMessagesSeed.preview = response.data;
                this.$bvModal.show('seed-form-messages-modal');
            } catch (error) {
                this.formMessagesSeed.error = `Preview failed: ${error.message || 'Unknown error'}`;
                this.$toast.error(this.formMessagesSeed.error);
            } finally {
                this.formMessagesSeed.previewing = false;
            }
        },

        async onSeedFormMessagesOk(bvModalEvt) {
            bvModalEvt.preventDefault(); // stay open until the run finishes (or fails, showing the error)
            await this.applySeedFormMessages();
        },

        onSeedFormMessagesHidden() {
            if (this.formMessagesSeed.applying) return;
            this.formMessagesSeed.preview = null;
            this.formMessagesSeed.error = '';
        },

        // "Load default form messages" step 2: the real write, only after the dryRun preview above. Never turns either message on.
        async applySeedFormMessages() {
            const preview = this.formMessagesSeed.preview;
            if (!this.selectedSiteForManagement || !preview) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.formMessagesSeed.applying = true;
            this.formMessagesSeed.error = '';
            try {
                const response = await this.sendAction({ siteID, action: FORM_MESSAGES_ACTION, dryRun: false });
                if (!(response && response.status === 200 && describeFormMessagesPreview(response.data))) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_FORM_MESSAGES'));
                }
                const d = response.data;
                this.$toast.success(describeFormMessagesResult(d));
                (d.warnings || []).forEach(w => this.$toast.warning(w));
                this.logActivity(`Loaded default form messages on site ${siteID}: New Patients ${d.newPatient.written ? 'written' : 'left as is'}, Medical History ${d.medHx.written ? 'written' : 'left as is'}`);
                this.formMessagesSeed.applying = false;
                this.$bvModal.hide('seed-form-messages-modal');
                if (this.selectedSiteForManagement && this.selectedSiteForManagement.SiteID === siteID) {
                    await this.loadSiteFeatures();
                }
            } catch (error) {
                this.formMessagesSeed.error = `Load failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Load default form messages failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.formMessagesSeed.error);
            } finally {
                this.formMessagesSeed.applying = false;
            }
        },

        async confirmSeedFormTemplates() {
            const r = (this.featureReadiness && this.featureReadiness.forms) || {};
            const tl = r.templateLibrary;
            if (!tl || !this.selectedSiteForManagement) return;
            const onHost = ((r.wikiTemplates && r.wikiTemplates.items) || []).filter(it => it.availableOnHost);
            if (!onHost.length) return;

            const siteName = this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID;
            // Logo state from readiness (live teamio.sites.LogoUrl) — see reminder modal note.
            const hasLogo = !!(this.featureReadiness && this.featureReadiness.site && this.featureReadiness.site.hasLogo);
            let message = `Copy ${onHost.length} form email & SMS template(s) from "${tl.hostName}" into "${siteName}"?\n\n` +
                onHost.map(it => `• ${it.name}`).join('\n') + '\n\n' +
                `No forms are copied. Each template links to a New Patient or Medical History form: if this site already has a form with the same name, the link is set; otherwise the office must link its own form in the template before using it (until then the message goes out with no form link).\n\n` +
                `The email templates end with {{email_signature_block}} — this also turns on the Email signature (Settings → Email), built from the practice profile, if none is saved yet.\n\n` +
                `Templates that already exist (same name) are skipped — this is safe to re-run.`;
            if (!hasLogo) {
                message += `\n\nNote: this site has NO logo on file — the email signature is seeded without one (add a logo, then switch "Show logo" on in Settings → Email).`;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm(message, {
                title: 'Load Form Email & SMS Templates',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'success',
                okTitle: 'Yes, Load',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                centered: true
            });
            if (!confirmed) return;
            await this.executeSeedFormTemplates();
        },

        async executeSeedFormTemplates() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.formTemplatesSeed.seeding = true;
            this.formTemplatesSeed.error = '';
            this.formTemplatesSeed.results = null;

            // Safety timeout — the copy is idempotent, so a timed-out run is completed by clicking again.
            const safetyTimeout = setTimeout(() => {
                this.formTemplatesSeed.seeding = false;
                this.$toast.info('Still running in the background — refresh the Features tab in a moment. Re-running is safe (existing templates are skipped).');
            }, 30000);

            try {
                this.logActivity(`Loading form email & SMS templates into site ${siteID}`);
                const response = await this.sendAction({ siteID, action: 'SEED_FORM_MESSAGE_TEMPLATES' });

                if (response && response.status === 200 && response.data && response.data.wikiTemplates && typeof response.data.wikiTemplates === 'object') {
                    const res = response.data;
                    const copied = res.wikiTemplates.successCount || 0;
                    const skipped = res.wikiTemplates.skippedCount || 0;
                    const failed = res.wikiTemplates.failureCount || 0;
                    if (failed === 0 && copied > 0) {
                        this.$toast.success(`Loaded ${copied} form email & SMS template(s)!`);
                    } else if (copied > 0 && failed > 0) {
                        this.$toast.warning(`Partially loaded: ${copied} copied, ${failed} failed, ${skipped} skipped`);
                    } else if (failed > 0) {
                        this.$toast.error(`Loading failed for ${failed} template(s)`);
                    } else {
                        this.$toast.info(`All ${skipped} templates already exist — nothing to load`);
                    }
                    this.logActivity(`Form templates seed: ${copied} copied, ${skipped} skipped, ${failed} failed`);

                    // Refresh readiness but keep the results visible —
                    // loadSiteFeatures resets formTemplatesSeed as part of resetSiteFeatures.
                    await this.loadSiteFeatures();
                    this.formTemplatesSeed.results = res;
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_FORM_MESSAGE_TEMPLATES'));
                }
            } catch (error) {
                this.formTemplatesSeed.error = `Loading form templates failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Form templates seed failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.formTemplatesSeed.error);
            } finally {
                this.formTemplatesSeed.seeding = false;
                clearTimeout(safetyTimeout);
            }
        },

        /**
         * The "Email signature" checklist row, shared by the Forms and Appointment Reminders cards.
         * The email templates both Load buttons seed end with {{email_signature_block}}, which renders
         * blank unless EMAIL_SIGNATURE is enabled with non-blank html (the consumers' rule —
         * teamio-admin getSiteFeatureStatus reports it as `configured`). Both Load buttons seed it
         * set-if-absent; a saved-but-OFF signature is deliberately left alone, hence the OFF badge.
         */
        emailSignatureChecklistItem(signature, loadButtonLabel) {
            const sig = signature || {};
            return {
                key: 'emailSignature',
                ok: !!sig.configured,
                label: 'Email signature',
                badge: sig.configured ? 'ON' : (sig.present ? 'OFF' : 'not set'),
                detail: sig.configured
                    ? 'Seeded email templates end with {{email_signature_block}}'
                    : (sig.present
                        ? 'Saved but turned OFF in Settings → Email — the seeded email templates\' signature renders blank until it\'s on'
                        : `${loadButtonLabel} seeds it from the practice profile (name, phone, address, logo)`),
            };
        },

        async confirmSeedReminderTemplates() {
            const ar = (this.featureReadiness && this.featureReadiness.aptReminders) || {};
            const tl = ar.templateLibrary;
            if (!tl || !this.selectedSiteForManagement) return;

            const siteName = this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID;
            const booked = tl.hostBookedCount || 0;
            const total = tl.hostAptCount + tl.hostRecallCount + booked;
            const bookedPart = booked ? ` + ${booked} booked-confirmation` : '';
            // Every office sees the plain message boxes (STAGE): nothing is copied from the template host any more.
            const noCopy = this.reminderWikiCopyRetired();
            let message = noCopy
                ? `Fill the reminder messages for "${siteName}" with the default wording?\n\n` +
                `This puts the default text and email messages into Appointment Reminders → Settings — nothing is switched on; a message the office already wrote or picked is never replaced. No wiki pages are copied.\n\n` +
                `Also turns on the Email signature (Settings → Email), built from the practice profile, if none is saved yet.\n\n` +
                `This is safe to re-run.`
                : `Copy ${total} reminder templates (${tl.hostAptCount} appointment + ${tl.hostRecallCount} recall${bookedPart}) into "${siteName}"?\n\n` +
                `Tags travel with each template ({{site_name}}/{{site_officephone}} variables resolve at send time). ` +
                `Email templates end with {{email_signature_block}}.\n\n` +
                `Also fills the empty reminder messages (text and email) in Appointment Reminders → Settings with the default wording — nothing is switched on; a message the office already wrote or picked is never replaced.\n\n` +
                `Also turns on the Email signature (Settings → Email), built from the practice profile, if none is saved yet.\n\n` +
                `Templates that already exist (same name) are skipped — this is safe to re-run.`;
            // Read the logo state from readiness (the live teamio.sites.LogoUrl),
            // NOT selectedSiteForManagement — getCustomers doesn't select LogoUrl,
            // so that object's LogoUrl is always undefined (false "NO logo" warning).
            const hasLogo = !!(this.featureReadiness && this.featureReadiness.site && this.featureReadiness.site.hasLogo);
            if (!hasLogo) {
                message += `\n\nNote: this site has NO logo on file — the email signature is seeded without one (add a logo, then switch "Show logo" on in Settings → Email).`;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm(message, {
                title: noCopy ? 'Load Default Reminder Messages' : 'Load Reminder Templates',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'success',
                okTitle: noCopy ? 'Yes, Load Messages' : 'Yes, Load Templates',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                centered: true
            });
            if (!confirmed) return;
            await this.executeSeedReminderTemplates();
        },

        async executeSeedReminderTemplates() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.reminderTemplatesSeed.seeding = true;
            this.reminderTemplatesSeed.error = '';
            this.reminderTemplatesSeed.results = null;

            const safetyTimeout = setTimeout(() => {
                this.reminderTemplatesSeed.seeding = false;
                this.$toast.info('Still running in the background — refresh the Features tab in a moment. Re-running is safe (existing templates are skipped).');
            }, 30000);

            try {
                this.logActivity(`Seeding reminder templates into site ${siteID}`);
                // `copyReminderWikiPages: false` only once every office sees the plain message boxes (STAGE); before
                // that the request is exactly what it always was, so an older admin deploy behaves the same.
                const payload = { siteID, action: 'SEED_REMINDER_TEMPLATES' };
                if (this.reminderWikiCopyRetired()) payload.copyReminderWikiPages = false;
                const response = await this.sendAction(payload);

                if (response && response.status === 200 && response.data && typeof response.data.successCount !== 'undefined') {
                    const res = response.data;
                    if (res.wikiPagesCopied === false) {
                        // Default messages only — there is no copy to report ("0 copied" would read like a failure).
                        if ((res.inlineSlotsWritten || 0) > 0) this.$toast.success(`Loaded the default reminder messages (${res.inlineSlotsWritten} filled)`);
                        else this.$toast.info('The reminder messages are already filled in — nothing to load');
                    } else if (res.status === 'NEEDS_FORMS') {
                        this.reminderTemplatesSeed.error = 'Some templates link to forms that are missing on this site — add those forms first (convert the office\'s packet, or Bulk Copy Forms from the template host), then retry.';
                        this.$toast.warning(this.reminderTemplatesSeed.error);
                    } else if (res.failureCount === 0 && res.successCount > 0) {
                        this.$toast.success(`Loaded ${res.successCount} reminder templates!`);
                    } else if (res.successCount > 0 && res.failureCount > 0) {
                        this.$toast.warning(`Partially loaded: ${res.successCount} copied, ${res.failureCount} failed, ${res.skippedCount} skipped`);
                    } else if (res.successCount === 0 && res.skippedCount > 0) {
                        this.$toast.info(`All ${res.skippedCount} templates already exist — nothing to load`);
                    } else if (res.failureCount > 0) {
                        this.$toast.error(`Loading failed for ${res.failureCount} templates`);
                    }
                    this.logActivity(res.wikiPagesCopied === false
                        ? `Default reminder messages loaded: ${res.inlineSlotsWritten || 0} filled, no wiki pages copied`
                        : `Reminder templates seed: ${res.successCount} copied, ${res.skippedCount} skipped, ${res.failureCount} failed`);

                    // Refresh readiness counts but keep results visible (reset wipes them).
                    await this.loadSiteFeatures();
                    this.reminderTemplatesSeed.results = res;
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_REMINDER_TEMPLATES'));
                }
            } catch (error) {
                this.reminderTemplatesSeed.error = `Loading reminder templates failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Reminder templates seed failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.reminderTemplatesSeed.error);
            } finally {
                this.reminderTemplatesSeed.seeding = false;
                clearTimeout(safetyTimeout);
            }
        },

        /**
         * Turn ON the Teamio platform email sender for a site with no verified sender.
         *
         * Confirms first because this changes what a PATIENT sees in their inbox: mail starts
         * arriving from the practice's name at a teamio.app address rather than not arriving
         * at all. The office should know that before it happens.
         *
         * No client-side precondition checks — the backend refuses (writing nothing) with a
         * plain-language reason, and duplicating those rules here would be a second
         * implementation free to drift from the one that actually gates the write.
         */
        async confirmEnablePlatformSender() {
            if (!this.selectedSiteForManagement) return;
            const siteName = this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Let "${siteName}" email patients through Teamio?\n\n` +
                `Patients will see the practice's name as the sender, at a teamio.app address, and replies go ` +
                `to the office email configured in Tx Presenter → Settings → General. No DNS setup is needed.\n\n` +
                `This covers Tx Presenter only — plan links, signed plans and reply notifications. Appointment ` +
                `reminders, recall and campaigns still require the practice's own verified domain.\n\n` +
                `If the practice verifies its own domain later, everything switches to it automatically.`,
                {
                    title: 'Send Patient Email Through Teamio',
                    size: 'md',
                    buttonSize: 'sm',
                    okVariant: 'success',
                    okTitle: 'Yes, Enable',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    centered: true,
                });
            if (!confirmed) return;
            await this.executeEnablePlatformSender();
        },

        async executeEnablePlatformSender() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.platformSender.enabling = true;
            this.platformSender.error = '';
            this.platformSender.results = null;

            try {
                this.logActivity(`Enabling platform email sender for site ${siteID}`);
                const response = await this.sendAction({ siteID, action: 'ENABLE_PLATFORM_SENDER' });
                const res = (response && response.data) || null;

                if (response && response.status === 200 && res && res.ok === true) {
                    this.platformSender.results = res;
                    this.$toast.success(`Now sending as ${res.address}`);
                    this.logActivity(`Platform sender enabled: ${res.address} (reply-to ${res.replyTo})`);
                    await this.loadSiteFeatures();
                } else if (res && res.ok === false && res.reason) {
                    // A REFUSAL, not a crash. The backend wrote nothing and told us why in
                    // plain language — surface that verbatim rather than a generic failure.
                    this.platformSender.error = res.reason;
                    this.logActivity(`Platform sender refused: ${res.reason}`, 'WARN');
                    this.$toast.warning('Could not enable — see the reason below.');
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from ENABLE_PLATFORM_SENDER'));
                }
            } catch (error) {
                this.platformSender.error = `Enabling the Teamio sender failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Platform sender enable failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.platformSender.error);
            } finally {
                this.platformSender.enabling = false;
            }
        },

        async confirmSeedTxPresenter() {
            const tx = (this.featureReadiness && this.featureReadiness.txPresenter) || {};
            if (!tx.hostAvailable || !this.selectedSiteForManagement) return;

            const siteName = this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID;
            let message = `Load the Tx Presenter house-style defaults into "${siteName}"?\n\n` +
                `Seeds General (practice name, plain-language labels, post-sign email, AI replies = hold-for-review, ` +
                `patient comments OFF — turn on in Tx Presenter Settings → General once a sender email is verified), ` +
                `Presentation, Payment options, and Delivery templates.\n\n` +
                `Sections already saved by the office are preserved — this is safe to re-run. ` +
                `Consent-form mappings are left for the office to configure.`;
            if (!tx.hasVerifiedEmail) {
                message += `\n\nNote: this site has NO verified email — the post-sign email, comment notifications, and plan-link email won't send until one is verified.`;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm(message, {
                title: 'Load Tx Presenter Defaults',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'success',
                okTitle: 'Yes, Load Defaults',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                centered: true
            });
            if (!confirmed) return;
            await this.executeSeedTxPresenter();
        },

        async executeSeedTxPresenter() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.txPresenterSeed.seeding = true;
            this.txPresenterSeed.error = '';
            this.txPresenterSeed.results = null;

            const safetyTimeout = setTimeout(() => {
                this.txPresenterSeed.seeding = false;
                this.$toast.info('Still running in the background — refresh the Features tab in a moment. Re-running is safe (saved sections are preserved).');
            }, 30000);

            try {
                this.logActivity(`Seeding Tx Presenter defaults into site ${siteID}`);
                const response = await this.sendAction({ siteID, action: 'SEED_TX_PRESENTER_DEFAULTS' });

                if (response && response.status === 200 && response.data && Array.isArray(response.data.seeded)) {
                    const res = response.data;
                    if (res.seeded.length > 0) {
                        this.$toast.success(`Loaded ${res.seeded.length} Tx Presenter section(s)!`);
                    } else if (res.skipped.length > 0) {
                        this.$toast.info(`All sections already configured — nothing to load`);
                    }
                    this.logActivity(`Tx Presenter seed: ${res.seeded.length} seeded, ${res.skipped.length} skipped`);

                    // Refresh readiness but keep results visible (reset wipes them).
                    await this.loadSiteFeatures();
                    this.txPresenterSeed.results = res;
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_TX_PRESENTER_DEFAULTS'));
                }
            } catch (error) {
                this.txPresenterSeed.error = `Loading Tx Presenter defaults failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Tx Presenter seed failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.txPresenterSeed.error);
            } finally {
                this.txPresenterSeed.seeding = false;
                clearTimeout(safetyTimeout);
            }
        },

        // ---- Insurance Verification onboarding ------------------------------------------

        /**
         * Enabling Insurance Verification starts REAL, BILLED Stedi spend, so it is the one
         * feature switch that confirms first. What the flag alone opens is the dashboard +
         * manual "Verify Now" — per-click spend. The nightly runner and booking sweep are
         * gated separately by autoPrecheckEnabled / verifyOnBooking, which both default
         * false and are NOT touched here.
         */
        async onToggleInsuranceVerification(newValue) {
            if (!newValue) {
                // Turning OFF is never gated and never needs a warning.
                await this.toggleFeature('INSURANCE_VERIFICATION_ENABLED', false);
                return;
            }

            const siteName = this.selectedSiteForManagement
                ? (this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID)
                : 'this site';
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Turn on Insurance Verification for "${siteName}"?\n\n` +
                `This opens the Benefits & Eligibility dashboard and the manual "Verify Now" button. ` +
                `Every manual check is a real eligibility request and is BILLED.\n\n` +
                `Automatic checking stays OFF: the nightly precheck and verify-on-booking each need ` +
                `their own switch in Precheck settings, and both default to off.`,
                {
                    title: 'Enable Insurance Verification (starts billable checks)',
                    size: 'md', buttonSize: 'sm', okVariant: 'warning',
                    okTitle: 'Yes, Enable', cancelTitle: 'Cancel',
                    footerClass: 'p-2', centered: true,
                });

            if (!confirmed) {
                this.siteFeatures.INSURANCE_VERIFICATION_ENABLED = false; // revert the switch
                return;
            }
            await this.toggleFeature('INSURANCE_VERIFICATION_ENABLED', true);
        },

        /** Override the readiness gate — names the failing items so it is a real decision. */
        async confirmEnableInsuranceVerificationAnyway() {
            const iv = this.ivReadiness;
            const failing = iv && Array.isArray(iv.items)
                ? iv.items.filter((i) => !i.ok && !i.info).map((i) => `• ${i.label}`)
                : ['• Readiness could not be checked'];

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `These readiness checks have NOT passed:\n\n${failing.join('\n')}\n\n` +
                `If the billing provider is unset, EVERY check will fail with "no billing provider" — ` +
                `those failures still cost money. Enable anyway?`,
                {
                    title: 'Enable despite failing checks?',
                    size: 'md', buttonSize: 'sm', okVariant: 'danger',
                    okTitle: 'Enable anyway', cancelTitle: 'Cancel',
                    footerClass: 'p-2', centered: true,
                });
            if (!confirmed) return;

            this.siteFeatures.INSURANCE_VERIFICATION_ENABLED = true;
            await this.toggleFeature('INSURANCE_VERIFICATION_ENABLED', true);
        },

        async confirmSeedInsuranceVerification() {
            if (!this.selectedSiteForManagement) return;
            const siteName = this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Create the default Insurance Verification settings for "${siteName}"?\n\n` +
                `Every automation is written OFF — nightly precheck off, verify-on-booking off, ` +
                `PDF import off. Nothing is verified and nothing is billed by this step.\n\n` +
                `An existing configuration is never overwritten, so this is safe to re-run.`,
                {
                    title: 'Load Insurance Verification Defaults',
                    size: 'md', buttonSize: 'sm', okVariant: 'success',
                    okTitle: 'Yes, Load Defaults', cancelTitle: 'Cancel',
                    footerClass: 'p-2', centered: true,
                });
            if (!confirmed) return;

            const siteID = this.selectedSiteForManagement.SiteID;
            this.ivSeed.seeding = true;
            this.ivSeed.error = '';
            this.ivSeed.results = null;
            try {
                this.logActivity(`Seeding Insurance Verification defaults into site ${siteID}`);
                const response = await this.sendAction({ siteID, action: 'SEED_INSURANCE_VERIFICATION_DEFAULTS' });
                if (response && response.status === 200 && response.data) {
                    const res = response.data;
                    this.$toast[res.skipped ? 'info' : 'success'](
                        res.skipped ? 'Settings already configured — nothing to load' : 'Insurance Verification defaults loaded');
                    await this.loadSiteFeatures();
                    this.ivSeed.results = res;
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_INSURANCE_VERIFICATION_DEFAULTS'));
                }
            } catch (error) {
                this.ivSeed.error = `Loading Insurance Verification defaults failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Insurance Verification seed failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.ivSeed.error);
            } finally {
                this.ivSeed.seeding = false;
            }
        },

        /**
         * Save the billing provider inline. Writes through the service's own ungated
         * /save-settings (which is why this works while the feature flag is still OFF).
         */
        async saveIvBillingProvider() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            const provNum = this.ivBillingProvider.provNum;
            if (provNum === null) return;

            this.ivBillingProvider.saving = true;
            this.ivBillingProvider.error = '';
            try {
                await axios.post('/insurance-verification/save-settings', {
                    siteID,
                    settings: { billingProviderNum: provNum },
                }, { crossdomain: true });
                this.logActivity(`Insurance Verification billing provider set to ProvNum ${provNum} for site ${siteID}`);
                this.$toast.success('Billing provider saved');
                await this.loadSiteFeatures();
            } catch (error) {
                this.ivBillingProvider.error = `Could not save the billing provider: ${(error.response && error.response.data && error.response.data.message) || error.message || 'Unknown error'}`;
                this.$toast.error(this.ivBillingProvider.error);
            } finally {
                this.ivBillingProvider.saving = false;
            }
        },

        /** Save the provider-name override (Super Admin only — see the template comment). */
        async saveIvProviderNameOverride() {
            const draft = this.ivOverrideDraft;
            if (!draft) return;
            await this.writeIvProviderNameOverride(draft);
        },

        /** Clear the override — posts null, so the 270 goes back to sending the NPI only. */
        async clearIvProviderNameOverride() {
            await this.writeIvProviderNameOverride(null);
        },

        /**
         * POST { siteID, settings: { providerNameOverride } } through the service's own ungated
         * /save-settings (same route as saveIvBillingProvider). `value` is null (clear) or EXACTLY
         * `{ organizationName }` / `{ firstName, lastName }`; the service 400s anything else.
         */
        async writeIvProviderNameOverride(value) {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            const clearing = value === null;

            this.ivOverride.saving = true;
            this.ivOverride.error = '';
            try {
                await axios.post('/insurance-verification/save-settings', {
                    siteID,
                    settings: { providerNameOverride: value },
                }, { crossdomain: true });
                const label = clearing
                    ? 'cleared'
                    : `set to ${value.organizationName ? `organization "${value.organizationName}"` : `person "${value.firstName} ${value.lastName}"`}`;
                this.logActivity(`Insurance Verification provider-name override ${label} for site ${siteID}`);
                this.$toast.success(clearing ? 'Provider-name override cleared' : 'Provider-name override saved');
                await this.loadSiteFeatures();
            } catch (error) {
                this.ivOverride.error = `Could not ${clearing ? 'clear' : 'save'} the provider-name override: ${(error.response && error.response.data && error.response.data.message) || error.message || 'Unknown error'}`;
                this.$toast.error(this.ivOverride.error);
            } finally {
                this.ivOverride.saving = false;
            }
        },

        /** Read-only, zero-cost analysis of the site's insurance book (census-lite). */
        async runInsuranceCensus() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.ivCensus.loading = true;
            this.ivCensus.error = '';
            this.ivCensus.results = null;
            try {
                this.logActivity(`Running Insurance Verification census for site ${siteID}`);
                const response = await this.sendAction({ siteID, action: 'GET_INSURANCE_VERIFICATION_CENSUS' });
                if (response && response.status === 200 && response.data && response.data.headline) {
                    this.ivCensus.results = response.data;
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from GET_INSURANCE_VERIFICATION_CENSUS'));
                }
            } catch (error) {
                this.ivCensus.error = `Insurance book analysis failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Insurance census failed: ${error.message}`, 'ERROR');
            } finally {
                this.ivCensus.loading = false;
            }
        },

        async confirmSeedReviews() {
            if (!this.selectedSiteForManagement) return;
            const rv = (this.featureReadiness && this.featureReadiness.reviews) || {};

            const siteName = this.selectedSiteForManagement.CustomerName || this.selectedSiteForManagement.Name || this.selectedSiteForManagement.SiteID;
            let message = `Load the Review Management house-style defaults into "${siteName}"?\n\n` +
                `Seeds timing, channels, gate threshold, private-feedback reasons, and blank review links ` +
                `(the office pastes + verifies its own Google/Facebook/Yelp links).\n\n` +
                `A settings config already saved by the office is preserved — this never overwrites it.`;
            if (!rv.notifyEmailSet) {
                message += `\n\nNote: this site has NO verified email — private-feedback alert emails have nowhere to go until one is verified.`;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm(message, {
                title: 'Load Reviews Defaults',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'success',
                okTitle: 'Yes, Load Defaults',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                centered: true
            });
            if (!confirmed) return;
            await this.executeSeedReviews();
        },

        async executeSeedReviews() {
            if (!this.selectedSiteForManagement) return;
            const siteID = this.selectedSiteForManagement.SiteID;
            this.reviewsSeed.seeding = true;
            this.reviewsSeed.error = '';
            this.reviewsSeed.results = null;

            const safetyTimeout = setTimeout(() => {
                this.reviewsSeed.seeding = false;
                this.$toast.info('Still running in the background — refresh the Features tab in a moment. Re-running is safe (a saved config is never overwritten).');
            }, 30000);

            try {
                this.logActivity(`Seeding Reviews defaults into site ${siteID}`);
                const response = await this.sendAction({ siteID, action: 'SEED_REVIEWS_DEFAULTS' });

                if (response && response.status === 200 && response.data && (response.data.seeded === true || response.data.skipped === true)) {
                    const res = response.data;
                    if (res.skipped) {
                        this.$toast.info('Reviews settings already configured — nothing to load');
                    } else {
                        this.$toast.success('Loaded Reviews defaults!');
                        if (!res.templatesSeeded) {
                            this.$toast.info('Select SMS/email review-request templates in Reviews → Settings.');
                        }
                    }
                    this.logActivity(`Reviews seed: seeded=${!!res.seeded}, skipped=${!!res.skipped}, templatesSeeded=${!!res.templatesSeeded}`);

                    // Refresh readiness but keep results visible (reset wipes them).
                    await this.loadSiteFeatures();
                    this.reviewsSeed.results = res;
                } else {
                    throw new Error(this.seedActionError(response, 'Unexpected response from SEED_REVIEWS_DEFAULTS'));
                }
            } catch (error) {
                this.reviewsSeed.error = `Loading Reviews defaults failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Reviews seed failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.reviewsSeed.error);
            } finally {
                this.reviewsSeed.seeding = false;
                clearTimeout(safetyTimeout);
            }
        },

        async fetchSiteA2PStatus(siteID) {
            this.selectedSiteA2PLoading = true;
            try {
                const result = await this.getA2PRegistrationStatus({ targetSiteID: siteID });
                if (result.success) {
                    this.selectedSiteA2PStatus = result.registration;
                } else {
                    this.selectedSiteA2PStatus = null;
                }
            } catch (error) {
                console.error('Error fetching A2P status for site:', error);
                this.selectedSiteA2PStatus = null;
            } finally {
                this.selectedSiteA2PLoading = false;
            }
        },

        async fetchLiveA2PStatus() {
            if (!this.selectedSiteForManagement) return;
            this.selectedSiteLiveA2PLoading = true;
            this.selectedSiteLiveA2P = null;
            try {
                const result = await this.getLiveA2PStatus({ targetSiteID: this.selectedSiteForManagement.SiteID });
                if (result && result.success) {
                    this.selectedSiteLiveA2P = result;
                }
            } catch (error) {
                console.error('Error fetching live A2P status:', error);
            } finally {
                this.selectedSiteLiveA2PLoading = false;
            }
        },

        getLiveStepIcon(step) {
            if (step.error) return '❌';
            const ts = (step.twilioStatus || '').toLowerCase();
            if (ts === 'twilio-approved' || ts === 'approved' || ts === 'verified' || ts === 'active') return '✅';
            if (ts.includes('review') || ts === 'pending' || ts === 'in_progress') return '⏳';
            if (ts.includes('failed') || ts.includes('rejected')) return '❌';
            if (!step.sid) return '⬜';
            return '⏳';
        },

        getLiveStepLabel(step) {
            if (!step.sid) return 'Pending';
            if (step.error) return 'Error';
            return step.twilioStatus || 'Created';
        },

        getLiveStepClass(step) {
            const ts = (step.twilioStatus || '').toLowerCase();
            if (ts === 'twilio-approved' || ts === 'approved' || ts === 'verified' || ts === 'active') return 'text-success font-weight-bold';
            if (ts.includes('failed') || ts.includes('rejected')) return 'text-danger font-weight-bold';
            if (ts.includes('review') || ts === 'pending' || ts === 'in_progress') return 'text-warning';
            if (!step.sid) return 'text-muted';
            return '';
        },

        // A2P Status Helper Methods
        getA2PStatusBgClass(status) {
            const s = (status || '').toLowerCase();
            if (s === 'approved' || s === 'twilio-approved' || s === 'twilio-verified') return 'bg-success-light';
            if (s === 'pending' || s === 'submitted' || s === 'in-review' || s === 'pending-review') return 'bg-warning-light';
            if (s === 'in_progress' || s === 'processing' || s === 'draft') return 'bg-info-light';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'bg-danger-light';
            return 'bg-light';
        },

        getA2PStatusTextClass(status) {
            const s = (status || '').toLowerCase();
            if (s === 'approved' || s === 'twilio-approved' || s === 'twilio-verified') return 'text-success';
            if (s === 'pending' || s === 'submitted' || s === 'in-review' || s === 'pending-review') return 'text-warning';
            if (s === 'in_progress' || s === 'processing' || s === 'draft') return 'text-info';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'text-danger';
            return 'text-secondary';
        },

        formatA2PStatus(status) {
            const s = (status || '').toLowerCase();
            const statusMap = {
                'approved': 'Approved',
                'twilio-approved': 'Approved',
                'twilio-verified': 'Verified',
                'pending': 'Pending Approval',
                'submitted': 'Submitted',
                'in-review': 'In Review',
                'pending-review': 'Pending Review',
                'in_progress': 'In Progress',
                'processing': 'Processing',
                'draft': 'Draft',
                'failed': 'Failed',
                'rejected': 'Rejected',
                'error': 'Error'
            };
            return statusMap[s] || status || 'Unknown';
        },

        getA2PButtonVariant() {
            if (!this.selectedSiteA2PStatus) return 'success';
            const s = (this.selectedSiteA2PStatus.Status || this.selectedSiteA2PStatus.status || '').toLowerCase();
            if (s === 'approved' || s === 'twilio-approved' || s === 'twilio-verified') return 'outline-success';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'warning';
            return 'primary';
        },

        getA2PButtonIcon() {
            if (!this.selectedSiteA2PStatus) return 'fa fa-rocket';
            const s = (this.selectedSiteA2PStatus.Status || this.selectedSiteA2PStatus.status || '').toLowerCase();
            if (s === 'approved' || s === 'twilio-approved' || s === 'twilio-verified') return 'fa fa-cog';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'fa fa-refresh';
            return 'fa fa-arrow-right';
        },

        getA2PButtonText() {
            if (!this.selectedSiteA2PStatus) return 'Start A2P Registration';
            const s = (this.selectedSiteA2PStatus.Status || this.selectedSiteA2PStatus.status || '').toLowerCase();
            if (s === 'approved' || s === 'twilio-approved' || s === 'twilio-verified') return 'Manage A2P Registration';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'Retry A2P Registration';
            return 'Resume A2P Registration';
        },

        getA2PStatusType(status) {
            const s = (status || '').toLowerCase();
            if (s === 'approved' || s === 'twilio-approved' || s === 'twilio-verified') return 'success';
            if (s === 'pending' || s === 'submitted' || s === 'in-review' || s === 'pending-review') return 'warning';
            if (s === 'in_progress' || s === 'processing' || s === 'draft') return 'info';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'danger';
            return 'secondary';
        },

        async listSiteDomains() {
            if (!this.selectedSiteForManagement) return;

            this.siteEmailDomainsLoading = true;
            this.siteEmailDomains = [];

            try {
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'LIST_SENDGRID_DOMAINS'
                });

                if (res && res.status === 200 && res.data && res.data.domains) {
                    this.siteEmailDomains = res.data.domains;
                    this.logActivity(`Found ${res.data.domains.length} authenticated domain(s) for ${this.selectedSiteForManagement.CustomerName}`);
                    if (res.data.domains.length === 0) {
                        this.$toast.info('No authenticated domains found for this site');
                    }
                } else {
                    this.$toast.warning(res.data?.message || 'Could not retrieve domains');
                }
            } catch (err) {
                console.error('Error listing domains:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to list domains');
                this.logActivity(`Failed to list domains for ${this.selectedSiteForManagement.CustomerName}: ${err.message}`, 'ERROR');
            } finally {
                this.siteEmailDomainsLoading = false;
            }
        },

        async getDnsRecordsForPanel() {
            if (!this.selectedSiteForManagement) return;

            this.panelDnsLoading = true;
            this.panelDnsRecords = [];
            this.panelDnsDomain = '';
            this.panelDnsDomainId = null;

            try {
                // Reuse listSiteDomains logic
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'LIST_SENDGRID_DOMAINS'
                });

                if (res && res.status === 200 && res.data && res.data.domains && res.data.domains.length > 0) {
                    const domain = res.data.domains[0];
                    this.panelDnsDomain = domain.domain;
                    this.panelDnsDomainId = domain.id;

                    // Also update siteEmailDomains so the domain list shows
                    this.siteEmailDomains = res.data.domains;

                    if (domain.dns) {
                        const records = [];
                        Object.keys(domain.dns).forEach(key => {
                            const rec = domain.dns[key];
                            if (rec && rec.host) {
                                records.push({
                                    type: rec.type || 'CNAME',
                                    host: rec.host,
                                    value: rec.data,
                                    valid: rec.valid,
                                    description: key
                                });
                            }
                        });
                        this.panelDnsRecords = records;
                    }

                    if (this.panelDnsRecords.length === 0) {
                        this.$toast.info('Domain found but no DNS records available');
                    }
                } else {
                    this.$toast.warning('No authenticated domains found for this site');
                }
            } catch (err) {
                console.error('Error getting DNS records:', err);
                this.$toast.error('Failed to get DNS records');
            } finally {
                this.panelDnsLoading = false;
            }
        },

        copyDnsRecordsToClipboard() {
            if (this.panelDnsRecords.length === 0) return;

            let text = `DNS Records for ${this.panelDnsDomain}\n`;
            text += '='.repeat(50) + '\n\n';
            this.panelDnsRecords.forEach((rec, i) => {
                text += `Record ${i + 1} (${rec.type})\n`;
                text += `  Host/Name:      ${rec.host}\n`;
                text += `  Value/Points To: ${rec.value}\n`;
                text += `  Status:          ${rec.valid ? 'Verified' : 'Pending'}\n\n`;
            });
            text += 'Notes:\n';
            text += '- DNS changes can take up to 48 hours to propagate globally\n';
            text += '- Contact your domain provider (GoDaddy, Namecheap, etc.) if you need help\n';

            navigator.clipboard.writeText(text).then(() => {
                this.$toast.success('DNS records copied to clipboard!');
            }).catch(() => {
                this.$toast.error('Failed to copy');
            });
        },

        // Both DNS-email buttons go through POST /email-settings/email-dns-records: the server
        // re-reads the records from SendGrid, always sends to the site's Admins, and sends
        // directly FROM nilay@teamio.app. (It used to loop /send-email-msg, which draws on the
        // practice's email credits.) Typed addresses ride along as extraRecipients.
        parseDnsExtraRecipients(str) {
            const recipients = String(str || '').split(',').map(e => e.trim()).filter(e => e);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const invalid = recipients.filter(e => !emailRegex.test(e));
            if (invalid.length > 0) return { error: `Invalid email(s): ${invalid.join(', ')}` };
            if (recipients.length > 5) return { error: 'You can add at most 5 extra recipients' };
            return { recipients };
        },

        async postDnsRecordsEmail({ siteID, domainId, extraRecipients }) {
            const idToken = await getIdToken(this.$store.state.getAuth ? this.$store.state.getAuth() : null);
            const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
            const payload = { siteID, domainId };
            if (extraRecipients && extraRecipients.length) payload.extraRecipients = extraRecipients;
            const res = await axios.post('/email-settings/email-dns-records', payload, { crossdomain: true, headers });
            return (res.data && res.data.sentTo) || [];
        },

        async sendPanelDnsRecordsEmail() {
            if (!this.selectedSiteForManagement || !this.panelDnsDomainId) return;
            const parsed = this.parseDnsExtraRecipients(this.panelDnsEmailRecipients);
            if (parsed.error) {
                this.$toast.error(parsed.error);
                return;
            }

            this.sendingPanelDnsEmail = true;
            const domain = this.panelDnsDomain;
            try {
                const sentTo = await this.postDnsRecordsEmail({
                    siteID: this.selectedSiteForManagement.SiteID,
                    domainId: this.panelDnsDomainId,
                    extraRecipients: parsed.recipients
                });
                this.$toast.success(`DNS records emailed to ${sentTo.join(', ')}`);
                this.logActivity(`Emailed DNS records for ${domain} to ${sentTo.join(', ')}`);
                this.showPanelDnsEmailForm = false;
                this.panelDnsEmailRecipients = '';
            } catch (err) {
                console.error('Error sending DNS records email:', err);
                this.$toast.error('Failed to send: ' + (err.response?.data?.message || err.message));
            } finally {
                this.sendingPanelDnsEmail = false;
            }
        },

        openDomainVerification() {
            if (!this.selectedSiteForManagement) return;
            this.siteIdForEmailVerification = this.selectedSiteForManagement.SiteID;
            this.customerNameForEmailVerification = this.selectedSiteForManagement.CustomerName;
            this.verificationType = 'domain';
            this.$bvModal.show('verify-email-modal');
        },

        openSingleSenderVerification() {
            if (!this.selectedSiteForManagement) return;
            this.siteIdForEmailVerification = this.selectedSiteForManagement.SiteID;
            this.customerNameForEmailVerification = this.selectedSiteForManagement.CustomerName;
            this.verificationType = 'single_sender';
            this.$bvModal.show('verify-email-modal');
        },

        async openVerifiedEmailsManager() {
            if (!this.selectedSiteForManagement) return;

            // Load existing emails first
            await this.loadVerifiedEmails();

            // Prompt to add new email
            const newEmail = prompt('Enter the verified sender email address to add:\n\n(Current emails will be shown in the panel after adding)');
            if (newEmail && newEmail.trim()) {
                await this.addVerifiedEmail(newEmail.trim());
            }
        },

        async loadVerifiedEmails() {
            if (!this.selectedSiteForManagement) return;

            this.siteVerifiedEmailsLoading = true;
            try {
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'GET_VERIFIED_EMAILS'
                });

                if (res && res.status === 200 && res.data) {
                    this.siteVerifiedEmails = res.data.emails || [];
                    this.logActivity(`Loaded ${this.siteVerifiedEmails.length} verified email(s) for ${this.selectedSiteForManagement.CustomerName}`);
                }
            } catch (err) {
                console.error('Error loading verified emails:', err);
                this.$toast.error('Failed to load verified emails');
            } finally {
                this.siteVerifiedEmailsLoading = false;
            }
        },

        async addVerifiedEmail(email) {
            if (!this.selectedSiteForManagement || !email) return;

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                this.$toast.error('Please enter a valid email address');
                return;
            }

            try {
                this.$toast.info(`Adding ${email}...`);
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'ADD_VERIFIED_EMAIL',
                    email: email
                });

                if (res && res.status === 200) {
                    this.$toast.success(`Email ${email} added successfully`);
                    this.siteVerifiedEmails = res.data.emails || [];
                    this.logActivity(`Added verified email ${email} for ${this.selectedSiteForManagement.CustomerName}`);
                } else {
                    this.$toast.error(res.data?.message || 'Failed to add email');
                }
            } catch (err) {
                console.error('Error adding verified email:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to add email');
            }
        },

        async removeVerifiedEmail(email) {
            if (!this.selectedSiteForManagement || !email) return;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to remove "${email}" from verified sender emails?`,
                {
                    title: 'Confirm Remove Email',
                    okVariant: 'danger',
                    okTitle: 'Remove',
                    cancelTitle: 'Cancel'
                }
            );

            if (!confirmed) return;

            try {
                this.$toast.info(`Removing ${email}...`);
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'REMOVE_VERIFIED_EMAIL',
                    email: email
                });

                if (res && res.status === 200) {
                    this.$toast.success(`Email ${email} removed successfully`);
                    this.siteVerifiedEmails = res.data.emails || [];
                    this.logActivity(`Removed verified email ${email} from ${this.selectedSiteForManagement.CustomerName}`);
                } else {
                    this.$toast.error(res.data?.message || 'Failed to remove email');
                }
            } catch (err) {
                console.error('Error removing verified email:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to remove email');
            }
        },

        async recheckDomainVerification(domain) {
            if (!this.selectedSiteForManagement || !domain.id) return;

            try {
                this.$toast.info(`Checking DNS status for ${domain.domain}...`);
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'VERIFY_EMAIL',
                    subAction: 'CHECK_DOMAIN_VERIFICATION',
                    domainId: domain.id
                });

                if (res && res.status === 200 && res.data && res.data.verificationDetails) {
                    const details = res.data.verificationDetails;
                    if (details.valid) {
                        this.$toast.success(`Domain ${domain.domain} is now verified!`);
                        // Update the domain in the list
                        const idx = this.siteEmailDomains.findIndex(d => d.id === domain.id);
                        if (idx !== -1) {
                            this.$set(this.siteEmailDomains, idx, { ...this.siteEmailDomains[idx], valid: true });
                        }
                    } else {
                        this.$toast.warning(`Domain ${domain.domain} is still pending DNS verification`);
                    }
                    this.logActivity(`DNS check for ${domain.domain}: ${details.valid ? 'Verified' : 'Pending'}`);
                }
            } catch (err) {
                console.error('Error checking domain verification:', err);
                this.$toast.error('Failed to check domain status');
            }
        },

        async confirmDeleteDomain(domain) {
            if (!this.selectedSiteForManagement) return;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to delete the domain authentication for "${domain.domain}"? This cannot be undone.`,
                {
                    title: 'Confirm Delete Domain',
                    okVariant: 'danger',
                    okTitle: 'Delete',
                    cancelTitle: 'Cancel'
                }
            );

            if (confirmed) {
                await this.deleteDomain(domain);
            }
        },

        async deleteDomain(domain) {
            if (!this.selectedSiteForManagement || !domain.id) return;

            this.deletingDomainId = domain.id;

            try {
                const res = await this.sendAction({
                    siteID: this.selectedSiteForManagement.SiteID,
                    action: 'DELETE_SENDGRID_DOMAIN',
                    domainId: domain.id
                });

                if (res && res.status === 200) {
                    this.$toast.success(`Domain ${domain.domain} deleted successfully`);
                    // Remove from local list
                    this.siteEmailDomains = this.siteEmailDomains.filter(d => d.id !== domain.id);
                    this.logActivity(`Deleted domain authentication for ${domain.domain}`);
                } else {
                    this.$toast.error(res.data?.message || 'Failed to delete domain');
                }
            } catch (err) {
                console.error('Error deleting domain:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to delete domain');
            } finally {
                this.deletingDomainId = null;
            }
        },

        viewEmailStats() {
            this.$toast.info('Email statistics feature coming soon!');
        },

        openPurchaseNumber() {
            if (!this.selectedSiteForManagement) return;
            this.siteIdForPurchase = this.selectedSiteForManagement.SiteID;
            this.existingPhoneNumber = this.selectedSiteForManagement.PhoneNumber;
            this.$bvModal.show('purchase-number-modal');
        },

        viewA2PStatus() {
            if (!this.selectedSiteForManagement) return;
            // Find the A2P registration for this site and show the modal
            this.showTwilioDashboard = true;
            this.twilioActiveTab = 1; // Switch to A2P tab
            // Optionally auto-select this site's registration
            this.logActivity(`Viewing A2P status for ${this.selectedSiteForManagement.CustomerName}`);
        },

        openA2PRegistration() {
            if (!this.selectedSiteForManagement) return;

            // Debug: Log available fields to check if Country is present
            const rawCountry = this.selectedSiteForManagement.Country;
            const normalizedCountry = this.normalizeCountryCode(rawCountry);
            console.log('🔍 A2P Wizard - Selected Site Data:', {
                SiteID: this.selectedSiteForManagement.SiteID,
                CustomerName: this.selectedSiteForManagement.CustomerName,
                RawCountry: rawCountry,
                NormalizedCountry: normalizedCountry,
                AllKeys: Object.keys(this.selectedSiteForManagement)
            });

            this.a2pWizardSiteID = this.selectedSiteForManagement.SiteID;
            this.a2pWizardSiteName = this.selectedSiteForManagement.CustomerName;
            this.a2pWizardSiteCountry = this.normalizeCountryCode(this.selectedSiteForManagement.Country);

            // Close the side panel first to avoid modal being behind it
            this.showSiteManagementPanel = false;

            // Small delay to let panel close animation complete
            this.$nextTick(() => {
                this.showA2PWizard = true;
                this.$bvModal.show('a2p-registration-wizard-modal');
            });

            this.logActivity(`Opening A2P Registration wizard for ${this.selectedSiteForManagement.CustomerName}`);
        },

        closeA2PWizard() {
            this.$bvModal.hide('a2p-registration-wizard-modal');
            this.resetA2PWizard();
        },

        /**
         * Normalize country to ISO 2-letter code
         * Maps various country formats (full names, variations) to ISO codes
         */
        normalizeCountryCode(country) {
            if (!country) return null;

            const normalized = String(country).trim().toUpperCase();

            const countryMap = {
                // Canada variations
                'CA': 'CA',
                'CAN': 'CA',
                'CANADA': 'CA',
                'CANADIAN': 'CA',
                // US variations
                'US': 'US',
                'USA': 'US',
                'UNITED STATES': 'US',
                'UNITED STATES OF AMERICA': 'US',
                'AMERICA': 'US',
                'AMERICAN': 'US',
            };

            return countryMap[normalized] || null;
        },

        resetA2PWizard() {
            this.showA2PWizard = false;
            this.a2pWizardSiteID = null;
            this.a2pWizardSiteName = null;
            this.a2pWizardSiteCountry = null;
        },

        async quickAction(actionName) {
            if (!this.selectedSiteForManagement) return;

            const siteID = this.selectedSiteForManagement.SiteID;
            const customerName = this.selectedSiteForManagement.CustomerName;

            // Handle actions that need special confirmation
            if (actionName === 'De-Activate') {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to DEACTIVATE site "${customerName}"? This is PERMANENT and will delete all data!`,
                    {
                        title: 'Confirm Deactivation',
                        okVariant: 'danger',
                        okTitle: 'Yes, Deactivate',
                        cancelTitle: 'Cancel',
                        size: 'lg'
                    }
                );
                if (!confirmed) return;
            }

            if (actionName === 'Pause') {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to PAUSE site "${customerName}"? Site will be temporarily suspended but data will be preserved.`,
                    {
                        title: 'Confirm Pause',
                        okVariant: 'warning',
                        okTitle: 'Yes, Pause',
                        cancelTitle: 'Cancel'
                    }
                );
                if (!confirmed) return;
            }

            this.logActivity(`Executing "${actionName}" for ${customerName}...`);
            this.$toast.info(`Executing ${actionName}...`);

            try {
                const res = await this.sendAction({
                    siteID: siteID,
                    action: actionName
                });

                if (res && (res.status === 200 || !res.status)) {
                    this.$toast.success(`${actionName} completed successfully`);
                    this.logActivity(`✅ ${actionName} completed for ${customerName}`);

                    // Refresh customers list for status-changing actions
                    if (['Pause', 'De-Activate', 'Re-Activate'].includes(actionName)) {
                        await this.init();
                        // Also refresh deactivation status after deactivation action
                        if (actionName === 'De-Activate') {
                            await this.fetchDeactivationStatus(siteID);
                        }
                    }
                } else {
                    this.$toast.error(res.data?.message || `${actionName} failed`);
                    this.logActivity(`❌ ${actionName} failed for ${customerName}`, 'ERROR');
                }
            } catch (err) {
                console.error(`Error executing ${actionName}:`, err);
                this.$toast.error(err.response?.data?.message || `Failed to execute ${actionName}`);
                this.logActivity(`❌ ${actionName} failed for ${customerName}: ${err.message}`, 'ERROR');
            }
        },

        // ========== Restart App / Set Debug Mode (exe 3.0.60+) ==========
        // Everything goes through the teamio-admin sendAction endpoint — no raw SQL, no /runquery. The
        // backend is the gate (it refuses an old exe and sends nothing); this page only shows its verdict.

        resetExeRestart() {
            this.stopExeRestartPoll();
            this.exeRestartSeq++; // drop any in-flight read so it can't paint the next site
            this.exeRestart = {
                siteID: null, loading: false, loaded: false, error: '', sending: '',
                ownerSiteID: null, isMultiClinic: false, exeVersion: null, minExeVersion: null,
                restartAllowed: false, restartMessage: '', debugModeAllowed: false, debugModeMessage: '',
                view: null,
            };
        },

        /** The gate verdict + status line for the managed site ('Get Exe Restart Status'). */
        async loadExeRestartStatus() {
            const site = this.selectedSiteForManagement;
            if (!site) return;
            const siteID = site.SiteID;
            const seq = ++this.exeRestartSeq;
            if (this.exeRestart.siteID !== siteID) {
                this.exeRestart.loaded = false;
                this.exeRestart.view = null;
                this.exeRestart.restartAllowed = false;
                this.exeRestart.debugModeAllowed = false;
            }
            this.exeRestart.siteID = siteID;
            this.exeRestart.loading = true;
            // Per site: a slow answer for a practice the operator has since left must never enable
            // another practice's buttons.
            const isStale = () => seq !== this.exeRestartSeq
                || !this.selectedSiteForManagement
                || this.selectedSiteForManagement.SiteID !== siteID;
            try {
                const response = await this.sendAction({ siteID, action: EXE_RESTART_STATUS_ACTION });
                if (isStale()) return;
                if (!(response && (response.status === 200 || !response.status) && response.data && typeof response.data === 'object')) {
                    throw new Error(this.seedActionError(response, `Unexpected response from ${EXE_RESTART_STATUS_ACTION}`));
                }
                const d = response.data;
                this.exeRestart = {
                    siteID,
                    loading: false,
                    loaded: true,
                    error: '',
                    sending: this.exeRestart.sending,
                    ownerSiteID: d.ownerSiteID || siteID,
                    isMultiClinic: d.isMultiClinic === true,
                    exeVersion: d.exeVersion || null,
                    minExeVersion: d.minExeVersion || null,
                    // Strictly true: anything else (missing field, old backend) keeps the buttons disabled.
                    restartAllowed: d.restartAllowed === true,
                    restartMessage: typeof d.restartMessage === 'string' ? d.restartMessage : '',
                    debugModeAllowed: d.debugModeAllowed === true,
                    debugModeMessage: typeof d.debugModeMessage === 'string' ? d.debugModeMessage : '',
                    view: (d.view && typeof d.view === 'object') ? d.view : null,
                };
            } catch (err) {
                if (isStale()) return;
                this.exeRestart.loading = false;
                this.exeRestart.loaded = false;
                this.exeRestart.restartAllowed = false;
                this.exeRestart.debugModeAllowed = false;
                this.exeRestart.error = `Couldn't check whether this exe can be restarted from here: ${err.message || 'Unknown error'}`;
            }
        },

        /** Enabled only on the backend's explicit "allowed" for THIS site, and never while a command is in flight. */
        exeRestartCanSend(kind) {
            const site = this.selectedSiteForManagement;
            const r = this.exeRestart;
            if (!site || r.siteID !== site.SiteID || !r.loaded || r.sending) return false;
            return kind === 'restart' ? r.restartAllowed === true : r.debugModeAllowed === true;
        },

        /** Why the button is disabled — the backend's own refusal wording. '' when it is enabled. */
        exeRestartBlockedReason(kind) {
            const site = this.selectedSiteForManagement;
            const r = this.exeRestart;
            if (!site) return '';
            if (r.siteID !== site.SiteID || (r.loading && !r.loaded)) return 'Checking the exe version…';
            if (r.error) return r.error;
            if (!r.loaded) return 'Checking the exe version…';
            if (kind === 'restart') return r.restartAllowed ? '' : (r.restartMessage || 'Not available for this exe.');
            return r.debugModeAllowed ? '' : (r.debugModeMessage || 'Not available for this exe.');
        },

        /**
         * Restart App / Set Debug Mode to Debug / to Default. Confirmed first; the backend's answer — sent,
         * sent by one route only, or refused — is shown verbatim, then the status line is re-read and
         * watched until the exe answers. Own handler, never quickAction: that one says "completed
         * successfully" for any 200, including a refusal.
         */
        async sendExeRestartCommand(kind) {
            const command = EXE_RESTART_COMMANDS[kind];
            const site = this.selectedSiteForManagement;
            if (!command || !site || !this.exeRestartCanSend(kind)) return;
            const siteID = site.SiteID;
            const customerName = site.CustomerName;
            const isStale = () => !this.selectedSiteForManagement || this.selectedSiteForManagement.SiteID !== siteID;

            const confirmed = await this.$bvModal.msgBoxConfirm(command.confirm(customerName, this.exeRestart.exeVersion), {
                title: command.title,
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'warning',
                okTitle: command.okTitle,
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                centered: true
            });
            if (!confirmed || isStale() || !this.exeRestartCanSend(kind)) return;

            this.exeRestart.sending = kind;
            this.logActivity(`Executing "${command.action}" for ${customerName}...`);
            let sent = false;
            try {
                const payload = { siteID, action: command.action };
                // Who asked — stored in the EXE_RESTART_REQUEST row (optional in the contract).
                if (kind === 'restart' && this.user && this.user.Username) payload.requestedBy = this.user.Username;
                const response = await this.sendAction(payload);
                if (!(response && (response.status === 200 || !response.status) && response.data && typeof response.data === 'object')) {
                    throw new Error(this.seedActionError(response, `Unexpected response from ${command.action}`));
                }
                const d = response.data;
                sent = d.sent === true;
                const message = (typeof d.message === 'string' && d.message) ? d.message : (sent ? 'Sent.' : 'Not sent.');
                const oneRouteFailed = !!(d.routes && ((d.routes.settingsRow && d.routes.settingsRow.ok === false)
                    || (d.routes.queue && d.routes.queue.ok === false)));
                if (sent && !oneRouteFailed) {
                    this.$toast.success(message);
                    this.logActivity(`✅ ${command.action} sent for ${customerName}: ${message}`);
                } else {
                    this.$toast.warning(message);
                    this.logActivity(`⚠️ ${command.action} for ${customerName}: ${message}`, 'WARNING');
                }
            } catch (err) {
                const message = `Couldn't send ${command.action}: ${extractApiError(err, err && err.message ? err.message : 'Unknown error')}`;
                this.$toast.error(message);
                this.logActivity(`❌ ${message} (${customerName})`, 'ERROR');
            } finally {
                if (this.exeRestart.siteID === siteID) this.exeRestart.sending = '';
            }
            if (isStale()) return;
            await this.loadExeRestartStatus();
            if (sent && !isStale()) this.startExeRestartPoll(siteID);
        },

        /** Re-read the status line every EXE_RESTART_POLL_EVERY_MS until the exe answers, the site changes, or time is up. */
        startExeRestartPoll(siteID) {
            this.stopExeRestartPoll();
            const until = Date.now() + EXE_RESTART_POLL_FOR_MS;
            const sameSite = () => !!this.selectedSiteForManagement && this.selectedSiteForManagement.SiteID === siteID;
            const tick = async () => {
                this.exeRestartPollTimer = null;
                if (!sameSite()) return;
                await this.loadExeRestartStatus();
                const view = this.exeRestart.siteID === siteID ? this.exeRestart.view : null;
                const settled = !view || view.state === 'restarted' || view.state === 'refused' || view.state === 'none';
                // A newer poll (another send) owns the timer now — leave it alone.
                if (!settled && Date.now() < until && sameSite() && !this.exeRestartPollTimer) {
                    this.exeRestartPollTimer = setTimeout(tick, EXE_RESTART_POLL_EVERY_MS);
                }
            };
            this.exeRestartPollTimer = setTimeout(tick, EXE_RESTART_POLL_EVERY_MS);
        },

        stopExeRestartPoll() {
            if (this.exeRestartPollTimer) clearTimeout(this.exeRestartPollTimer);
            this.exeRestartPollTimer = null;
        },

        // ========== Exe per-site OD table overrides ==========
        // Reads/writes EXE_OD_TABLE_OVERRIDES through the teamio-admin sendAction endpoint —
        // there is NO raw SQL and no /runquery here, and the browser's validation is a
        // convenience: the backend re-validates and is the gate.

        resetExeTableOverrides() {
            this.exeTableOverridesSeq++; // drop any in-flight load so it can't paint the next site
            this.exeTableOverrides = {
                siteID: null, loading: false, saving: false, loaded: false, error: '',
                ownerSiteID: null, isMultiClinic: false, exeVersion: null,
                minExeVersion: EXE_MIN_VERSION, exeSupportsOverrides: null, malformed: false,
                saved: { unignore: [], ignore: [] }, applied: null,
                unignoreText: '', ignoreText: '',
            };
        },

        async loadExeTableOverrides() {
            const site = this.selectedSiteForManagement;
            if (!site) return;
            const siteID = site.SiteID;
            const seq = ++this.exeTableOverridesSeq;
            this.exeTableOverrides.siteID = siteID;
            this.exeTableOverrides.loading = true;
            this.exeTableOverrides.error = '';
            // Stale-response guard: table overrides are PER SITE. A slow answer for a site the
            // operator has since left must never paint another practice's lists here.
            const isStale = () => seq !== this.exeTableOverridesSeq
                || !this.selectedSiteForManagement
                || this.selectedSiteForManagement.SiteID !== siteID;
            try {
                const response = await this.sendAction({ siteID, action: 'Get Exe Table Overrides' });
                if (isStale()) return;
                if (!(response && (response.status === 200 || !response.status) && response.data && typeof response.data === 'object')) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from Get Exe Table Overrides'));
                }
                const d = response.data;
                const overrides = d.overrides || {};
                const unignore = Array.isArray(overrides.unignore) ? overrides.unignore : [];
                const ignore = Array.isArray(overrides.ignore) ? overrides.ignore : [];
                this.exeTableOverrides = {
                    siteID,
                    loading: false,
                    saving: false,
                    loaded: true,
                    error: '',
                    ownerSiteID: d.ownerSiteID || siteID,
                    isMultiClinic: d.isMultiClinic === true,
                    exeVersion: d.exeVersion || null,
                    minExeVersion: d.minExeVersion || EXE_MIN_VERSION,
                    exeSupportsOverrides: d.exeSupportsOverrides === true,
                    malformed: d.malformed === true,
                    saved: { unignore, ignore },
                    applied: (d.applied && typeof d.applied === 'object') ? d.applied : null,
                    unignoreText: unignore.join(', '),
                    ignoreText: ignore.join(', '),
                };
            } catch (err) {
                if (isStale()) return;
                this.exeTableOverrides.loading = false;
                this.exeTableOverrides.loaded = false;
                this.exeTableOverrides.error = `Couldn't load the exe table overrides: ${err.message || 'Unknown error'}`;
            }
        },

        /**
         * ⛔ Saves the setting and NOTHING ELSE. No refresh, no restart, no dump is sent from here —
         * a mysqldump kicked off automatically would run on the practice's own server mid-day. The
         * operator clicks Request MySQL Dump / Refresh Exe Table List when they mean to.
         */
        async saveExeTableOverrides() {
            const site = this.selectedSiteForManagement;
            if (!site || this.exeTableOverridesSaveBlocked) return;
            const siteID = site.SiteID;
            this.exeTableOverrides.saving = true;
            try {
                const response = await this.sendAction({
                    siteID,
                    action: 'Save Exe Table Overrides',
                    unignore: this.exeTableOverridesUnignoreNames,
                    ignore: this.exeTableOverridesIgnoreNames,
                });
                if (!(response && (response.status === 200 || !response.status) && response.data)) {
                    // The server's own refusal wording is the useful part — show it verbatim.
                    throw new Error(this.seedActionError(response, 'Unexpected response from Save Exe Table Overrides'));
                }
                this.$toast.success('Table overrides saved — nothing was sent to the exe yet');
                this.logActivity(`Saved exe table overrides for ${site.CustomerName}`);
            } catch (err) {
                this.$toast.error(err.message || 'Failed to save table overrides');
                this.logActivity(`❌ Saving exe table overrides failed for ${site.CustomerName}: ${err.message}`, 'ERROR');
            } finally {
                this.exeTableOverrides.saving = false;
            }
            // Re-read either way: on success to pick up the stored value, on failure to prove
            // nothing changed.
            await this.loadExeTableOverrides();
        },

        // ========== Test File Write Methods ==========
        async loadTestFileWriteCategories() {
            if (this.testFileWrite.categoriesLoaded || !this.selectedSiteForManagement) return;
            this.testFileWrite.categoriesLoading = true;
            try {
                // getForms returns { forms, categories } — categories are OD imaging categories
                const res = await this.$store.dispatch('forms/getForms', {
                    siteID: this.selectedSiteForManagement.SiteID
                });
                this.testFileWrite.categories = res.categories || [];
                this.testFileWrite.categoriesLoaded = true;
            } catch (err) {
                console.error('Failed to fetch imaging categories:', err);
                this.$toast.error('Failed to load imaging categories');
            } finally {
                this.testFileWrite.categoriesLoading = false;
            }
        },

        async sendTestFileWrite() {
            if (!this.selectedSiteForManagement || !this.testFileWrite.pdfCategory || !this.testFileWrite.patNum) return;

            const siteID = this.selectedSiteForManagement.SiteID;
            const customerName = this.selectedSiteForManagement.CustomerName;

            this.testFileWrite.loading = true;
            this.$toast.info('Sending test file to EXE...');

            try {
                const res = await this.sendAction({
                    siteID: siteID,
                    action: 'Test File Write',
                    patNum: this.testFileWrite.patNum,
                    pdfCategory: this.testFileWrite.pdfCategory
                });

                if (res && (res.status === 200 || !res.status)) {
                    this.$toast.success(`Test file sent to EXE for ${customerName}. Check Open Dental Imaging for PatNum ${this.testFileWrite.patNum}.`);
                    this.logActivity(`✅ Test File Write sent for ${customerName} - PatNum: ${this.testFileWrite.patNum}`);
                } else {
                    this.$toast.error(res.data?.message || 'Test File Write failed');
                    this.logActivity(`❌ Test File Write failed for ${customerName}`, 'ERROR');
                }
            } catch (err) {
                console.error('Error sending test file write:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to send test file');
                this.logActivity(`❌ Test File Write failed for ${customerName}: ${err.message}`, 'ERROR');
            } finally {
                this.testFileWrite.loading = false;
            }
        },

        // ========== Deactivation Status Methods ==========
        async fetchDeactivationStatus(siteID) {
            this.siteDeactivationStatusLoading = true;
            try {
                const res = await this.getDeactivationStatus({ siteID });
                if (res && res.status === 200 && res.data) {
                    this.siteDeactivationStatus = res.data;
                } else {
                    this.siteDeactivationStatus = null;
                }
            } catch (err) {
                console.error('Error fetching deactivation status:', err);
                this.siteDeactivationStatus = null;
            } finally {
                this.siteDeactivationStatusLoading = false;
            }
        },

        async handleResumeDeactivation() {
            if (!this.selectedSiteForManagement) return;

            const siteID = this.selectedSiteForManagement.SiteID;
            const customerName = this.selectedSiteForManagement.CustomerName;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Resume deactivation for "${customerName}"? This will retry the cleanup from where it failed.`,
                {
                    title: 'Resume Deactivation',
                    okVariant: 'warning',
                    okTitle: 'Yes, Resume',
                    cancelTitle: 'Cancel'
                }
            );
            if (!confirmed) return;

            this.deactivationActionLoading = true;
            this.logActivity(`Resuming deactivation for ${customerName}...`);

            try {
                const res = await this.resumeDeactivation({ siteID });
                if (res && res.status === 200) {
                    this.$toast.success('Deactivation resumed successfully');
                    this.logActivity(`✅ Deactivation resumed for ${customerName}`);
                    // Refresh status
                    await this.fetchDeactivationStatus(siteID);
                    // Refresh customer list
                    await this.init();
                } else {
                    this.$toast.error(res.data?.message || 'Failed to resume deactivation');
                    this.logActivity(`❌ Failed to resume deactivation for ${customerName}`, 'ERROR');
                }
            } catch (err) {
                console.error('Error resuming deactivation:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to resume deactivation');
                this.logActivity(`❌ Resume deactivation failed for ${customerName}: ${err.message}`, 'ERROR');
            } finally {
                this.deactivationActionLoading = false;
            }
        },

        async handleCancelDeactivation() {
            if (!this.selectedSiteForManagement) return;

            const siteID = this.selectedSiteForManagement.SiteID;
            const customerName = this.selectedSiteForManagement.CustomerName;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Cancel pending deactivation for "${customerName}"? The site will be reactivated.`,
                {
                    title: 'Cancel Deactivation',
                    okVariant: 'success',
                    okTitle: 'Yes, Cancel & Reactivate',
                    cancelTitle: 'Keep Pending'
                }
            );
            if (!confirmed) return;

            this.deactivationActionLoading = true;
            this.logActivity(`Canceling deactivation for ${customerName}...`);

            try {
                const res = await this.cancelDeactivation({ siteID });
                if (res && res.status === 200) {
                    this.$toast.success('Deactivation canceled - site reactivated');
                    this.logActivity(`✅ Deactivation canceled for ${customerName} - site reactivated`);
                    // Refresh status
                    await this.fetchDeactivationStatus(siteID);
                    // Refresh customer list
                    await this.init();
                } else {
                    this.$toast.error(res.data?.message || 'Failed to cancel deactivation');
                    this.logActivity(`❌ Failed to cancel deactivation for ${customerName}`, 'ERROR');
                }
            } catch (err) {
                console.error('Error canceling deactivation:', err);
                this.$toast.error(err.response?.data?.message || 'Failed to cancel deactivation');
                this.logActivity(`❌ Cancel deactivation failed for ${customerName}: ${err.message}`, 'ERROR');
            } finally {
                this.deactivationActionLoading = false;
            }
        },

        getDeactivationStatusColor(status) {
            const colors = {
                'active': 'success',
                'phase1_complete': 'warning',
                'phase2_scheduled': 'info',
                'phase2_in_progress': 'primary',
                'completed': 'secondary',
                'failed': 'danger'
            };
            return colors[status] || 'secondary';
        },

        getDeactivationStatusIcon(status) {
            const icons = {
                'active': 'fa-check-circle',
                'phase1_complete': 'fa-clock',
                'phase2_scheduled': 'fa-calendar-check',
                'phase2_in_progress': 'fa-spinner fa-spin',
                'completed': 'fa-trash-alt',
                'failed': 'fa-exclamation-triangle'
            };
            return icons[status] || 'fa-question-circle';
        },
        // ========== End Deactivation Status Methods ==========

        // ========== End Site Management UI Methods ==========

        async initiateEmailVerification() {
            // Validate inputs based on verification type
            if (this.verificationType === 'domain' && !this.domainToVerify.trim()) {
                this.verificationModalError = 'Please enter a domain to verify';
                return;
            }
            if (this.verificationType === 'single_sender' && !this.emailToVerify.trim()) {
                this.verificationModalError = 'Please enter an email address to verify';
                return;
            }

            this.verificationModalBusy = true;
            this.verificationModalError = '';
            try {
                const payload = {
                    siteID: this.siteIdForEmailVerification,
                    action: 'VERIFY_EMAIL',
                    verificationType: this.verificationType,
                    domainName: this.verificationType === 'domain' ? this.domainToVerify : null,
                    emailAddress: this.verificationType === 'single_sender' ? this.emailToVerify : null
                };
                console.log('Frontend: Sending email verification request', payload);
                const res = await this.sendAction(payload);

                if (res && res.status === 200 && res.data && res.data.verificationDetails) {
                    this.verificationStatus = 'success';
                    this.verificationSuccessMessage = res.data.message || 'Verification initiated successfully!';
                    
                    if (this.verificationType === 'domain') {
                        // Handle domain authentication response
                        const details = res.data.verificationDetails;
                        this.dnsRecordsToShow = details.dns_records || [];
                        this.domainBeingVerified = details.domain;
                        this.domainId = details.domain_id;
                        if (details.already_existed) {
                            this.verificationSuccessMessage = details.status === 'verified'
                                ? `Domain ${details.domain} is already verified and ready to use!`
                                : `Domain ${details.domain} was already registered. DNS records shown below.`;
                        }
                        this.logActivity(`Domain authentication ${details.already_existed ? '(already existed)' : 'initiated'} for ${details.domain}. DNS records provided.`);
                    } else if (this.verificationType === 'single_sender') {
                        // Handle single sender verification response
                        this.showSingleSenderInstructions = true;
                        this.emailBeingVerified = this.emailToVerify;
                        this.logActivity(`Single sender verification email sent to ${this.emailToVerify}`);
                    }
                    
                    this.logActivity(`Email verification initiated successfully for Site ID: ${this.siteIdForEmailVerification}`);
                } else {
                    this.verificationModalError = res.data?.message || 'An error occurred while initiating email verification';
                    this.logActivity(`Email verification failed for Site ID: ${this.siteIdForEmailVerification}. Error: ${this.verificationModalError}`, 'ERROR');
                }
            } catch (err) {
                console.error('Email verification error:', err);
                this.verificationModalError = err.response?.data?.message || err.message || 'An error occurred while initiating email verification';
                this.logActivity(`Email verification failed for Site ID: ${this.siteIdForEmailVerification}. Error: ${this.verificationModalError}`, 'ERROR');
            } finally {
                this.verificationModalBusy = false;
            }
        },

        canInitiateVerification() {
            if (this.verificationType === 'domain') {
                return this.siteIdForEmailVerification && this.domainToVerify.trim();
            } else {
                return this.siteIdForEmailVerification && this.emailToVerify.trim();
            }
        },

        async checkDomainVerification() {
            if (!this.domainId) return;
            
            this.checkingVerification = true;
            try {
                const payload = {
                    siteID: this.siteIdForEmailVerification,
                    action: 'VERIFY_EMAIL',
                    subAction: 'CHECK_DOMAIN_VERIFICATION',
                    domainId: this.domainId
                };
                
                const res = await this.sendAction(payload);
                
                if (res && res.status === 200 && res.data && res.data.verificationDetails) {
                    const details = res.data.verificationDetails;
                    if (details.valid) {
                        this.$toast.success('Domain verification successful!');
                        this.verificationSuccessMessage = 'Domain successfully verified! Emails can now be sent from this domain.';
                        this.logActivity(`Domain ${this.domainBeingVerified} successfully verified for Site ID: ${this.siteIdForEmailVerification}`);
                    } else {
                        this.$toast.warning('Domain verification pending. Please ensure DNS records are added correctly.');
                        this.logActivity(`Domain ${this.domainBeingVerified} verification still pending for Site ID: ${this.siteIdForEmailVerification}`);
                    }
                } else {
                    this.$toast.error('Error checking domain verification status');
                }
            } catch (err) {
                console.error('Domain verification check error:', err);
                this.$toast.error('Error checking domain verification status');
            } finally {
                this.checkingVerification = false;
            }
        },

        async sendDnsRecordsEmail() {
            if (!this.siteIdForEmailVerification || !this.domainId) return;
            const parsed = this.parseDnsExtraRecipients(this.dnsEmailRecipients);
            if (parsed.error) {
                this.$toast.error(parsed.error);
                return;
            }

            this.sendingDnsEmail = true;
            const domain = this.domainBeingVerified || 'your domain';
            try {
                const sentTo = await this.postDnsRecordsEmail({
                    siteID: this.siteIdForEmailVerification,
                    domainId: this.domainId,
                    extraRecipients: parsed.recipients
                });
                this.$toast.success(`DNS records emailed to ${sentTo.join(', ')}`);
                this.logActivity(`Emailed DNS records for ${domain} to ${sentTo.join(', ')}`);
                this.showDnsEmailForm = false;
                this.dnsEmailRecipients = '';
            } catch (err) {
                console.error('Error sending DNS records email:', err);
                this.$toast.error('Failed to send DNS records email: ' + (err.response?.data?.message || err.message));
            } finally {
                this.sendingDnsEmail = false;
            }
        },

        async checkSenderVerification() {
            this.checkingVerification = true;
            try {
                const payload = {
                    siteID: this.siteIdForEmailVerification,
                    action: 'VERIFY_EMAIL',
                    subAction: 'CHECK_SENDER_VERIFICATION'
                };
                
                const res = await this.sendAction(payload);
                
                if (res && res.status === 200 && res.data && res.data.verificationDetails) {
                    const details = res.data.verificationDetails;
                    if (details.sender_verified) {
                        this.$toast.success('Sender verification successful!');
                        this.verificationSuccessMessage = 'Sender successfully verified! Emails can now be sent from this address.';
                        this.logActivity(`Sender ${this.emailBeingVerified} successfully verified for Site ID: ${this.siteIdForEmailVerification}`);
                    } else {
                        this.$toast.warning('Sender verification pending. Please check email and click verification link.');
                        this.logActivity(`Sender ${this.emailBeingVerified} verification still pending for Site ID: ${this.siteIdForEmailVerification}`);
                    }
                } else {
                    this.$toast.error('Error checking sender verification status');
                }
            } catch (err) {
                console.error('Sender verification check error:', err);
                this.$toast.error('Error checking sender verification status');
            } finally {
                this.checkingVerification = false;
            }
        },

        copyToClipboard(text) {
            if (navigator.clipboard && window.isSecureContext) {
                // Use the modern clipboard API
                navigator.clipboard.writeText(text).then(() => {
                    this.$toast.success('Copied to clipboard!');
                }).catch(err => {
                    console.error('Failed to copy: ', err);
                    this.fallbackCopyTextToClipboard(text);
                });
            } else {
                // Fallback for older browsers
                this.fallbackCopyTextToClipboard(text);
            }
        },

        fallbackCopyTextToClipboard(text) {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            
            // Avoid scrolling to bottom
            textArea.style.top = "0";
            textArea.style.left = "0";
            textArea.style.position = "fixed";

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    this.$toast.success('Copied to clipboard!');
                } else {
                    this.$toast.error('Failed to copy to clipboard');
                }
            } catch (err) {
                console.error('Failed to copy: ', err);
                this.$toast.error('Failed to copy to clipboard');
            }

            document.body.removeChild(textArea);
        },



        async checkExistingVerifiedEmails() {
            // TODO: Implement API call to get existing verified emails/domains for this site
            // For now, just initialize empty array - this will be implemented when backend is ready
            this.currentVerifiedEmails = [];
            
            try {
                // Future implementation:
                // const res = await this.sendAction({
                //     siteID: this.siteIdForEmailVerification,
                //     action: 'GET_VERIFIED_EMAILS'
                // });
                // this.currentVerifiedEmails = res.data.verifiedEmails || [];
            } catch (error) {
                console.error('Error fetching verified emails:', error);
                this.currentVerifiedEmails = [];
            }
        },

        // ---- Bulk Form Copy Methods ----
        async loadSourceSiteForms() {
            if (!this.bulkFormCopy.sourceSiteID) {
                this.bulkFormCopy.availableForms = [];
                this.bulkFormCopy.selectedFormIDs = [];
                this.bulkFormCopy.selectAll = false;
                this.bulkFormCopy.error = '';
                return;
            }

            console.log(`[DEBUG] loadSourceSiteForms: Starting to load forms for sourceSiteID: ${this.bulkFormCopy.sourceSiteID}`);

            this.bulkFormCopy.loading = true;
            this.bulkFormCopy.error = '';
            this.bulkFormCopy.availableForms = [];
            this.bulkFormCopy.selectedFormIDs = [];
            this.bulkFormCopy.selectAll = false;

            try {
                console.log(`[DEBUG] loadSourceSiteForms: Calling forms/getForms with payload:`, {
                    siteID: this.bulkFormCopy.sourceSiteID
                });

                // Use the existing forms store action to get forms for the source site
                const response = await this.$store.dispatch('forms/getForms', {
                    siteID: this.bulkFormCopy.sourceSiteID
                });

                console.log(`[DEBUG] loadSourceSiteForms: Raw response from forms/getForms:`, response);

                // Handle the actual response structure: {forms: Array, categories: Array}
                const formsData = response?.forms || response?.data?.forms || response?.data;
                
                console.log(`[DEBUG] loadSourceSiteForms: Extracted formsData:`, formsData);
                console.log(`[DEBUG] loadSourceSiteForms: formsData is array:`, Array.isArray(formsData));
                console.log(`[DEBUG] loadSourceSiteForms: formsData length:`, formsData?.length);
                
                if (formsData && Array.isArray(formsData)) {
                    this.bulkFormCopy.availableForms = formsData;
                    
                    console.log(`[DEBUG] loadSourceSiteForms: First few forms:`, formsData.slice(0, 3));
                    console.log(`[DEBUG] loadSourceSiteForms: Form field names from first form:`, formsData[0] ? Object.keys(formsData[0]) : 'No forms');
                    
                    if (formsData.length === 0) {
                        this.bulkFormCopy.error = 'This site has no forms to copy. Please select a different source site.';
                        this.logActivity(`Source site has no forms available for copying`, 'INFO');
                    } else {
                        this.logActivity(`Loaded ${formsData.length} forms from source site ${this.bulkFormCopy.sourceSiteID}`);
                        console.log(`[DEBUG] loadSourceSiteForms: Successfully loaded ${formsData.length} forms`);
                    }
                } else {
                    this.bulkFormCopy.error = 'Invalid response from server. Please try again or contact support.';
                    this.logActivity(`Invalid response structure from forms API: ${JSON.stringify(response)}`, 'ERROR');
                    console.error(`[DEBUG] loadSourceSiteForms: Invalid response structure:`, response);
                }
            } catch (error) {
                this.bulkFormCopy.error = `Error loading forms: ${error.message || 'Unknown error'}`;
                this.logActivity(`Error loading forms from source site ${this.bulkFormCopy.sourceSiteID}: ${error.message}`, 'ERROR');
                console.error(`[DEBUG] loadSourceSiteForms: Error loading forms:`, error);
            } finally {
                this.bulkFormCopy.loading = false;
            }
        },

        toggleSelectAll() {
            if (this.bulkFormCopy.selectAll) {
                this.bulkFormCopy.selectedFormIDs = this.bulkFormCopy.availableForms.map(form => form.FormIDForWeb);
            } else {
                this.bulkFormCopy.selectedFormIDs = [];
            }
        },

        async executeBulkFormCopy() {
            if (!this.canExecuteBulkCopy) {
                this.$toast.error('Please fill in all required fields and select forms to copy');
                return;
            }

            // Get site names for confirmation dialog
            const sourceSite = this.customers.find(c => c.SiteID === this.bulkFormCopy.sourceSiteID);
            const targetSite = this.customers.find(c => c.SiteID === this.bulkFormCopy.targetSiteID);
            const sourceName = sourceSite ? sourceSite.CustomerName : this.bulkFormCopy.sourceSiteID;
            const targetName = targetSite ? targetSite.CustomerName : this.bulkFormCopy.targetSiteID;

            // Target-site OD imaging category for each FormType in the selection (only the required types are sent).
            const imaging = this.bulkFormCopy.imaging;
            const categoryMap = {};
            this.bulkCopyRequiredTypes.forEach(t => { categoryMap[t] = String(imaging.map[t]).trim(); });
            const mappingText = this.bulkCopyRequiredTypes
                .map(t => `• ${FORM_IMAGING_TYPE_LABELS[t]} → ${categoryName(imaging.categories, categoryMap[t]) || categoryMap[t]}`)
                .join('\n');

            // Confirmation dialog
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to copy ${this.bulkFormCopy.selectedFormIDs.length} forms from "${sourceName}" to "${targetName}"?` +
                `\n\nOpen Dental imaging categories on "${targetName}":\n${mappingText}` +
                `${this.bulkFormCopy.overwriteExisting ? '\n\nWARNING: This will overwrite existing forms with the same name!' : ''}`,
                {
                    title: 'Confirm Bulk Form Copy',
                    size: 'lg',
                    buttonSize: 'sm',
                    okVariant: 'success',
                    okTitle: 'Yes, Copy Forms',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                }
            );

            if (!confirmed) {
                this.logActivity('User cancelled bulk form copy operation');
                this.bulkFormCopy.copying = false; // Ensure copying state is cleared
                return;
            }

            this.bulkFormCopy.copying = true;
            this.bulkFormCopy.error = '';
            this.bulkFormCopy.results = null;

            // Snapshot: the selection and options must not change under a run that takes minutes.
            const formIDs = this.bulkFormCopy.selectedFormIDs.slice();
            const sourceSiteID = this.bulkFormCopy.sourceSiteID;
            const targetSiteID = this.bulkFormCopy.targetSiteID;
            const overwriteExisting = this.bulkFormCopy.overwriteExisting;
            const formNames = {};
            this.bulkFormCopy.availableForms.forEach(f => { formNames[f.FormIDForWeb] = f.Name; });

            // No overall timer: each request is one form and API Gateway ends any request at 29s,
            // so the run always finishes. (The old 30s timer fired while the copy was still going.)
            try {
                this.logActivity(`Starting bulk copy of ${formIDs.length} forms from "${sourceName}" to "${targetName}" (one form per request)`);

                const results = await runBulkFormCopy({
                    formIDs,
                    nameOf: id => formNames[id],
                    describeError: response => this.seedActionError(response, 'Unknown error occurred'),
                    onProgress: p => { this.bulkFormCopy.progress = p; },
                    send: formID => this.sendAction({
                        siteID: targetSiteID,
                        action: 'BULK_COPY_FORMS',
                        sourceSiteID,
                        formIDs: [formID],
                        overwriteExisting,
                        categoryMap,
                    }),
                });

                this.bulkFormCopy.results = results;
                const { successCount, failureCount, skippedCount, unknownCount, notAttemptedCount, stoppedReason } = results;
                const summary = `${successCount} copied, ${failureCount} failed, ${skippedCount} skipped` +
                    (unknownCount ? `, ${unknownCount} unconfirmed` : '') +
                    (notAttemptedCount ? `, ${notAttemptedCount} not attempted` : '');

                if (stoppedReason) {
                    // Backend refusals (e.g. incomplete imaging map) come back as a bare JSON string.
                    this.bulkFormCopy.error = `Copy stopped: ${stoppedReason}`;
                    this.$toast.error(`${this.bulkFormCopy.error} (${summary})`);
                    this.logActivity(`Bulk form copy stopped: ${stoppedReason} (${summary})`, 'ERROR');
                } else if (unknownCount > 0) {
                    this.$toast.warning(`${summary}. Reload the target site's forms to confirm the unconfirmed ones.`);
                    this.logActivity(`Bulk form copy finished with unconfirmed forms: ${summary}`, 'WARN');
                } else if (failureCount === 0 && successCount > 0) {
                    this.$toast.success(`Successfully copied ${successCount} forms!`);
                    this.logActivity(`Bulk form copy completed successfully: ${summary}`);
                } else if (successCount > 0 && failureCount > 0) {
                    this.$toast.warning(`Partially successful: ${summary}`);
                    this.logActivity(`Bulk form copy partially completed: ${summary}`);
                } else if (failureCount > 0 && successCount === 0) {
                    this.$toast.error(`Copy failed: ${failureCount} forms failed to copy`);
                    this.logActivity(`Bulk form copy failed: ${summary}`, 'ERROR');
                } else if (skippedCount === formIDs.length) {
                    this.$toast.info(`All forms skipped (${skippedCount} forms already exist)`);
                    this.logActivity(`Bulk form copy: all ${skippedCount} forms skipped (already exist)`);
                } else {
                    this.$toast.info(`Operation completed: ${summary}`);
                    this.logActivity(`Bulk form copy completed with mixed results: ${summary}`);
                }
            } catch (error) {
                this.bulkFormCopy.error = `Copy failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Bulk form copy failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.bulkFormCopy.error);
            } finally {
                this.bulkFormCopy.copying = false;
                this.bulkFormCopy.progress = null;
            }
        },

        resetBulkFormCopy() {
            this.showBulkFormCopy = false;
            this.bulkCopyImagingRequestSeq++; // drop any in-flight target-category load
            this.bulkFormCopy = {
                sourceSiteID: null,
                targetSiteID: null,
                overwriteExisting: false,
                availableForms: [],
                selectedFormIDs: [],
                selectAll: false,
                loading: false,
                copying: false,
                progress: null,
                error: '',
                results: null,
                imaging: { siteID: null, loading: false, error: '', categories: [], consentCandidates: [], map: emptyCategoryMap() },
            };
        },

        // ---- Copy Form Section Methods ----
        /**
         * Load the forms (ConfigData included, like loadSourceSiteForms) of the source or target site.
         * The section pickers parse the chosen form's ConfigData client-side.
         * @param {'source'|'target'} which
         * @param {String} [keepFormID] - re-select this form once loaded (after a copy changed it)
         */
        async loadSectionCopyForms(which, keepFormID) {
            const sc = this.sectionCopy;
            const isSource = which === 'source';
            const siteID = isSource ? sc.sourceSiteID : sc.targetSiteID;
            const formsKey = isSource ? 'sourceForms' : 'targetForms';
            const formKey = isSource ? 'sourceFormID' : 'targetFormID';
            const loadingKey = isSource ? 'loadingSource' : 'loadingTarget';
            const seq = ++this.sectionCopySeq[which];
            const isStale = () => seq !== this.sectionCopySeq[which] || siteID !== (isSource ? sc.sourceSiteID : sc.targetSiteID);

            sc[formsKey] = [];
            sc[formKey] = null;
            if (isSource) sc.sectionId = null; else sc.position = 'end';
            sc.dryRunDone = false;
            sc.dryRunKey = '';
            sc.error = '';
            if (!keepFormID) sc.result = null;
            if (!siteID) {
                sc[loadingKey] = false;
                return;
            }

            sc[loadingKey] = true;
            try {
                const response = await this.$store.dispatch('forms/getForms', { siteID });
                if (isStale()) return;
                const formsData = response?.forms || response?.data?.forms || response?.data;
                if (!Array.isArray(formsData)) {
                    throw new Error('Invalid response from the forms API');
                }
                sc[formsKey] = formsData.slice().sort((a, b) =>
                    String(a.Name || '').localeCompare(String(b.Name || ''), undefined, { sensitivity: 'base' }));
                if (keepFormID && sc[formsKey].some(f => f.FormIDForWeb === keepFormID)) {
                    sc[formKey] = keepFormID;
                }
                if (formsData.length === 0) {
                    sc.error = `The ${which} site has no forms.`;
                }
                this.logActivity(`Copy Form Section: loaded ${formsData.length} forms from ${which} site ${siteID}`);
            } catch (error) {
                if (isStale()) return;
                sc.error = `Error loading the ${which} site's forms: ${error.message || 'Unknown error'}`;
                this.logActivity(`Copy Form Section: error loading ${which} forms for ${siteID}: ${error.message}`, 'ERROR');
            } finally {
                if (seq === this.sectionCopySeq[which]) sc[loadingKey] = false;
            }
        },

        onSectionCopySourceFormChange() {
            this.sectionCopy.sectionId = null;
        },

        onSectionCopyTargetFormChange() {
            this.sectionCopy.position = 'end';
        },

        /**
         * COPY_FORM_SECTION through sendAction (which never throws). dryRun=true changes nothing and
         * unlocks Copy for these exact picks; the real run asks first, then the server backs the
         * target up to S3 and saves. A gateway "no answer" on the real run may still have applied.
         */
        async runSectionCopy(dryRun) {
            const sc = this.sectionCopy;
            if (dryRun ? !this.canDryRunSectionCopy : !this.canRunSectionCopy) {
                this.$toast.error(dryRun
                    ? 'Pick the source site, form and section, and the target site and form first'
                    : 'Run a dry run of these exact picks first');
                return;
            }

            const selectionKey = this.sectionCopySelectionKey;
            const section = this.sectionCopySelectedSection;
            const siteName = (id) => {
                const c = (this.customers || []).find(x => x.SiteID === id);
                return c ? c.CustomerName : id;
            };
            const formName = (forms, id) => {
                const f = forms.find(x => x.FormIDForWeb === id);
                return f ? f.Name : id;
            };
            const sourceText = `"${formName(sc.sourceForms, sc.sourceFormID)}" (${siteName(sc.sourceSiteID)})`;
            const targetText = `"${formName(sc.targetForms, sc.targetFormID)}" (${siteName(sc.targetSiteID)})`;
            const anchor = this.sectionCopyPositionOptions.find(o => o.value === sc.position);
            const positionText = sc.position === 'end' ? 'at the end' : (anchor ? anchor.text.replace(/^After /, 'after ') : `after ${sc.position}`);
            const payload = {
                siteID: sc.targetSiteID,
                action: 'COPY_FORM_SECTION',
                sourceSiteID: sc.sourceSiteID,
                sourceFormID: sc.sourceFormID,
                sectionId: sc.sectionId,
                targetFormID: sc.targetFormID,
                dryRun: !!dryRun,
                position: sc.position === 'end' ? 'end' : { afterSectionId: sc.position },
            };

            if (!dryRun) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Copy the section "${section.title}" from ${sourceText} into ${targetText}, ${positionText}?` +
                    `\n\nThe target form's current version is backed up to S3 first, then the form is saved with the new section.`,
                    {
                        title: 'Confirm Copy Form Section',
                        size: 'lg',
                        buttonSize: 'sm',
                        okVariant: 'success',
                        okTitle: 'Yes, Copy Section',
                        cancelTitle: 'Cancel',
                        footerClass: 'p-2',
                        hideHeaderClose: false,
                        centered: true
                    }
                );
                if (!confirmed) {
                    this.logActivity('User cancelled Copy Form Section');
                    return;
                }
                if (!this.canRunSectionCopy || this.sectionCopySelectionKey !== selectionKey) {
                    return; // the picks changed while the dialog was open
                }
            }

            const label = dryRun ? 'dry run' : 'copy';
            sc.running = dryRun ? 'dry' : 'copy';
            sc.error = '';
            this.logActivity(`Copy Form Section ${label}: section "${section.title}" from ${sourceText} into ${targetText}, ${positionText}`);
            try {
                const response = await this.sendAction(payload);

                if (isNoAnswer(response)) {
                    sc.dryRunDone = false;
                    sc.dryRunKey = '';
                    sc.error = dryRun
                        ? 'No answer from the server in time. A dry run changes nothing, so it is safe to run it again.'
                        : 'No answer from the server in time — the copy may have been applied. Reload the target form and check it before running anything again.';
                    this.$toast.warning(sc.error);
                    this.logActivity(`Copy Form Section ${label}: no answer from the server (status ${response && response.status})${dryRun ? '' : ' — may have been applied, reload and check'}`, 'WARN');
                    return;
                }

                const data = response && response.data;
                if (!response || response.status !== 200 || !data || typeof data !== 'object' || Array.isArray(data)) {
                    throw new Error(this.seedActionError(response, 'Unexpected response from COPY_FORM_SECTION'));
                }

                sc.result = {
                    dryRun: !!dryRun,
                    status: data.status || 'unknown',
                    report: Array.isArray(data.report) ? data.report : [],
                    backupKey: data.backupKey || null,
                };
                const changes = sc.result.report.filter(e => !e || e.kind !== 'copied').length;

                if (dryRun) {
                    sc.dryRunDone = data.status === 'dry-run';
                    sc.dryRunKey = sc.dryRunDone ? selectionKey : '';
                    this.$toast.info(`Dry run done: ${changes} ${changes === 1 ? 'note' : 'notes'} to review. Nothing was saved.`);
                    this.logActivity(`Copy Form Section dry run: status ${sc.result.status}, ${changes} notes to review`);
                } else {
                    sc.dryRunDone = false;
                    sc.dryRunKey = '';
                    if (data.status === 'copied') {
                        this.$toast.success(`Section "${section.title}" copied into ${targetText}.`);
                        this.logActivity(`Copy Form Section: copied "${section.title}" into ${targetText}; backup ${sc.result.backupKey || '(none reported)'}`, 'SUCCESS');
                    } else {
                        this.$toast.warning(`Copy finished with status "${sc.result.status}" — check the target form.`);
                        this.logActivity(`Copy Form Section: unexpected status "${sc.result.status}"`, 'WARN');
                    }
                    // The target changed: reload its forms so a next copy (and "After …") start from the saved version.
                    await this.loadSectionCopyForms('target', sc.targetFormID);
                }
            } catch (error) {
                sc.error = `Copy Form Section ${label} failed: ${error.message || 'Unknown error'}`;
                this.$toast.error(sc.error);
                this.logActivity(`Copy Form Section ${label} failed: ${error.message}`, 'ERROR');
            } finally {
                sc.running = false;
            }
        },

        resetSectionCopy() {
            this.showSectionCopy = false;
            this.sectionCopySeq.source++; // drop any in-flight forms load
            this.sectionCopySeq.target++;
            this.sectionCopy = emptySectionCopyState();
        },

        // Bulk Form Copy: load the TARGET site's OD imaging categories (watcher on bulkFormCopy.targetSiteID).
        async loadBulkCopyImagingCategories(siteID) {
            const seq = ++this.bulkCopyImagingRequestSeq;
            const blank = { loading: false, error: '', categories: [], consentCandidates: [], map: emptyCategoryMap() };
            if (!siteID) {
                this.bulkFormCopy.imaging = { siteID: null, ...blank };
                return;
            }
            this.bulkFormCopy.imaging = { siteID, ...blank, loading: true };
            // Stale-response guard: the target may have changed while this was in flight.
            const isStale = () => seq !== this.bulkCopyImagingRequestSeq || this.bulkFormCopy.targetSiteID !== siteID;
            try {
                const response = await this.sendAction({ siteID, action: 'GET_FORM_IMAGING_CATEGORIES' });
                if (isStale()) return;
                this.bulkFormCopy.imaging = { siteID, ...this.imagingStateFromResponse(response) };
            } catch (err) {
                if (isStale()) return;
                this.bulkFormCopy.imaging = {
                    siteID, ...blank,
                    error: `Couldn't load the target site's Open Dental imaging categories: ${err.message || 'Unknown error'}`,
                };
            }
        },

        getResultsAlertVariant() {
            // An unconfirmed form or a stopped run needs a look before anyone moves on.
            if (this.bulkFormCopy.results.unknownCount > 0 || this.bulkFormCopy.results.stoppedReason) {
                return 'warning';
            }
            if (this.bulkFormCopy.results.successCount > 0 && this.bulkFormCopy.results.failureCount === 0 && this.bulkFormCopy.results.skippedCount === 0) {
                return 'success';
            } else if (this.bulkFormCopy.results.successCount > 0 && this.bulkFormCopy.results.failureCount > 0 && this.bulkFormCopy.results.skippedCount === 0) {
                return 'warning';
            } else if (this.bulkFormCopy.results.successCount === 0 && this.bulkFormCopy.results.failureCount > 0 && this.bulkFormCopy.results.skippedCount === 0) {
                return 'danger';
            } else if (this.bulkFormCopy.results.successCount === 0 && this.bulkFormCopy.results.failureCount === 0 && this.bulkFormCopy.results.skippedCount > 0) {
                return 'info';
            } else {
                return 'secondary';
            }
        },

        // ---- Bulk Wiki Copy Methods ----
        async loadSourceSiteWikiPages() {
            if (!this.bulkWikiCopy.sourceSiteID) {
                this.bulkWikiCopy.availableWikiPages = [];
                this.bulkWikiCopy.selectedWikiPageIDs = [];
                this.bulkWikiCopy.selectAll = false;
                this.bulkWikiCopy.error = '';
                return;
            }

            console.log(`[DEBUG] loadSourceSiteWikiPages: Starting to load wiki pages for sourceSiteID: ${this.bulkWikiCopy.sourceSiteID}`);

            this.bulkWikiCopy.loading = true;
            this.bulkWikiCopy.error = '';
            this.bulkWikiCopy.availableWikiPages = [];
            this.bulkWikiCopy.selectedWikiPageIDs = [];
            this.bulkWikiCopy.selectAll = false;

            try {
                // Use the wiki store action to get wiki pages for the source site
                const response = await this.$store.dispatch('wiki/getWikiPages', {
                    siteID: this.bulkWikiCopy.sourceSiteID
                });

                console.log(`[DEBUG] loadSourceSiteWikiPages: Raw response from wiki/getWikiPages:`, response);

                // Response structure: array of result sets, first one contains wiki pages
                const wikiPagesData = response && response[0] ? response[0] : [];

                console.log(`[DEBUG] loadSourceSiteWikiPages: Extracted wikiPagesData:`, wikiPagesData);
                console.log(`[DEBUG] loadSourceSiteWikiPages: wikiPagesData is array:`, Array.isArray(wikiPagesData));
                console.log(`[DEBUG] loadSourceSiteWikiPages: wikiPagesData length:`, wikiPagesData?.length);

                if (wikiPagesData && Array.isArray(wikiPagesData)) {
                    this.bulkWikiCopy.availableWikiPages = wikiPagesData;

                    console.log(`[DEBUG] loadSourceSiteWikiPages: First few wiki pages:`, wikiPagesData.slice(0, 3));

                    if (wikiPagesData.length === 0) {
                        this.bulkWikiCopy.error = 'This site has no wiki pages to copy. Please select a different source site.';
                        this.logActivity(`Source site has no wiki pages available for copying`, 'INFO');
                    } else {
                        this.logActivity(`Loaded ${wikiPagesData.length} wiki pages from source site ${this.bulkWikiCopy.sourceSiteID}`);
                        console.log(`[DEBUG] loadSourceSiteWikiPages: Successfully loaded ${wikiPagesData.length} wiki pages`);
                    }
                } else {
                    this.bulkWikiCopy.error = 'Invalid response from server. Please try again or contact support.';
                    this.logActivity(`Invalid response structure from wiki API: ${JSON.stringify(response)}`, 'ERROR');
                    console.error(`[DEBUG] loadSourceSiteWikiPages: Invalid response structure:`, response);
                }
            } catch (error) {
                this.bulkWikiCopy.error = `Error loading wiki pages: ${error.message || 'Unknown error'}`;
                this.logActivity(`Error loading wiki pages from source site ${this.bulkWikiCopy.sourceSiteID}: ${error.message}`, 'ERROR');
                console.error(`[DEBUG] loadSourceSiteWikiPages: Error loading wiki pages:`, error);
            } finally {
                this.bulkWikiCopy.loading = false;
            }
        },

        toggleSelectAllWiki() {
            if (this.bulkWikiCopy.selectAll) {
                this.bulkWikiCopy.selectedWikiPageIDs = this.bulkWikiCopy.availableWikiPages.map(page => page.WikiPageID);
            } else {
                this.bulkWikiCopy.selectedWikiPageIDs = [];
            }
        },

        async executeBulkWikiCopy() {
            if (!this.canExecuteBulkWikiCopy) {
                this.$toast.error('Please fill in all required fields and select wiki pages to copy');
                return;
            }

            // Get site names for confirmation dialog
            const sourceSite = this.customers.find(c => c.SiteID === this.bulkWikiCopy.sourceSiteID);
            const targetSite = this.customers.find(c => c.SiteID === this.bulkWikiCopy.targetSiteID);
            const sourceName = sourceSite ? sourceSite.CustomerName : this.bulkWikiCopy.sourceSiteID;
            const targetName = targetSite ? targetSite.CustomerName : this.bulkWikiCopy.targetSiteID;

            // Build warning messages
            let warningMessages = [];
            if (this.bulkWikiCopy.overwriteExisting) {
                warningMessages.push('This will overwrite existing wiki pages with the same name!');
            }
            warningMessages.push('Images will be re-uploaded to the target site.');
            warningMessages.push('Internal wiki links and form template links may need manual updates.');

            // Confirmation dialog
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to copy ${this.bulkWikiCopy.selectedWikiPageIDs.length} wiki pages from "${sourceName}" to "${targetName}"?\n\n${warningMessages.map(m => '• ' + m).join('\n')}`,
                {
                    title: 'Confirm Bulk Wiki Copy',
                    size: 'lg',
                    buttonSize: 'sm',
                    okVariant: 'success',
                    okTitle: 'Yes, Copy Wiki Pages',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                }
            );

            if (!confirmed) {
                this.logActivity('User cancelled bulk wiki copy operation');
                this.bulkWikiCopy.copying = false;
                return;
            }

            this.bulkWikiCopy.copying = true;
            this.bulkWikiCopy.error = '';
            this.bulkWikiCopy.results = null;

            // Add a safety timeout to ensure copying state doesn't get stuck
            const safetyTimeout = setTimeout(() => {
                this.bulkWikiCopy.copying = false;
                this.$toast.error('Operation timed out. Please try again.');
            }, 60000); // 60 second timeout (longer due to image re-uploads)

            try {
                this.logActivity(`Starting bulk copy of ${this.bulkWikiCopy.selectedWikiPageIDs.length} wiki pages from "${sourceName}" to "${targetName}"`);

                const payload = {
                    siteID: this.bulkWikiCopy.targetSiteID,
                    action: 'BULK_COPY_WIKI_PAGES',
                    sourceSiteID: this.bulkWikiCopy.sourceSiteID,
                    wikiPageIDs: this.bulkWikiCopy.selectedWikiPageIDs,
                    overwriteExisting: this.bulkWikiCopy.overwriteExisting,
                    copyTags: this.bulkWikiCopy.copyTags
                };

                const response = await this.sendAction(payload);

                // Check for NEEDS_FORMS pre-flight response
                if (response.data && response.data.status === 'NEEDS_FORMS') {
                    this.bulkWikiCopy.copying = false;
                    await this.handleNeedsFormsResponse(response.data);
                    return;
                }

                if (response && response.status === 200 && response.data) {
                    const successCount = response.data.successCount || 0;
                    const failureCount = response.data.failureCount || 0;
                    const skippedCount = response.data.skippedCount || 0;

                    // Set the results for display
                    this.bulkWikiCopy.results = response.data;

                    // Determine if the operation was successful
                    if (failureCount === 0 && successCount > 0) {
                        this.$toast.success(`Successfully copied ${successCount} wiki pages!`);
                        this.logActivity(`Bulk wiki copy completed successfully: ${successCount} wiki pages copied`);
                    } else if (successCount > 0 && failureCount > 0) {
                        this.$toast.warning(`Partially successful: ${successCount} copied, ${failureCount} failed, ${skippedCount} skipped`);
                        this.logActivity(`Bulk wiki copy partially completed: ${successCount} success, ${failureCount} failed, ${skippedCount} skipped`);
                    } else if (failureCount > 0 && successCount === 0) {
                        this.$toast.error(`Copy failed: ${failureCount} wiki pages failed to copy`);
                        this.logActivity(`Bulk wiki copy failed: ${failureCount} wiki pages failed to copy`, 'ERROR');
                    } else if (skippedCount === this.bulkWikiCopy.selectedWikiPageIDs.length) {
                        this.$toast.info(`All wiki pages skipped (${skippedCount} pages already exist)`);
                        this.logActivity(`Bulk wiki copy: all ${skippedCount} wiki pages skipped (already exist)`);
                    } else {
                        this.$toast.info(`Operation completed: ${successCount} success, ${failureCount} failed, ${skippedCount} skipped`);
                        this.logActivity(`Bulk wiki copy completed with mixed results: ${successCount} success, ${failureCount} failed, ${skippedCount} skipped`);
                    }
                } else {
                    throw new Error(response?.data?.message || 'Unknown error occurred');
                }
            } catch (error) {
                this.bulkWikiCopy.error = `Copy failed: ${error.message || 'Unknown error'}`;
                this.logActivity(`Bulk wiki copy failed: ${error.message}`, 'ERROR');
                this.$toast.error(this.bulkWikiCopy.error);
            } finally {
                this.bulkWikiCopy.copying = false;
                clearTimeout(safetyTimeout);
            }
        },

        resetBulkWikiCopy() {
            this.showBulkWikiCopy = false;
            this.bulkWikiCopy = {
                sourceSiteID: null,
                targetSiteID: null,
                overwriteExisting: false,
                copyTags: true,
                availableWikiPages: [],
                selectedWikiPageIDs: [],
                selectAll: false,
                loading: false,
                copying: false,
                error: '',
                results: null,
                // NEEDS_FORMS modal state
                needsFormsModal: false,
                missingForms: [],
                formIDsToCopy: [],
                targetCategories: [],
                applyAllConfig: {
                    pdfCategory: '',
                    attachmentCategory: '',
                    enableSpanish: true,
                    autoPrefillTeeth: true,
                    autoPrefillDate: true
                },
                perFormConfig: {},
                copyingForms: false,
            };
        },

        async handleNeedsFormsResponse(responseData) {
            console.log('[SuperAdmin] NEEDS_FORMS response:', responseData);

            this.bulkWikiCopy.missingForms = responseData.missingForms || [];
            this.bulkWikiCopy.formIDsToCopy = responseData.formIDsToCopy || [];

            // Fetch OD categories for target site
            try {
                const catResponse = await this.$store.dispatch('forms/getFormCategories', {
                    siteID: this.bulkWikiCopy.targetSiteID
                });
                this.bulkWikiCopy.targetCategories = catResponse.data || [];
            } catch (err) {
                console.error('Failed to fetch target site categories:', err);
                this.bulkWikiCopy.targetCategories = [];
            }

            // Initialize per-form config with defaults
            this.bulkWikiCopy.perFormConfig = {};
            for (const form of this.bulkWikiCopy.missingForms) {
                this.$set(this.bulkWikiCopy.perFormConfig, form.sourceFormId, {
                    pdfCategory: this.bulkWikiCopy.applyAllConfig.pdfCategory,
                    attachmentCategory: this.bulkWikiCopy.applyAllConfig.attachmentCategory,
                    enableSpanish: this.bulkWikiCopy.applyAllConfig.enableSpanish,
                    autoPrefillTeeth: this.bulkWikiCopy.applyAllConfig.autoPrefillTeeth,
                    autoPrefillDate: this.bulkWikiCopy.applyAllConfig.autoPrefillDate
                });
            }

            this.bulkWikiCopy.needsFormsModal = true;
        },

        applyAllFormConfig() {
            const defaults = this.bulkWikiCopy.applyAllConfig;
            for (const formId of Object.keys(this.bulkWikiCopy.perFormConfig)) {
                this.$set(this.bulkWikiCopy.perFormConfig, formId, {
                    pdfCategory: defaults.pdfCategory,
                    attachmentCategory: defaults.attachmentCategory,
                    enableSpanish: defaults.enableSpanish,
                    autoPrefillTeeth: defaults.autoPrefillTeeth,
                    autoPrefillDate: defaults.autoPrefillDate
                });
            }
            this.$toast.info(`Applied settings to all ${this.bulkWikiCopy.missingForms.length} form(s)`);
        },

        async copyFormsAndRetryWiki() {
            // The button is disabled until then; BULK_COPY_FORMS refuses a form with no PDF category anyway.
            if (!this.bulkWikiMissingFormsReady) {
                this.$toast.error('Pick a PDF category for every form first.');
                return;
            }
            this.bulkWikiCopy.copyingForms = true;

            try {
                // Step 1: Copy the missing forms with per-form config — ONE form per request
                // (runBulkFormCopy: a single request for all of them outruns API Gateway's 29s cap).
                const perFormConfig = this.bulkWikiCopy.perFormConfig || {};
                const formResult = await runBulkFormCopy({
                    formIDs: this.bulkWikiCopy.formIDsToCopy,
                    describeError: response => this.seedActionError(response, 'Unknown error'),
                    send: formID => this.sendAction({
                        siteID: this.bulkWikiCopy.targetSiteID,
                        action: 'BULK_COPY_FORMS',
                        sourceSiteID: this.bulkWikiCopy.sourceSiteID,
                        formIDs: [formID],
                        overwriteExisting: false,
                        perFormConfig: perFormConfig[formID] ? { [formID]: perFormConfig[formID] } : {},
                    }),
                });

                // A refusal (e.g. a PDF category not on the target site) writes nothing — don't report success.
                if (formResult.stoppedReason) {
                    this.$toast.error('Failed to copy forms: ' + formResult.stoppedReason);
                    this.bulkWikiCopy.copyingForms = false;
                    return;
                }

                if (formResult.failureCount > 0) {
                    this.$toast.error(`${formResult.failureCount} form(s) failed to copy. Please check and try again.`);
                    this.bulkWikiCopy.copyingForms = false;
                    return;
                }

                // The wiki retry remaps links to the copied forms, so it must not run on a guess.
                if (formResult.unknownCount > 0) {
                    this.$toast.warning(`${formResult.unknownCount} form(s) could not be confirmed (no answer from the server in time). ` +
                        "Reload the target site's forms, then run the wiki copy again.");
                    this.bulkWikiCopy.copyingForms = false;
                    return;
                }

                const successCount = formResult.successCount || 0;
                this.$toast.success(`${successCount} form(s) copied successfully. Now copying wiki templates...`);

                // Step 2: Close modal and retry wiki copy
                this.bulkWikiCopy.needsFormsModal = false;
                this.bulkWikiCopy.copyingForms = false;

                // Small delay to let modal close
                await new Promise(r => setTimeout(r, 500));

                // Retry wiki copy (forms now exist, links will be remapped)
                await this.executeBulkWikiCopy();

            } catch (err) {
                console.error('[SuperAdmin] Copy forms & retry failed:', err);
                this.$toast.error('Failed to copy forms: ' + (err.message || 'Unknown error'));
                this.bulkWikiCopy.copyingForms = false;
            }
        },

        getWikiResultsAlertVariant() {
            if (!this.bulkWikiCopy.results) return 'secondary';
            if (this.bulkWikiCopy.results.successCount > 0 && this.bulkWikiCopy.results.failureCount === 0 && this.bulkWikiCopy.results.skippedCount === 0) {
                return 'success';
            } else if (this.bulkWikiCopy.results.successCount > 0 && this.bulkWikiCopy.results.failureCount > 0) {
                return 'warning';
            } else if (this.bulkWikiCopy.results.successCount === 0 && this.bulkWikiCopy.results.failureCount > 0) {
                return 'danger';
            } else if (this.bulkWikiCopy.results.successCount === 0 && this.bulkWikiCopy.results.failureCount === 0 && this.bulkWikiCopy.results.skippedCount > 0) {
                return 'info';
            } else {
                return 'secondary';
            }
        },

        async loadCacheStats() {
            this.cacheStats.loading = true;
            this.cacheStats.momentoCacheName = '';
            this.cacheActions.loading = true;
            this.cacheActions.loadingText = 'Loading cache statistics...';
            this.cacheActions.success = false;
            this.cacheActions.message = null;
            this.activeFollowups = [];
            this.activeFollowupsFields = [];
            this.specificPatientId = '';
            this.showClearAllConfirm = false;

            try {
                // Use frontend Momento SDK directly
                const momentoClient = await this.getMomentoWebClient();
                const cacheName = this.momentoCacheName;
                
                console.log(`[loadCacheStats] Fetching set 'fuattempts' from cache: ${cacheName}`);
                const setFetchResponse = await momentoClient.setFetch(cacheName, 'fuattempts');
                
                let totalCount = 0;
                if (setFetchResponse.type === 'Hit') {
                    const setItems = setFetchResponse.valueSet();
                    totalCount = setItems.size;
                    console.log(`[loadCacheStats] Found ${totalCount} active follow-ups in cache`);
                } else if (setFetchResponse.type === 'Miss') {
                    console.log('[loadCacheStats] No fuattempts set found in cache (normal if no follow-ups active)');
                } else if (setFetchResponse.type === 'Error') {
                    console.error('[loadCacheStats] Error fetching set:', setFetchResponse.errorCode(), setFetchResponse.message());
                    throw new Error(`Error fetching cache set: ${setFetchResponse.errorCode()}: ${setFetchResponse.message()}`);
                }
                
                this.cacheStats.totalCount = totalCount;
                this.cacheStats.momentoCacheName = cacheName;
                this.cacheStats.loading = false;
                this.cacheActions.loading = false;
                this.cacheActions.success = true;
                this.cacheActions.message = 'Cache statistics loaded successfully!';
                
            } catch (error) {
                this.cacheStats.loading = false;
                this.cacheActions.loading = false;
                this.cacheActions.success = false;
                this.cacheActions.message = `Error loading cache statistics: ${error.message}`;
                console.error('Error loading cache statistics:', error);
            }
        },

        async loadActiveFollowups() {
            this.cacheActions.loading = true;
            this.cacheActions.loadingText = 'Loading active follow-ups...';
            this.cacheActions.success = false;
            this.cacheActions.message = null;
            this.activeFollowups = [];
            this.activeFollowupsFields = [];
            this.specificPatientId = '';
            this.showClearAllConfirm = false;

            try {
                const momentoClient = await this.getMomentoWebClient();
                const cacheName = this.momentoCacheName;
                
                console.log(`[loadActiveFollowups] Fetching active follow-ups from cache: ${cacheName}`);
                const setFetchResponse = await momentoClient.setFetch(cacheName, 'fuattempts');
                
                let followups = [];
                if (setFetchResponse.type === 'Hit') {
                    const setItems = setFetchResponse.valueSet();
                    followups = Array.from(setItems).map(patientId => ({
                        patientId: patientId,
                        name: `Patient ${patientId}` // We only have patient IDs in the cache
                    }));
                    console.log(`[loadActiveFollowups] Found ${followups.length} active follow-ups`);
                } else if (setFetchResponse.type === 'Miss') {
                    console.log('[loadActiveFollowups] No active follow-ups found (normal)');
                } else if (setFetchResponse.type === 'Error') {
                    console.error('[loadActiveFollowups] Error fetching active follow-ups:', setFetchResponse.errorCode(), setFetchResponse.message());
                    throw new Error(`Error fetching active follow-ups: ${setFetchResponse.errorCode()}: ${setFetchResponse.message()}`);
                }
                
                this.activeFollowups = followups;
                this.activeFollowupsFields = [
                    { key: 'patientId', label: 'Patient ID' },
                    { key: 'name', label: 'Name' },
                    { key: 'actions', label: 'Actions' }
                ];
                this.cacheActions.loading = false;
                this.cacheActions.success = true;
                this.cacheActions.message = 'Active follow-ups loaded successfully!';
                
            } catch (error) {
                this.cacheActions.loading = false;
                this.cacheActions.success = false;
                this.cacheActions.message = `Error loading active follow-ups: ${error.message}`;
                console.error('Error loading active follow-ups:', error);
            }
        },

                 async clearSpecificPatient() {
             if (!this.specificPatientId || this.specificPatientId.trim() === '') {
                 this.$toast.error('Please enter a Patient ID to clear');
                 return;
             }

             this.cacheActions.loading = true;
             this.cacheActions.loadingText = `Clearing patient ${this.specificPatientId}...`;
             this.cacheActions.success = false;
             this.cacheActions.message = null;

             try {
                 const momentoClient = await this.getMomentoWebClient();
                 const cacheName = this.momentoCacheName;
                 const patientId = this.specificPatientId.trim();
                 
                 console.log(`[clearSpecificPatient] Removing patient ${patientId} from cache: ${cacheName}`);
                 const removeResponse = await momentoClient.setRemoveElement(cacheName, 'fuattempts', patientId);
                 
                 if (removeResponse.type === 'Success') {
                     this.cacheActions.loading = false;
                     this.cacheActions.success = true;
                     this.cacheActions.message = `Patient ${patientId} cleared successfully!`;
                     this.specificPatientId = '';
                     // Refresh the cache stats and active list
                     this.loadCacheStats();
                     this.loadActiveFollowups();
                 } else if (removeResponse.type === 'Error') {
                     console.error('[clearSpecificPatient] Error removing patient:', removeResponse.errorCode(), removeResponse.message());
                     throw new Error(`Error removing patient: ${removeResponse.errorCode()}: ${removeResponse.message()}`);
                 }
                 
             } catch (error) {
                 this.cacheActions.loading = false;
                 this.cacheActions.success = false;
                 this.cacheActions.message = `Error clearing patient: ${error.message}`;
                 console.error('Error clearing specific patient:', error);
             }
         },

         async clearSpecificPatientById(patientId) {
             if (!patientId) return;
             
             this.cacheActions.loading = true;
             this.cacheActions.loadingText = `Clearing patient ${patientId}...`;
             this.cacheActions.success = false;
             this.cacheActions.message = null;

                           try {
                  const momentoClient = await this.getMomentoWebClient();
                  const cacheName = this.momentoCacheName;
                  
                  console.log(`[clearSpecificPatientById] Removing patient ${patientId} from cache: ${cacheName}`);
                 const removeResponse = await momentoClient.setRemoveElement(cacheName, 'fuattempts', patientId);
                 
                 if (removeResponse.type === 'Success') {
                     this.cacheActions.loading = false;
                     this.cacheActions.success = true;
                     this.cacheActions.message = `Patient ${patientId} cleared successfully!`;
                     // Refresh the cache stats and active list
                     this.loadCacheStats();
                     this.loadActiveFollowups();
                 } else if (removeResponse.type === 'Error') {
                     console.error('[clearSpecificPatientById] Error removing patient:', removeResponse.errorCode(), removeResponse.message());
                     throw new Error(`Error removing patient: ${removeResponse.errorCode()}: ${removeResponse.message()}`);
                 }
                 
             } catch (error) {
                 this.cacheActions.loading = false;
                 this.cacheActions.success = false;
                 this.cacheActions.message = `Error clearing patient: ${error.message}`;
                 console.error('Error clearing specific patient:', error);
             }
         },

        async clearAllFollowups() {
            this.showClearAllConfirm = false;
            this.cacheActions.loading = true;
            this.cacheActions.loadingText = 'Clearing all follow-ups...';
            this.cacheActions.success = false;
            this.cacheActions.message = null;

                         try {
                 const momentoClient = await this.getMomentoWebClient();
                 const cacheName = this.momentoCacheName;
                 
                 // First get the count for logging
                const setFetchResponse = await momentoClient.setFetch(cacheName, 'fuattempts');
                let clearedCount = 0;
                if (setFetchResponse.type === 'Hit') {
                    clearedCount = setFetchResponse.valueSet().size;
                }
                
                console.log(`[clearAllFollowups] Clearing all follow-ups from cache: ${cacheName}`);
                
                // Delete the entire set (which clears all follow-ups)
                const deleteResponse = await momentoClient.delete(cacheName, 'fuattempts');
                
                if (deleteResponse.type === 'Success') {
                    this.cacheActions.loading = false;
                    this.cacheActions.success = true;
                    this.cacheActions.message = `All follow-ups cleared successfully! (${clearedCount} items removed)`;
                    this.loadCacheStats();
                } else if (deleteResponse.type === 'Error') {
                    console.error('[clearAllFollowups] Error clearing all follow-ups:', deleteResponse.errorCode(), deleteResponse.message());
                    throw new Error(`Error clearing all follow-ups: ${deleteResponse.errorCode()}: ${deleteResponse.message()}`);
                }
                
            } catch (error) {
                this.cacheActions.loading = false;
                this.cacheActions.success = false;
                this.cacheActions.message = `Error clearing all follow-ups: ${error.message}`;
                console.error('Error clearing all follow-ups:', error);
            }
        },

                 openCacheManagement() {
             this.showCacheManagement = true;
             // Automatically load cache stats when opening
             this.loadCacheStats();
         },

         resetCacheManagement() {
             this.showCacheManagement = false;
             this.cacheManagementTab = 'momento';
             this.cacheStats = {
                 loading: false,
                 totalCount: 0,
                 momentoCacheName: ''
             };
             this.cacheActions = {
                 loading: false,
                 loadingText: '',
                 success: false,
                 message: null
             };
             this.activeFollowups = [];
             this.activeFollowupsFields = [];
             this.specificPatientId = '';
             this.showClearAllConfirm = false;
             // Reset Upstash state
             this.upstashStats = {
                 loading: false,
                 totalKeys: 0,
                 environment: '',
                 redisEndpoint: '',
                 stats: []
             };
             this.upstashActions = {
                 loading: false,
                 loadingText: '',
                 success: false,
                 message: null
             };
             this.upstashKeys = [];
             this.upstashKeysPage = 1;
             this.upstashSearchPattern = '';
             this.showUpstashClearConfirm = false;
             this.upstashClearTarget = null;
         },

         // ===== Upstash/Redis Cache Methods =====
         switchToUpstashTab() {
             this.cacheManagementTab = 'upstash';
             // Auto-load stats when switching to Upstash tab
             if (!this.upstashStats.stats || this.upstashStats.stats.length === 0) {
                 this.loadUpstashStats();
             }
         },

         async loadUpstashStats() {
             this.upstashStats.loading = true;
             this.upstashActions.message = null;

             try {
                 const response = await this.sendAction({
                     siteID: this.thisSiteID,
                     action: 'UPSTASH_CACHE_STATS'
                 });

                 // sendAction returns axios response, data is in response.data
                 const data = response?.data || response;

                 if (data && data.success) {
                     this.upstashStats.stats = data.stats || [];
                     this.upstashStats.totalKeys = data.totalKeys || 0;
                     this.upstashStats.environment = data.environment || '';
                     this.upstashStats.redisEndpoint = data.redisEndpoint || '';
                     this.upstashActions.success = true;
                     this.upstashActions.message = 'Cache statistics loaded successfully!';
                 } else {
                     throw new Error(data?.message || 'Failed to load cache stats');
                 }
             } catch (error) {
                 console.error('[loadUpstashStats] Error:', error);
                 this.upstashActions.success = false;
                 this.upstashActions.message = `Error loading cache stats: ${error.message}`;
             } finally {
                 this.upstashStats.loading = false;
             }
         },

         async viewUpstashKeys(pattern) {
             this.upstashSearchPattern = pattern;
             await this.searchUpstashKeys();
         },

         async searchUpstashKeys() {
             if (!this.upstashSearchPattern) return;

             this.upstashActions.loading = true;
             this.upstashActions.loadingText = 'Searching keys...';
             this.upstashActions.message = null;
             this.upstashKeys = [];
             this.upstashKeysPage = 1;

             try {
                 const response = await this.sendAction({
                     siteID: this.thisSiteID,
                     action: 'UPSTASH_CACHE_KEYS',
                     pattern: this.upstashSearchPattern,
                     includeValues: true,
                     limit: 100
                 });

                 // sendAction returns axios response, data is in response.data
                 const data = response?.data || response;

                 if (data && data.success) {
                     this.upstashKeys = data.items || [];
                     this.upstashActions.success = true;
                     this.upstashActions.message = `Found ${data.count} key(s)${data.truncated ? ' (results truncated)' : ''}`;
                 } else {
                     throw new Error(data?.message || 'Failed to search keys');
                 }
             } catch (error) {
                 console.error('[searchUpstashKeys] Error:', error);
                 this.upstashActions.success = false;
                 this.upstashActions.message = `Error searching keys: ${error.message}`;
             } finally {
                 this.upstashActions.loading = false;
             }
         },

         confirmClearUpstashPattern(item) {
             this.upstashClearTarget = {
                 pattern: item.pattern,
                 count: item.count,
                 type: item.type,
                 name: item.name
             };
             this.showUpstashClearConfirm = true;
         },

         confirmClearAllUpstashKeys() {
             if (this.upstashKeys.length === 0) return;

             this.upstashClearTarget = {
                 pattern: this.upstashSearchPattern,
                 count: this.upstashKeys.length,
                 type: this.upstashKeys[0]?.type === 'set_member' ? 'set' : 'keys'
             };
             this.showUpstashClearConfirm = true;
         },

         async executeClearUpstashPattern() {
             if (!this.upstashClearTarget) return;

             this.showUpstashClearConfirm = false;
             this.upstashActions.loading = true;
             this.upstashActions.loadingText = 'Deleting cache entries...';
             this.upstashActions.message = null;

             try {
                 let payload = {
                     siteID: this.thisSiteID,
                     action: 'UPSTASH_CACHE_DELETE'
                 };

                 // Handle different types of deletions
                 if (this.upstashClearTarget.type === 'set') {
                     // For SET type (like db_needs_reloading)
                     payload.setKey = this.upstashClearTarget.pattern;
                     payload.clearAll = true;
                 } else {
                     // For regular keys
                     payload.pattern = this.upstashClearTarget.pattern;
                 }

                 const response = await this.sendAction(payload);

                 // sendAction returns axios response, data is in response.data
                 const data = response?.data || response;

                 if (data && data.success) {
                     this.upstashActions.success = true;
                     this.upstashActions.message = data.message || `Successfully deleted ${data.deletedCount} item(s)`;
                     // Refresh stats
                     await this.loadUpstashStats();
                     // Clear keys list
                     this.upstashKeys = [];
                 } else {
                     throw new Error(data?.message || 'Failed to delete cache entries');
                 }
             } catch (error) {
                 console.error('[executeClearUpstashPattern] Error:', error);
                 this.upstashActions.success = false;
                 this.upstashActions.message = `Error deleting cache entries: ${error.message}`;
             } finally {
                 this.upstashActions.loading = false;
                 this.upstashClearTarget = null;
             }
         },

         async deleteUpstashKey(item) {
             this.upstashActions.loading = true;
             this.upstashActions.loadingText = 'Deleting key...';
             this.upstashActions.message = null;

             try {
                 let payload = {
                     siteID: this.thisSiteID,
                     action: 'UPSTASH_CACHE_DELETE'
                 };

                 if (item.type === 'set_member') {
                     // Remove member from set
                     payload.setKey = item.key;
                     payload.setMembers = [item.member];
                 } else {
                     // Delete regular key
                     payload.keys = [item.key];
                 }

                 const response = await this.sendAction(payload);

                 // sendAction returns axios response, data is in response.data
                 const data = response?.data || response;

                 if (data && data.success) {
                     this.upstashActions.success = true;
                     this.upstashActions.message = data.message || 'Key deleted successfully';
                     // Remove from local list
                     const index = this.upstashKeys.findIndex(k =>
                         k.key === item.key && k.member === item.member
                     );
                     if (index > -1) {
                         this.upstashKeys.splice(index, 1);
                     }
                     // Refresh stats
                     await this.loadUpstashStats();
                 } else {
                     throw new Error(data?.message || 'Failed to delete key');
                 }
             } catch (error) {
                 console.error('[deleteUpstashKey] Error:', error);
                 this.upstashActions.success = false;
                 this.upstashActions.message = `Error deleting key: ${error.message}`;
             } finally {
                 this.upstashActions.loading = false;
             }
         },

         formatTTL(ttl) {
             if (ttl < 0) return 'N/A';
             if (ttl < 60) return `${ttl}s`;
             if (ttl < 3600) return `${Math.floor(ttl / 60)}m`;
             if (ttl < 86400) return `${Math.floor(ttl / 3600)}h`;
             return `${Math.floor(ttl / 86400)}d`;
         },

         // ===== TeamIO Knowledge Base Methods =====
        resetTeamioKnowledge() {
            this.showTeamioKnowledge = false;
            this.teamioKnowledgeTab = 'ai';
            this.teamioKnowledgeSaving = false;
            this.voiceTestLoading = false;
            this.knowledgeUpdateLoading = false;
            // Reset to clean state
            this.teamioBot.knowledgeUrlsText = '';
        },

        // Note: addKnowledgeUrl and removeKnowledgeUrl methods removed - now using textarea for bulk URL entry

        clearAllUrls() {
            this.teamioBot.knowledgeUrlsText = '';
            console.log('Textarea cleared');
        },

        loadDefaultTeamioUrls() {
            // Start with the main FAQ/How-to page - the system will automatically discover all linked pages
            const rootUrl = 'https://teamioapp.notion.site/Teamio-Open-Dental-How-to-Videos-FAQs-6a84d959cc1f40bebb135ebc52d908cf';
            
            this.teamioBot.knowledgeUrlsText = rootUrl;
            
            this.$toast.success('Loaded main TeamIO FAQ page. The system will automatically crawl all linked pages up to 3 levels deep, discovering all related content and YouTube videos.');
            console.log('Root TeamIO URL loaded - system will auto-discover linked pages:', rootUrl);
        },

        async testBotWithQuestion(question) {
            try {
                // First, save current settings to ensure they're applied
                await this.saveTeamioKnowledgeSettings();
                
                // Wait a moment for settings to be saved
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Find the TeamIO bot widget component in the app
                const app = this.$root;
                let botWidget = null;
                
                // Look for the bot widget in the component tree
                const findBotWidget = (component) => {
                    if (component.$options.name === 'TeamIOBotWidget' || 
                        component.$el?.classList?.contains('teamio-bot-widget')) {
                        return component;
                    }
                    for (let child of component.$children || []) {
                        const found = findBotWidget(child);
                        if (found) return found;
                    }
                    return null;
                };
                
                botWidget = findBotWidget(app);
                
                if (botWidget && botWidget.testWithQuestion) {
                    // Call the widget's test method directly
                    botWidget.testWithQuestion(question);
                    this.$toast.success(`Testing with question: "${question}"`);
                } else {
                    // Fallback to manual instruction
                    this.$toast.info(`Please manually ask the bot: "${question}"`);
                }
                
            } catch (error) {
                console.error('Error testing bot:', error);
                this.$toast.error('Failed to test bot. Please try manually using the Help button.');
            }
        },

         async testVoice() {
             this.voiceTestLoading = true;
             try {
                 // Test voice with current settings
                 const testText = "Hello! This is a test of your TeamIO assistant voice settings.";
                 
                 // Here you would integrate with ElevenLabs or your voice service
                 // For now, we'll simulate the test
                 await new Promise(resolve => setTimeout(resolve, 2000));
                 
                 this.$toast.success('Voice test completed successfully!');
             } catch (error) {
                 console.error('Voice test failed:', error);
                 this.$toast.error('Voice test failed. Please try again.');
             } finally {
                 this.voiceTestLoading = false;
             }
         },

        async updateKnowledgeBase() {
            // Parse URLs from textarea (split by newlines and filter empty)
            const urls = this.teamioBot.knowledgeUrlsText
                .split('\n')
                .map(url => url.trim())
                .filter(url => url !== '' && url.startsWith('http'));
            
            if (urls.length === 0) {
                this.$toast.error('Please enter at least one valid URL (must start with http:// or https://)');
                return;
            }

            console.log(`Sending ${urls.length} URL(s) to backend for indexing:`, urls);

            this.busyAction = 'updating';
            this.busy = true; // Show full-screen loading overlay
            try {
                // Use the existing sendAction pattern for consistency
                // Backend will handle deduplication via Supabase checks
                const result = await this.sendAction({
                    siteID: null, // null for TeamIO knowledge
                    action: 'UPDATE_TEAMIO_KNOWLEDGE',
                    urls: urls, // Send all URLs - backend handles deduplication
                    knowledgeType: 'teamio_platform'
                });

                console.log('Knowledge base update result:', result);
                
                // The sendAction method returns the full axios response, so data is in result.data
                const responseData = result.data;
                console.log('responseData:', responseData);

                if (responseData && responseData.success === true) {
                    // Check if this is an asynchronous indexing response (status: "indexing")
                    if (responseData.status === 'indexing') {
                        // Asynchronous indexing started - show progress message
                        console.log('🔄 Updating status to indexing...');
                        
                        // Update status (don't track URLs - backend handles deduplication)
                        this.$set(this.teamioBot.knowledgeStatus, 'lastUpdated', new Date().toLocaleString());
                        this.$set(this.teamioBot.knowledgeStatus, 'status', 'indexing');
                        this.$set(this.teamioBot.knowledgeStatus, 'message', responseData.message || 'Knowledge base indexing in progress...');
                        
                        console.log('✅ Status updated:', this.teamioBot.knowledgeStatus.status);
                        console.log('✅ isKnowledgeStatusConsistent:', this.isKnowledgeStatusConsistent);
                        
                        // Force Vue to update the UI
                        this.$forceUpdate();
                        
                        const progressMessage = `Knowledge base indexing started for ${urls.length} URL(s). Estimated time: ${responseData.estimatedTime || '2-5 minutes'}. Backend will skip any duplicates automatically.`;
                        this.$toast.info(progressMessage);
                        
                        // Clear the textarea after starting indexing
                        this.teamioBot.knowledgeUrlsText = '';
                        
                    } else {
                        // Synchronous/completed indexing - handle as before
                        this.teamioBot.knowledgeStatus.lastUpdated = new Date().toLocaleString();
                        
                        // Update status with indexed count
                        if (responseData.crawledPages && responseData.crawledPages.length > 0) {
                            this.teamioBot.knowledgeStatus.docsIndexed = responseData.indexedChunks || 0;
                            this.teamioBot.knowledgeStatus.crawledPages = responseData.crawledPages;
                        } else {
                            this.teamioBot.knowledgeStatus.docsIndexed = responseData.totalDocsIndexed || 0;
                        }
                        
                        this.$toast.success(`Successfully indexed ${urls.length} URL(s)!`);
                        
                        // Clear the textarea after successful indexing
                        this.teamioBot.knowledgeUrlsText = '';
                    }
                } else {
                    console.error('Success check failed. responseData:', responseData);
                    throw new Error(responseData?.message || 'Knowledge update failed');
                }
            } catch (error) {
                console.error('Knowledge base update failed:', error);
                this.$toast.error(`Failed to update knowledge base: ${error.message}`);
            } finally {
                this.busy = false; // Hide full-screen loading overlay
                this.busyAction = '';
            }
         },

         // Diagnostic method to identify blocking issues
         diagnosticCheck() {
             console.log('=== DIAGNOSTIC CHECK ===');
             console.log('1. Busy state:', this.busy);
             
             // AGGRESSIVE overlay removal
             this.busy = false;
             this.$nextTick(() => {
                 // Remove any overlay elements
                 const overlays = document.querySelectorAll('[style*="position: fixed"]');
                 overlays.forEach(overlay => {
                     if (overlay.style.zIndex > 1000) {
                         console.log('Removing overlay:', overlay);
                         overlay.remove();
                     }
                 });
                 
                 // Hide bot widget temporarily
                 const botWidgets = document.querySelectorAll('.teamio-bot-widget');
                 botWidgets.forEach(widget => {
                     widget.style.display = 'none';
                     setTimeout(() => widget.style.display = '', 2000);
                 });
                 
                 console.log('Diagnostic complete - overlays cleared, bot hidden for 2s');
                 this.$toast.info('Diagnostic complete - check console');
             });
         },

         async clearAllStatus() {
             console.log('Clearing all status...');
             
             try {
                 // Show loading overlay
                 this.busyAction = 'clearing';
                 this.busy = true;
                 
                 // Call backend to clear knowledge base data
                 const result = await this.sendAction({
                     siteID: null,
                     action: 'CLEAR_TEAMIO_KNOWLEDGE',
                     knowledgeType: 'teamio_platform'
                 });
                 
                 console.log('Clear knowledge base result:', result);
                 
                if (result.data && result.data.success) {
                    // Clear frontend status (don't track URLs)
                    this.teamioBot.knowledgeStatus = {
                        docsIndexed: 0,
                        lastUpdated: null,
                        crawledPages: [],
                        status: 'cleared',
                        message: 'Ready for new indexing',
                        knowledgeType: 'teamio_platform'
                    };
                    
                    // Save the cleared state to backend settings (silently)
                    await this.saveTeamioKnowledgeSettingsSilent();
                    
                    this.$toast.success('Knowledge base cleared successfully! You can now index URLs.');
                } else {
                     throw new Error(result.data?.message || 'Failed to clear knowledge base');
                 }
                 
             } catch (error) {
                 console.error('Error clearing knowledge base:', error);
                 this.$toast.error(`Failed to clear knowledge base: ${error.message}`);
             } finally {
                 // Hide loading overlay
                 this.busy = false;
                 this.busyAction = '';
             }
         },

         async saveTeamioKnowledgeSettings() {
             this.teamioKnowledgeSaving = true;
             try {
                 // Use the existing sendAction pattern for consistency
                 const result = await this.sendAction({
                     siteID: null, // null for TeamIO settings
                     action: 'SAVE_TEAMIO_BOT_SETTINGS',
                     settings: this.teamioBot
                 });

                 console.log('Save settings result:', result);
                 
                 // Fix: sendAction returns full axios response, so data is in result.data
                 const responseData = result.data;
                 console.log('responseData:', responseData);
                 console.log('responseData.success:', responseData.success);
                 console.log('typeof responseData.success:', typeof responseData.success);
                 
                 if (responseData && responseData.success === true) {
                     this.$toast.success('TeamIO bot settings saved successfully!');
                 } else {
                     console.error('Success check failed. responseData:', responseData);
                     throw new Error(responseData?.message || 'Settings save failed');
                 }
             } catch (error) {
                 console.error('Failed to save TeamIO bot settings:', error);
                 this.$toast.error(`Failed to save settings: ${error.message}`);
             } finally {
                 this.teamioKnowledgeSaving = false;
             }
         },

         async saveTeamioKnowledgeSettingsSilent() {
             try {
                 // Use the existing sendAction pattern for consistency (no loading state or toast)
                 const result = await this.sendAction({
                     siteID: null, // null for TeamIO settings
                     action: 'SAVE_TEAMIO_BOT_SETTINGS',
                     settings: this.teamioBot
                 });

                 console.log('Save settings result (silent):', result);
                 
                 // Fix: sendAction returns full axios response, so data is in result.data
                 const responseData = result.data;
                 
                 if (!(responseData && responseData.success === true)) {
                     console.error('Silent save failed. responseData:', responseData);
                     throw new Error(responseData?.message || 'Settings save failed');
                 }
             } catch (error) {
                 console.error('Failed to save TeamIO bot settings (silent):', error);
                 throw error; // Re-throw so parent can handle
             }
         },

        async openTeamioKnowledge() {
            this.showTeamioKnowledge = true;
            await this.loadTeamioKnowledgeSettings();
        },

        async loadTeamioKnowledgeSettings() {
            try {
                // Load existing TeamIO bot settings
                const result = await this.sendAction({
                    siteID: null,
                    action: 'LOAD_TEAMIO_BOT_SETTINGS'
                });

                console.log('Load settings result:', result);
                
                const responseData = result.data;
                console.log('Load settings responseData:', responseData);

                if (responseData && responseData.success && responseData.data) {
                    // Merge all loaded settings
                    this.teamioBot = { ...this.teamioBot, ...responseData.data };
                    
                    // Always keep textarea empty for new entries
                    this.teamioBot.knowledgeUrlsText = '';
                    
                    console.log('Settings loaded. Textarea cleared for new URLs.');
                }
            } catch (error) {
                console.error('Failed to load TeamIO bot settings:', error);
                // Continue with defaults if loading fails
            }
        },

        // Enhanced crawler display helper methods
        getStatusIcon(status) {
            switch (status) {
                case 'success':
                    return 'fa fa-check-circle';
                case 'skipped':
                    return 'fa fa-exclamation-triangle';
                case 'error':
                    return 'fa fa-times-circle';
                default:
                    return 'fa fa-question-circle';
            }
        },

        getStatusColor(status) {
            switch (status) {
                case 'success':
                    return 'color: #28a745;';
                case 'skipped':
                    return 'color: #ffc107;';
                case 'error':
                    return 'color: #dc3545;';
                default:
                    return 'color: #6c757d;';
            }
        },

        formatDate(dateString) {
            if (!dateString) return 'Unknown';
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
            } catch (error) {
                return 'Invalid date';
            }
        },

        // Clear cached knowledge base status
        async clearKnowledgeBaseStatus() {
            try {
                console.log('Clearing knowledge base status...');
                
                // Reset the local state
                this.teamioBot.knowledgeStatus = {
                    lastUpdated: null,
                    docsIndexed: 0,
                    urls: [],
                    crawledPages: []
                };
                
                // Also save the cleared state to prevent it from reloading old data
                await this.saveTeamioKnowledgeSettings();
                
                this.$toast.success('Knowledge base status cleared! Ready for fresh indexing.');
                console.log('Knowledge base status cleared successfully');
                
            } catch (error) {
                console.error('Error clearing knowledge base status:', error);
                this.$toast.error('Failed to clear status: ' + error.message);
            }
        },

        // ===== Twilio Dashboard Methods =====
        openTwilioDashboard() {
            this.showTwilioDashboard = true;
            this.twilioActiveTab = 0;
            if (!this.twilioUsageMonth) {
                const now = new Date();
                this.twilioUsageMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            }
            this.loadTwilioUsage();
        },

        async loadTwilioUsage() {
            if (!this.twilioUsageMonth) return;
            this.twilioUsageLoading = true;
            try {
                const response = await this.sendAction({
                    action: 'TWILIO_GET_USAGE',
                    month: this.twilioUsageMonth
                });
                if (response && response.data) {
                    this.twilioUsageData = response.data;
                }
            } catch (error) {
                console.error('Failed to load Twilio usage:', error);
                this.$toast.error('Failed to load Twilio usage: ' + (error.message || 'Unknown error'));
            } finally {
                this.twilioUsageLoading = false;
            }
        },

        // SMS-by-feature rollup from the sms_send_log ledger (per-site attribution: which feature
        // drove the SMS). Complements loadTwilioUsage (the authoritative billing total).
        async loadSmsFeatureRollup() {
            const month = this.smsRollupMonth || new Date().toISOString().slice(0, 7);
            this.smsRollupMonth = month;
            this.smsRollupLoading = true;
            this.smsRollupData = null;
            try {
                const response = await this.sendAction({ action: 'SMS_FEATURE_ROLLUP', month });
                // Same guard as loadIvRollup (isValidRollupPayload) — deliberate BACK-APPLICATION of the
                // a9f7884e / 2026-08-15 sendAction-never-throws fix to the rollup tab it missed: an
                // error STRING body used to land in smsRollupData and `smsRollupData.sites.length` then
                // threw during render.
                if (isValidRollupPayload(response)) {
                    this.smsRollupData = response.data;
                } else {
                    throw new Error(this.seedActionError(response, 'Failed to load SMS breakdown.'));
                }
            } catch (error) {
                console.error('Failed to load SMS feature rollup:', error);
                this.$toast.error(error.message || 'Failed to load SMS breakdown.');
            } finally {
                this.smsRollupLoading = false;
            }
        },

        // Insurance Verification per-site rollup (INSURANCE_USAGE_ROLLUP). Mirrors
        // loadSmsFeatureRollup: month defaults to current YYYY-MM, response.data is the payload.
        async loadIvRollup() {
            const month = this.ivRollupMonth || new Date().toISOString().slice(0, 7);
            this.ivRollupMonth = month;
            this.ivRollupLoading = true;
            this.ivRollupData = null;
            try {
                const response = await this.sendAction({ action: 'INSURANCE_USAGE_ROLLUP', month });
                // sendAction NEVER throws — a backend failure RESOLVES { status, data } with `data` a
                // BARE STRING. The old `if (response.data)` guard let that error string land in
                // ivRollupData, and the template's `.sites.length` read then threw during render (the
                // 2026-08-15 SEED_TX_PRESENTER_DEFAULTS class). Only a 2xx object with an array `sites`
                // is a real rollup; otherwise raise the server's real reason and render nothing.
                if (isValidRollupPayload(response)) {
                    this.ivRollupData = response.data;
                } else {
                    throw new Error(this.seedActionError(response, 'Failed to load insurance checks.'));
                }
            } catch (error) {
                console.error('Failed to load insurance usage rollup:', error);
                this.$toast.error(error.message || 'Failed to load insurance checks.');
            } finally {
                this.ivRollupLoading = false;
            }
        },

        // ---- Insurance Checks presenters (thin wrappers over the shared pure helpers) ----
        // The reconciliation band, VERBATIM from usageMeterView — never summed. Used for both the
        // totals card and each per-site cell, so the practice-facing band and this one can't differ.
        ivBand(counts) {
            return usageMeterView(counts).headline;
        },
        // BILLABLE per-trigger split for the totals cards (what we actually charged), off the meter.
        ivBillable(counts, key) {
            const entry = usageMeterView(counts).split.find((s) => s.key === key);
            return entry ? entry.billable : 0;
        },
        // Refused-free count via the SAME meter (consistent with band/billable on this tab).
        ivFree(counts) {
            return usageMeterView(counts).free;
        },
        // ALL-persisted per-trigger count for the per-site table (byTriggerTotal, fallback byTrigger).
        ivRollupTrigger(counts, key) {
            return ivRollupTriggerCount(counts, key);
        },
        // Merged Unknown column = unconfirmed-billable + unclassified.
        ivUnknown(counts) {
            return ivRollupUnknownCount(counts);
        },

        fmtPrice(num) {
            if (num == null || isNaN(num)) return '0.00';
            return Number(num).toFixed(2);
        },

        /**
         * Load internal cost for one site + month. SuperAdmin only — the server verifies the token
         * and role BEFORE querying, so a 401/403 here means the gate worked, not that the UI broke.
         * Surfaced as a plain message rather than a toast so it stays on screen next to the controls.
         */
        async loadUsageCost() {
            if (!this.usageCostSiteId) return;
            this.usageCostLoading = true;
            this.usageCostError = null;
            this.usageCostData = null;
            try {
                const month = this.usageCostMonth || new Date().toISOString().slice(0, 7);
                this.usageCostMonth = month;
                // The endpoint reads an omitted month as "current month, DB clock". Sending the
                // current YYYY-MM explicitly would take the past-month branch and drop the balance,
                // so pass null when the picker is on this month.
                const isCurrent = month === new Date().toISOString().slice(0, 7);
                this.usageCostData = await this.getSiteUsageCost({
                    siteID: this.usageCostSiteId,
                    month: isCurrent ? null : month,
                    channel: this.usageCostChannel,
                });
            } catch (error) {
                const status = error && error.response && error.response.status;
                const body = error && error.response && error.response.data;
                this.usageCostError = status === 401 || status === 403
                    ? 'Not authorized — this report is Super Admin only.'
                    : `Failed to load cost: ${(body && (body.message || body)) || (error && error.message) || 'Unknown error'}`;
            } finally {
                this.usageCostLoading = false;
            }
        },

        wasteReasonLabel(reason) {
            return ({
                orphan: 'No site (orphan subaccount)',
                'inactive-site': 'Site deactivated',
                idle: 'No SMS activity this month',
            })[reason] || reason;
        },

        viewTwilioSiteDetail(site) {
            this.twilioSelectedSite = site;
            // Reset expansion state so each site opens collapsed by default
            this.twilioExpandedCategories = [];
            this.$bvModal.show('twilio-site-detail-modal');
        },

        // Toggle a parent category's children open/closed in the usage detail modal
        toggleTwilioCategory(category) {
            const idx = this.twilioExpandedCategories.indexOf(category);
            if (idx === -1) {
                this.twilioExpandedCategories.push(category);
            } else {
                this.twilioExpandedCategories.splice(idx, 1);
            }
        },

        isTwilioCategoryExpanded(category) {
            return this.twilioExpandedCategories.includes(category);
        },

        // A child is "redundant" when its count and price match the parent within rounding —
        // happens when 100% of a parent's traffic falls into a single subcategory (e.g. all
        // inbound SMS is long-code). Hiding these removes pure noise from the modal.
        isRedundantTwilioChild(parent, child) {
            if (!parent || !child) return false;
            const sameCount = (parent.count || 0) === (child.count || 0);
            const samePrice = Math.abs((parent.price || 0) - (child.price || 0)) < 0.005;
            return sameCount && samePrice;
        },

        // Returns only the children worth showing — drops redundant ones, keeps the rest
        twilioVisibleChildren(item) {
            if (!item || !item.children) return [];
            return item.children.filter(c => !this.isRedundantTwilioChild(item, c));
        },

        twilioHasVisibleChildren(item) {
            return this.twilioVisibleChildren(item).length > 0;
        },

        async viewTwilioSiteNumbers() {
            const site = this.twilioSelectedSite;
            if (!site || !site.SubaccountSID) return;

            // Open the sub-modal first so the user gets immediate feedback,
            // then load (or reuse cached) data.
            this.$bvModal.show('twilio-site-numbers-modal');

            // Cache hit — same subaccount as last fetch within this modal session
            if (this.twilioNumbersFetchedForSid === site.SubaccountSID && this.twilioNumbersList.length) {
                return;
            }

            this.twilioNumbersLoading = true;
            this.twilioNumbersError = null;
            this.twilioNumbersList = [];
            this.twilioNumbersFetchedForSid = null;

            try {
                const response = await this.sendAction({
                    action: 'TWILIO_LIST_NUMBERS',
                    subaccountSid: site.SubaccountSID,
                });
                const numbers = response && response.numbers ? response.numbers : [];
                this.twilioNumbersList = numbers;
                this.twilioNumbersFetchedForSid = site.SubaccountSID;
            } catch (err) {
                this.twilioNumbersError = (err && err.message) || 'Failed to load phone numbers';
                console.error('[viewTwilioSiteNumbers] error:', err);
            } finally {
                this.twilioNumbersLoading = false;
            }
        },

        formatTwilioNumberCapabilities(caps) {
            if (!caps) return '';
            const parts = [];
            if (caps.voice) parts.push('Voice');
            if (caps.sms) parts.push('SMS');
            if (caps.mms) parts.push('MMS');
            if (caps.fax) parts.push('Fax');
            return parts.join(', ');
        },

        exportTwilioReport() {
            if (!this.twilioUsageData || !this.twilioUsageData.sites) return;

            const rows = this.twilioSiteRows;
            const headers = ['Site', 'Total ($)', 'SMS Out ($)', 'SMS In ($)', 'Carrier Fees ($)', 'Phone #s ($)', 'SMS Sent', 'SMS Received'];
            let csv = headers.join(',') + '\n';

            for (const row of rows) {
                csv += [
                    `"${row.CustomerName}"`,
                    this.fmtPrice(row.totalPrice),
                    this.fmtPrice(row.smsOut),
                    this.fmtPrice(row.smsIn),
                    this.fmtPrice(row.carrierFees),
                    this.fmtPrice(row.phoneNumbers),
                    row.smsOutCount,
                    row.smsInCount
                ].join(',') + '\n';
            }

            // Add totals row
            csv += `"TOTAL",${this.fmtPrice(this.twilioUsageData.siteSubtotal)},,,,,,\n`;
            csv += `"MASTER TOTAL",${this.fmtPrice(this.twilioUsageData.masterTotal)},,,,,,\n`;
            csv += `"DISCREPANCY",${this.fmtPrice(this.twilioUsageData.discrepancy)},,,,,,\n`;

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `twilio-usage-${this.twilioUsageMonth}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        },

        // A2P Status Dashboard Methods
        async refreshA2PStatus() {
            this.a2pLoading = true;
            try {
                const response = await this.getAllA2PRegistrations();

                if (response.success && response.registrations) {
                    this.a2pRegistrations = response.registrations;
                    this.calculateA2PStats();
                    this.$toast.success(`A2P status refreshed - ${response.count} registration(s) found`);
                } else {
                    // Handle case where no registrations found
                    this.a2pRegistrations = [];
                    this.calculateA2PStats();
                    this.$toast.info('No A2P registrations found');
                }
            } catch (error) {
                console.error('Error refreshing A2P status:', error);
                this.$toast.error('Failed to refresh A2P status: ' + (error.message || 'Unknown error'));
            } finally {
                this.a2pLoading = false;
            }
        },

        calculateA2PStats() {
            this.a2pStats = {
                approved: 0,
                pending: 0,
                inProgress: 0,
                failed: 0
            };

            this.a2pRegistrations.forEach(reg => {
                const status = reg.Status?.toLowerCase() || '';
                if (status === 'approved') {
                    this.a2pStats.approved++;
                } else if (status === 'pending' || status === 'submitted') {
                    this.a2pStats.pending++;
                } else if (status === 'in_progress' || status === 'processing') {
                    this.a2pStats.inProgress++;
                } else if (status === 'failed' || status === 'rejected' || status === 'error') {
                    this.a2pStats.failed++;
                }
            });
        },

        getStatusVariant(status) {
            const s = status?.toLowerCase() || '';
            if (s === 'approved') return 'success';
            if (s === 'pending' || s === 'submitted') return 'warning';
            if (s === 'in_progress' || s === 'processing') return 'info';
            if (s === 'failed' || s === 'rejected' || s === 'error') return 'danger';
            return 'secondary';
        },

        getProgressVariant(percentage) {
            if (percentage >= 80) return 'success';
            if (percentage >= 50) return 'warning';
            if (percentage >= 25) return 'info';
            return 'danger';
        },

        getResourceStatusVariant(status) {
            return status === 'Created' ? 'success' : 'secondary';
        },

        formatStatusDetails(statusDetails) {
            try {
                if (typeof statusDetails === 'string') {
                    return JSON.stringify(JSON.parse(statusDetails), null, 2);
                }
                return JSON.stringify(statusDetails, null, 2);
            } catch (error) {
                return statusDetails;
            }
        },

        viewA2PDetails(registration) {
            this.selectedA2PRegistration = registration;
            this.$bvModal.show('a2p-details-modal');
        },

        resetA2PDetails() {
            this.selectedA2PRegistration = null;
        },

        async refreshSiteStatus(registration) {
            try {
                const response = await this.sendAction({
                    action: 'GET_A2P_STATUS',
                    siteID: registration.SiteID
                });
                
                if (response.success) {
                    // Update the registration in the list
                    const index = this.a2pRegistrations.findIndex(r => r.SiteID === registration.SiteID);
                    if (index !== -1) {
                        Vue.set(this.a2pRegistrations, index, response.data);
                    }
                    this.calculateA2PStats();
                    this.$toast.success('Site status refreshed');
                } else {
                    this.$toast.error('Failed to refresh site status');
                }
            } catch (error) {
                console.error('Error refreshing site status:', error);
                this.$toast.error('Failed to refresh site status');
            }
        },

        async retryA2PRegistration() {
            if (!this.selectedA2PRegistration) return;
            
            try {
                const response = await this.sendAction({
                    action: 'RETRY_A2P_REGISTRATION',
                    siteID: this.selectedA2PRegistration.SiteID
                });
                
                if (response.success) {
                    this.$toast.success('A2P registration retry initiated');
                    this.$bvModal.hide('a2p-details-modal');
                    await this.refreshA2PStatus();
                } else {
                    this.$toast.error('Failed to retry registration: ' + response.message);
                }
            } catch (error) {
                console.error('Error retrying A2P registration:', error);
                this.$toast.error('Failed to retry registration');
            }
        },

        viewTwilioConsole() {
            if (!this.selectedA2PRegistration?.TwilioAccountSID) {
                this.$toast.warning('No Twilio Account SID available');
                return;
            }
            
            const url = `https://console.twilio.com/us1/develop/phone-numbers/manage/incoming/${this.selectedA2PRegistration.TwilioAccountSID}`;
            window.open(url, '_blank');
        },

        exportA2PReport() {
            if (this.a2pRegistrations.length === 0) {
                this.$toast.warning('No A2P registrations to export');
                return;
            }

            const csvContent = this.generateA2PCSV();
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `a2p-status-report-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        },

        generateA2PCSV() {
            const headers = ['Customer Name', 'Site ID', 'Business Name', 'Status', 'Progress %', 'Created', 'Updated'];
            const rows = this.a2pRegistrations.map(reg => [
                reg.CustomerName || '',
                reg.SiteID || '',
                reg.BusinessName || '',
                reg.Status || '',
                reg.ProgressPercentage || 0,
                reg.CreatedAt || '',
                reg.UpdatedAt || ''
            ]);

            return [headers, ...rows].map(row => row.join(',')).join('\n');
        },

        toggleAutoRefresh() {
            if (this.autoRefresh) {
                this.autoRefreshInterval = setInterval(() => {
                    this.refreshA2PStatus();
                }, 30000); // 30 seconds
            } else {
                if (this.autoRefreshInterval) {
                    clearInterval(this.autoRefreshInterval);
                    this.autoRefreshInterval = null;
                }
            }
        },

        // ===== PDF Import Methods =====
        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        getProcessingIcon(status) {
            if (status === 'success') return 'fa fa-check-circle text-success';
            if (status === 'processing') return 'fa fa-spinner fa-spin text-info';
            if (status === 'failed') return 'fa fa-times-circle text-danger';
            return 'fa fa-circle text-secondary';
        },

        async processPdfUpload() {
            if (!this.canProceedToProcessing) {
                this.$toast.error('Please fix file size issues before proceeding');
                return;
            }

            this.pdfImport.step = 3;
            this.pdfImport.processing = true;
            this.pdfImport.progress = 0;
            this.pdfImport.error = null;
            this.pdfImport.statusMessage = 'Preparing PDF files...';
            this.pdfImport.processingDetails = [];
            this.pdfImport.results = [];

            try {
                this.logActivity('Starting PDF import process...');

                // Convert files to base64 for transmission
                const pdfFilesData = [];
                for (let i = 0; i < this.pdfImport.files.length; i++) {
                    const file = this.pdfImport.files[i];
                    this.pdfImport.progress = Math.round((i / this.pdfImport.files.length) * 20);
                    this.pdfImport.statusMessage = `Reading file ${i + 1} of ${this.pdfImport.files.length}: ${file.name}...`;

                    const base64 = await this.fileToBase64(file);
                    pdfFilesData.push({
                        fileName: file.name,
                        fileSize: file.size,
                        content: base64.split(',')[1]
                    });

                    this.pdfImport.processingDetails.push({
                        fileName: file.name,
                        status: 'processing',
                        message: 'Queued for processing'
                    });
                }

                this.pdfImport.progress = 25;
                this.pdfImport.statusMessage = 'Uploading PDFs to server...';

                // Start async import
                const response = await this.sendAction({
                    siteID: this.pdfImport.targetSiteID,
                    action: 'START_PDF_IMPORT',
                    pdfFiles: pdfFilesData
                });

                const resultData = response.data?.data || response.data;

                if (!resultData || !resultData.jobId) {
                    throw new Error(resultData?.message || 'Failed to start PDF import');
                }

                this.pdfImport.jobId = resultData.jobId;
                this.pdfImport.progress = 30;
                this.pdfImport.statusMessage = `Processing ${pdfFilesData.length} PDF(s)... This may take a minute.`;
                this.logActivity(`PDF import job started: ${resultData.jobId}`);

                // Start polling
                this.startPdfImportPolling();

            } catch (error) {
                console.error('Error starting PDF import:', error);
                this.pdfImport.error = error.message || 'Failed to start PDF import';
                this.pdfImport.statusMessage = '';
                this.pdfImport.processing = false;
                this.$toast.error(`Failed to start PDF import: ${error.message}`);
                this.logActivity(`PDF import failed to start: ${error.message}`, 'ERROR');
            }
        },

        startPdfImportPolling() {
            // Clear any existing timer
            if (this.pdfImport.pollTimer) {
                clearInterval(this.pdfImport.pollTimer);
            }

            this.pdfImport.pollTimer = setInterval(async () => {
                try {
                    const response = await this.sendAction({
                        action: 'GET_PDF_IMPORT_STATUS',
                        jobId: this.pdfImport.jobId
                    });

                    const status = response.data?.data || response.data;

                    if (!status || !status.totalFiles) {
                        return; // Ignore bad responses, keep polling
                    }

                    // Update per-file progress
                    const completedPct = 30 + Math.round((status.completedFiles / status.totalFiles) * 60);
                    this.pdfImport.progress = Math.min(completedPct, 90);
                    this.pdfImport.statusMessage = `Processed ${status.completedFiles} of ${status.totalFiles} PDF(s)...`;

                    // Update processing details per file
                    if (status.results && status.results.length > 0) {
                        for (const result of status.results) {
                            const existing = this.pdfImport.processingDetails.find(d => d.fileName === result.fileName);
                            if (existing) {
                                existing.status = result.status;
                                existing.message = result.status === 'success'
                                    ? `Done — ${result.sectionCount} sections, ${result.fieldCount} fields`
                                    : `Failed: ${result.error}`;
                            }
                        }
                    }

                    // Check if complete
                    if (status.status === 'complete') {
                        clearInterval(this.pdfImport.pollTimer);
                        this.pdfImport.pollTimer = null;
                        this.pdfImport.progress = 100;
                        this.pdfImport.processing = false;

                        // Map results for preview (same structure as before)
                        this.pdfImport.results = (status.results || []).map(result => {
                            const fields = [];
                            if (result.formData && result.formData.controls) {
                                Object.values(result.formData.controls).forEach(control => {
                                    fields.push({
                                        uniqueId: control.uniqueId,
                                        name: control.name || '',
                                        label: control.label || '',
                                        type: control.type || 'input',
                                        required: control.validations && control.validations.some(v => v.ruleType === 'required')
                                    });
                                });
                            }
                            return {
                                fileName: result.fileName,
                                formName: result.formName || this.extractFormNameFromFile(result.fileName),
                                formType: result.formType || 'OTHER',
                                formData: result.formData,
                                status: result.status,
                                error: result.error,
                                sectionCount: result.sectionCount || 0,
                                fieldCount: result.fieldCount || 0,
                                fields
                            };
                        });

                        const successCount = this.pdfImport.results.filter(r => r.status === 'success').length;
                        this.pdfImport.statusMessage = 'Processing complete!';
                        this.pdfImport.step = 4;
                        this.logActivity(`PDF import completed: ${successCount} successful, ${this.pdfImport.results.length - successCount} failed`,
                            successCount > 0 ? 'SUCCESS' : 'ERROR');
                        this.$toast.success(`Processed ${this.pdfImport.results.length} PDF(s). ${successCount} successful.`);
                    }

                } catch (pollError) {
                    console.error('Error polling PDF import status:', pollError);
                    // Don't stop polling on transient errors — just log and retry
                }
            }, 3000); // Poll every 3 seconds
        },

        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },

        extractFormNameFromFile(fileName) {
            // Remove .pdf extension and clean up
            return fileName.replace(/\.pdf$/i, '').replace(/[_-]/g, ' ').trim() || 'Imported Form';
        },

        async saveImportedForms() {
            if (!this.canSavePdfForms) {
                this.$toast.error('No valid forms to save');
                return;
            }

            this.pdfImport.processing = true;
            this.pdfImport.progress = 0;
            this.pdfImport.statusMessage = 'Saving forms to database...';

            try {
                const successfulForms = this.pdfImport.results.filter(r => r.status === 'success');
                const savedForms = [];

                for (let i = 0; i < successfulForms.length; i++) {
                    const form = successfulForms[i];
                    this.pdfImport.progress = Math.round((i / successfulForms.length) * 90);
                    this.pdfImport.statusMessage = `Saving form ${i + 1} of ${successfulForms.length}: ${form.formName}...`;

                    // Update formData with edited field values
                    const updatedFormData = this.updateFormDataWithEdits(form.formData, form.fields);

                    const response = await this.sendAction({
                        siteID: this.pdfImport.targetSiteID,
                        action: 'SAVE_IMPORTED_PDF_FORM',
                        formName: form.formName,
                        formType: form.formType,
                        formData: updatedFormData
                    });

                    if (response && response.data && response.data.formID) {
                        savedForms.push({
                            formID: response.data.formID,
                            formName: form.formName
                        });
                    }
                }

                this.pdfImport.progress = 100;
                this.pdfImport.savedForms = savedForms;
                this.pdfImport.step = 5; // Move to success step

                this.logActivity(`Successfully saved ${savedForms.length} form(s) from PDF import`, 'SUCCESS');
                this.$toast.success(`Successfully imported ${savedForms.length} form(s)!`);

            } catch (error) {
                console.error('Error saving imported forms:', error);
                this.$toast.error(`Failed to save forms: ${error.message}`);
                this.logActivity(`Failed to save imported forms: ${error.message}`, 'ERROR');
            } finally {
                this.pdfImport.processing = false;
            }
        },

        updateFormDataWithEdits(formData, editedFields) {
            // Update formData controls with edited field values
            if (!formData || !formData.controls || !editedFields) return formData;

            const updatedFormData = JSON.parse(JSON.stringify(formData)); // Deep clone

            editedFields.forEach(editedField => {
                const control = updatedFormData.controls[editedField.uniqueId];
                if (control) {
                    // For Wiki controls, don't allow changing type or name
                    if (control.type === 'wiki') {
                        // Wiki controls are read-only in preview
                        return;
                    }
                    
                    control.name = editedField.name;
                    control.label = editedField.label;
                    control.type = editedField.type;

                    // Update validations for required field
                    if (!control.validations) {
                        control.validations = [];
                    }

                    if (editedField.required) {
                        // Add required validation if not present
                        const hasRequired = control.validations.some(v => v.ruleType === 'required');
                        if (!hasRequired) {
                            control.validations.push({
                                ruleType: 'required',
                                errorMessage: `${control.label || control.name} is required`,
                                additionalValue: ''
                            });
                        }
                    } else {
                        // Remove required validation
                        control.validations = control.validations.filter(v => v.ruleType !== 'required');
                    }
                }
            });

            return updatedFormData;
        },

        resetPdfImport() {
            if (this.pdfImport.pollTimer) {
                clearInterval(this.pdfImport.pollTimer);
            }
            this.showPdfImport = false;
            this.pdfImport = {
                step: 1,
                targetSiteID: null,
                files: [],
                processing: false,
                progress: 0,
                statusMessage: '',
                processingDetails: [],
                results: [],
                savedForms: [],
                jobId: null,
                pollTimer: null,
                error: null,
            };
        },

        // ===== STRIPE BILLING METHODS =====
    
    async initializeStripe() {
        if (!this.stripe) {
            this.stripeLoading = true;
            try {
                this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
                console.log('✅ Stripe initialized successfully');
            } catch (error) {
                console.error('❌ Failed to initialize Stripe:', error);
                this.$toast.error('Failed to initialize Stripe. Please check your configuration.');
            } finally {
                this.stripeLoading = false;
            }
        }
    },

    async loadStripeCustomer() {
        if (!this.selectedSiteForManagement) return;
        
        this.billingLoading.customer = true;
        try {
            // Call your backend API to get or create Stripe customer
            const response = await this.sendAction({
                siteID: this.selectedSiteForManagement.SiteID,
                action: 'STRIPE_GET_CUSTOMER',
                customerData: {
                    name: this.selectedSiteForManagement.CustomerName,
                    email: null, // You might want to get this from somewhere
                    metadata: {
                        teamio_site_id: this.selectedSiteForManagement.SiteID,
                        teamio_customer_name: this.selectedSiteForManagement.CustomerName
                    }
                }
            });
            
            if (response && response.data && response.data.customer) {
                this.selectedSiteStripeData.customer = response.data.customer;
                this.$toast.success('Stripe customer loaded successfully');
            } else {
                this.selectedSiteStripeData.customer = null;
                this.$toast.info('No Stripe customer found for this site');
            }
        } catch (error) {
            console.error('Error loading Stripe customer:', error);
            this.$toast.error('Failed to load Stripe customer');
        } finally {
            this.billingLoading.customer = false;
        }
    },

    async loadSubscriptions() {
        if (!this.selectedSiteStripeData.customer) {
            this.$toast.warning('Please load the customer first');
            return;
        }

        this.billingLoading.subscriptions = true;
        try {
            const response = await this.sendAction({
                siteID: this.selectedSiteForManagement.SiteID,
                action: 'STRIPE_GET_SUBSCRIPTIONS',
                customerId: this.selectedSiteStripeData.customer.id
            });
            
            if (response && response.data && response.data.subscriptions) {
                this.selectedSiteStripeData.subscriptions = response.data.subscriptions.data || [];
                this.$toast.success(`Found ${this.selectedSiteStripeData.subscriptions.length} subscription(s)`);
            }
        } catch (error) {
            console.error('Error loading subscriptions:', error);
            this.$toast.error('Failed to load subscriptions');
        } finally {
            this.billingLoading.subscriptions = false;
        }
    },

    async loadInvoices() {
        if (!this.selectedSiteStripeData.customer) {
            this.$toast.warning('Please load the customer first');
            return;
        }

        this.billingLoading.invoices = true;
        try {
            const response = await this.sendAction({
                siteID: this.selectedSiteForManagement.SiteID,
                action: 'STRIPE_GET_INVOICES',
                customerId: this.selectedSiteStripeData.customer.id
            });
            
            if (response && response.data && response.data.invoices) {
                this.selectedSiteStripeData.invoices = response.data.invoices.data || [];
                this.$toast.success(`Found ${this.selectedSiteStripeData.invoices.length} invoice(s)`);
            }
        } catch (error) {
            console.error('Error loading invoices:', error);
            this.$toast.error('Failed to load invoices');
        } finally {
            this.billingLoading.invoices = false;
        }
    },

    async loadPaymentMethods() {
        if (!this.selectedSiteStripeData.customer) {
            this.$toast.warning('Please load the customer first');
            return;
        }

        this.billingLoading.paymentMethods = true;
        try {
            const response = await this.sendAction({
                siteID: this.selectedSiteForManagement.SiteID,
                action: 'STRIPE_GET_PAYMENT_METHODS',
                customerId: this.selectedSiteStripeData.customer.id
            });
            
            if (response && response.data && response.data.paymentMethods) {
                this.selectedSiteStripeData.paymentMethods = response.data.paymentMethods.data || [];
                this.$toast.success(`Found ${this.selectedSiteStripeData.paymentMethods.length} payment method(s)`);
            }
        } catch (error) {
            console.error('Error loading payment methods:', error);
            this.$toast.error('Failed to load payment methods');
        } finally {
            this.billingLoading.paymentMethods = false;
        }
    },

    showCreateStripeCustomer() {
        // Pre-populate form with site data
        this.stripeCustomerForm.name = this.selectedSiteForManagement.CustomerName;
        this.stripeCustomerForm.email = ''; // You might want to get this from somewhere
        this.showStripeCustomerModal = true;
    },

    showEditStripeCustomer() {
        if (!this.selectedSiteStripeData.customer) return;
        
        // Pre-populate form with current customer data
        this.stripeCustomerForm.name = this.selectedSiteStripeData.customer.name || '';
        this.stripeCustomerForm.email = this.selectedSiteStripeData.customer.email || '';
        this.stripeCustomerForm.phone = this.selectedSiteStripeData.customer.phone || '';
        
        const address = this.selectedSiteStripeData.customer.address || {};
        this.stripeCustomerForm.address = {
            line1: address.line1 || '',
            line2: address.line2 || '',
            city: address.city || '',
            state: address.state || '',
            postal_code: address.postal_code || '',
            country: address.country || 'US'
        };
        
        this.showStripeCustomerModal = true;
    },

    showCreateSubscription() {
        if (!this.selectedSiteStripeData.customer) {
            this.$toast.warning('Please create/load a customer first');
            return;
        }
        this.showCreateSubscriptionModal = true;
    },

    async cancelSubscription(subscriptionId) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            'Are you sure you want to cancel this subscription? This action cannot be undone.',
            {
                title: 'Cancel Subscription',
                okVariant: 'danger',
                okTitle: 'Yes, Cancel',
                cancelTitle: 'No, Keep'
            }
        );

        if (!confirmed) return;

        try {
            const response = await this.sendAction({
                siteID: this.selectedSiteForManagement.SiteID,
                action: 'STRIPE_CANCEL_SUBSCRIPTION',
                subscriptionId: subscriptionId
            });
            
            if (response && response.data && response.data.success) {
                this.$toast.success('Subscription cancelled successfully');
                await this.loadSubscriptions(); // Refresh the list
            } else {
                this.$toast.error('Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            this.$toast.error('Failed to cancel subscription');
        }
    },

    viewSubscriptionDetails(subscription) {
        // You can implement a detailed subscription modal here
        this.$toast.info(`Subscription ${subscription.id} - Status: ${subscription.status}`);
    },

    async openStripePortal() {
        if (!this.selectedSiteStripeData.customer) {
            this.$toast.warning('No customer found');
            return;
        }

        try {
            const response = await this.sendAction({
                siteID: this.selectedSiteForManagement.SiteID,
                action: 'STRIPE_CREATE_PORTAL_SESSION',
                customerId: this.selectedSiteStripeData.customer.id,
                returnUrl: window.location.href
            });
            
            if (response && response.data && response.data.url) {
                window.open(response.data.url, '_blank');
            } else {
                this.$toast.error('Failed to create portal session');
            }
        } catch (error) {
            console.error('Error creating portal session:', error);
            this.$toast.error('Failed to open customer portal');
        }
    },

    async syncStripeData() {
        await this.loadStripeCustomer();
        if (this.selectedSiteStripeData.customer) {
            await Promise.all([
                this.loadSubscriptions(),
                this.loadInvoices(),
                this.loadPaymentMethods()
            ]);
        }
    },

    viewInvoice(url) {
        if (url) {
            window.open(url, '_blank');
        }
    },

    // Helper methods for styling
    getSubscriptionStatusVariant(status) {
        const variants = {
            'active': 'success',
            'trialing': 'info',
            'past_due': 'warning',
            'canceled': 'danger',
            'unpaid': 'danger',
            'incomplete': 'warning',
            'incomplete_expired': 'danger'
        };
        return variants[status] || 'secondary';
    },

    getInvoiceStatusVariant(status) {
        const variants = {
            'paid': 'success',
            'open': 'warning',
            'draft': 'info',
            'uncollectible': 'danger',
            'void': 'secondary'
        };
        return variants[status] || 'secondary';
    },

    // Reset Stripe data when switching sites
    resetStripeData() {
        this.selectedSiteStripeData = {
            customer: null,
            subscriptions: [],
            invoices: [],
            paymentMethods: []
        };
        this.billingLoading = {
            customer: false,
            subscriptions: false,
            invoices: false,
            paymentMethods: false
        };
    },

    // ===== CUSTOMER CREATION BILLING METHODS =====

    async loadStripePlans() {
        this.stripePlansLoading = true;
        this.stripePlansError = null;
        try {
            const response = await this.sendAction({
                action: 'STRIPE_GET_PLANS'
            });
            
            if (response && response.data && response.data.plans) {
                this.subscriptionPlans = response.data.plans.map(plan => ({
                    id: plan.id,
                    name: plan.nickname || plan.product.name,
                    description: plan.product.description || '',
                    amount: plan.unit_amount,
                    interval: plan.recurring.interval,
                    currency: plan.currency
                }));
                this.stripePlansLastLoaded = new Date();
                // Plans loaded successfully (no toast notification needed)
            } else {
                this.subscriptionPlans = [];
                this.stripePlansError = 'No plans returned from Stripe API';
            }
        } catch (error) {
            console.error('Error loading Stripe plans:', error);
            this.stripePlansError = error.response?.data?.message || error.message || 'Failed to load plans';
            this.$toast.error(`Failed to load Stripe plans: ${this.stripePlansError}`);
            
            // Fallback to default plans if API fails
            this.subscriptionPlans = [
                { 
                    id: 'fallback_basic',
                    name: 'Basic Monthly (Fallback)',
                    amount: 9900,
                    interval: 'month',
                    description: 'Basic TeamIO features - API connection failed'
                },
                { 
                    id: 'fallback_pro',
                    name: 'Professional Monthly (Fallback)', 
                    amount: 19900,
                    interval: 'month',
                    description: 'Full TeamIO features - API connection failed'
                }
            ];
        } finally {
            this.stripePlansLoading = false;
        }
    },

    getSelectedPlanDetails(planId) {
        const plan = this.subscriptionPlans.find(p => p.id === planId);
        return plan || {
            id: planId,
            name: 'Unknown Plan',
            description: 'Plan details not found',
            amount: 0,
            interval: 'month'
        };
    },

    resetOfficeBilling(officeIndex) {
        // When "Skip billing setup" is toggled, reset billing fields
        if (this.offices[officeIndex].skipStripeCreation) {
            this.offices[officeIndex].selectedPlan = null;
            this.offices[officeIndex].billingEmail = '';
            this.offices[officeIndex].trialDays = 0;
            this.offices[officeIndex].billingNotes = '';
        }
    },

    initializeOfficeDefaults() {
        // Initialize billing defaults for all offices
        this.offices.forEach(office => {
            if (!office.hasOwnProperty('skipStripeCreation')) {
                Vue.set(office, 'skipStripeCreation', false);
            }
            if (!office.hasOwnProperty('selectedPlan')) {
                Vue.set(office, 'selectedPlan', null);
            }
            if (!office.hasOwnProperty('billingEmail')) {
                Vue.set(office, 'billingEmail', '');
            }
            if (!office.hasOwnProperty('trialDays')) {
                Vue.set(office, 'trialDays', 14); // Default 14-day trial
            }
            if (!office.hasOwnProperty('billingNotes')) {
                Vue.set(office, 'billingNotes', '');
            }
        });
    },

    // ===== STRIPE MAPPING METHODS =====

    async loadAllSitesForMapping() {
        this.mappingLoading.sites = true;
        this.mappingError = null;
        try {
            const response = await this.sendAction({
                action: 'GET_ALL_SITES_FOR_STRIPE_MAPPING'
            });
            
            if (response && response.data && response.data.sites) {
                this.allSitesForMapping = response.data.sites.map(site => ({
                    ...site,
                    selectedStripeCustomer: site.stripe_customer_id || null // Pre-select current mapping
                }));
                // Sites loaded successfully (no success message needed)
            } else {
                this.mappingError = 'Failed to load sites data';
            }
        } catch (error) {
            console.error('Error loading sites for mapping:', error);
            this.mappingError = 'Failed to load sites: ' + (error.response?.data?.message || error.message);
        } finally {
            this.mappingLoading.sites = false;
        }
    },

    async loadAllStripeCustomers() {
        this.mappingLoading.customers = true;
        this.mappingError = null;
        try {
            const response = await this.sendAction({
                action: 'STRIPE_GET_ALL_CUSTOMERS'
            });
            
            if (response && response.data && response.data.customers) {
                this.allStripeCustomers = response.data.customers.data || response.data.customers;
                // Stripe customers loaded successfully (no success message needed)
            } else {
                this.mappingError = 'Failed to load Stripe customers';
            }
        } catch (error) {
            console.error('Error loading Stripe customers:', error);
            this.mappingError = 'Failed to load Stripe customers: ' + (error.response?.data?.message || error.message);
        } finally {
            this.mappingLoading.customers = false;
        }
    },

    async saveSiteStripeMapping(site) {
        if (!site.selectedStripeCustomer) {
            this.mappingError = 'Please select a Stripe customer first';
            return;
        }

        this.savingMapping = site.SiteID;
        this.mappingError = null;
        try {
            const response = await this.sendAction({
                siteID: site.SiteID,
                action: 'SAVE_SITE_STRIPE_MAPPING',
                stripeCustomerId: site.selectedStripeCustomer
            });
            
            if (response && response.success !== false) {
                // Update local data
                site.stripe_customer_id = site.selectedStripeCustomer;
                this.mappingSuccess = `Successfully linked ${site.CustomerName} (Site ${site.SiteID}) to Stripe customer`;
            } else {
                this.mappingError = response?.message || 'Failed to save mapping';
            }
        } catch (error) {
            console.error('Error saving site Stripe mapping:', error);
            this.mappingError = 'Failed to save mapping: ' + (error.response?.data?.message || error.message);
        } finally {
            this.savingMapping = null;
        }
    },

    async unlinkSiteStripeMapping(site) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to unlink ${site.CustomerName} (Site ${site.SiteID}) from Stripe?`,
            {
                title: 'Confirm Unlink',
                okVariant: 'danger',
                okTitle: 'Yes, Unlink',
                cancelTitle: 'Cancel'
            }
        );

        if (!confirmed) return;

        this.savingMapping = site.SiteID;
        this.mappingError = null;
        try {
            const response = await this.sendAction({
                siteID: site.SiteID,
                action: 'UNLINK_SITE_STRIPE_MAPPING'
            });
            
            if (response && response.success !== false) {
                // Update local data
                site.stripe_customer_id = null;
                site.selectedStripeCustomer = null;
                this.mappingSuccess = `Successfully unlinked ${site.CustomerName} (Site ${site.SiteID}) from Stripe`;
            } else {
                this.mappingError = response?.message || 'Failed to unlink mapping';
            }
        } catch (error) {
            console.error('Error unlinking site Stripe mapping:', error);
            this.mappingError = 'Failed to unlink mapping: ' + (error.response?.data?.message || error.message);
        } finally {
            this.savingMapping = null;
        }
    },

    getStripeCustomerNameById(customerId) {
        const customer = this.allStripeCustomers.find(c => c.id === customerId);
        return customer ? `${customer.name} (${customer.email})` : customerId;
    },

    autoMatchSuggestions() {
        this.autoMatchRunning = true;
        let matchCount = 0;

        try {
            // Helper: normalize string for comparison
            const normalize = (str) => (str || '').toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');

            // Helper: extract significant words (drop common filler words)
            const stopWords = new Set(['dental', 'dentistry', 'family', 'group', 'office', 'clinic', 'center', 'llc', 'inc', 'pc', 'pllc', 'dds', 'dmd', 'dr', 'the', 'and', 'of']);
            const significantWords = (str) => normalize(str).split(/\s+/).filter(w => w.length > 1 && !stopWords.has(w));

            // Only try to match active, unlinked sites
            const sitesToMatch = this.activeSitesForMapping.filter(s => !s.stripe_customer_id);

            for (const site of sitesToMatch) {
                const siteNameNorm = normalize(site.SiteName);
                const custNameNorm = normalize(site.CustomerName);
                const siteWords = significantWords(site.SiteName);
                const custWords = significantWords(site.CustomerName);

                let bestMatch = null;
                let bestScore = 0;
                let bestReason = '';

                for (const sc of this.allStripeCustomers) {
                    const scNameNorm = normalize(sc.name);
                    const scEmailNorm = normalize(sc.email || '');
                    const scWords = significantWords(sc.name);

                    let score = 0;
                    let reason = '';

                    // 1. Exact name match (highest confidence)
                    if (siteNameNorm === scNameNorm || custNameNorm === scNameNorm) {
                        score = 100;
                        reason = 'Exact name match';
                    }
                    // 2. Site or Customer name contains Stripe name or vice versa
                    else if (siteNameNorm && scNameNorm && (siteNameNorm.includes(scNameNorm) || scNameNorm.includes(siteNameNorm))) {
                        score = 80;
                        reason = 'Site name contains Stripe name';
                    }
                    else if (custNameNorm && scNameNorm && (custNameNorm.includes(scNameNorm) || scNameNorm.includes(custNameNorm))) {
                        score = 75;
                        reason = 'Customer name contains Stripe name';
                    }
                    // 3. Significant word overlap
                    else {
                        // Check how many significant words from site/customer match Stripe customer
                        const allSiteWords = [...new Set([...siteWords, ...custWords])];
                        if (allSiteWords.length > 0 && scWords.length > 0) {
                            const matchedWords = allSiteWords.filter(w => scWords.some(sw => sw.includes(w) || w.includes(sw)));
                            const overlapRatio = matchedWords.length / Math.max(allSiteWords.length, scWords.length);
                            if (matchedWords.length >= 2 || (matchedWords.length === 1 && overlapRatio >= 0.5)) {
                                score = Math.round(overlapRatio * 60);
                                reason = `Word match: ${matchedWords.join(', ')}`;
                            }
                        }
                    }

                    // 4. Email contains customer/site name words (bonus)
                    if (score < 50 && scEmailNorm) {
                        const emailLocal = scEmailNorm.split('@')[0];
                        const allWords = [...new Set([...siteWords, ...custWords])];
                        const emailMatches = allWords.filter(w => emailLocal.includes(w));
                        if (emailMatches.length >= 1) {
                            const emailScore = 40 + emailMatches.length * 10;
                            if (emailScore > score) {
                                score = Math.min(emailScore, 55);
                                reason = `Email match: ${scEmailNorm}`;
                            }
                        }
                    }

                    if (score > bestScore) {
                        bestScore = score;
                        bestMatch = sc;
                        bestReason = reason;
                    }
                }

                // Only suggest if confidence is reasonable (>= 40)
                if (bestMatch && bestScore >= 40) {
                    this.$set(site, 'selectedStripeCustomer', bestMatch.id);
                    this.$set(site, 'autoMatchSuggested', true);
                    this.$set(site, 'autoMatchReason', `${bestReason} (${bestScore}% confidence)`);
                    matchCount++;
                }
            }

            if (matchCount > 0) {
                this.mappingSuccess = `Auto-matched ${matchCount} site(s). Please review each suggestion before saving.`;
            } else {
                this.mappingSuccess = 'No confident matches found. You may need to link these manually.';
            }
        } catch (error) {
            console.error('Error in auto-match:', error);
            this.mappingError = 'Auto-match failed: ' + error.message;
        } finally {
            this.autoMatchRunning = false;
        }
    }
    },

    watch: {
        // Forms card shown for a site → load its OD imaging categories. loadSiteFeatures also calls
        // ensureFormsImagingLoaded; this covers the operator switching Forms on. toggleFeature stays a
        // pure settings write.
        'siteFeatures.FORMS_PAID_FOR'(on) {
            if (on) this.ensureFormsImagingLoaded();
        },
        // Bulk Form Copy: categories belong to the TARGET site.
        'bulkFormCopy.targetSiteID'(siteID) {
            this.loadBulkCopyImagingCategories(siteID);
        },
        // A new URL means the browser hasn't told us anything about it yet.
        logoUrl() {
            this.createLogoPreview = { loaded: undefined, naturalWidth: 0, naturalHeight: 0 };
        },
        'siteLogoEdit.url'() {
            this.siteLogoEdit.preview = { loaded: undefined, naturalWidth: 0, naturalHeight: 0 };
        },
      allSettings(newValue, oldValue) {
        if(!oldValue) {
          this.init();
        }
      },

      /**
       * Default the Credit Cost site picker to the site you're already on, once the sites list has
       * loaded. `immediate` so it also fires when the list was already in the store (it is cached in
       * localStorage, so the watcher would otherwise never run). Only ever sets it when unset, so it
       * never yanks the picker out from under a selection.
       */
      sites: {
        immediate: true,
        handler() {
          if (!this.usageCostSiteId && this.siteID) this.usageCostSiteId = this.siteID;
        },
      },

      numberOfOffices(newVal, oldVal) {
        // Adjust offices array based on numberOfOffices
        const currentCount = this.offices.length;
        const targetCount = parseInt(newVal) || 1;

        if (targetCount > currentCount) {
          // Add new offices
          for (let i = currentCount; i < targetCount; i++) {
            this.offices.push(this.createEmptyOffice(i + 1));
          }
        } else if (targetCount < currentCount) {
          // Remove excess offices
          this.offices.splice(targetCount);
        }
      },

      // Auto-load when Twilio Dashboard is opened
      showTwilioDashboard(newVal) {
        if (newVal === true) {
          this.refreshA2PStatus();
        }
      }
    }
}
</script>

<style lang='scss' scoped>

/* Practice-logo entry point (Add Customer preview + Site Management → Features → Practice logo).
   The preview <img> is capped exactly like every logo render surface — pinned by
   cypress/node-tests/logo-size-cap-guardrail.test.js. */
.logo-check {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fff;
}
.logo-check-img {
    max-width: 200px;
    max-height: 80px;
    width: auto;
    height: auto;
    object-fit: contain;
    flex: 0 0 auto;
}
.logo-check-msgs { font-size: 12px; color: #6c757d; line-height: 1.35; }
.logo-check--ok .logo-check-msgs { color: #28a745; }
.logo-check--warn { border-color: #f0ad4e; }
.logo-check--warn .logo-check-msgs { color: #b7791f; }
.logo-check--error, .logo-check--invalid { border-color: #dc3545; }
.logo-check--error .logo-check-msgs, .logo-check--invalid .logo-check-msgs { color: #dc3545; }
.site-logo-body { padding: 10px 14px 14px; }

.multiselect__content-wrapper {
    max-height: 500px;
    z-index: 1150;
}

/* Loading spinner animation */
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Customer Creation Progress Styles */
.bg-success-light {
    background-color: #d4edda !important;
}

.bg-danger-light {
    background-color: #f8d7da !important;
}

.bg-warning-light {
    background-color: #fff3cd !important;
}

.bg-info-light {
    background-color: #d1ecf1 !important;
}

/* A2P Status Box Styles */
.a2p-status-box {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
}

.a2p-status-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
}

.a2p-status-label {
    color: #6c757d;
    font-size: 12px;
}

.a2p-status-value {
    font-weight: 600;
}

.a2p-status-none {
    color: #6c757d;
    text-align: center;
}

.a2p-status-success {
    background: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
}

.a2p-status-warning {
    background: #fff3cd;
    border-color: #ffeeba;
    color: #856404;
}

.a2p-status-info {
    background: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
}

.a2p-status-danger {
    background: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
}

.progress-step {
    transition: all 0.3s ease;
}

.progress-step:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-container::-webkit-scrollbar {
    width: 8px;
}

.progress-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.progress-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.progress-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}


/* TeamIO Knowledge Base Styles */
.text-purple {
    color: #6f42c1 !important;
}

/* Minimal CSS for TeamIO Knowledge Base */
.teamio-knowledge-urls {
    /* Clean slate - no complex overrides */
    display: block;
}

.tab-content {
    min-height: 400px;
}

.VueTables__table {
    height: 500px;
    overflow-y: auto;
}

/* Specific fixes for dropdown in SuperAdmin table */
.overdue-recall-table .dropdown-menu {
    z-index: 1050 !important;
    position: absolute !important;
}

/* Allow table container to show dropdowns outside bounds */
.table-responsive.overdue-recall-table {
    overflow: visible !important;
}

/* Ensure dropdown button works properly */
.overdue-recall-table .b-dropdown {
    position: relative;
}

.overdue-recall-table .b-dropdown .dropdown-toggle {
    text-align: left;
    border: 1px solid #e8e8e8;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.overdue-recall-table .b-dropdown .dropdown-toggle::after {
    float: right;
    margin-top: 1px;
}

.overdue-recall-table .b-dropdown .dropdown-item {
    font-size: 12px;
    white-space: nowrap;
}

/* Bulk Form Copy Styling - Scoped to this component only */
.form-selection-container {
    background-color: #f8f9fa;
    padding: 20px 20px 20px 30px !important; /* Extra left padding */
    
    ::v-deep .custom-control-label {
        color: #495057 !important;
        font-weight: 500;
        font-size: 14px;
        padding-left: 12px; /* More space between checkbox and text */
    }
    
    ::v-deep .custom-checkbox .custom-control-label::before {
        border-color: #ced4da;
    }
    
    ::v-deep .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
        background-color: #28a745;
        border-color: #28a745;
    }
    
    ::v-deep .custom-control {
        margin-bottom: 10px;
        padding-left: 20px; /* Push checkboxes further from left edge */
        margin-left: 10px; /* Additional left margin */
    }
    
    ::v-deep .form-check {
        padding-left: 20px; /* For any form-check elements */
        margin-left: 10px;
    }
}

/* PDF Import Styling */
#pdf-import-modal .modal-body {
    max-height: 75vh;
    overflow-y: auto;
}

.pdf-import-step {
    min-height: 300px;
}

.extracted-form-preview .card {
    border: 1px solid #e3e6f0;
    box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
}

.processing-item {
    padding: 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    margin-bottom: 8px;
}

/* DNS Records Styling */
.dns-records-container {
    max-height: 400px;
    overflow-y: auto;
}

.dns-record-card .card {
    border: 1px solid #e3e6f0;
    box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
}

.dns-record-card .card-header {
    background-color: #f8f9fc;
    border-bottom: 1px solid #e3e6f0;
    font-size: 0.875rem;
}

.dns-record-card .input-group input {
    font-family: monospace;
    font-size: 0.85rem;
    background-color: #f8f9fc;
}

.dns-record-card .btn-outline-secondary {
    border-color: #d1d3e2;
}

.dns-record-card .btn-outline-secondary:hover {
    background-color: #5a5c69;
    border-color: #5a5c69;
}

.dns-important-notes {
    background-color: #f0f0f0;
    border: 1px solid #ccc;
    color: #333;
    font-size: 0.9rem;
}

.dns-important-notes ul li {
    margin-bottom: 0.25rem;
}

.dns-email-form {
    background-color: #eef5ff;
    border: 1px solid #b8d4f0;
}

.panel-dns-section {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 10px;
    background: #fafbfc;
}

.dns-panel-record {
    background: #fff;
    border: 1px solid #e9ecef;
}

.dns-panel-value {
    font-size: 0.8rem;
    word-break: break-all;
    cursor: pointer;
}

.dns-panel-value:hover {
    background: #f0f7ff;
    border-radius: 3px;
}

.dns-panel-value code {
    font-size: 0.78rem;
    color: #333;
    background: transparent;
}

/* ========== Site Management UI Styles ========== */

/* Side Panel */
.site-management-panel {
    position: fixed;
    top: 0;
    right: 0;
    width: 90vw;
    max-width: 1400px;
    height: 100vh;
    background: #fff;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    z-index: 1060;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.site-management-panel .panel-header {
    flex-shrink: 0;
}

.site-management-panel .panel-body {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.site-management-panel .status-overview {
    flex-shrink: 0;
}

.site-management-panel .status-item i {
    font-size: 18px;
}

/* Panel tabs styling - horizontal tabs */
.site-management-panel .panel-tabs-nav {
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    padding: 10px 15px 0 15px;
    flex-shrink: 0;
}

.site-management-panel .panel-tabs-nav .nav-link {
    border-radius: 4px 4px 0 0 !important;
    padding: 8px 16px;
    font-size: 13px;
    color: #495057;
    margin-right: 5px;
}

.site-management-panel .panel-tabs-nav .nav-link.active {
    background-color: #007bff !important;
    color: #fff !important;
}

.site-management-panel .panel-tab-content {
    flex: 1;
    overflow-y: auto;
}

/* Panel overlay */
.panel-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1055;
}

/* Panel slide animation */
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.3s ease;
}

.slide-right-enter,
.slide-right-leave-to {
    transform: translateX(100%);
}

/* Domain cards */
.domain-item,
.domain-card {
    border: 1px solid #e3e6f0;
    transition: all 0.2s ease;
}

.domain-item:hover,
.domain-card:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.domain-card {
    padding: 10px;
    border-radius: 6px;
    background: #f8f9fa;
}

/* Action groups in panel */
.action-group {
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
}

.action-group:last-child {
    border-bottom: none;
}

/* Exe per-site OD table overrides card (System tab) */
.exe-overrides-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 2px;
}

.exe-overrides-applied {
    background: #f6f8fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 6px 8px;
}

/* b-alert internals — kept under a scoped ancestor so this can't leak to other pages. */
.action-group ::v-deep .alert.exe-overrides-note {
    padding: 6px 8px;
    font-size: 0.75rem;
    margin-bottom: 8px;
}

/* Restart App / Set Debug Mode (System tab): why a button is disabled, and the status line. */
.exe-restart-note {
    font-size: 0.75rem;
    line-height: 1.35;
}

.exe-restart-status {
    font-size: 0.75rem;
    line-height: 1.35;
    background: #f6f8fa;
    border: 1px solid #e9ecef;
    border-left-width: 3px;
    border-radius: 4px;
    padding: 6px 8px;
}

.exe-restart-status--success { border-left-color: #28a745; }
.exe-restart-status--info { border-left-color: #17a2b8; }
.exe-restart-status--warning { border-left-color: #ffc107; }
.exe-restart-status--danger { border-left-color: #dc3545; }
.exe-restart-status--muted { border-left-color: #ced4da; }

/* Deactivation status panel styles */
.deactivation-panel {
    background: linear-gradient(135deg, #fff9e6 0%, #fff 100%);
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 12px;
    margin-top: 8px;
}

.deactivation-panel.status-failed {
    background: linear-gradient(135deg, #ffe6e6 0%, #fff 100%);
    border-color: #dc3545;
}

.deactivation-panel.status-completed {
    background: linear-gradient(135deg, #e6ffe6 0%, #fff 100%);
    border-color: #28a745;
}

.deactivation-panel .progress {
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

/* Gap utility for button spacing */
.gap-1 {
    gap: 0.25rem;
}

.gap-2 {
    gap: 0.5rem;
}

/* Opacity helpers */
.opacity-75 {
    opacity: 0.75;
}
.opacity-50 {
    opacity: 0.5;
}

/* Feature Toggle Cards */
.feature-toggles {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.feature-card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.2s;
}
.feature-card:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.feature-card-header {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    gap: 12px;
}
.feature-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 15px;
}
.feature-info {
    flex: 1;
    min-width: 0;
}
.feature-name {
    font-weight: 600;
    font-size: 13px;
    color: #2c3e50;
    line-height: 1.3;
}
.feature-desc {
    font-size: 11px;
    color: #95a5a6;
    line-height: 1.3;
}
.feature-sub-option {
    padding: 8px 14px 10px 62px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
}
.feature-sub-label {
    font-weight: 600;
    font-size: 12px;
    color: #2c3e50;
}
.feature-sub-desc {
    font-size: 11px;
    color: #95a5a6;
}
/* Forms onboarding checklist (under the Forms toggle) */
.feature-checklist {
    padding: 10px 14px 12px 20px;
}
.checklist-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px dashed #e9ecef;
}
.checklist-item:last-of-type {
    border-bottom: none;
}
.checklist-item > .fa {
    margin-top: 2px;
    font-size: 14px;
    width: 16px;
    text-align: center;
}
.checklist-body {
    flex: 1;
    min-width: 0;
}
.checklist-label {
    font-weight: 600;
    font-size: 12px;
    color: #2c3e50;
}
.checklist-sublist {
    margin-top: 2px;
    padding-left: 4px;
    max-height: 110px;
    overflow-y: auto;
}
.checklist-action {
    white-space: nowrap;
    font-size: 12px;
    align-self: center;
}
.checklist-checkbox {
    margin-top: 1px;
}
/* Appointment Reminders checklist: amber detail text, and the "patients with words in the cell-phone field" list. */
.checklist-detail-warn {
    color: #b9770e;
}
.junk-cells-tools {
    margin-top: 2px;
}
.junk-cells-list {
    margin-top: 4px;
    padding: 6px 8px;
    max-height: 220px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.6;
    color: #2c3e50;
    word-break: break-word;
}
.junk-cells-line-urgent {
    color: #c0392b;
    font-weight: 600;
}
.junk-cells-more {
    margin-top: 4px;
    color: #95a5a6;
}
.seed-results {
    font-size: 12px;
}
/* "Load default form messages" preview cards. NOT Bootstrap's `rounded`: the theme redefines it as a 1.5em square (_utils.scss) and the
   card collapsed to that box (dev 2026-10-23). Pinned by cypress/node-tests/seed-form-messages-modal-layout.test.js. */
.seed-fm-card {
    border-radius: 5px;
}
/* Forms card: OD imaging category per form type (the FormImagingCategoryMap child styles its own rows). */
.forms-imaging-map {
    padding: 2px 0 8px;
    margin-bottom: 2px;
    border-bottom: 1px dashed #e9ecef;
}
.apply-imaging-table {
    font-size: 12px;
}
.bulk-imaging-map {
    border: 1px solid #ddd;
    padding: 12px 15px;
    border-radius: 5px;
}
/* Bulk Wiki Copy missing-forms modal: the PDF category is required. Plain <select> → carries this scope attr. */
.imaging-select--required {
    border-color: #dc3545;
    box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.25);
}
.imaging-required-text {
    display: block;
    color: #dc3545;
    font-size: 11px;
    font-weight: 600;
}

/* Insurance Verification: inline billing-provider picker.
   A plain <select> (not b-form-select) so it inherits this component's scope attribute
   directly and needs no ::v-deep. appearance:none + an inline SVG chevron because the
   native arrow clips inside the checklist row. */
.iv-provider-picker {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}
select.iv-provider-select {
    flex: 1 1 320px;
    min-width: 0;
    max-width: 100%;
    font-size: 12px;
    padding: 4px 26px 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    color: #495057;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23495057' d='M4.5 6.5 8 10l3.5-3.5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;
    background-size: 14px 14px;
}
select.iv-provider-select:disabled {
    background-color: #e9ecef;
}
/* Provider-name override (rare): plain inputs, same look as the provider select above. */
.iv-override-toggle {
    font-size: 12px;
    color: #6c757d;
}
.iv-override-modes {
    font-size: 12px;
}
input.iv-override-input {
    flex: 1 1 180px;
    min-width: 0;
    max-width: 100%;
    font-size: 12px;
    padding: 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    color: #495057;
}
input.iv-override-input:disabled {
    background-color: #e9ecef;
}
.iv-blocked-row {
    border-top: 1px solid #f1c9c9;
    margin-top: 6px;
    padding-top: 8px;
}
/* Taller variant of slide-down — the checklist exceeds slide-down's 60px cap */
.checklist-slide-enter-active, .checklist-slide-leave-active {
    transition: all 0.25s ease;
    max-height: 700px;
    overflow: hidden;
}
.checklist-slide-enter, .checklist-slide-leave-to {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
}
.slide-down-enter-active, .slide-down-leave-active {
    transition: all 0.2s ease;
    max-height: 60px;
    overflow: hidden;
}
.slide-down-enter, .slide-down-leave-to {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
}

/* Manage button active state */
.btn-outline-primary.active {
    background-color: #007bff;
    color: #fff;
}

/* Mobile Responsiveness for Side Panel */
@media (max-width: 768px) {
    .site-management-panel {
        width: 100% !important; /* Full width on mobile */
        max-width: 100vw;
    }

    .site-management-panel .panel-header h5 {
        font-size: 1rem;
    }

    /* Smaller tabs on mobile */
    .site-management-panel .panel-tabs-nav .nav-link {
        padding: 6px 10px;
        font-size: 12px;
    }

    /* Larger touch targets on mobile */
    .site-management-panel .btn {
        padding: 10px 15px;
        font-size: 14px;
    }

    .domain-card {
        padding: 12px;
    }

    .domain-card .btn-group .btn {
        padding: 8px 12px;
    }
}

@media (max-width: 576px) {
    .site-management-panel .status-overview .row > div {
        margin-bottom: 10px;
    }

    /* Stack tabs text on very small screens */
    .site-management-panel .panel-tabs-nav .nav-link {
        padding: 6px 8px;
        font-size: 11px;
    }
}

/* ========== Twilio Usage badge styles ========== */
.twilio-usage-cell {
    line-height: 1.3;
}
.twilio-usage-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}
.twilio-usage-badge--ok {
    background: #f0fdf4;
    color: #15803d;
}
.twilio-usage-badge--warning {
    background: #fef3c7;
    color: #92400e;
}
.twilio-usage-badge--overage {
    background: #fee2e2;
    color: #b91c1c;
}

/* ========== End Site Management UI Styles ========== */

</style>

<!-- Non-scoped styles for modals (rendered outside component scope) -->
<style lang="scss">
/* Ensure all modals render above the site management panel (z-index: 1060) */
#verify-email-modal___BV_modal_outer_ {
    z-index: 1070 !important;
}
.modal {
    z-index: 1070 !important;
}
.modal-backdrop {
    z-index: 1065 !important;
}

/* Twilio site usage detail — collapsible tree */
#twilio-site-detail-modal .twilio-tree-row--clickable {
    cursor: pointer;
    user-select: none;
}
#twilio-site-detail-modal .twilio-tree-row--clickable:hover {
    background-color: #f1f5f9 !important;
}
#twilio-site-detail-modal .twilio-tree-caret {
    display: inline-block;
    width: 14px;
    color: #64748b;
    font-size: 12px;
    margin-right: 4px;
    transition: color 0.15s ease;
}
#twilio-site-detail-modal .twilio-tree-row--clickable:hover .twilio-tree-caret {
    color: #15803d;
}
#twilio-site-detail-modal .twilio-tree-caret-spacer {
    display: inline-block;
    width: 18px;
}
#twilio-site-detail-modal .twilio-view-numbers-btn {
    padding: 0 6px;
    font-size: 12px;
    font-weight: 600;
    margin-left: 12px;
    text-decoration: none;
}
#twilio-site-detail-modal .twilio-view-numbers-btn:hover {
    text-decoration: underline;
}
#twilio-site-numbers-modal .twilio-webhook-cell {
    display: inline-block;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

/* A2P Registration Wizard Modal - ensure scrollability */
#a2p-registration-wizard-modal .modal-body {
    max-height: calc(100vh - 200px) !important;
    overflow-y: auto !important;
    padding: 1rem !important;
}

/* Custom scrollbar for A2P modal */
#a2p-registration-wizard-modal .modal-body::-webkit-scrollbar {
    width: 8px;
}

#a2p-registration-wizard-modal .modal-body::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 4px;
}

#a2p-registration-wizard-modal .modal-body::-webkit-scrollbar-thumb {
    background: #d6dee5;
    border-radius: 4px;
}

#a2p-registration-wizard-modal .modal-body::-webkit-scrollbar-thumb:hover {
    background: #c1ccd3;
}

/* ========== Stripe Mapping Styles ========== */

.mapping-table-container .table {
    font-size: 0.875rem;
}

.mapping-table-container .table td {
    vertical-align: middle;
    padding: 12px 8px;
}

.mapping-table-container .table tr.table-success {
    background-color: #d4edda !important;
}

.mapping-select .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    min-height: 32px;
    padding: 2px 6px;
    font-size: 0.875rem;
}

.mapping-select .vs__selected {
    font-size: 0.875rem;
    padding: 0 2px;
    margin: 2px;
}

.mapping-select .vs__dropdown-option {
    padding: 8px 12px;
    font-size: 0.875rem;
}

.mapping-summary {
    border-left: 4px solid #007bff;
}

.mapping-summary .h4 {
    font-weight: 600;
    margin-bottom: 0;
}

/* Inactive site rows - faded appearance */
.inactive-site-row {
    opacity: 0.5;
    background-color: #f8f9fa !important;
}

.inactive-site-row:hover {
    opacity: 0.7;
}

.inactive-separator {
    background-color: #e9ecef !important;
    border-top: 2px solid #dee2e6;
}

/* Auto-match suggestion badge */
.badge-info {
    font-size: 0.7rem;
    white-space: nowrap;
}

/* Small improvements for mobile */
@media (max-width: 768px) {
    .site-management-panel {
        width: 95vw; /* Use more width on mobile */
        max-width: none;
    }
    
    .mapping-table-container {
        overflow-x: auto;
    }
    
    .mapping-table-container .table {
        min-width: 800px;
    }
    
    .mapping-select {
        min-width: 200px;
    }
}
</style>